import AdUnit from './AdUnit';
import { createAdFrequency } from './adFrequency';
/**
 * App.js — Root component of the KickViz visualizer frontend.
 *
 * Manages all top-level application state:
 * - Authentication (account, authChecked, isPremium)
 * - Teams (home/away team info + IDs, full team list from backend)
 * - Scenarios (current scenario, scenario list, current phase index, edit mode)
 * - Selection (selectedItem — the single source of truth for which player/
 *   ball/cone/target/zone is currently selected in the 3D viewport)
 * - Action mode ('none' | 'place' | 'remove') + pending object type
 * - Undo stack (max 10 snapshots of currentScenario)
 * - Play All mode (auto-cycling through phase transitions)
 * - Viewport dimensions (measured via ResizeObserver for the 3D canvas)
 *
 * Layout (V2): full-screen 3D viewport (top account bar, top-left toolbar)
 * + bottom video-editor-style timeline strip (player tracks) + master timeline.
 *
 * Key child components:
 * - Viewport3D          — the Three.js 3D pitch renderer (GUI stripped for V2)
 * - TopToolbar          — FILE / ADD PLAYER HOME / ADD PLAYER AWAY / ADD ITEM
 * - TimelineEditor      — master timeline + per-player keyframe tracks
 * - LoginScreen         — shown when no account is logged in
 * - TeamSelectionModal  — pick a team (premium)
 * - ScenarioSelectionModal — pick/create a scenario (premium)
 * - FeedbackButton      — feedback dialog opened from the Account menu
 */
import './App.css';
import Viewport3D from "./Viewport3D";
import {useEffect, useRef, useState, useLayoutEffect, useCallback} from "react";
import {createEmptyPitchState, createEmptyScenario, migrateScenario, findSegment, interpolateWithSegment, DEFAULT_SEGMENT, buildZoneVisualKeyValue, ballIsVisible, lastKeyTime, nearestKeyTime, promptKeyAction, rankActions, scaleKeyTimes, KEY_ARRAYS, rippleMoveTracks, mirrorTracks, arrowOnAt, graphicKeyAt, attachRefAt, actionKeyInForce, trackDisplayLabel, nextHeight, nextBendStyle, toastLife} from './data';
import { isPlayerTrackId, sampleEffectivePosition, markOffset, markBearing, sideOfAttachRef, markKeyAt, markTurnSeconds, markTravelSeconds, MARK_DEFAULT_DIST, MARK_DIRS } from './sampling';
import TopToolbar from "./TopToolbar";
import { planFormation, formationLabel, bandOfPlayer } from './formations';
import TimelineEditor, { ACTION_TYPES, snapKeyTime, fmtTime } from "./TimelineEditor";
import PromptModal from "./PromptModal";
import { ContextCloseBar, contextPanelPosition } from './ContextPanel';
import MarkPickerModal from "./MarkPickerModal";
import BallPanel from "./BallPanel";
import BranchNavigator from "./BranchNavigator";
import MasterTimeline from "./MasterTimeline";
import {
  createEmptyScenarioV3, migrateScenarioV3, scenarioForSave, findBranch, findBranchPath, addChild,
  removeBranch, renameBranch, setBranchOrder, moveBranchPriority, updateBranch, addSibling, findParent,
  countBranches, chosenPath, masterPlaybackStart, advanceMasterPlayback, lockedStartTime, DEFAULT_BRANCH_DURATION,
  resolveMasterPos, masterPosFor, masterTimeFor, playheadFraction, syncTreeInheritance, sortedChildren,
  branchTimelineSnapshot, pasteBranchTimeline, reconcileWorkingItems, flattenTree,
} from './branchTree';
import SegmentModal from "./SegmentModal";
import { resolvePasteTarget, applyPasteToStart, duplicateItemInStart, shiftRoute, DUPLICATE_OFFSET } from './itemClipboard';
import LoginScreen from "./LoginScreen";
import TeamSelectionModal from "./TeamSelectionModal";
import ScenarioSelectionModal from "./ScenarioSelectionModal";
import ConfirmModal from "./ConfirmModal";
import NameModal from "./NameModal";
import PublicScenarioModal from "./PublicScenarioModal";
import AccountModal from "./AccountModal";
import TeamEditorModal from "./TeamEditorModal";
import HelpModal from "./HelpModal";
import InfoModal from "./InfoModal";
import {
  authAPI,
  accountsAPI,
  scenariosAPI,
  stripeAPI,
  teamsAPI,
  configAPI, dbScenarioToMenuItem,
  dbTeamToTeamInfo,
  getToken,
  clearToken,
  HELP_URL,
} from './api';
import FeedbackButton from './FeedbackButton';
import { getRuntimeAdapter, isMobileBuild, macChords } from './platform';
// i18n: LanguageProvider wraps the whole app — exposes t() / lang / setLang
import { LanguageProvider, useI18n } from './i18n';

// ═══════════════════════════════════════════════════════════════════
// Constants & Helper Functions
// ═══════════════════════════════════════════════════════════════════

/** Height of the master timeline bar, in px. The 3D viewport sits above it, so
 *  this is added to the viewport's bottom offset whenever the bar is shown. */
const MASTER_H = 28;

/**
 * How many steps back Ctrl+Z reaches. Snapshots are whole-scene clones, but a
 * scene is small (tens of tracks, a few keys each), so 60 costs well under a
 * megabyte — and 20 was not: a session of building a drill (placement chains,
 * group nudges, key edits) blew past it and the OLDEST work became
 * unrecoverable with no warning at all. The Undo button now says how far back
 * it reaches, and says so when this limit is what stops you.
 */
const UNDO_LIMIT = 60;

// ── Drag group membership (Shift+click) ──
// Players, cones, targets and zones can join a move-together group; balls are
// excluded because their drop runs the attach/pass flow instead of a move.
const GROUPABLE_TYPES = ['player', 'cone', 'target', 'zone'];
/** Store form of a selectable item as a group member. */
const groupMemberOf = (it) => (it.type === 'player'
  ? { type: 'player', side: it.side, index: it.index }
  : { type: it.type, id: it.id });
/** Does group member g refer to selectable item it? */
const isSameGroupMember = (g, it) => (g.type === 'player'
  ? it.type === 'player' && g.side === it.side && g.index === it.index
  : g.type === it.type && g.id === it.id);
/**
 * The count-aware form of a message key. The dictionary already pairs `…Many` with
 * `…One` (arrowsOnOne/Many, alreadyActionOne/Many); this is the same rule in ONE
 * place, so a count-bearing sentence cannot be written once in the plural and once
 * in the singular by accident. "Lifted 1 items" and "1 follow a carrier" were the
 * cost of not having it.
 */
const pluralKey = (base, n) => (n === 1 ? `${base}One` : base);
/**
 * The LANE a group member's keys live on. Players are spelled by side and index
 * ('home:3'), every other member by type and id ('cone:cone-07'). One spelling,
 * because three doors now hand a member to the action prompt — the pitch tap, the
 * strip's action button, and a verb that re-opens the card after it has changed
 * the selection — and a card opened on a lane nobody can find is a card that
 * speaks for nothing.
 */
const groupTrackId = (g) => (g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`);
/** A member spelled the way handleActionPrompt reads a target: a player needs its
 *  side and index, not just a track id, or the card loses the squad name. */
const promptTargetOf = (g) => (g.type === 'player'
  ? { trackId: `${g.side}:${g.index}`, side: g.side, index: g.index }
  : { trackId: `${g.type}:${g.id}` });

/**
 * Field-object placement buttons (Ball / Cone / Target / Zone) shown in the
 * top toolbar's ADD ITEM menu. Each entry defines a button with its icon,
 * background colour, and the placement mode string passed to `onModeChange`.
 */
export const fieldObjectButtons = [
    { name: "Ball",   icon: "/icons/ball.svg",   background: "#2d9e8e", color: "#ffffff", mode: 'ball' },
    { name: "Cone",   icon: "/icons/cone.svg",   background: "#1e293b", color: "#ffffff", mode: 'cone' },
    { name: "Target", icon: "/icons/target.svg", background: "#800000", color: "#ffffff", mode: 'target' },
    { name: "Zone",   icon: "/icons/zone.svg",   background: "#6d28d9", color: "#ffffff", mode: 'zone' },
];

/**
 * Update a pitch state's `start` for object placement.
 *
 * - Ball: only moved in the current phase (it always exists).
 * - Cone/Target: added to the start state with a unique id.
 * - Zone: added as a square polygon (4 corner points) centred on the click.
 *
 * @param {Object}  start           - The pitch state to update.
 * @param {string}  type            - Object type: 'ball', 'cone', 'target', or 'zone'.
 * @param {number[]} coords         - [x, y, z] placement coordinates.
 * @param {boolean} isCurrentPhase  - Whether this is the currently active phase (affects ball).
 * @returns {Object} A new pitch state with the object added/moved.
 */
/**
 * Next sequential, human-friendly item id for a type, e.g. 'ball-01', 'cone-02'.
 * Scans every phase's items of that type for the highest numeric suffix, then +1.
 * Falls back to a timestamp-based id when no numeric suffix exists yet (legacy data).
 */
function nextItemId(type, phases) {
  const items = [];
  (phases || []).forEach(ph => {
    const s = ph.start || {};
    if (type === 'ball') (s.balls || []).forEach(b => items.push(String(b.id)));
    else if (type === 'cone') (s.cones || []).forEach(c => items.push(String(c.id)));
    else if (type === 'target') (s.targets || []).forEach(t => items.push(String(t.id)));
    else if (type === 'zone' || type.startsWith('zone')) (s.zones || []).forEach(z => items.push(String(z.id)));
  });
  let max = 0;
  items.forEach(id => {
    const m = id.match(/(\d+)$/);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  });
  const next = max + 1;
  return `${type}-${String(next).padStart(2, '0')}`;
}

function updatePhaseStartForObjectPlace(start, type, coords, isCurrentPhase, itemId) {
    if (type === 'ball') {
        const id = itemId || `ball-${Date.now()}`;
        // Ball added to ALL phases at the same position
        const existingBall = (start.balls || []).find(b => b.id === id);
        if (existingBall) return start; // Don't add duplicate
        return { ...start, balls: [...(start.balls || []), { id, color: '#ffffff', position: coords, attached: null }] };
    }
    if (type === 'cone') {
        const id = itemId || `cone-${Date.now()}`;
        return { ...start, cones: [...(start.cones || []), { id, color: '#ffcc00', position: coords }] };
    }
    if (type === 'target') {
        const id = itemId || `target-${Date.now()}`;
        return { ...start, targets: [...(start.targets || []), { id, color: '#ff0000', position: coords }] };
    }
    // Zones — three flavours: 'zone-static' (polygon), 'zone-circle' (radius),
    // 'zone-dynamic' (N vertices, each optionally attached to a scene object).
    // The legacy type 'zone' maps to a static polygon.
    if (type === 'zone' || type.startsWith('zone')) {
        const id = itemId || `zone-${Date.now()}`;
        const cx = coords[0], cz = coords[2];
        const zoneMode = type.startsWith('zone-') ? type.slice(5) : 'static';
        const base = { id, color: '#ffeb3b', opacity: 0.35, position: coords, mode: zoneMode, closed: true, thickness: 2 };
        if (zoneMode === 'circle') {
            // Circle zone: radius around the centre position — no vertices needed
            return { ...start, zones: [...(start.zones || []), { ...base, radius: 3, points: [] }] };
        }
        if (zoneMode === 'dynamic') {
            // Dynamic zone: N vertices around the centre, each linkable to a scene object
            const n = 5;
            const points = [];
            for (let i = 0; i < n; i++) {
                const a = (i / n) * Math.PI * 2;
                points.push([cx + Math.cos(a) * 3, 0, cz + Math.sin(a) * 3]);
            }
            return { ...start, zones: [...(start.zones || []), { ...base, points, pointRefs: points.map(() => ({ ref: null })) }] };
        }
        // Static polygon zone: square centred on the click (as before)
        const s = 3; // half-size of initial square
        const points = [
            [cx - s, 0, cz - s],
            [cx + s, 0, cz - s],
            [cx + s, 0, cz + s],
            [cx - s, 0, cz + s],
        ];
        return { ...start, zones: [...(start.zones || []), { ...base, points, pointRefs: [] }] };
    }
    return start;
}

// ═══════════════════════════════════════════════════════════════════
// App Component
// ═══════════════════════════════════════════════════════════════════

/**
 * App — the root React component for KickViz.
 *
 * Holds all application state and wires together the 3D viewport, team menus,
 * field-object toolbar, scenario management bar, and modals. See the file-level
 * header for a full overview of the state managed here.
 *
 * @returns {JSX.Element} The full application UI.
 */
function App() {
  // ── i18n — translate the UI (t, lang, setLang from the provider) ──
  const { t, lang, setLang } = useI18n();
  // ── Refs ──────────────────────────────────────────
  const refViewportDiv = useRef(null);
  const refResizeQueued = useRef(false);

  // ── Auth & account state ──────────────────────────
  const [account, setAccount] = useState(null);
  // Ad config served by the backend (GOOGLE_ADS_* env vars). null = no ads.
  const [adsConfig, setAdsConfig] = useState(null);
  // What Premium costs, from the same response. The visualizer has never shown
  // a price — it hands off to Stripe Checkout, where the number appears — so a
  // coach taps "Upgrade" without knowing what they are about to pay. The price
  // lives in the backend's config/env.js and arrives here; no component in this
  // app is allowed to type a number.
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    // Guests have no session, so fetch the ad config independently.
    configAPI.get().then((d) => { setAdsConfig(d.ads || null); setPricing(d.pricing || null); }).catch(() => {});
  }, []);
  const [authChecked, setAuthChecked] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const billingWindowPending = useRef(false);
  const isPremium = account?.tier === 'premium';
  const [stateWidthViewport3D, setStateWidthViewport3D] = useState(0);
  const [stateHeightViewport3D, setStateHeightViewport3D] = useState(0);

  // ── Action mode (placement / look / remove) ──────
  const [actionMode, setActionMode] = useState('none');
  const [pendingObjectType, setPendingObjectType] = useState(null);

  // ── Teams & scenarios (from backend) ─────────────
  const [teams, setTeams] = useState([]);
  const [scenarios, setScenarios] = useState([]);

  // ── Error overlay (shown when backend is unreachable) ──
  const [loadError, setLoadError] = useState(null);

  // ── Home / away team state ───────────────────────
  // Start as null — fetched from backend /api/teams/defaults on mount.
  // This ensures the frontend never uses stale hardcoded data.
  const [homeTeam, setHomeTeam] = useState(null);
  const [awayTeam, setAwayTeam] = useState(null);
  const [homeTeamId, setHomeTeamId] = useState(null);
  const [awayTeamId, setAwayTeamId] = useState(null);

  // ── SINGLE SOURCE OF TRUTH: selectedItem ──
  // { type: 'player', side, index } | { type: 'ball', id } | { type: 'cone', id } | { type: 'target', id } | null
  const [selectedItem, _setSelectedItem] = useState(null);
  // Reusable central prompt (shown just above the timeline) + the item track
  // id currently in player-pick mode (e.g. 'ball:ball-02' while attaching).
  const [promptState, _setPromptState] = useState(null);
  const refPromptState = useRef(null);
  useEffect(() => { refPromptState.current = promptState; }, [promptState]);
  // A card that re-opens AFTER a verb has changed the scene has to wait for the
  // scene. The name of a cone placed on this tick is not in the list yet, so a
  // header written right now would read "Cone 01" over the seventh cone — and a
  // header that lies is worse than a card that closed. The verb queues the target
  // here; the effect that runs after handleActionPrompt opens it on the render
  // that already knows about the new item. The card on screen is not cleared in
  // the meantime, so there is no flicker — one card becomes another.
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [attachPickMode, setAttachPickMode] = useState(null);
  // MARK PLAYER compass: { trackId, value, time, dir, dist } while the coach
  // picks which of the eight spots around the marked player to stand at.
  const [markPicker, setMarkPicker] = useState(null);
  // The dial's dir/dist, mirrored back up as they are tapped so the pitch can
  // show a GHOST at the spot the marker will take. The compass picks a bearing
  // and a gap — two numbers on a dial — and until now neither was a place you
  // could see. The modal owns its own state; this copy exists only to draw, and
  // it is written on every change and on open, so it cannot fall behind.
  // The dial keeps one door back to the pitch: "mark a different opponent".
  // Held in a ref because the action is built inside handleMarkPrompt, and the
  // JSX that renders the dial cannot close over it.
  const refMarkRepick = useRef(null);
  const refMarkRelease = useRef(null);
  // Segment transition editor: { trackId, from, to } of the segment being edited.
  const [segmentEdit, _setSegmentEdit] = useState(null);
  // Keyframe height editor: { trackId, time } — a clicked position keyframe
  // whose arrival height (position[1]) the user can adjust with a slider.
  const [keyframeEdit, _setKeyframeEdit] = useState(null);
  // Arrow-colour editor for a graphic window: { trackId, start, end } (between an ON key and the next key).
  const [graphicEdit, _setGraphicEdit] = useState(null);
  // Zone-visuals editor for a visual window: { trackId, keyIndex } — the window
  // starting at visual key `keyIndex` (radius / shape / vertex attachments).
  const [zoneVisualEdit, _setZoneVisualEdit] = useState(null);
  // Refs mirroring the four editor states, so handlers with empty deps (like
  // handleKeyframeDelete) can close editors anchored on data they are about
  // to destroy — a stale anchor would RE-CREATE it on Apply.
  const refSegmentEdit = useRef(null);
  const refKeyframeEdit = useRef(null);
  const refGraphicEdit = useRef(null);
  const refZoneVisualEdit = useRef(null);
  useEffect(() => {
    refSegmentEdit.current = segmentEdit;
    refKeyframeEdit.current = keyframeEdit;
    refGraphicEdit.current = graphicEdit;
    refZoneVisualEdit.current = zoneVisualEdit;
  }, [segmentEdit, keyframeEdit, graphicEdit, zoneVisualEdit]);
  // Opening a context panel dismisses the previous panel without losing selection.
  const [ballPanelOpen, setBallPanelOpen] = useState(false);
  const [captureOpen, _setCaptureOpen] = useState(false);
  // Bumped by P: Viewport3D takes a screenshot (the capture panel's verb as
  // one key — camera button → panel → Screenshot collapses 3 clicks to 1).
  const [screenshotTick, setScreenshotTick] = useState(0);
  // Bumped by ⇧P: CaptureControl toggles the video recording session.
  const [recordToggleTick, setRecordToggleTick] = useState(0);
  const setPromptState = useCallback((value) => {
    if (value) {
      _setCaptureOpen(false);
      setBallPanelOpen(false);
      _setSegmentEdit(null);
      _setKeyframeEdit(null);
      _setGraphicEdit(null);
      _setZoneVisualEdit(null);
    }
    _setPromptState(value);
  }, []);
  const setSegmentEdit = useCallback((value) => {
    if (value) {
      _setCaptureOpen(false);
      setBallPanelOpen(false);
      _setPromptState(null);
      _setKeyframeEdit(null);
      _setGraphicEdit(null);
      _setZoneVisualEdit(null);
      setAttachPickMode(null);
    }
    _setSegmentEdit(value);
  }, []);
  const setKeyframeEdit = useCallback((value) => {
    if (value) {
      _setCaptureOpen(false);
      setBallPanelOpen(false);
      _setPromptState(null);
      _setSegmentEdit(null);
      _setGraphicEdit(null);
      _setZoneVisualEdit(null);
      setAttachPickMode(null);
    }
    _setKeyframeEdit(value);
  }, []);
  const setGraphicEdit = useCallback((value) => {
    if (value) {
      _setCaptureOpen(false);
      setBallPanelOpen(false);
      _setPromptState(null);
      _setSegmentEdit(null);
      _setKeyframeEdit(null);
      _setZoneVisualEdit(null);
      setAttachPickMode(null);
    }
    _setGraphicEdit(value);
  }, []);
  const setZoneVisualEdit = useCallback((value) => {
    if (value) {
      _setCaptureOpen(false);
      setBallPanelOpen(false);
      _setPromptState(null);
      _setSegmentEdit(null);
      _setKeyframeEdit(null);
      _setGraphicEdit(null);
      setAttachPickMode(null);
    }
    _setZoneVisualEdit(value);
  }, []);
  const refSelectedItem = useRef(null); // mirror for snapshots: undo restores WHAT you were working on
  const setSelectedItem = useCallback((item) => {
    refSelectedItem.current = item || null;
    if (item) {
      _setCaptureOpen(false);
      _setPromptState(null);
      _setSegmentEdit(null);
      _setKeyframeEdit(null);
      _setGraphicEdit(null);
      _setZoneVisualEdit(null);
      setAttachPickMode(null);
    }
    setBallPanelOpen(item?.type === 'ball');
    _setSelectedItem(item);
  }, []);
  const setCaptureOpen = useCallback((open) => {
    if (open) {
      setBallPanelOpen(false);
      _setPromptState(null); _setSegmentEdit(null); _setKeyframeEdit(null);
      _setGraphicEdit(null); _setZoneVisualEdit(null); setAttachPickMode(null);
    }
    _setCaptureOpen(open);
  }, []);
  // Persistent edit binding: the window whose key shape edits write to. Stays
  // set while the editor modal is open AND while polygon edit mode is active
  // (the modal is closed during edit, but edits must still hit the key).
  const [zoneVisualTarget, setZoneVisualTarget] = useState(null);
  // One-shot requests sent to Viewport3D from the zone-visuals editor:
  // enter polygon edit mode / start linking a vertex to a scene object.
  const [zoneEditRequest, setZoneEditRequest] = useState(null); // { zoneId, ts }
  const [zoneLinkRequest, setZoneLinkRequest] = useState(null); // { zoneId, pointIndex, ts }
  // The zone-visuals editor auto-dismisses whenever the selection changes —
  // it always describes the currently selected zone's window. The edit
  // binding clears too (edits must not leak into the old zone's key).
  const selectedZoneTrackId = selectedItem?.type === 'zone' ? `zone:${selectedItem.id}` : null;
  useEffect(() => {
    // Re-selecting the same zone (including double-click and drag) must not
    // dismiss the editor or detach shape edits from their visual key.
    setZoneVisualEdit(prev => prev?.trackId === selectedZoneTrackId ? prev : null);
    setZoneVisualTarget(prev => prev?.trackId === selectedZoneTrackId ? prev : null);
  }, [selectedZoneTrackId]);
  const [clipboard, setClipboard] = useState(null); // { type, side?, index?, id?, data } — copied item data
  const [toast, setToast] = useState(null); // { message, id } — transient toast notification
  const [defaultScenarioNotice, setDefaultScenarioNotice] = useState(null);
  // Keep only useful notices transient, so they never cover the pitch or tree.
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(cur => (cur && cur.id === toast.id ? null : cur)),
      toastLife(toast.message));
    return () => clearTimeout(id);
  }, [toast]);

  // ── Scenario & layout state ───────────────────────
  // V1-LEGACY: the multi-phase system is retired. Every scenario keeps a
  // single layout phase (index 0) whose `.start` mirrors the items for the
  // scene build + backwards-compatible exports; the live state is the V2
  // timeline (`keyframes`). Old multi-phase documents still load — only
  // phase 0 is used.
  const currentPhaseIndex = 0;
  const [currentScenario, _setCurrentScenario] = useState(null);
  const [teamModalTarget, setTeamModalTarget] = useState(null);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  // One in-app yes/no for the things undo cannot reach (a saved scenario, a
  // team). window.confirm() left the workspace to ask — a system strip, no
  // translation of the consequence, and the least tappable control on a phone.
  const [confirmState, setConfirmState] = useState(null);
  // …and the app's own text entry for the two names it needs (Save a copy,
  // Rename scenario) — the last moments that used a browser prompt().
  const [nameState, setNameState] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showTeamEditor, setShowTeamEditor] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  // One-shot camera preset requests for Viewport3D (keys 1/2/3). The counter
  // makes repeated presses of the same preset fire again.
  const [cameraRequest, setCameraRequest] = useState(null); // { kind, n }
  const [showPublicModal, setShowPublicModal] = useState(false);
  const [scenarioAd, setScenarioAd] = useState(null);
  const adFrequency = useRef(null);
  if (!adFrequency.current) { let storage; try { storage = window.localStorage; } catch {} adFrequency.current = createAdFrequency({ storage }); }
  const [infoModal, setInfoModal] = useState(null); // { title, message, type } for InfoModal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // ── V2 timeline system ──────────────────────────────
  const [timelineDuration, _setTimelineDuration] = useState(DEFAULT_BRANCH_DURATION); // master timeline length (seconds)
  // Bottom editor strip height (min 128).
  // Starts SHORTER on short screens (landscape phones/tablets) so the 3D
  // viewport keeps more height — the strip is user-resizable anyway.
  const [requestedStripHeight, setStripHeight] = useState(() => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
    return vh < 500 ? 128 : vh < 700 ? 160 : 200;
  });
  const [screenSize, setScreenSize] = useState(() => ({ width: window.innerWidth, height: window.visualViewport?.height || window.innerHeight }));
  useEffect(() => {
    const update = () => setScreenSize({ width: window.innerWidth, height: window.visualViewport?.height || window.innerHeight });
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => { window.removeEventListener('resize', update);window.visualViewport?.removeEventListener('resize', update); };
  }, []);
  // Keep the pitch visible after rotating a phone, without losing the user's
  // preferred strip height when they rotate back or return to desktop.
  const stripHeight = screenSize.width <= 1024
    ? Math.min(requestedStripHeight, Math.max(100, Math.floor(screenSize.height * (screenSize.height < 500 ? .38 : .5))))
    : requestedStripHeight;
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackSpeedRef = useRef(1);
  const changePlaybackSpeed = useCallback((speed) => {
    if (![0.5, 1, 2, 4].includes(speed)) return;
    playbackSpeedRef.current = speed;
    setPlaybackSpeed(speed);
  }, []);
  /** +/- keys: step the shared playback speed through the menu's presets.
   *  Returns the new speed, or null when already at the end of the range. */
  const cyclePlaybackSpeed = useCallback((dir) => {
    const steps = [0.5, 1, 2, 4];
    const i = steps.indexOf(playbackSpeedRef.current);
    const next = steps[Math.max(0, Math.min(steps.length - 1, (i < 0 ? 1 : i) + dir))];
    if (next === playbackSpeedRef.current) return null;
    changePlaybackSpeed(next);
    return next;
  }, [changePlaybackSpeed]);
  /** The bubble's × chip: cycle 0.5→1→2→4→0.5 — WRAPS, because the chip is
   *  the mouse entry point and dead-ending at 4× would trap the click. */
  const handleSpeedCycle = useCallback(() => {
    const steps = [0.5, 1, 2, 4];
    const i = steps.indexOf(playbackSpeedRef.current);
    const next = steps[((i < 0 ? 1 : i) + 1) % steps.length];
    changePlaybackSpeed(next);
  }, [changePlaybackSpeed]);
  const [playheadTime, setPlayheadTime] = useState(0);          // current playback time (seconds)
  const refPlayheadTime = useRef(0); // always-current playhead for prompt/key callbacks
  // V2 timeline playback: play/pause the master timeline (advances the playhead).
  // V3: during a MASTER PLAY the end of a branch's timeline hands over to the
  // next branch in the tree walk (top-priority child) and keeps playing.
  // The handover goes through `refBranchOps` (assigned once the branch helpers
  // further down exist) because this effect is defined before them.
  const refBranchOps = useRef({ committedTree: null, openBranchInEditor: null });
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => { if (!isPlaying) refPlayheadTime.current = playheadTime; }, [playheadTime, isPlaying]);
  // Playback should never be covered by an edit notification left on screen.
  useEffect(() => { if (isPlaying) setToast(null); }, [isPlaying]);
  useEffect(() => {
    if (!isPlaying) return;
    let raf;
    let last = performance.now();
    let lastUi = 0;
    const tick = (now) => {
      const dt = (now - last) / 1000 * playbackSpeedRef.current;
      last = now;
      const mp = refMasterPlay.current;
      if (mp?.active) {
        const next = advanceMasterPlayback(mp.branches, mp.index, refPlayheadTime.current, dt, mp.stopAtNextBranch);
        if (next.index !== mp.index) {
          const branch = mp.branches[next.index];
          const duration = branch.timelineDuration ?? DEFAULT_BRANCH_DURATION;
          refBranchOps.current.openBranchInEditor(branch, null, true,
            duration > 0 ? next.time / duration : 0, true);
          const walk = { ...mp, index: next.index };
          refMasterPlay.current = walk;
          setMasterPlay(walk);
        }
        refPlayheadTime.current = next.time;
        if (next.ended || next.index !== mp.index || now - lastUi >= 33) {
          lastUi = now;
          setPlayheadTime(next.time);
        }
        if (next.ended) {
          refMasterPlay.current = null;
          setMasterPlay(null);
          setIsPlaying(false);
          return;
        }
      } else {
        const duration = refTimelineDurationState.current;
        const next = Math.min(refPlayheadTime.current + dt, duration);
        refPlayheadTime.current = next;
        if (next >= duration || now - lastUi >= 33) {
          lastUi = now;
          setPlayheadTime(next);
        }
        if (next >= duration) { setIsPlaying(false); return; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);
  // V2 keyframes — track list. Each track: { id, type, side?, index?, position: [{ time, position }] }
  // "position" is the movement track; keyframe times snap to the 0.25s grid.
  // NOTE: `setKeyframesRaw` is the raw setter — ALL normal edits must go through
  // the `setKeyframes` wrapper below so the undo/redo system snapshots first.
  const [keyframes, _setKeyframesRaw] = useState([]);
  // Auto-key: when ON, moving anything records a position keyframe at the rounded playhead second.
  const [autoKeyOn, setAutoKeyOn] = useState(true);
  // NOTE: `playAllActive` / `animSpeed` / `phaseDuration` (V1 Play-All + speed
  // controls) were removed — the V2 timeline Play is the only playback path.

  // ── Undo/Redo infrastructure (max 20 steps each) ────────────────────────
  // The undo system snapshots the COMPLETE scene state — scenario (phases),
  // timeline tracks (keyframes), timeline length and the current phase — so
  // every state change a scene can go through is undoable: item placement /
  // movement / removal, keyframe add / move / delete, attachments, zone
  // visual windows, arrow colours, timeline length, player actions, etc.
  const refUndoStack = useRef([]);
  const refRedoStack = useRef([]);
  // Human names for each undo step, kept parallel to the snapshot stacks so
  // Ctrl+Z / Ctrl+Y can say WHAT they just reverted.
  const refUndoLabels = useRef([]);
  const refRedoLabels = useRef([]);
  const refCurrentScenario = useRef(null);
  const refKeyframesState = useRef([]);
  // Which branch the two refs above describe (see foldWorking).
  const refWorkingBranchId = useRef('root');
  const refTimelineDurationState = useRef(DEFAULT_BRANCH_DURATION);

  /**
   * Setters that update their ref in the SAME tick as the state.
   *
   * The commit path reads these refs (foldWorking / snapshotState), and every
   * branch switch commits synchronously — including the one that happens right
   * after opening a branch. While the refs were synced by an effect (i.e. only
   * after the next render), a commit landing in that window wrote the PREVIOUS
   * branch's timeline into the branch just opened — replacing its own keys and
   * start state with another branch's — and an edit made just before a switch
   * could miss its commit and be lost. All writes go through these setters, so
   * the refs can never lag behind the state they describe.
   */
  const setCurrentScenario = useCallback((value) => {
    const next = typeof value === 'function' ? value(refCurrentScenario.current) : value;
    refCurrentScenario.current = next;
    _setCurrentScenario(next);
  }, []);
  const setKeyframesRaw = useCallback((value) => {
    const next = typeof value === 'function' ? value(refKeyframesState.current) : value;
    refKeyframesState.current = next;
    _setKeyframesRaw(next);
  }, []);
  const setTimelineDuration = useCallback((value) => {
    const next = typeof value === 'function' ? value(refTimelineDurationState.current) : value;
    refTimelineDurationState.current = next;
    _setTimelineDuration(next);
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // V3 SCENARIO TREE
  // The scenario is a tree of branches. Every branch owns a complete timeline
  // (phases + keyframes + timelineDuration) — exactly the working state below —
  // so the timeline editor edits one branch at a time:
  //   • scenarioTree      — the whole tree (source of truth for saving)
  //   • currentBranchId   — the SELECTED branch. It is the branch the timeline
  //                         shows and plays, and the one the tree highlights.
  //                         It is deliberately NOT part of the saved scenario —
  //                         the "default" (priority 1) is what persists and what
  //                         governs where a timeline continues; the selection is
  //                         just where the user happens to be looking right now.
  //   • viewMode          — 'branches' (tree) or 'timeline' (editing a branch)
  // Switching branches commits the working state back into its branch first,
  // so nothing is ever lost by navigating.
  // ═══════════════════════════════════════════════════════════════════
  const [scenarioTree, _setScenarioTree] = useState(() => createEmptyScenarioV3().tree);
  const refScenarioTree = useRef(scenarioTree);
  // Same-tick ref (see the setters above) — the tree is read by the commit path.
  const setScenarioTree = useCallback((value) => {
    const next = typeof value === 'function' ? value(refScenarioTree.current) : value;
    refScenarioTree.current = next;
    _setScenarioTree(next);
  }, []);
  // The branch timeline currently on the clipboard ({ id, name, phases,
  // keyframes, timelineDuration }). Session-only, like the branch picks — a copy
  // is a working step, not part of the scenario, so it is never saved.
  const [branchClipboard, setBranchClipboard] = useState(null);
  const [currentBranchId, setCurrentBranchIdRaw] = useState('root');
  const refCurrentBranchId = useRef('root');
  // Same-tick ref (see the setters above): the snapshot/restore paths read this,
  // and a branch switch must be visible to them immediately.
  const setCurrentBranchId = useCallback((value) => {
    const next = typeof value === 'function' ? value(refCurrentBranchId.current) : value;
    refCurrentBranchId.current = next;
    setCurrentBranchIdRaw(next);
  }, []);
  // The branch the working state belongs to (committed when we switch away).
  const refOpenBranchId = useRef('root');
  // After B lands you IN the new branch, the next B must still fan from the
  // ORIGINAL parent — otherwise B B B chains. Cleared by any explicit branch
  // selection (handleSelectBranch), which re-anchors B to the chosen branch.
  const refBranchParentForB = useRef(null);
  // Last object type placed on the pitch (cone/target/zone…) — Shift+9
  // re-arms placement of this type without remembering which key armed it.
  const refLastPlaceType = useRef(null);
  // The user's picks, one per sibling set: { parentBranchId: chosenBranchId }.
  // A pick is made by selecting a branch, is NOT saved with the scenario, and
  // only ever supplies the path where the user has actually chosen — every set
  // they have not touched keeps playing its default (priority 1). Kept in a ref
  // alongside the state so the commit/playback paths read it in the same tick.
  const [branchChoices, setBranchChoicesState] = useState({});
  const refBranchChoices = useRef({});
  const setBranchChoices = useCallback((value) => {
    const next = typeof value === 'function' ? value(refBranchChoices.current) : value;
    refBranchChoices.current = next;
    setBranchChoicesState(next);
  }, []);

  // ── Drag group (Shift+click on players / cones / targets / zones) ──
  // Members move together: holding any one of them applies the same delta to
  // all — a formation shift or a whole drill shape in one gesture (Viewport3D
  // runs the drag, each member gets its own auto key). Balls are excluded:
  // their drop runs the attach/pass flow. Plain selection of a NON-member or
  // empty space clears the group; selecting a member keeps it.
  const [groupItems, setGroupItems] = useState([]); // {type:'player',side,index} | {type,id}
  const handleSelectItem = useCallback((item, shift = false) => {
    const groupable = item && GROUPABLE_TYPES.includes(item.type);
    if (shift && groupable) {
      const leaving = groupItems.some(g => isSameGroupMember(g, item));
      // First Shift+click seeds the group with the current selection (mirrors
      // the updater below) — needed here to announce the true size.
      const seeding = groupItems.length === 0 && !leaving && selectedItem
        && GROUPABLE_TYPES.includes(selectedItem.type) && !isSameGroupMember(selectedItem, item);
      const nextSize = leaving ? groupItems.length - 1 : groupItems.length + 1 + (seeding ? 1 : 0);
      setGroupItems(prev => {
        let base = prev;
        // First Shift+click seeds the group with the current selection, so
        // "click #6, Shift+click #8" groups both — not just #8.
        if (prev.length === 0 && selectedItem && GROUPABLE_TYPES.includes(selectedItem.type)
            && !isSameGroupMember(selectedItem, item)) {
          base = [groupMemberOf(selectedItem)];
        }
        return base.some(g => isSameGroupMember(g, item))
          ? base.filter(g => !isSameGroupMember(g, item))
          : [...base, groupMemberOf(item)];
      });
      // Confirm the toggle out loud — essential on touch, where the long-press
      // gesture has no hover feedback. (getItemDisplayName is defined below
      // this callback; safe via closure, deliberately not in deps.)
      setToast({ message: t(leaving ? 'toast.groupLeft' : 'toast.groupJoined', { name: getItemDisplayName(item) })
        + (leaving ? ''
          : nextSize >= 2 ? t('toast.groupMany', { n: nextSize })
            // A lone group member is a dead end on touch, so name the next rung
            // of the ladder instead: long-pressing it AGAIN opens its actions
            // (double-click on a mouse). Both paths are named because both work.
            : t('toast.groupOne')), id: Date.now() });
      setSelectedItem(item);
      return;
    }
    if (shift && !item) return; // Shift+click on open pitch keeps the group
    if (shift && !groupable) {
      // ⇧ is the GROUP modifier — Shift+clicking a non-groupable item (only
      // balls qualify) must NOT silently destroy the group the user built.
      // Select the ball, keep the group, and say why it didn't join.
      setSelectedItem(item);
      setToast({ message: t('toast.ballNoGroup', { n: groupItems.length }), id: Date.now() });
      return;
    }
    if (!groupable || !groupItems.some(g => isSameGroupMember(g, item))) {
      setGroupItems([]);
    }
    setSelectedItem(item);
  }, [groupItems, selectedItem, setSelectedItem, t]);
  // Branch switch drops the group — tracks and positions differ per branch.
  useEffect(() => { setGroupItems([]); }, [currentBranchId]);
  const [viewMode, setViewMode] = useState('timeline');
  // Bumped by Shift+R: BranchNavigator focuses the selected branch's name
  // input so a branch can be renamed without ever touching the card with a mouse.
  const [renameNonce, setRenameNonce] = useState(0);
  // Which polygon-zone vertex the pitch editor currently has grabbed
  // (null = none). While set, Ctrl+←→/↑↓ nudge THAT vertex instead of dialing
  // radius/height — the vertex-drag's keyboard twin.
  const refZonePointSelected = useRef(null);
  const panelHeight = stripHeight + (viewMode === 'branches' ? 0 : 30);
  // Master play-through: { active: true, ids: [...branchIds in play order], index }.
  const [masterPlay, setMasterPlay] = useState(null);
  const refMasterPlay = useRef(null);
  useEffect(() => { refMasterPlay.current = masterPlay; }, [masterPlay]);
  // The active playback scope drives the two separate play/pause controls.
  const masterPlayActive = !!masterPlay?.active;
  const refDragging = useRef(false);      // suppresses per-move snapshots during a drag
  const refKeyBlockToastAt = useRef(0);   // throttles the "occupied instant" drag refusal
  const refLastAction = useRef(null);     // { value, label } — ⇧X repeats it on the next item
  const refLastUndoAt = useRef(0);        // coalescing window for continuous inputs (sliders)
  const [undoCount, setUndoCount] = useState(0);
  // Top-of-stack labels: the Undo/Redo BUTTON tooltips name the actual change
  // ("Undo: move key in time"), not just the generic verb — same self-teaching
  // trick as the undo toast, applied before you click instead of after.
  const [undoTopLabel, setUndoTopLabel] = useState(null);
  const [redoTopLabel, setRedoTopLabel] = useState(null);
  const [redoCount, setRedoCount] = useState(0);

  /**
   * Capture the complete scene — the WHOLE branch tree plus the timeline of the
   * branch being edited.
   *
   * A snapshot is taken just before a change. Since the open branch's edits only
   * reach the tree when they are folded in (on the next commit), the tree alone
   * would be a step behind: the working state is captured alongside it, together
   * with the ids that say WHICH branch it describes. Restoring the pair puts the
   * scene back exactly as it was — and because the tree travels with it, undoing
   * a structural change (add / remove / rename / reprioritise a branch) works the
   * same way as undoing a movement key, and it can never bring back a timeline
   * belonging to the wrong branch.
   *
   * The user's branch PICKS are deliberately not part of this: they are session
   * state, not scene state, so undoing an edit does not silently re-route the
   * master path.
   */
  const snapshotState = useCallback(() => ({
    tree: refScenarioTree.current ? JSON.parse(JSON.stringify(refScenarioTree.current)) : null,
    scenario: refCurrentScenario.current ? JSON.parse(JSON.stringify(refCurrentScenario.current)) : null,
    keyframes: JSON.parse(JSON.stringify(refKeyframesState.current || [])),
    timelineDuration: refTimelineDurationState.current ?? DEFAULT_BRANCH_DURATION,
    openBranchId: refOpenBranchId.current,
    workingBranchId: refWorkingBranchId.current,
    selection: refSelectedItem.current ? { ...refSelectedItem.current } : null,
  }), []);

  /**
   * ONE-SHOT verb label for the setKeyframes auto-push. Writers that edit
   * without a drag wrapper (K, G, X commits, attach prompts, height keys…)
   * name their verb just before writing, so Ctrl+Z says 'Undid — position
   * key' instead of the generic 'Undid — edit'. The label is consumed by the
   * next auto-push (or dropped by a drag wrapper, which brings its own label).
   */
  const refUndoVerb = useRef(null);
  const nameUndo = useCallback((label) => { refUndoVerb.current = label; }, []);

  /**
   * Push the PRE-CHANGE state onto the undo stack and clear redo.
   * Rapid successive changes (slider drags, item drags, live key updates) are
   * coalesced into ONE undo step: only the FIRST snapshot of a burst is kept,
   * so undo restores the state before the whole gesture — never a
   * transitional mid-gesture state.
   *
   * A burst is the SAME verb arriving again inside the window — not merely any
   * two changes close together. A slider fires dozens of edits under one label,
   * and those must merge. Two DIFFERENT verbs never merge, however fast they
   * are pressed: adding an action key and deleting it a heartbeat later used to
   * become a single step that silently reverted BOTH and was labelled with only
   * the first, so the ↩ button promised one thing and did another.
   */
  const pushUndo = useCallback((label = 'undoVerb.edit') => {
    const snap = snapshotState();
    const now = Date.now();
    const top = refUndoLabels.current[refUndoLabels.current.length - 1];
    const sameGesture = label === top && now - refLastUndoAt.current < 600;
    if (refUndoStack.current.length === 0 || !sameGesture) {
      refUndoStack.current.push(snap);
      refUndoLabels.current.push(label); // parallel label: Ctrl+Z names what it undid
      setUndoTopLabel(label);
      if (refUndoStack.current.length > UNDO_LIMIT) { refUndoStack.current.shift(); refUndoLabels.current.shift(); }
      refLastUndoAt.current = now;
      setUndoCount(refUndoStack.current.length);
      // A new action invalidates redo history
      refRedoStack.current = [];
      refRedoLabels.current = [];
      setRedoCount(0);
      setRedoTopLabel(null);
    }
    // else: mid-gesture — keep the original pre-gesture snapshot
  }, [snapshotState]);

  /** Begin a drag gesture: snapshot the pre-drag state ONCE, then suppress per-move snapshots. */
  const handleDragStart = useCallback((label) => {
    refUndoVerb.current = null; // the gesture's own label wins over any pending verb
    pushUndo(label);
    refDragging.current = true;
  }, [pushUndo]);

  /** End a drag gesture — allow snapshots again. */
  const handleDragEnd = useCallback(() => {
    refDragging.current = false;
  }, []);

  // Wrapper that snapshots before any scenario change (unless mid-drag).
  const setScenario = useCallback((updater) => {
    if (!refDragging.current && refCurrentScenario.current) pushUndo();
    setCurrentScenario(updater);
  }, [pushUndo]);

  // Wrapper that snapshots before any keyframe change (unless mid-drag).
  // Every keyframe mutator (position / action / graphic / attach / visual /
  // segment) goes through this, so ALL timeline edits are undoable.
  const setKeyframes = useCallback((updater) => {
    if (!refDragging.current) {
      pushUndo(refUndoVerb.current || undefined);
      refUndoVerb.current = null;
    }
    setKeyframesRaw(updater);
  }, [pushUndo]);

  /**
   * Put a snapshot back on screen — the whole tree, the branch that was open and
   * that branch's timeline — with every ref kept in step (they are what the next
   * commit reads). Playback is stopped first: a walk from before the undo has no
   * business resuming afterwards.
   */
  const restoreSnapshot = useCallback((snap) => {
    if (!snap) return;
    setIsPlaying(false);
    setMasterPlay(null);
    // Restore the SELECTION too — "undo my nudge" should keep the thing you
    // were working on selected, not drop you back to nothing-selected. Only
    // if it still exists in the restored scene (undoing an ADD removes it).
    let sel = null;
    const ps = snap.scenario?.phases?.[0]?.start;
    const s = snap.selection;
    if (s && ps) {
      sel = s.type === 'player'
        ? ((ps[s.side]?.players || []).length > s.index ? s : null)
        : ((ps[`${s.type}s`] || []).some(o => o.id === s.id) ? s : null);
    }
    setSelectedItem(sel);
    if (snap.tree) setScenarioTree(snap.tree);
    // Which branch the restored working state describes — restored with it, so
    // foldWorking can never write one branch's timeline into another.
    const openId = snap.openBranchId ?? refOpenBranchId.current;
    refOpenBranchId.current = openId;
    refWorkingBranchId.current = snap.workingBranchId ?? openId;
    if (snap.scenario !== undefined) setCurrentScenario(snap.scenario);
    setKeyframesRaw(snap.keyframes || []);
    const snapDur = snap.timelineDuration ?? DEFAULT_BRANCH_DURATION;
    setTimelineDuration(snapDur);
    // Show the branch the undone edit belongs to: undoing something you cannot
    // see would look like nothing happened.
    setCurrentBranchId(openId);
    // Undo must NOT rewind: you stay where you were working (mid-pass, you
    // undo and you are still mid-pass). The playhead hands over its NORMALISED
    // position to the restored timeline — the same rule a branch switch uses —
    // so it can never land past the restored end either.
    const frac = Math.min(1, Math.max(0, playheadFraction(refPlayheadTime.current, refTimelineDurationState.current)));
    refPlayheadTime.current = frac * snapDur;
    setPlayheadTime(frac * snapDur);
  }, [setCurrentBranchId, setCurrentScenario, setKeyframesRaw, setTimelineDuration, setScenarioTree, setSelectedItem]);

  const handleUndo = useCallback(() => {
    const stack = refUndoStack.current;
    if (stack.length === 0) return;
    // Push current state to redo stack before restoring
    refRedoStack.current.push(snapshotState());
    const undoneLabel = refUndoLabels.current.pop() || 'undoVerb.edit';
    refRedoLabels.current.push(undoneLabel); // redo re-applies the SAME named change
    if (refRedoStack.current.length > UNDO_LIMIT) { refRedoStack.current.shift(); refRedoLabels.current.shift(); }
    setRedoCount(refRedoStack.current.length);
    setRedoTopLabel(undoneLabel);
    const prev = stack.pop();
    setUndoCount(stack.length);
    setUndoTopLabel(refUndoLabels.current[refUndoLabels.current.length - 1] ?? null);
    restoreSnapshot(prev);
    setToast(null);
  }, [snapshotState, restoreSnapshot]);

  const handleRedo = useCallback(() => {
    const stack = refRedoStack.current;
    if (stack.length === 0) return;
    // Push current state to undo stack before restoring
    refUndoStack.current.push(snapshotState());
    const redoneLabel = refRedoLabels.current.pop() || 'undoVerb.edit';
    refUndoLabels.current.push(redoneLabel);
    if (refUndoStack.current.length > UNDO_LIMIT) { refUndoStack.current.shift(); refUndoLabels.current.shift(); }
    setUndoCount(refUndoStack.current.length);
    setUndoTopLabel(redoneLabel);
    const next = stack.pop();
    setRedoCount(stack.length);
    setRedoTopLabel(refRedoLabels.current[refRedoLabels.current.length - 1] ?? null);
    restoreSnapshot(next);
    setToast(null);
  }, [snapshotState, restoreSnapshot]);

  // Keyboard shortcuts live in ONE effect further down (after the handlers
  // they call are defined): undo/redo, copy/paste/duplicate, delete, playhead
  // stepping and Escape. Search for "Keyboard shortcuts — the fast path".

  const currentPhase = currentScenario?.phases?.[currentPhaseIndex];
  // The layout phase (0) mirrors every item's position for the 3D scene build.
  const pitchState = currentPhase?.start;

  // Clear selection if selected zone doesn't exist in current phase (undo/phase switch)
  useEffect(() => {
    if (selectedItem?.type === 'zone' && pitchState) {
      if (!pitchState.zones?.find(z => z.id === selectedItem.id)) {
        setSelectedItem(null);
      }
    }
  }, [selectedItem, pitchState]);

  // ═══════════════════════════════════════════════════════════════════
  // Session & Authentication Handlers
  // ═══════════════════════════════════════════════════════════════════

  // ── Fetch default teams from backend (public, no auth) ──
  // This replaces hardcoded data.js teams. If this fails, the backend is
  // unreachable — show an error instead of silently using stale data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const defaults = await teamsAPI.getDefaults();
        if (cancelled) return;
        if (defaults.home) setHomeTeam(defaults.home);
        if (defaults.away) setAwayTeam(defaults.away);
      } catch (err) {
        console.error('Failed to fetch default teams from backend:', err);
        if (!cancelled) setLoadError('Cannot connect to server. Please check your connection and refresh.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Session (fetch account + teams + scenarios on mount) ──
  // Tries the stored localStorage token first. If none exists, falls back
  // to checking the httpOnly cookie (syncs a backend EJS login → visualizer).
  useEffect(() => {
    const initSession = async () => {
      // If no localStorage token, try syncing from the backend cookie
      if (!getToken() && typeof authAPI.checkCookie === 'function') {
        // This await used to sit outside any try/catch. api.js request() throws on any
        // non-ok response, so when the backend is unhealthy — the Mongoose buffering
        // timeout we keep hitting — initSession aborted right here and never reached
        // setAuthChecked(true) at the end of this function. authChecked stayed false
        // forever, the effect that opens the site's default scenario returns early on
        // !authChecked, and the coach was left with an empty pitch, no scenario, and no
        // message of any kind. The inner session fetch below already had a catch that
        // calls setLoadError; the call gating it had nothing.
        let cookieData = null;
        try {
          cookieData = await authAPI.checkCookie();
        } catch (e) {
          console.error('Cookie session check failed:', e);
          if (e.message?.includes('Failed to fetch') || e.message?.includes('NetworkError')) {
            setLoadError('Cannot connect to server. Please check your connection and refresh.');
          }
        }
        if (cookieData?.token) {
          // Cookie synced — now fetch the full session
          try {
            const data = await authAPI.session();
            setAccount(data.account);
            setAdsConfig(data.ads || null);
      setPricing(data.pricing || null);
            setTeams(data.teams || []);
            setScenarios((data.scenarios || []).map(dbScenarioToMenuItem));
            // All tiers load their own teams (free = all features)
            if (data.teams?.length > 0) {
              const t0 = dbTeamToTeamInfo(data.teams[0]); setHomeTeam(t0); setHomeTeamId(data.teams[0]._id);
              if (data.teams.length > 1) { const t1 = dbTeamToTeamInfo(data.teams[1]); setAwayTeam(t1); setAwayTeamId(data.teams[1]._id); }
            }
          } catch (e) {
            console.error('Session fetch failed (cookie path):', e);
            // Don't silently stay logged out — show error if backend is unreachable
            if (e.message?.includes('Failed to fetch') || e.message?.includes('NetworkError')) {
              setLoadError('Cannot connect to server. Please check your connection and refresh.');
            }
          }
        }
      } else {
        // Have a localStorage token — fetch session normally
        try {
          const data = await authAPI.session();
          setAccount(data.account);
          setAdsConfig(data.ads || null);
      setPricing(data.pricing || null);
          setTeams(data.teams || []);
          setScenarios((data.scenarios || []).map(dbScenarioToMenuItem));
          // All tiers load their own teams (free = all features)
          if (data.teams?.length > 0) {
            const t0 = dbTeamToTeamInfo(data.teams[0]); setHomeTeam(t0); setHomeTeamId(data.teams[0]._id);
            if (data.teams.length > 1) { const t1 = dbTeamToTeamInfo(data.teams[1]); setAwayTeam(t1); setAwayTeamId(data.teams[1]._id); }
          }
        } catch (e) {
          console.error('Session fetch failed (token path):', e);
          // Token invalid — clear it so user sees login screen
          clearToken();
        }
      }
      setAuthChecked(true);
    };
    initSession();
  }, []);

  // ── Show the welcome popup once per browsing session, after the 3D assets load ──
  // Non-premium only. It used to fire on EVERY page load: the dismissal was state alone,
  // so a reload put the backdrop straight back — position:fixed, z-index 1000, full
  // viewport, pointer-events:auto — and while it is up nothing behind it can be clicked.
  // sessionStorage rather than localStorage: whether the free-tier notice should ever be
  // suppressed permanently is a product decision, not a bug fix. This only stops it
  // interrupting the same session over and over.
  const handleAssetsLoaded = useCallback(() => {
    if (isPremium || isMobileBuild) return;
    let seen = false;
    try { seen = !!window.sessionStorage.getItem('kv.welcomeSeen'); } catch { /* private mode: just show it */ }
    if (seen) return;
    try { window.sessionStorage.setItem('kv.welcomeSeen', '1'); } catch { /* non-fatal */ }
    setShowWelcomeModal(true);
  }, [isPremium]);

  // ═══════════════════════════════════════════════════════════════════
  // V3 BRANCH HELPERS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Fold the working timeline (the open branch's phases/keyframes/duration)
   * back into its branch. Pure — reads the state refs, so it can run inside a
   * setState updater. Every navigation away from a branch goes through this,
   * which is why switching branches can never lose edits.
   */
  const foldWorking = useCallback((tree) => {
    const openId = refOpenBranchId.current;
    // Which branch does the working state actually describe? It is set together
    // with the refs below, in openBranchInEditor. If it ever disagrees with the
    // open branch, the working state belongs to a DIFFERENT branch and folding it
    // would overwrite the branch's own keys and start state with another
    // branch's. Keep the branch's own timeline and say so loudly instead.
    const inStep = refWorkingBranchId.current === openId;
    if (!inStep && process.env.NODE_ENV !== 'production') {
      console.warn(
        `[kickviz] fold skipped: the working state belongs to branch "${refWorkingBranchId.current}" but "${openId}" is open`,
      );
    }
    const scen = refCurrentScenario.current;
    const folded = updateBranch(tree, openId, (b) => ({
      ...b,
      phases: inStep && scen?.phases ? JSON.parse(JSON.stringify(scen.phases)) : b.phases,
      keyframes: inStep ? JSON.parse(JSON.stringify(refKeyframesState.current || [])) : b.keyframes,
      timelineDuration: inStep ? (refTimelineDurationState.current ?? b.timelineDuration) : b.timelineDuration,
    }));
    return inStep ? reconcileWorkingItems(folded,
      findBranch(tree, openId)?.phases?.[0]?.start, scen?.phases?.[0]?.start) : folded;
  }, []);

  /**
   * The tree with the open branch's timeline refreshed from the working state —
   * and with inheritance re-derived below it. Every operation that can change a
   * branch's timeline (select / edit / add / remove / master play / save) comes
   * through here, which is why a parent's last key and its children's first keys
   * cannot drift apart.
   */
  const committedTree = useCallback(() => {
    const folded = foldWorking(refScenarioTree.current);
    return syncTreeInheritance(folded, refOpenBranchId.current);
  }, [foldWorking]);

  /**
   * Load a branch's timeline into the working state — that is what the timeline
   * view edits. `baseScenario` keeps the scenario-level fields (id, name,
   * teams) that are not part of a branch.
   *
   * `keepSelection` is for a master-play hand-over: the branch being PLAYED
   * changes, but the user's SELECTED branch does not — a walk plays the chain
   * that starts at the selection without ever moving the selection, so playback
   * keeps using the branch the user chose until they choose another one.
   */
  const openBranchInEditor = useCallback((branch, baseScenario = null, keepSelection = false, keepFraction = null, duringPlayback = false) => {
    if (!branch) return;
    // The timeline under any OPEN EDITOR just changed wholesale: its anchors
    // ({trackId,time}, {trackId,from,to}, keyIndex) belong to the branch being
    // LEFT, and a stale segment/graphic anchor would silently WRITE into the
    // new branch on Apply. Close them all (setSelectedItem(null) alone does
    // not — its editor-clearing guard only fires when an item is SELECTED).
    _setSegmentEdit(null);
    _setKeyframeEdit(null);
    _setGraphicEdit(null);
    _setZoneVisualEdit(null);
    setAttachPickMode(null);
    // A pending prompt targets THIS branch's keys too — the keyboard
    // prompt-pause guard already blocks nav keys mid-prompt; a mouse card
    // click must follow the same rule, so cancel the prompt on the swap.
    if (refPromptState.current) refPromptState.current.cancelAction?.();
    const source = baseScenario || refCurrentScenario.current || createEmptyScenario('New Scenario');
    const base = { ...source, phases: JSON.parse(JSON.stringify(branch.phases || [])) };
    const branchKeys = JSON.parse(JSON.stringify(branch.keyframes || []));
    setCurrentScenario(base);
    setKeyframesRaw(branchKeys);
    const dur = branch.timelineDuration ?? DEFAULT_BRANCH_DURATION;
    // Where the playhead lands. A branch SWITCH hands over the same fraction of
    // the timeline (keepFraction), so a half-played 50 s branch arrives half way
    // into the next one however long that one is — and the master bar's handle,
    // which is derived from this playhead, keeps its place inside the new segment
    // instead of snapping back to its start. Every other caller opens at 00:00.
    const startAt = (keepFraction === null || keepFraction === undefined)
      ? 0
      : Math.min(1, Math.max(0, keepFraction)) * dur;
    setTimelineDuration(dur);
    setPlayheadTime(startAt);
    if (!duringPlayback) setIsPlaying(false);
    // Master scrubbing and playback change the displayed branch, not the
    // user's selection. Scenario items retain their identity across branches.
    if (!keepSelection) setSelectedItem(null);
    // The branch being edited/played is always this one …
    refOpenBranchId.current = branch.id;
    // … but the SELECTION is the user's: a walk must not move it.
    if (!keepSelection) setCurrentBranchId(branch.id);
    // Keep the refs the playback loop reads in step immediately (they are
    // otherwise synced after the next render, which a master-play handover
    // would run past).
    refPlayheadTime.current = startAt;
    refTimelineDurationState.current = dur;
    // The commit path (foldWorking) writes the OPEN branch's timeline back into
    // the tree out of these two refs. They are otherwise synced from state after
    // a render, but opening a branch is exactly when the next commit can arrive
    // BEFORE that render — a scrub, Play, or a master-play hand-over all commit
    // synchronously. A fold at that moment would carry the PREVIOUS branch's
    // keyframes and phases into the branch just opened, overwriting its own keys
    // (and its own start state) with another branch's. So they are kept in step
    // right here, with the branch that is actually being opened.
    refCurrentScenario.current = base;
    refKeyframesState.current = branchKeys;
    // …and record which branch those refs now describe, so foldWorking can never
    // fold this branch's timeline out of another branch's working state.
    refWorkingBranchId.current = branch.id;
  }, []);

  /**
   * Replace the whole scenario with a V3 tree — used by every load path
   * (own scenario, default scenario, public copy, new scenario). A V2 scenario
   * arrives as a single root branch, so nothing is lost.
   */
  const loadScenarioAsTree = useCallback((scen) => {
    const migrated = migrateScenarioV3(scen);
    // Scenarios saved before inheritance was kept in sync can carry a mismatch
    // between a branch's end and its children's inherited first keys. Re-derive
    // the lot as it is opened, so old work is repaired on load rather than
    // showing the user a seam that no longer exists in the code.
    const tree = syncTreeInheritance(migrated.tree);
    if (isMobileBuild) getRuntimeAdapter().beginScenario?.(migrated.id, scenarioForSave({ name: migrated.name, tree }));
    refScenarioTree.current = tree;
    setScenarioTree(tree);
    // The user's picks belong to the session, not the scenario: a freshly loaded
    // scenario starts on the default path (every set falls back to priority 1).
    refBranchChoices.current = {};
    setBranchChoicesState({});
    setMasterPlay(null);
    refUndoStack.current = []; setUndoCount(0); setUndoTopLabel(null);
    refRedoStack.current = []; setRedoCount(0); setRedoTopLabel(null);
    refDragging.current = false;
    openBranchInEditor(tree, migrated);
    // A lone root branch has nothing to navigate — go straight to its timeline.
    setViewMode(countBranches(tree) > 1 ? 'branches' : 'timeline');
  }, [openBranchInEditor]);

  /** Select a branch: it becomes the CURRENT branch — the one the timeline
   *  shows and plays. The viewport follows it immediately. Clicking the branch
   *  that is already current changes nothing (so it never resets the playhead). */
  const handleSelectBranch = (branchId) => {
    // An explicit selection re-anchors B's fan: the remembered creation parent
    // is no longer relevant once the user picks a branch themselves.
    refBranchParentForB.current = null;
    const tree = committedTree();
    setScenarioTree(tree);
    const branch = findBranch(tree, branchId);
    if (!branch) return;
    // Selecting a branch CHOOSES it for its sibling set — and for every set on the
    // way down to it, so the path a master play-through follows actually reaches
    // it. Selecting a branch that is already on the chosen path is only looking
    // at it: walking back up your own path must never re-choose the default.
    const onChosenPath = chosenPath(tree, refBranchChoices.current).some(b => b.id === branchId);
    if (!onChosenPath) {
      // findBranchPath returns branch OBJECTS (root → … → this branch), so the
      // parent of each step is simply the previous entry — take their ids.
      const picked = { ...refBranchChoices.current };
      const path = findBranchPath(tree, branchId);
      for (let i = 1; i < path.length; i += 1) picked[path[i - 1].id] = path[i].id;
      setBranchChoices(picked);
    }
    if (branchId === currentBranchId && branchId === refOpenBranchId.current) return;
    // Choosing takes over from any running walk.
    setMasterPlay(null);
    setIsPlaying(false);
    // Hand the playhead's NORMALISED position to the branch being opened, so
    // switching never rewinds: half way through the branch you are leaving is
    // half way through the branch you arrive in, whatever their lengths are. The
    // master bar derives its handle from this playhead, so the handle keeps its
    // place inside the new segment rather than snapping to its start.
    const frac = playheadFraction(refPlayheadTime.current, refTimelineDurationState.current);
    openBranchInEditor(branch, null, false, frac);
  };

  /** Enter the current branch's timeline (tree view → timeline view). */
  const handleEnterBranch = (branchId) => {
    if (branchId && branchId !== currentBranchId) handleSelectBranch(branchId);
    setViewMode('timeline');
  };

  /** Timeline view → back to the tree (committing the open branch first). */
  const handleBackToTree = () => {
    setScenarioTree(committedTree());
    setIsPlaying(false);
    setMasterPlay(null);
    setViewMode('branches');
  };

  /** Tree view: add a sub-branch under `parentId` (inherits its end state).
   *  The new branch OPENS and becomes current — creating a branch means you
   *  want to author it — while the creation anchor keeps the next B fanning
   *  from the same parent instead of chaining into this one. */
  /**
   * Land the playhead right after openBranchInEditor, keeping the ref the
   * playback/commit loop reads in step (same rule openBranchInEditor follows
   * internally — a fold before the next render must see the landed time).
   */
  const landPlayhead = (time) => {
    const dur = refTimelineDurationState.current ?? DEFAULT_BRANCH_DURATION;
    const t = Math.min(time, dur);
    setPlayheadTime(t);
    refPlayheadTime.current = t;
  };

  const handleAddBranch = (parentId) => {
    pushUndo('undoVerb.addBranch');   // structural changes are undoable, like any other edit
    const { tree, branch } = addChild(committedTree(), parentId);
    if (!branch) return;
    setScenarioTree(syncTreeInheritance(tree, parentId));
    // LAND IN the new branch (it is what you meant to edit) — but B's next
    // press still fans from the ORIGINAL parent (refBranchParentForB), so
    // B B B makes three siblings, never a chain.
    const fresh = findBranch(tree, branch.id);
    if (fresh) {
      openBranchInEditor(fresh, null, true);
      // A CHILD branch opens ON its locked seam (00:00 is the inherited
      // start — every first gesture would hit the refusal and have to step
      // off first). Land one snap INTO the editable range instead: the
      // scene at 0.25 is visually identical to the seam (nothing has moved
      // yet), and the very first drag/K/G just works — one keypress saved
      // per branch, no dead-end at step one.
      landPlayhead(0.25);
      setCurrentBranchId(branch.id);
      setViewMode('timeline'); // the editor must actually be VISIBLE (B is usually pressed from the tree view)
    }
    refBranchParentForB.current = parentId;
    setToast({ message: t('branch.addedToast', {
      name: branch.name,
      parent: findBranch(tree, parentId)?.name || t('branch.samePoint'),
    }), id: Date.now() });
  };

  /** Tree view: remove a branch and everything under it. */
  const handleRemoveBranch = (branchId) => {
    pushUndo('undoVerb.deleteBranch');   // structural changes are undoable, like any other edit
    const removedName = findBranch(committedTree(), branchId)?.name || 'Branch';
    const tree = syncTreeInheritance(removeBranch(committedTree(), branchId));
    setScenarioTree(tree);
    // If we just deleted the branch we were looking at (or an ancestor of it),
    // fall back to the root so the timeline always shows a branch that exists.
    if (!findBranch(tree, currentBranchId)) {
      const root = findBranch(tree, tree.id);
      if (root) openBranchInEditor(root);
    }
    // Announce the invisible commit and name the way back — this is why the
    // tree button does not need a confirm dialog: the toast says the removal is
    // not the end of the story, and the ↩ button is on the viewport for anyone
    // without a keyboard.
    setToast({ message: t('toast.branchRemoved', { name: removedName }), id: Date.now() });
  };

  /**
   * "Clip time" — set the CURRENT branch's timeline length to the time of its
   * last keyframe, so the timeline ends exactly where the animation ends
   * (instead of trailing empty seconds, or cutting off keys past the old end).
   * The new length is committed straight into the tree so it saves with the
   * branch, exactly like editing the TIME box does.
   */
  const handleClipBranchTime = (branchId) => {
    // The tree's ✂ names the ROW's branch. Normally that IS the branch open in
    // the strip (clicking a row opens it) — but during a walk the strip shows
    // the PLAYED branch while the selection stays put. ✂ must clip the branch
    // it was clicked on, never the one merely on screen.
    const offRow = branchId && branchId !== refOpenBranchId.current;
    const srcKeys = offRow ? (findBranch(committedTree(), branchId)?.keyframes || []) : keyframes;
    const last = lastKeyTime(srcKeys);
    if (!last) {
      setToast({ message: t('branch.clipNothing'), id: Date.now() });
      return;
    }
    const oldDur = offRow
      ? (Number(findBranch(committedTree(), branchId)?.timelineDuration) || DEFAULT_BRANCH_DURATION)
      : (refTimelineDurationState.current ?? DEFAULT_BRANCH_DURATION);
    if (Math.abs(oldDur - last) < 1e-9) {
      // Nothing to do — say so instead of pushing a no-op undo step.
      setToast({ message: t('toast.clipTight', { t: fmtTime(last) }), id: Date.now() });
      return;
    }
    pushUndo('undoVerb.clipBranchTime');
    if (offRow) {
      // A duration change re-forms no keys — children inherit the parent's END
      // STATE (its keys), so no re-derivation is needed for this branch's kids.
      setScenarioTree(prev => updateBranch(foldWorking(prev), branchId,
        (b) => ({ ...b, timelineDuration: last })));
      setToast({ message: `${t('branch.clippedTo')} ${fmtTime(last)}` + t('toast.clipRestores', { old: fmtTime(oldDur) }), id: Date.now() });
      return;
    }
    setTimelineDuration(last);
    refTimelineDurationState.current = last;   // commit paths read the ref
    if (refPlayheadTime.current > last) {      // never leave the playhead past the end
      refPlayheadTime.current = last;
      setPlayheadTime(last);
    }
    setScenarioTree(committedTree());
    setToast({ message: `${t('branch.clippedTo')} ${fmtTime(last)}` + t('toast.clipRestores', { old: fmtTime(oldDur) }), id: Date.now() });
  };

  /**
   * STRETCH / COMPRESS the whole branch (⌥+ / ⌥−, and the ruler's timing menu):
   * every key of every track scaled by `factor` and snapped to the 0.25 s grid,
   * with the branch window scaled to match. "Same move, played slower" without
   * touching a single keyframe. Returns false when nothing could change, so a
   * caller never reports a move that did not happen.
   */
  const handleStretchKeys = useCallback((factor) => {
    const oldDur = refTimelineDurationState.current ?? DEFAULT_BRANCH_DURATION;
    const newDur = Math.max(1, Math.round(Math.max(0, oldDur * factor) * 4) / 4);
    // A stretch that changes NOTHING (duration at the 1 s floor and every key
    // already snapped back to its old time) must not burn an undo step or
    // toast a fake "1.00 → 1.00" move.
    const sTimes = (tr) => ['position', 'action', 'attach', 'graphic', 'visual']
      .flatMap(l => (tr?.[l] || []).map(k => k.time));
    const sScaled = scaleKeyTimes(keyframes, factor);
    if (newDur === oldDur
        && JSON.stringify(sScaled.map(sTimes)) === JSON.stringify(keyframes.map(sTimes))) {
      setToast({ message: factor > 1
        ? t('toast.stretchNothing')
        : t('toast.stretchFloor'), id: Date.now() });
      return false;
    }
    handleDragStart('undoVerb.stretchTimeline');
    setKeyframes(prev => scaleKeyTimes(prev, factor));
    setTimelineDuration(newDur);
    handleDragEnd();
    // Re-anchor any OPEN editor to the scaled times (same snap transform
    // as scaleKeyTimes) — otherwise the height/segment editors keep their
    // stale anchors and silently detach from what they edit.
    const sSnap = (t) => Math.round(Math.max(0, (Number(t) || 0) * factor) * 4) / 4;
    if (keyframeEdit) setKeyframeEdit({ ...keyframeEdit, time: sSnap(keyframeEdit.time) });
    if (segmentEdit) {
      const sf = sSnap(segmentEdit.from), st = sSnap(segmentEdit.to);
      setSegmentEdit(sf < st ? { ...segmentEdit, from: sf, to: st } : null);
    }
    setToast({ message: t(factor > 1 ? 'toast.stretchSlower' : 'toast.stretchFaster', { old: fmtTime(oldDur), new: fmtTime(newDur) })
      + t('toast.revertSuffix'), id: Date.now() });
    return true;
  }, [keyframes, keyframeEdit, segmentEdit, handleDragStart, handleDragEnd,
      setKeyframes, setTimelineDuration, setToast, t]);

  /**
   * The branch's TIMING menu — long-press the ruler. Until now the whole
   * when-things-happen set was keyboard-only (⌥+/− stretch, ⇧K clip to the last
   * key), while the ruler's own drag only ever changed the WINDOW. Every verb
   * here moves TIME itself, never a position.
   */
  const handleTimingMenu = useCallback(() => {
    const dur = refTimelineDurationState.current ?? DEFAULT_BRANCH_DURATION;
    const last = lastKeyTime(keyframes);
    const subActions = [
      { label: t('timing.slower'), action: () => { setPromptState(null); handleStretchKeys(1.25); } },
      { label: t('timing.quicker'), action: () => { setPromptState(null); handleStretchKeys(0.8); } },
    ];
    // Only offered when there ARE empty seconds to drop — handleClipBranchTime
    // refuses a tight branch, and a button that can only refuse is a dead end.
    if (last && dur - last > 0.01) {
      subActions.push({
        label: t('timing.clip', { t: fmtTime(last) }),
        action: () => { setPromptState(null); handleClipBranchTime(); },
      });
    }
    // RIPPLE — open or close a GAP after the playhead. The verb existed only as
    // [ / ] and an ⌥-drag, so on a phone there was no way to make room to key
    // something later: the one thing a coach reaches for when a drill runs long.
    // Gated on the VERB's own probe — no movable key after the playhead, no
    // entry — because a button that can only refuse is a dead end.
    const cut = refPlayheadTime.current ?? 0;
    const movableAfter = keyframes.some(tr => tr && KEY_ARRAYS.some(lane => (tr[lane] || [])
      .some(k => k && Number(k.time) > cut + 1e-6 && !isLockedKeyTime(k.time))));
    if (movableAfter) {
      subActions.push(
        { label: t('timing.rippleOpen', { t: fmtTime(cut) }),
          action: () => { setPromptState(null); handleRippleKeys(0.5); } },
        { label: t('timing.rippleClose', { t: fmtTime(cut) }),
          action: () => { setPromptState(null); handleRippleKeys(-0.5); } },
      );
    }
    setPromptState({
      title: t('timing.title', { t: fmtTime(dur) }),
      description: t('timing.desc'),
      subActions,
      cancelAction: () => setPromptState(null),
    });
    // handleRippleKeys is declared further down the component body: it is used
    // in the body only, never in this array, which is read during the render.
  }, [keyframes, handleStretchKeys, handleClipBranchTime, setPromptState, t]);

  /** Tree view: rename / re-prioritise. */
  const handleRenameBranch = (branchId, name) => {
    // No-op renames (same name, or blank — renameBranch keeps the old one)
    // must not burn an undo step: blurring the ⇧R field without editing it
    // would otherwise poison Ctrl+Z and mislabel the undo button.
    const cur = findBranch(committedTree(), branchId);
    const clean = String(name || '').trim();
    if (!cur || !clean || clean === cur.name) return;
    pushUndo('undoVerb.renameBranch');
    setScenarioTree(prev => renameBranch(foldWorking(prev), branchId, name));
  };
  const handleSetBranchOrder = (branchId, order) => {
    const n = parseInt(order, 10);
    if (!Number.isFinite(n)) return;
    if (findBranch(committedTree(), branchId)?.order === n) return; // same slot: no undo burn
    pushUndo('undoVerb.branchOrder');
    setScenarioTree(prev => setBranchOrder(foldWorking(prev), branchId, n));
  };
  const handleMoveBranchPriority = (branchId, dir) => {
    // No-op moves (root, already first/last) must NOT burn an undo step —
    // returns false so callers can explain instead of toast a lie.
    // Checked inline: moveBranchPriority mutates shared order fields, so
    // probing with it would double-apply the move.
    const tree = committedTree();
    const parent = findParent(tree, branchId);
    if (!parent) return false;
    const sibs = sortedChildren(parent);
    const i = sibs.findIndex(s => s.id === branchId);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (i === -1 || j < 0 || j >= sibs.length) return false;
    pushUndo('undoVerb.branchPriority');
    setScenarioTree(prev => moveBranchPriority(foldWorking(prev), branchId, dir));
    return true;
  };

  /**
   * Tree-view ▲▼ twin of the keyboard ⇧↑↓: same rich announcement, same
   * honest refusal. Before this, the navigator arrows were SILENT both ways
   * — a mouse user could re-route the master path and never learn it, or
   * press "already first" into a dead button.
   */
  const handleMoveBranchPriorityUI = (branchId, dir) => {
    const tree = committedTree();
    const parent = findParent(tree, branchId);
    const sibs0 = parent ? sortedChildren(parent) : [];
    const i0 = sibs0.findIndex(s => s.id === branchId);
    if (!handleMoveBranchPriority(branchId, dir)) {
      setToast({ message: !parent
        ? t('toast.rootNoPriority')
        : dir === 'up'
          ? ((refBranchChoices.current || {})[parent?.id] ? t('toast.alreadyFirstChosen')
            : t('toast.alreadyFirstPath'))
          : t('toast.alreadyLastFork'), id: Date.now() });
      return;
    }
    const newRank = dir === 'up' ? i0 : i0 + 2; // swap lands one step that way
    const explicitChoice = parent && (refBranchChoices.current || {})[parent.id];
    setToast({ message: t('toast.priorityMoved', { dir: t(dir === 'up' ? 'toast.dirUp' : 'toast.dirDown'), rank: newRank, n: sibs0.length })
      + (explicitChoice ? t('toast.chosenUnchanged') : t('toast.pathFollowsPriority'))
      + t('toast.revertSuffix'), id: Date.now() });
  };

  /**
   * Tree view: put a branch's TIMELINE on the clipboard — its keys, its phase
   * start states and its length. Its name, priority and sub-branches are not
   * part of the copy, so a paste can never rename or re-parent anything.
   *
   * The tree is committed first, so copying the branch that is currently open in
   * the timeline carries the edits still being made to it.
   */
  const handleCopyBranchTimeline = (branchId) => {
    const tree = committedTree();
    const branch = findBranch(tree, branchId);
    if (!branch) return;
    setBranchClipboard({ id: branch.id, name: branch.name, ...branchTimelineSnapshot(branch) });
    setToast({ message: `${t('branch.copiedToast')} “${branch.name}”`, id: Date.now() });
  };

  /**
   * Tree view: paste the copied timeline INTO a branch. That branch's keys,
   * phase start states and length are replaced; its name, priority and
   * sub-branches stay exactly as they were.
   *
   * The read-only first key does NOT travel with the clipboard: a non-root
   * branch starts where ITS parent ended, and the target may hang from a
   * different parent than the copy came from. So the target's own inherited key
   * is re-derived from its own parent — and re-derived again for every branch
   * beneath it, whose start follows this branch's new end.
   */
  const handlePasteBranchTimeline = (targetId) => {
    if (!branchClipboard) {
      setToast({ message: t('branch.pasteEmpty'), id: Date.now() });
      return;
    }
    if (branchClipboard.id === targetId) {
      setToast({ message: t('branch.pasteSame'), id: Date.now() });
      return;
    }
    const tree = committedTree();
    const target = findBranch(tree, targetId);
    if (!target) return;
    // NO-OP PASTE burns nothing and says so: if the target already holds the
    // EXACT timeline (same keys, same start states, same length), the paste
    // would rewrite identical data — and an identical end re-derives an
    // identical subtree, so nothing anywhere would change. Ctrl+Z after such
    // a paste would "undo" a step that never happened.
    const snapNow = branchTimelineSnapshot(target);
    if (JSON.stringify(snapNow) === JSON.stringify({
      phases: branchClipboard.phases,
      keyframes: branchClipboard.keyframes,
      timelineDuration: branchClipboard.timelineDuration,
    })) {
      setToast({ message: t('toast.timelineSame', { name: target.name }), id: Date.now() });
      return;
    }
    pushUndo('undoVerb.pasteBranchTimeline');   // a paste overwrites a timeline — undoable like any other edit
    const pasted = pasteBranchTimeline(tree, targetId, branchClipboard);
    setScenarioTree(pasted);
    // Pasting into the branch that is open in the timeline has to reload it, or
    // the editor would keep showing the keys that were just replaced.
    if (targetId === refOpenBranchId.current) {
      const fresh = findBranch(pasted, targetId);
      if (fresh) openBranchInEditor(fresh, null, true);
    }
    setToast({ message: t('branch.pastedToast', { name: target.name }), id: Date.now() });
  };

  /**
   * Tree view: add a SIBLING of the selected branch — one that carries on from
   * the same point (its parent), rather than from the END of its timeline the
   * way "+ Add sub-branch" does. The root has no parent and so cannot gain
   * siblings; the button is not offered for it.
   */
  const handleAddSibling = (branchId) => {
    const tree = committedTree();
    const parent = findParent(tree, branchId);
    if (!parent) return;
    pushUndo('undoVerb.addSiblingBranch');
    const { tree: added, branch } = addSibling(tree, branchId);
    if (!branch) return;
    setScenarioTree(syncTreeInheritance(added, parent.id));
    // LAND IN the new sibling (empty branch, ready to author). The creation
    // anchor becomes the SIBLING SET (parent.id) — ⌥B ⌥B fans, never nests.
    const fresh = findBranch(added, branch.id);
    if (fresh) {
      openBranchInEditor(fresh, null, true);
      landPlayhead(0.25); // siblings are children too — skip the locked seam
      setCurrentBranchId(branch.id);
      setViewMode('timeline'); // land where the editor is actually visible
    }
    refBranchParentForB.current = parent.id;
    setToast({ message: t('branch.siblingAddedToast', { name: branch.name }), id: Date.now() });
  };

  /**
   * DUPLICATE BRANCH (Shift+B): a sibling of the current branch carrying the
   * SAME timeline — keys, start states, length. What used to be
   * menu → Copy timeline → menu → Add sibling → menu → Paste is one key.
   * The root has no siblings; its variants are sub-branches (B).
   */
  const handleDuplicateBranch = (branchId) => {
    const tree = committedTree();
    const src = findBranch(tree, branchId);
    if (!src) return;
    const parent = findParent(tree, branchId);
    if (!parent) {
      setToast({ message: t('toast.rootNoSiblings'), id: Date.now() });
      return;
    }
    pushUndo('undoVerb.duplicateBranch');
    const { tree: added, branch } = addSibling(tree, branchId);
    if (!branch) return;
    const withTimeline = pasteBranchTimeline(added, branch.id, {
      id: src.id, name: src.name, ...branchTimelineSnapshot(src),
    });
    setScenarioTree(syncTreeInheritance(withTimeline, parent.id));
    // LAND IN the duplicate — you made it to edit the variant, so edit it.
    // (Unlike B, consecutive ⇧B stays correct: duplicating a duplicate of an
    // identical timeline fans out the same way.)
    const fresh = findBranch(withTimeline, branch.id);
    if (fresh) {
      // keepSelection keeps the ITEM selection; currentBranchId must move
      // explicitly — openBranchInEditor leaves it alone on that path (walks).
      openBranchInEditor(fresh, null, true);
      landPlayhead(0.25); // the duplicate is a child too — skip its locked seam
      setCurrentBranchId(branch.id);
      setViewMode('timeline'); // land where the editor is actually visible
    }
    setToast({ message: t('toast.branchDuplicated', { src: src.name, name: branch.name }), id: Date.now() });
  };

  /** Master playback starts at the visible playhead and follows the chosen path. */
  const startTreeWalk = useCallback((stopAtNextBranch = false) => {
    const tree = committedTree();
    const { chain, time: clamped } = masterPlaybackStart(
      tree, refOpenBranchId.current, refPlayheadTime.current, refBranchChoices.current,
    );
    if (!chain.length) return;
    setScenarioTree(tree);
    openBranchInEditor(chain[0], null, true);
    refPlayheadTime.current = clamped;
    setPlayheadTime(clamped);
    const walk = { active: true, ids: chain.map(b => b.id), branches: chain, index: 0, stopAtNextBranch };
    refMasterPlay.current = walk;
    setMasterPlay(walk);
    setIsPlaying(true);
    setToast(null);
  }, [committedTree, openBranchInEditor]);

  const handleMasterPlay = useCallback(() => {
    if (isPlaying && masterPlayActive && !refMasterPlay.current?.stopAtNextBranch) {
      setIsPlaying(false);
      return;
    }
    // This also upgrades branch-only playback without rewinding its playhead.
    startTreeWalk();
  }, [isPlaying, masterPlayActive, startTreeWalk]);

  const handleNextBranchPlay = useCallback(() => {
    if (isPlaying && refMasterPlay.current?.stopAtNextBranch) {
      setIsPlaying(false);
      return;
    }
    startTreeWalk(true);
  }, [isPlaying, startTreeWalk]);

  // ── MASTER TIMELINE ────────────────────────────────────────────────
  // The bar represents the path the user has CHOSEN: from the root, at every
  // sibling set the branch they picked, and the default (priority 1) for sets
  // they have not touched. It is never re-derived from priority, so walking back
  // up the tree, playing through it or reloading a branch cannot make it fall
  // back to the default path. One equal segment per branch, never times.
  const selectedBranch = findBranch(scenarioTree, currentBranchId) || scenarioTree;
  const masterChain = chosenPath(scenarioTree, branchChoices);
  const chosenIds = new Set(Object.values(branchChoices || {}));
  // Which chain steps pass a FORK with alternatives — the master bar badges
  // them ⑂N so "why did it play THIS way?" is answerable without opening
  // the tree, and users discover forks they never knew existed.
  const masterForks = {};
  for (let i = 1; i < masterChain.length; i += 1) {
    const fp = findParent(scenarioTree, masterChain[i].id);
    const alts = fp ? sortedChildren(fp).length - 1 : 0;
    if (alts > 0) masterForks[masterChain[i].id] = alts;
  }
  const [timelineHidden, setTimelineHidden] = useState(false);
  const showMaster = timelineHidden || masterChain.length > 1;
  useLayoutEffect(() => { handleResize(); }, [timelineHidden, panelHeight, showMaster]);
  // While a walk runs, the timeline and the pitch are showing the branch being
  // PLAYED, which is not the selected one — the walk never moves the choice. The
  // bar handle, the timeline title and its inherited-key lock all describe the
  // branch on screen, so they follow the played branch during a walk.
  const playedId = refOpenBranchId.current || currentBranchId;
  const shownBranch = (playedId !== currentBranchId && findBranch(scenarioTree, playedId)) || selectedBranch;
  const masterPos = masterPosFor(masterChain, shownBranch?.id || currentBranchId, playheadTime, timelineDuration);

  /** Pull the master handle: load the branch it lands on, then set the playhead
   *  to that fraction OF THAT BRANCH'S OWN LENGTH (the normalisation step). */
  const handleMasterScrub = (pos) => {
    const { frac, branch } = resolveMasterPos(masterChain, pos);
    if (!branch) return;
    setIsPlaying(false);
    setMasterPlay(null);   // pulling the bar takes over from any running walk

    let dur;
    if (branch.id !== refOpenBranchId.current) {
      // Commit the branch we are leaving, then open the one under the handle.
      const tree = committedTree();
      setScenarioTree(tree);
      const target = findBranch(tree, branch.id);
      if (!target) return;
      openBranchInEditor(target, null, true); // retain item selection
      // …but the BRANCH selection follows the handle: grabbing the bar is an
      // explicit user act (unlike a walk, which must never move it), and the
      // mixed state — strip editing the shown branch while B/⇧R/⌥E act on
      // the old currentBranchId — is exactly the confusion the bar's docs
      // promise against ("the scenario tree follows it").
      setCurrentBranchId(branch.id);
      dur = Number(target.timelineDuration) || DEFAULT_BRANCH_DURATION;
    } else {
      dur = Number(timelineDuration) || DEFAULT_BRANCH_DURATION;
    }

    const tsec = masterTimeFor(dur, frac);
    refPlayheadTime.current = tsec;          // viewport reads this immediately
    setPlayheadTime(tsec);
  };

  // Hand the branch helpers to the playback loop (which is defined earlier and
  // must not reference these directly — they would be in its temporal dead zone).
  useEffect(() => {
    refBranchOps.current = { committedTree, openBranchInEditor };
  }, [committedTree, openBranchInEditor]);

  // ── First-run discoverability: ONE toast pointing at the shortcut sheet ──
  // Everything this project has built — drag-attach, group verbs, placement
  // keys, timing keys — lives in the ? modal. A brand-new visitor has no way
  // to know it exists. Shown once per browser (localStorage flag), after the
  // boot toasts have had their moment.
  useEffect(() => {
    let flag = false;
    try { flag = !!window.localStorage.getItem('kv.helpHintShown'); } catch { /* private mode: just show it */ }
    if (flag) return;
    try { window.localStorage.setItem('kv.helpHintShown', '1'); } catch { /* non-fatal */ }
    const timer = setTimeout(() => setToast({
      message: t('app.newHere'),
      id: Date.now(),
    }), 2500);
    return () => clearTimeout(timer);
  }, [t]);

  // ── Load the site's default scenario at boot (guest / logged-out view) ──
  // The admin picks which public scenario is the default (backend config).
  // If one is set and reachable, the visualizer opens on it instead of a
  // blank pitch. Runs once after auth is checked, and again whenever a
  // session ends (logout / backend-cookie logout on window focus).
  const applyScenarioData = useCallback((scen) => {
    if (!scen) return;
    // Local editable copy — strip the DB id so Save creates a new scenario.
    // V3: the scenario becomes a branch tree (a V2 scenario arrives as its root).
    loadScenarioAsTree({ ...migrateScenario(scen), id: `local-${Date.now()}` });
  }, [loadScenarioAsTree]);
  const bootDefaultScenario = useCallback(async () => {
    setDefaultScenarioNotice(null);
    try {
      if (isMobileBuild) {
        const saved = await getRuntimeAdapter().initialScenario();
        if (saved) {
          loadScenarioAsTree(dbScenarioToMenuItem(saved));
        }
        return;
      }
      const data = await scenariosAPI.getDefaultOpen();
      if (data?.scenario) applyScenarioData(data.scenario);
      else setDefaultScenarioNotice('app.scenarioUnconfigured');
    } catch (e) {
      // Keep the editor usable, but retain the explanation until dismissed.
      // A transient toast can expire behind the first-visit welcome screen.
      console.error('Failed to load default scenario:', e);
      setDefaultScenarioNotice('app.scenarioUnavailable');
    }
  }, [applyScenarioData, loadScenarioAsTree, t]);
  const defaultLoadedOnceRef = useRef(false);
  // Mobile-only autosave: keep the entire V3 tree, not just the open branch.
  // The native workspace owns serialized writes and visible save status.
  useEffect(() => {
    if (!isMobileBuild || !authChecked || !currentScenario) return;
    getRuntimeAdapter().queueDraft(currentScenario.id, {
      ...scenarioForSave({ name: currentScenario.name, tree: committedTree() }),

    });
  }, [authChecked, currentScenario, keyframes, scenarioTree, timelineDuration, committedTree]);
  useEffect(() => {
    if (!isMobileBuild) return;
    const runtime = getRuntimeAdapter();
    const open = async event => {
      if (!event.detail) return;
      loadScenarioAsTree(dbScenarioToMenuItem(event.detail));
    };
    const refresh = async () => {
      const data = await runtime.request('GET', '/auth/session');
      setTeams(data.teams); setScenarios(data.scenarios.map(dbScenarioToMenuItem));
      const home = data.teams.find(team => team._id === homeTeamId) || data.teams[0];
      const away = data.teams.find(team => team._id === awayTeamId) || data.teams[1];
      if (home) { setHomeTeam(dbTeamToTeamInfo(home)); setHomeTeamId(home._id); }
      if (away) { setAwayTeam(dbTeamToTeamInfo(away)); setAwayTeamId(away._id); }
    };
    const updateAccount = event => { if (event.detail) setAccount(event.detail); };
    window.addEventListener('kickviz-open-scenario', open);
    window.addEventListener('kickviz-teams-changed', refresh);
    window.addEventListener('kickviz-account-changed', updateAccount);
    return () => { window.removeEventListener('kickviz-open-scenario', open); window.removeEventListener('kickviz-teams-changed', refresh); window.removeEventListener('kickviz-account-changed', updateAccount); };
  }, [homeTeamId, awayTeamId, loadScenarioAsTree]);
  useEffect(() => {
    if (!authChecked) return;
    // Page load → always open the site default scenario (the admin-configured
    // public scenario). This applies to guests AND signed-in users: a fresh
    // page load has nothing on the pitch, so the default is what it opens on.
    if (!defaultLoadedOnceRef.current) {
      defaultLoadedOnceRef.current = true;
      bootDefaultScenario();
    }
  }, [authChecked, account, bootDefaultScenario]);

  // ── Window focus: detect backend logout ──
  // When the user switches back to the visualizer after logging out via
  // the backend EJS pages, check if the cookie is still valid. If not,
  // clear localStorage and force re-login.
  useEffect(() => {
    const onFocus = async () => {
      if (!account) return; // already logged out
      const cookieData = await authAPI.checkCookie();
      if (!cookieData) {
        // Cookie is gone — backend logout happened
        clearToken();
        setAccount(null);
        setTeams([]);
        setScenarios([]);
        // Authentication changes must not replace the working scenario.
      } else if (billingWindowPending.current) {
        // Checkout runs in a separate tab so the current pitch and unsaved
        // editing state stay mounted. Refresh entitlement on return.
        try {
          const status = await stripeAPI.status();
          setAccount(previous => previous ? { ...previous, tier: status.tier } : previous);
          if (status.tier === 'premium') billingWindowPending.current = false;
        } catch (error) { console.error('Subscription refresh failed:', error); }
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [account]);

  const handleLogin = async (acct) => {
    setShowLoginModal(false);
    setAccount(acct);
    try {
      const data = await authAPI.session();
      setAccount(data.account);
      setAdsConfig(data.ads || null);
      setPricing(data.pricing || null);
      setTeams(data.teams || []);
      setScenarios((data.scenarios || []).map(dbScenarioToMenuItem));
      // Keep the teams currently on the pitch. Signing in only changes access.
    } catch (err) {
      console.error('Session fetch failed:', err);
      setLoadError('Failed to load your session. Please refresh or try again.');
    }
  };

  const handleLogout = async () => {
    if (isMobileBuild) {
      await getRuntimeAdapter().signOut();
      return;
    }
    await authAPI.logout();
    setAccount(null); setTeams([]); setScenarios([]);
  };

  const handleTeamSelect = (teamId) => {
    const team = teams.find(t => t._id === teamId);
    if (!team) return;
    const teamInfo = dbTeamToTeamInfo(team);
    if (teamModalTarget === 'home') { setHomeTeam(teamInfo); setHomeTeamId(teamId); }
    else if (teamModalTarget === 'away') { setAwayTeam(teamInfo); setAwayTeamId(teamId); }
    setTeamModalTarget(null);
  };

  const handleTeamButtonClick = (side) => {
    if (!account) {
      setShowLoginModal(true);
      return;
    }
    // All tiers can select their own teams (free = all features)
    setTeamModalTarget(side);
  };

  /** Quick-apply from the Team editor: assign a team to a side + toast. */
  const handleTeamApply = useCallback((teamId, side) => {
    const team = teams.find(t => t._id === teamId);
    if (!team) return;
    const teamInfo = dbTeamToTeamInfo(team);
    if (side === 'home') { setHomeTeam(teamInfo); setHomeTeamId(teamId); }
    else { setAwayTeam(teamInfo); setAwayTeamId(teamId); }
    setToast({ message: t('team.switchTo', {
      name: team.name, side: capWord(t(side === 'home' ? 'side.home' : 'side.away')),
    }), id: Date.now() });
  }, [teams]);

  /** Re-pull teams + account after edits in the Team editor modal. */
  const refreshTeams = useCallback(async () => {
    try {
      const data = await authAPI.session();
      if (data.teams) setTeams(data.teams);
      if (data.account) setAccount(data.account);
    } catch (err) { console.error('Refresh teams failed:', err); }
  }, []);

  const handleScenarioSelect = (scen) => {
    // V3: loads into the branch tree. A V2 scenario becomes a lone root branch.
    loadScenarioAsTree(scen);
  };

  // ═══════════════════════════════════════════════════════════════════
  // Phase & Object State Handlers
  // ═══════════════════════════════════════════════════════════════════

  // ── Phase state update (updates the layout phase's start state — phase 0) ──
  const updatePhaseState = useCallback((updater) => {
    setScenario(prev => {
      if (!prev) {
        const newScen = createEmptyScenario('New Scenario');
        newScen.phases[0].start = updater(newScen.phases[0].start || createEmptyPitchState());
        return newScen;
      }
      if (!prev.phases?.[0]) return prev;
      const phases = [...prev.phases];
      const phase = { ...phases[0] };
      phase.start = updater(phase.start || createEmptyPitchState());
      phases[0] = phase;
      return { ...prev, phases };
    });
  }, [setScenario]);

  // ═══════════════════════════════════════════════════════════════════
  // V2 Keyframes (timeline)
  // "position" is the movement track in the schema. Keyframe times are
  // always snapped to the nearest 0.25s step.
  // ═══════════════════════════════════════════════════════════════════

  /** Upsert a position keyframe for a track (replace same-second key, keep sorted). */
  const upsertPositionKey = useCallback((tracks, itemId, meta, pos, timeSec) => {
    const time = snapKeyTime(timeSec);
    let found = false;
    const next = tracks.map(t => {
      if (t.id !== itemId) return t;
      found = true;
      const keys = (t.position || []).filter(k => k.time !== time);
      keys.push({ time, position: pos });
      keys.sort((a, b) => a.time - b.time);
      return { ...t, position: keys };
    });
    if (!found) next.push({ id: itemId, ...meta, position: [{ time, position: pos }], graphic: [{ time: 0, value: meta.type === 'ball' ? 'off' : 'on' }] });
    return next;
  }, []);

  /** Auto-key: record a position keyframe at the rounded playhead second (only when AUTO is on). */
  const recordAutoKey = useCallback((itemId, meta, pos) => {
    if (!autoKeyOn || !pos) return;
    // On a CHILD branch the instant 00:00 IS the parent's end state (the
    // inherited, read-only key). Upserting there would silently REPLACE the
    // continuity key and teleport the item away from where the previous
    // branch ended — the one invariant the branch model exists to hold.
    if (refuseLockedDrag(itemId, snapKeyTime(playheadTime))) return;
    setKeyframes(prev => upsertPositionKey(prev, itemId, meta, pos, playheadTime));
    // Snap the playhead to the key's whole-second time so the scene sample
    // EXACTLY matches the dropped position. Without this, a mid-second drop
    // (e.g. playhead 3.6 → key at 4) leaves the playhead between keys and the
    // movement-track interpolation pulls the item/cross toward the
    // neighbouring key the moment the drag ends.
    const snapped = snapKeyTime(playheadTime);
    if (Math.abs(snapped - playheadTime) > 1e-9) setPlayheadTime(snapped);
  }, [autoKeyOn, playheadTime, upsertPositionKey, keyframes]);

  /**
   * AUTO off + an item that ALREADY has position keys = the drag you just did
   * will not play. Nothing else says so: the item moves on screen, then snaps
   * back the moment you press play, and the usual conclusion is "the app lost
   * my work". Announce it ONCE per AUTO-off period — the strip's AUTO button is
   * the way back (A is the keyboard twin).
   */
  const refAutoOffHintShown = useRef(false);
  useEffect(() => { if (autoKeyOn) refAutoOffHintShown.current = false; }, [autoKeyOn]);
  const warnUnrecordedMove = useCallback((trackId) => {
    if (autoKeyOn || refAutoOffHintShown.current) return;
    const tr = keyframes.find(t => t.id === trackId);
    // A base-pose-only item loses nothing — AUTO off is exactly right for
    // setting up a static formation, and saying so every drag would be noise.
    if (!tr || !(tr.position || []).length) return;
    refAutoOffHintShown.current = true;
    setToast({ message: t('toast.autoOff'), id: Date.now() });
  }, [autoKeyOn, keyframes, t]);

  /** Track id + meta for any selectable item. */
  const itemTrackMeta = useCallback((item) => {
    if (!item) return null;
    if (item.type === 'player') return { id: `${item.side}:${item.index}`, meta: { type: 'player', side: item.side, index: item.index } };
    if (item.type === 'ball') return { id: `ball:${item.id}`, meta: { type: 'ball', ballId: item.id } };
    if (item.type === 'cone') return { id: `cone:${item.id}`, meta: { type: 'cone', coneId: item.id } };
    if (item.type === 'target') return { id: `target:${item.id}`, meta: { type: 'target', targetId: item.id } };
    if (item.type === 'zone') return { id: `zone:${item.id}`, meta: { type: 'zone', zoneId: item.id } };
    return null;
  }, []);

  /** Current [x,y,z] position of an item in the current phase. */
  const itemCurrentPosition = useCallback((item) => {
    if (!item || !pitchState) return null;
    if (item.type === 'player') {
      const players = item.side === 'home' ? pitchState?.home?.players : pitchState?.away?.players;
      return players?.[item.index]?.position || null;
    }
    if (item.type === 'ball') return pitchState?.balls?.find(b => b.id === item.id)?.position || null;
    if (item.type === 'cone') return pitchState?.cones?.find(c => c.id === item.id)?.position || null;
    if (item.type === 'target') return pitchState?.targets?.find(t => t.id === item.id)?.position || null;
    if (item.type === 'zone') return pitchState?.zones?.find(z => z.id === item.id)?.position || null;
    return null;
  }, [pitchState]);

  /**
   * Current INTERPOLATED player position at the playhead time, taken from the
   * movement keyframes (same math the 3D viewport uses). Falls back to the
   * phase position when the player has no movement keys yet.
   */
  const interpolatedPlayerPosition = useCallback((side, index, atTime = null) => {
    const trackId = `${side}:${index}`;
    const track = keyframes.find(k => k.id === trackId);
    const keys = track?.position || [];
    const fallback = itemCurrentPosition({ type: 'player', side, index });
    if (keys.length === 0) return fallback;
    if (keys.length === 1) return keys[0].position;
    // The ✚ asks at the playhead; holding the lane asks at the second the
    // finger touched. Same interpolation, one implementation.
    const t = atTime === null ? playheadTime : atTime;
    let k0 = null, k1 = null;
    for (const k of keys) {
      if (k.time <= t) k0 = k; else { k1 = k; break; }
    }
    if (!k0) k0 = keys[0];
    if (!k1) k1 = keys[keys.length - 1];
    const span = k1.time - k0.time;
    if (span <= 0) return k1.position;
    const f = (t - k0.time) / span;
    return [
      k0.position[0] + (k1.position[0] - k0.position[0]) * f,
      k0.position[1] ?? 0,
      k0.position[2] + (k1.position[2] - k0.position[2]) * f,
    ];
  }, [keyframes, playheadTime, itemCurrentPosition]);

  /** KEY MOVEMENT: record a position keyframe for a player at the CURRENT interpolated position.
   *  atTime: hold an empty stretch of the Movement lane and the key is written
   *  at the second the finger touched, with the position the player has THERE. */
  const handleKeyPress = useCallback((side, index, atTime = null) => {
    const tm = itemTrackMeta({ type: 'player', side, index });
    // NOT named `t` — the translator is `t`, and this callback toasts.
    // The key grid, ALWAYS — including when the instant comes from the playhead.
    // Pausing playback leaves the playhead off the 0.25s grid (say 3.17s), and this
    // write used to follow it: the item menu labelled its own entry "✚ Key here
    // (0:03.00)" — that label calls snapKeyTime — and then wrote at 3.17. A key the
    // ruler can never land on again (scrub snaps to the grid), two ✚ presses a few
    // hundredths apart making a dead spot nobody intended, and a magnet that reads
    // key distances in grid steps left aiming at nothing. The label already
    // promised the grid; the write now keeps that promise.
    const at = snapKeyTime(atTime === null ? playheadTime : atTime);
    const pos = interpolatedPlayerPosition(side, index, at);
    if (!tm || !pos) {
      setToast({ message: t('toast.noPosPlayer'), id: Date.now() });
      return;
    }
    // K at the inherited 00:00 of a child branch: the keyed position EQUALS
    // the inherited key (interpolation at the seam), so the write looks like
    // a no-op — but upsert REPLACES the key object and silently strips its
    // `inherited` flag, making the continuity key editable. Refuse honestly.
    if (refuseLockedDrag(tm.id, at)) return;
    nameUndo('undoVerb.positionKey');
    setKeyframes(prev => upsertPositionKey(prev, tm.id, tm.meta, pos, at));
  }, [itemTrackMeta, interpolatedPlayerPosition, upsertPositionKey, playheadTime, keyframes, nameUndo, t]);

  /** KEY ACTION: record an action keyframe (e.g. 'pose_kick_right') for a player at the playhead. */
  const handleActionKey = useCallback((side, index, value) => {
    const trackId = `${side}:${index}`;
    const time = snapKeyTime(playheadTime);
    nameUndo('undoVerb.actionKey');
    setKeyframes(prev => {
      let found = false;
      const next = prev.map(t => {
        if (t.id !== trackId) return t;
        found = true;
        const keys = (t.action || []).filter(k => k.time !== time);
        keys.push({ time, value });
        keys.sort((a, b) => a.time - b.time);
        return { ...t, action: keys };
      });
      if (!found) next.push({ id: trackId, type: 'player', side, index, action: [{ time, value }] });
      return next;
    });
  }, [playheadTime, nameUndo]);

  /** Move a keyframe to a new time (drag on the timeline). */
  // ═══════════════════════════════════════════════════════════════════
  // V3: INHERITED KEY GUARD
  // A sub-branch's first key (t = 0) is the previous branch's last key — it is
  // what makes the branch continue from its parent, so no editing path may
  // touch it. The root branch has no lock. Reads refs, so it is safe inside
  // the state updaters below.
  // ═══════════════════════════════════════════════════════════════════
  const inheritedLockTime = () => {
    const tree = refScenarioTree.current;
    const openId = refOpenBranchId.current;
    if (!tree || !openId) return null;
    return tree.id === openId ? null : 0;
  };
  const isLockedKeyTime = (time) => {
    const lt = inheritedLockTime();
    return lt !== null && Math.abs(time - lt) < 0.001;
  };
  /** Refuse an edit of an inherited key, telling the user why. */
  const refuseLockedKey = () => {
    setToast({ message: t('branch.lockedKeyToast'), id: Date.now() });
    return false;
  };
  /**
   * Drag-path twin. A drag at the locked instant is only a continuity break
   * when the item's track ACTUALLY holds an inherited key there (position or
   * visual — the lanes a move writes). Placing/moving a brand-new item at
   * 00:00 of a child branch is legitimate: nothing was inherited for it.
   * Throttled toast: a live drag fires per pointermove, the lesson says once.
   */
  const refLockedDragToastAt = useRef(0);
  const refuseLockedDrag = (itemId, time, lanes = ['position', 'visual']) => {
    if (!isLockedKeyTime(time)) return false;
    const tr = keyframes.find(t => t.id === itemId);
    const inheritedHere = tr && lanes.some(l =>
      (tr[l] || []).some(k => Math.abs(k.time - time) < 1e-9 && k.inherited));
    if (!inheritedHere) return false;
    const now = Date.now();
    if (now - refLockedDragToastAt.current > 1200) {
      refLockedDragToastAt.current = now;
      setToast({ message: t('branch.lockedDragToast'), id: now });
    }
    return true;
  };

  /**
   * RIPPLE — [ / ] open or close a GAP after the playhead.
   *
   * ⌥+ / ⌥− already re-times the WHOLE branch (stretch/compress). This is its
   * sibling for LOCAL retiming: the playhead is a CUT, and everything strictly
   * AFTER it slides ±0.25 s (±1 s with ⇧) — every track, every lane, ONE undo
   * step — instead of dragging each later key by hand. The key AT the cut
   * anchors the move and never shifts.
   *
   * Plan-before-burn: every landing is validated first (the inherited seam and
   * occupied instants refuse the whole operation, burning nothing), because a
   * half-rippled timeline is worse than none at all.
   */
  const handleRippleKeys = useCallback((delta) => {
    const EPS = 1e-6;
    const cut = refPlayheadTime.current ?? 0;
    const after = (k) => k && Number(k.time) > cut + EPS;
    let moved = 0, stayedLocked = 0;
    for (const tr of keyframes) {
      for (const lane of KEY_ARRAYS) {
        for (const k of (tr?.[lane] || [])) {
          if (!after(k)) continue;
          if (isLockedKeyTime(k.time)) { stayedLocked++; continue; }
          const nt = snapKeyTime(Number(k.time) + delta);
          if (nt < 0 || isLockedKeyTime(nt)) {
            setToast({ message: t('toast.rippleSeamBlocked', { t: fmtTime(Math.max(0, nt)) }), id: Date.now() });
            return;
          }
          if ((tr[lane] || []).some(o => o && !after(o) && Math.abs(Number(o.time) - nt) < EPS)) {
            setToast({ message: t('toast.rippleKeyBlocked', { t: fmtTime(nt) }), id: Date.now() });
            return;
          }
          moved++;
        }
      }
    }
    if (!moved) {
      setToast({ message: t('toast.rippleNothingAfter', { t: fmtTime(cut) }), id: Date.now() });
      return;
    }
    const oldLast = lastKeyTime(keyframes);
    const oldDur = refTimelineDurationState.current ?? DEFAULT_BRANCH_DURATION;
    handleDragStart('undoVerb.rippleKeys');
    setKeyframes(prev => prev.map(tr => {
      const out = { ...tr };
      for (const lane of KEY_ARRAYS) {
        if (!Array.isArray(tr?.[lane])) continue;
        out[lane] = tr[lane].map(k => (after(k) && !isLockedKeyTime(k.time))
          ? { ...k, time: snapKeyTime(Number(k.time) + delta) } : k);
      }
      return out;
    }));
    // Duration follows the LAST key only when it was tight to it — slack stays slack.
    if (Math.abs(oldDur - oldLast) < 1e-9) {
      const newDur = Math.max(0, snapKeyTime(oldLast + delta));
      setTimelineDuration(newDur);
      refTimelineDurationState.current = newDur;
    }
    // Re-anchor any OPEN editor to the shifted times (same rule as ⌥+/−) —
    // otherwise it keeps editing a key that has moved out from under it.
    const shiftAnchor = (t) => (Number(t) > cut + EPS ? snapKeyTime(Number(t) + delta) : t);
    if (keyframeEdit) setKeyframeEdit({ ...keyframeEdit, time: shiftAnchor(keyframeEdit.time) });
    if (segmentEdit) {
      const rf = shiftAnchor(segmentEdit.from), rt = shiftAnchor(segmentEdit.to);
      setSegmentEdit(rf < rt ? { ...segmentEdit, from: rf, to: rt } : null);
    }
    handleDragEnd();
    setToast({
      // The PLURAL is a dictionary word, never `n + ' key' + (n === 1 ? '' : 's')`:
      // Portuguese says "1 chave movida" and "{n} chaves movidas" — the stem, the
      // article and the ending all move together, and only the dictionary knows.
      message: t(moved === 1
        ? (delta > 0 ? 'ripple.movedLaterOne' : 'ripple.movedEarlierOne')
        : (delta > 0 ? 'ripple.movedLater' : 'ripple.movedEarlier'), { n: moved, t: fmtTime(cut) })
        + (stayedLocked ? t('ripple.stayed', { n: stayedLocked }) : '')
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
  }, [keyframes, setKeyframes, handleDragStart, handleDragEnd, setTimelineDuration,
      keyframeEdit, segmentEdit, setKeyframeEdit, setSegmentEdit, setToast, t]);

  const handleKeyframeTimeChange = useCallback((trackId, trackType, oldTime, newTime) => {
    if (oldTime === newTime) return false;
    if (isLockedKeyTime(oldTime) || isLockedKeyTime(newTime)) { refuseLockedKey(); return false; }
    const tr0 = refKeyframesState.current.find(t => t.id === trackId);
    if (!(tr0?.[trackType] || []).some(k => k.time === oldTime)) return false;
    // Each lane is independent. Only another key of this property can block it.
    if ((tr0[trackType] || []).some(k => Math.abs(k.time - newTime) < 1e-9)) {
      const now = Date.now();
      if (now - refKeyBlockToastAt.current > 800) {
        refKeyBlockToastAt.current = now;
        setToast({ message: t('kf.blockedDrag', { t: fmtTime(newTime) }), id: now });
      }
      return false;
    }
    setKeyframes(prev => prev.map(track => {
      if (track.id !== trackId) return track;
      const keys = track[trackType] || [];
      if (!keys.some(k => k.time === oldTime) || keys.some(k => k.time === newTime)) return track;
      const updated = { ...track, [trackType]: keys.map(k => k.time === oldTime ? { ...k, time: newTime } : k)
        .sort((a, b) => a.time - b.time) };
      if (trackType === 'position' && track.segments?.length) {
        updated.segments = track.segments.map(s => ({ ...s,
          from: s.from === oldTime ? newTime : s.from,
          to: s.to === oldTime ? newTime : s.to,
        })).sort((a, b) => a.from - b.from);
      }
      return updated;
    }));
    // Only editors for the moved property should follow its key.
    const near = (a, b) => Math.abs(a - b) < 1e-9;
    const kf = refKeyframeEdit.current;
    if (trackType === 'position' && kf && kf.trackId === trackId && near(kf.time, oldTime)) _setKeyframeEdit({ trackId, time: newTime });
    const seg = refSegmentEdit.current;
    if (trackType === 'position' && seg && seg.trackId === trackId && (near(seg.from, oldTime) || near(seg.to, oldTime))) {
      _setSegmentEdit({ trackId,
        from: near(seg.from, oldTime) ? newTime : seg.from,
        to: near(seg.to, oldTime) ? newTime : seg.to });
    }
    const ge = refGraphicEdit.current;
    if (trackType === 'graphic' && ge && ge.trackId === trackId && (near(ge.start, oldTime) || near(ge.end, oldTime))) {
      _setGraphicEdit({ trackId,
        start: near(ge.start, oldTime) ? newTime : ge.start,
        end: near(ge.end, oldTime) ? newTime : ge.end });
    }
    const ze = refZoneVisualEdit.current;
    if (trackType === 'visual' && ze?.trackId === trackId) {
      const editedTime = tr0.visual?.[ze.keyIndex]?.time;
      const sorted = tr0.visual.map(k => k.time === oldTime ? { ...k, time: newTime } : k).sort((a, b) => a.time - b.time);
      const keyIndex = sorted.findIndex(k => k.time === (editedTime === oldTime ? newTime : editedTime));
      if (keyIndex >= 0) _setZoneVisualEdit({ ...ze, keyIndex });
    }
    return true;
  }, [keyframes]);
  /**
   * ⌥-DRAG a key = RIPPLE drag — the mouse twin of [ / ].
   *
   * A plain drag moves one moment and leaves the gap where it was. Holding ⌥
   * while dragging ALSO slides everything after the cut by the same delta, so
   * gaps open and close as you pull — the ripple trim video editors use, with
   * no new widget. Same guards as the plain drag, all-or-nothing per step: the
   * inherited seam and occupied instants stall the drag (throttled toast)
   * instead of half-shifting the timeline. One undo step for the whole gesture
   * (the strip wraps the drag in handleDragStart/End).
   */
  const handleKeyframeRippleDrag = useCallback((trackId, trackType, oldTime, newTime) => {
    const EPS = 1e-6;
    if (isLockedKeyTime(oldTime) || isLockedKeyTime(newTime)) { refuseLockedKey(); return; }
    const say = (message) => {
      const now = Date.now();
      if (now - refKeyBlockToastAt.current > 800) {
        refKeyBlockToastAt.current = now;
        setToast({ message, id: now });
      }
    };
    const delta = newTime - oldTime;
    const grid = (t) => Math.round(t * 4) / 4;
    // PLAN the whole cut before burning anything. The cut is UNIFORM: every
    // key at oldTime (in any track) lands at newTime, every key after it
    // slides by delta — so a cross-track moment (a pass catch) never splits.
    for (const tr of keyframes) {
      for (const lane of KEY_ARRAYS) {
        const arr = tr?.[lane] || [];
        for (const k of arr) {
          const kt = Number(k.time);
          if (!(kt > oldTime - EPS)) continue;              // before the cut: stays
          const nt = Math.abs(kt - oldTime) < EPS ? newTime : grid(kt + delta);
          if (nt < 0 || isLockedKeyTime(nt)) { refuseLockedKey(); return; }
          if (arr.some(o => {
            const ot = Number(o.time);
            return ot < oldTime - EPS && Math.abs(ot - nt) < EPS;
          })) {
            say(t('kf.blockedDrag', { t: fmtTime(nt) }));
            return;
          }
        }
      }
    }
    setKeyframes(prev => rippleMoveTracks(prev, oldTime, newTime));
    // RE-ANCHOR open editors with the SAME transform the keys got — the cut is
    // uniform, so an editor pointing at a shifted instant (in any track) must
    // slide with it or its Apply writes into a stale time.
    const slide = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return v;
      if (Math.abs(n - oldTime) < EPS) return newTime;
      if (n > oldTime + EPS) return grid(n + delta);
      return v;
    };
    const kf = refKeyframeEdit.current;
    if (kf && Number(kf.time) > oldTime - EPS) _setKeyframeEdit({ ...kf, time: slide(kf.time) });
    const seg = refSegmentEdit.current;
    if (seg && (Number(seg.from) > oldTime - EPS || Number(seg.to) > oldTime - EPS)) {
      _setSegmentEdit({ ...seg, from: slide(seg.from), to: slide(seg.to) });
    }
    const ge = refGraphicEdit.current;
    if (ge && (Number(ge.start) > oldTime - EPS || Number(ge.end) > oldTime - EPS)) {
      _setGraphicEdit({ ...ge, start: slide(ge.start), end: slide(ge.end) });
    }
  }, [keyframes, setKeyframes, setToast]);

  /**
   * NUDGE KEY TIMING (Alt+←/→): move the selected item's movement key that is
   * NEAREST the playhead by one 0.25 s grid step (Shift = 1 s). Timing edits
   * without touching the timeline strip at all — select the player, nudge.
   * A nudge that would land on another key is refused (no silent swallowing).
   * Keys sharing the moved instant (a pass catch = position + attach) travel
   * together as one MOMENT — the pass never splits.
   * With a group active, EVERY member's nearest key moves by the same delta —
   * planned all-or-nothing so a group never half-shifts its timing.
   */
  const handleNudgeSelectedKey = useCallback((delta, note, forItems = null) => {
    // FOR-ITEMS: the item menu speaks for the item whose menu this is, never for
    // whatever happens to be selected — the strip's action button opens that menu
    // without selecting. With forItems the caller owns the target list, and the
    // open-editor swap stands aside: the menu entry is about THIS item's key.
    if (!forItems && !selectedItem) return;
    const editorKind = forItems ? null : (graphicEdit ? 'graphic' : (zoneVisualEdit ? 'visual' : null));
    if (editorKind) {
      // EDITOR-DRIVEN: with a WINDOW editor open (graphic colours / zone
      // visuals), the arrows move THAT WINDOW'S boundary key nearest the
      // playhead — the strip-drag's keyboard twin. The boundary can never cross
      // its neighbours (keeps windows ordered, keyIndex stable, editor honest).
      const ed = graphicEdit || zoneVisualEdit;
      const eTrack = keyframes.find(t => t.id === ed.trackId);
      const eKeys = [...(eTrack?.[editorKind] || [])].sort((a, b) => a.time - b.time);
      if (eKeys.length === 0) return;
      const eDur = refTimelineDurationState.current;
      const eT0 = refPlayheadTime.current;
      let eBest = null, eBestD = Infinity;
      for (const k of eKeys) {
        const d = Math.abs(k.time - eT0);
        if (d < eBestD) { eBestD = d; eBest = k; }
      }
      if (!eBest) return;
      if (isLockedKeyTime(eBest.time)) { refuseLockedKey(); return; }
      const eNext = snapKeyTime(eBest.time + delta);
      if (eNext < 0 || eNext > eDur) {
        setToast({ message: t('toast.noRoomToStep', { t: fmtTime(eDur) }), id: Date.now() });
        return;
      }
      const eIdx = eKeys.indexOf(eBest);
      const eLo = eIdx > 0 ? eKeys[eIdx - 1].time : -0.001;
      const eHi = eIdx < eKeys.length - 1 ? eKeys[eIdx + 1].time : eDur + 0.001;
      if (eNext <= eLo || eNext >= eHi) {
        setToast({ message: t('toast.edgeCross', { t: fmtTime(eNext <= eLo ? eLo : eHi) }), id: Date.now() });
        return;
      }
      handleDragStart('undoVerb.moveKeyInTime');
      handleKeyframeTimeChange(ed.trackId, editorKind, eBest.time, eNext);
      handleDragEnd();
      setPlayheadTime(eNext);
      if (graphicEdit) {
        const gs = Math.abs(graphicEdit.start - eBest.time) < 1e-9 ? eNext : graphicEdit.start;
        const ge = Math.abs(graphicEdit.end - eBest.time) < 1e-9 ? eNext : graphicEdit.end;
        setGraphicEdit({ ...graphicEdit, start: gs, end: ge });
      }
      setToast({ message: t('toast.edgeStepped', { from: fmtTime(eBest.time), to: fmtTime(eNext) }) + t('toast.undoSuffix'), id: Date.now() });
      return;
    }
    const inGroup = !forItems && groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
    const targets = forItems || (inGroup ? groupItems : [selectedItem]);
    const dur = refTimelineDurationState.current;
    const t0 = refPlayheadTime.current;
    const plan = [];
    for (const it of targets) {
      const trackId = it.type === 'player'
        ? `${it.side}:${it.index}`
        : `${it.type}:${it.id}`;
      const track = keyframes.find(t => t.id === trackId);
      const keys = track?.position || [];
      if (keys.length === 0) continue;
      let best = null, bestD = Infinity;
      for (const k of keys) {
        const d = Math.abs(k.time - t0);
        if (d < bestD) { bestD = d; best = k; }
      }
      if (!best) continue;
      if (isLockedKeyTime(best.time)) { refuseLockedKey(); return; }
      const next = snapKeyTime(best.time + delta);
      if (next < 0 || next > dur) {
        setToast({ message: t('toast.noRoomToStep', { t: fmtTime(dur) }), id: Date.now() });
        return;
      }
      const blocked = (tt) => setToast({ message: t('toast.nudgeKeyBlocked', { t: fmtTime(tt) }), id: Date.now() });
      if (keys.some(k => k.time !== best.time && Math.abs(k.time - next) < 1e-9)) { blocked(next); return; }
      // A whole MOMENT moves together (see apply below) — a key in ANY lane
      // already sitting at the target time is a collision, refuse honestly.
      if (['attach', 'graphic', 'visual', 'action'].some(lane =>
        (track?.[lane] || []).some(k => Math.abs(k.time - next) < 1e-9))) { blocked(next); return; }
      plan.push({ trackId, from: best.time, to: next });
    }
    if (plan.length === 0) return;
    // Remember each member's moved key BEFORE applying — the segment editor
    // re-anchor below needs the OLD times after the keys have moved.
    const moved = new Map(); // trackId -> { from, to }
    handleDragStart('undoVerb.moveKeyInTime');
    for (const p of plan) {
      moved.set(p.trackId, p);
      handleKeyframeTimeChange(p.trackId, 'position', p.from, p.to);
      // A MOMENT moves as a unit: a pass catch is a position key AND an
      // attach key at the same time — nudging only the position key would
      // split the catch and silently break the pass. Every lane keyed at the
      // moved instant travels with it.
      const tr = keyframes.find(t => t.id === p.trackId);
      for (const lane of ['attach', 'graphic', 'visual', 'action']) {
        if ((tr?.[lane] || []).some(k => Math.abs(k.time - p.from) < 1e-9)) {
          handleKeyframeTimeChange(p.trackId, lane, p.from, p.to);
        }
      }
    }
    handleDragEnd();
    setPlayheadTime(plan[0].to); // follow the selection's key
    // If the height editor is open on the key that just moved, RE-ANCHOR it to
    // the new time — otherwise its { trackId, time } lookup fails and the
    // editor silently vanishes mid-nudge.
    if (keyframeEdit && keyframeEdit.trackId === plan[0].trackId
        && Math.abs(keyframeEdit.time - plan[0].from) < 1e-9) {
      setKeyframeEdit({ ...keyframeEdit, time: plan[0].to });
    }
    // Same orphan guard for the SEGMENT editor: if the nudged key is one of
    // the open segment's bounds, slide the bound with it (group nudges check
    // every member) so the transition editor keeps editing the same segment.
    if (segmentEdit) {
      let segFrom = segmentEdit.from, segTo = segmentEdit.to;
      for (const { trackId, from, to } of moved.values()) {
        if (trackId !== segmentEdit.trackId) continue;
        if (Math.abs(segmentEdit.from - from) < 1e-9) segFrom = to;
        if (Math.abs(segmentEdit.to - from) < 1e-9) segTo = to;
      }
      if (segFrom !== segmentEdit.from || segTo !== segmentEdit.to) {
        setSegmentEdit({ ...segmentEdit, from: segFrom, to: segTo });
      }
    }
    // The nudge is otherwise SILENT — and the pass toast promises
    // "⌥←/⌥→ tunes the catch". Name the new instant, and say when the
    // whole moment (attach + position) travelled together.
    const lanes = (keyframes.find(t => t.id === plan[0].trackId) || {});
    const moment = ['attach', 'graphic', 'visual', 'action'].some(lane =>
      (lanes[lane] || []).some(k => Math.abs(k.time - plan[0].from) < 1e-9));
    setToast({ message: t('toast.nudgeKey', { t: fmtTime(plan[0].to) })
      + (note ? t('toast.nudgeNote', { note }) : '')
      + (moment ? t('toast.nudgeMoment') : '')
      + (plan.length > 1 ? t('toast.nudgeGroup', { n: plan.length }) : '')
      + (note ? t('toast.undoSuffix') : t('toast.nudgeStepSuffix')), id: Date.now() });
  }, [selectedItem, groupItems, keyframes, handleKeyframeTimeChange, handleDragStart, handleDragEnd, keyframeEdit, setKeyframeEdit, segmentEdit, setSegmentEdit, graphicEdit, zoneVisualEdit, setGraphicEdit, t]);

  /**
   * S — LAND ON THE SAME SECOND. targets.ejs:31 states the exercise: "key the
   * target sweeping across the box, then key a striker's run to ARRIVE AT THE
   * SAME SECOND". Until now that alignment meant opening the timeline and
   * dragging a diamond onto another track's diamond — or stepping ⌥←/→ a
   * quarter second at a time and watching two toasts.
   *
   * S takes the selected item's key nearest the playhead and jumps it to the
   * NEAREST instant another track already keys (either side). It reuses the
   * nudge's apply path, so everything that verb guarantees still holds: the
   * whole MOMENT travels (position + attach + arrow + shape keys at that
   * instant), collisions refuse instead of swallowing, editors re-anchor, and
   * with a group active EVERY member lands on the same instant — "these four
   * arrive together" is one press.
   */
  /**
   * The instant ANOTHER track keys that this key should land on — the S verb's
   * question, asked two ways: by the chord (from the selection and the
   * playhead) and by the key editor (from the key on screen). Every lane of
   * every other track is a candidate — an action key at 4.0 is a moment worth
   * syncing to just as much as a position key — and the inherited seam never
   * is. Nearest wins; a tie lands on the EARLIER instant (the setup, not the
   * follow-up), so the answer is deterministic either way round.
   */
  const nearestOtherKeyTime = useCallback((trackId, time, excludeIds = null) => {
    const mine = new Set(excludeIds && excludeIds.length ? excludeIds : [trackId]);
    const seen = new Set();
    let best = null;
    for (const tr of keyframes) {
      if (!tr || mine.has(tr.id)) continue;
      for (const lane of KEY_ARRAYS) {
        for (const k of (tr[lane] || [])) {
          const t = Number(k?.time);
          if (!Number.isFinite(t) || isLockedKeyTime(t)) continue; // the seam is read-only
          if (Math.abs(t - time) < 1e-9 || seen.has(t)) continue;
          seen.add(t);
          const d = Math.abs(t - time);
          if (!best || d < best.d || (d === best.d && t < best.t)) best = { t, d, trackId: tr.id };
        }
      }
    }
    return best;
  }, [keyframes, isLockedKeyTime]);

  const handleLandOnNearestKeyTime = useCallback(() => {
    if (!selectedItem) {
      setToast({ message: t('toast.sNeedItem'), id: Date.now() });
      return;
    }
    const trackIdOf = (it) => (it.type === 'player' ? `${it.side}:${it.index}` : `${it.type}:${it.id}`);
    const inGroup = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
    const members = inGroup ? groupItems : [selectedItem];
    const t0 = refPlayheadTime.current;
    const own = (keyframes.find(t => t.id === trackIdOf(selectedItem)) || {}).position || [];
    let from = null, fromD = Infinity;
    for (const k of own) {
      const d = Math.abs(Number(k.time) - t0);
      if (d < fromD) { fromD = d; from = Number(k.time); }
    }
    if (from === null) {
      setToast({ message: t('toast.sNoOwnKey'), id: Date.now() });
      return;
    }
    const best = nearestOtherKeyTime(trackIdOf(selectedItem), from, members.map(trackIdOf));
    if (!best) {
      setToast({ message: t('toast.sNothingElse'), id: Date.now() });
      return;
    }
    handleNudgeSelectedKey(best.t - from, t('toast.sameSecond', { who: trackName(best.trackId), t: fmtTime(best.t) }));
    // `trackName` is deliberately NOT in this array: the helper is declared
    // later in the file, and a name in a dependency array is read during the
    // render pass — a TDZ error, not a warning. The call itself happens long
    // after the render, when the binding is initialised.
  }, [selectedItem, groupItems, keyframes, handleNudgeSelectedKey, setToast, nearestOtherKeyTime, t]);

  /** Delete a keyframe (double-click on the timeline). */
  const handleKeyframeDelete = useCallback((trackId, trackType, time) => {
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    // POSITION ↔ ATTACH are two halves of ONE physical event at one instant:
    // a catch = arrival key + claim key, a release = detach key + flight key.
    // Deleting either dot deletes the whole MOMENT — orphan halves leave a
    // claim with no arrival on the strip and cost a second delete + a second
    // undo step. graphic / visual / action stay INDEPENDENT: they are overlays
    // ON the instant (an arrow window often shares a movement key's time),
    // not the instant itself.
    const lanes = (trackType === 'position' || trackType === 'attach')
      ? ['position', 'attach'] : [trackType];
    const tr0 = keyframes.find(t => t.id === trackId);
    const present = lanes.filter(l => (tr0?.[l] || []).some(k => k.time === time));
    if (present.length) {
      // Name the verb BEFORE the write: setKeyframes reads the pending label,
      // and without it the ↩ button read "Undo: edit" over a deletion the
      // coach could not see named anywhere.
      nameUndo(present.length > 1 ? 'undoVerb.deleteMoment' : 'undoVerb.deleteKey');
      // A delete that took one key says so; a delete that took the whole
      // moment says that. Silence was the bug: on a phone the dot simply
      // vanished, and the only sign anything had happened was a ↩ button that
      // changed colour.
      setToast({
        message: present.length > 1
          ? t('toast.momentDeleted', { t: fmtTime(time) })
          : t('toast.keyDeleted', { t: fmtTime(time) }),
        id: Date.now(),
      });
      setKeyframes(prev => prev.map(track => {
        if (track.id !== trackId) return track;
        let updated = track;
        for (const lane of present) {
          updated = { ...updated, [lane]: (track[lane] || []).filter(k => k.time !== time) };
        }
        // Segments touching the deleted key are no longer valid.
        if (present.includes('position') && track.segments?.length) {
          updated.segments = track.segments.filter(s => s.from !== time && s.to !== time);
        }
        return updated;
      }));
    }
    // Editor cleanup runs EVEN when no key was there — an editor anchored on
    // a vanished instant is a ghost either way.
    // Close any editor anchored on the deleted key — its {time}/{from,to}/
    // {start,end} is now a ghost, and Apply would re-create exactly what the
    // user just deleted. Visual-key deletion also shifts keyIndex, so the
    // zone editor on that track closes rather than edit a shifted window.
    const near = (a, b) => Math.abs(a - b) < 1e-9;
    const kf = refKeyframeEdit.current;
    if (kf && kf.trackId === trackId && near(kf.time, time)) _setKeyframeEdit(null);
    const seg = refSegmentEdit.current;
    if (seg && seg.trackId === trackId && (near(seg.from, time) || near(seg.to, time))) _setSegmentEdit(null);
    const ge = refGraphicEdit.current;
    if (ge && ge.trackId === trackId && (near(ge.start, time) || near(ge.end, time))) _setGraphicEdit(null);
    const zve = refZoneVisualEdit.current;
    if (zve && zve.trackId === trackId && trackType === 'visual') _setZoneVisualEdit(null);
  }, [keyframes, t, nameUndo]);

  /**
   * Set the HEIGHT (position[1]) of a position keyframe — used by the
   * transition editor's Height slider. The height is a property of the
   * keyframe itself, so the NEXT transition automatically starts from
   * whatever height this one ends at.
   */
  const handleKeyframeHeightChange = useCallback((trackId, time, height) => {
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    nameUndo('undoVerb.keyHeight');
    setKeyframes(prev => prev.map(track => {
      if (track.id !== trackId) return track;
      return {
        ...track,
        position: (track.position || []).map(k => (
          k.time === time ? { ...k, position: [k.position[0], height, k.position[2]] } : k
        )),
      };
    }));
  }, [nameUndo]);

  /**
   * Nudge a position keyframe's SPOT on the pitch — position[0] is end↔end
   * (x) and position[2] is flank↔flank (z), the same axes the pitch is drawn
   * on. This is the MEASURED twin of dragging an item: until now the only
   * numeric repositioning anywhere was a grabbed zone VERTEX (Ctrl+arrows),
   * so putting a player or cone exactly 2 m upfield meant dragging and hoping.
   */
  const handleKeyframeXZNudge = useCallback((trackId, time, dx, dz) => {
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    // Compute from current state FIRST — the updater stays pure, and the
    // toast can report the coordinate the key actually landed on.
    const track = keyframes.find(t => t.id === trackId);
    const key = track?.position?.find(k => k.time === time);
    if (!key) return;
    const np = [key.position[0] + dx, key.position[1], key.position[2] + dz];
    nameUndo('undoVerb.keyPosition');
    setKeyframes(prev => prev.map(t => (t.id !== trackId ? t : {
      ...t,
      position: (t.position || []).map(k => (k.time === time ? { ...k, position: np } : k)),
    })));
    // The coordinate IS the state change: without it a 0.5 m step is a
    // half-pixel twitch on screen and you cannot tell which way it went.
    setToast({
      message: t('vertex.at', {
        x: Math.round(np[0] * 100) / 100, y: Math.round(np[2] * 100) / 100,
      }) + t('toast.undoSuffix'),
      id: Date.now(),
    });
  }, [keyframes, nameUndo, setToast, t]);

  /**
   * Slide a WHOLE GROUP's spots at once. Dragging one member already moves
   * them all, so the keyboard twin has to as well — one undo step for the
   * block, one toast that names the leader's new coordinate. Seam-locked keys
   * are skipped and COUNTED, never silently swallowed.
   */
  const handleGroupXZNudge = useCallback((items, dx, dz) => {
    const trackIdOf = (it) => (it.type === 'player' ? `${it.side}:${it.index}` : `${it.type}:${it.id}`);
    const plan = [];
    let locked = 0;
    for (const it of items) {
      const tid = trackIdOf(it);
      const active = [...(keyframes.find(t => t.id === tid)?.position || [])]
        .sort((a, b) => a.time - b.time)
        .filter(kk => kk.time <= refPlayheadTime.current + 1e-6).pop();
      if (!active) continue;
      if (isLockedKeyTime(active.time)) { locked++; continue; }
      plan.push({ tid, time: active.time, leader: it });
    }
    if (!plan.length) {
      setToast({
        message: locked
          ? t('seam.keysReadOnly')
          : (items.length === 1
            ? t('kf.noPosKey')
            : t('kf.noPosKeyMany')),
        id: Date.now(),
      });
      return false;
    }
    nameUndo('undoVerb.keyPosition');
    setKeyframes(prev => prev.map(t => {
      const p = plan.find(x => x.tid === t.id);
      if (!p) return t;
      return {
        ...t,
        position: (t.position || []).map(k => (k.time === p.time
          ? { ...k, position: [k.position[0] + dx, k.position[1], k.position[2] + dz] }
          : k)),
      };
    }));
    const lead = plan[0];
    const leadKey = keyframes.find(t => t.id === lead.tid)?.position?.find(k => k.time === lead.time);
    const nx = Math.round((leadKey.position[0] + dx) * 100) / 100;
    const nz = Math.round((leadKey.position[2] + dz) * 100) / 100;
    setToast({
      message: (plan.length > 1
        ? t('toast.groupSlidAll', { n: plan.length, d: Math.abs(dx || dz), who: getItemDisplayName(lead.leader), x: nx, z: nz })
        : t('toast.groupSlidOne', { who: getItemDisplayName(lead.leader), x: nx, z: nz }))
        + t('vertex.shiftFine')
        + (locked ? t('vis.lockedAtSeam', { n: locked }) : '')
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
    return true;
  // NOTE: getItemDisplayName is declared BELOW this callback — naming it in the
  // deps array would hit TDZ during render. It is a stable useCallback, safe to
  // read through the closure when the toast fires.
  }, [keyframes, nameUndo, setToast]);

  /** Position-keyframe click → open the height editor and scrub to its time
      so the object sits exactly at the height being edited. Any other open
      editor/prompt is dismissed first — only one popup at a time. */
  const handleKeyframeClick = useCallback((trackId, time) => {
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    if (attachPickMode) setAttachPickMode(null);
    if (promptState) setPromptState(null);
    setSegmentEdit(null);
    setGraphicEdit(null);
    setZoneVisualEdit(null);
    setIsPlaying(false);
    setKeyframeEdit({ trackId, time });
    if (Math.abs(playheadTime - time) > 1e-9) setPlayheadTime(time);
  }, [playheadTime, attachPickMode, promptState]);

  /** Graphic track key (on/off) for a player. */
  const handleGraphicKey = useCallback((side, index, value) => {
    const trackId = `${side}:${index}`;
    const time = snapKeyTime(playheadTime);
    // Graphic keys ARE inherited at the seam (an item's visibility at the
    // branch start is part of the parent's end state) — writing at 00:00
    // would silently re-form the seam. Key at 0.25+ instead.
    if (refuseLockedDrag(trackId, time, ['graphic'])) return;
    nameUndo('undoVerb.arrowKey');
    setKeyframes(prev => {
      let found = false;
      const next = prev.map(t => {
        if (t.id !== trackId) return t;
        found = true;
        const keys = (t.graphic || []).filter(k => k.time !== time);
        keys.push({ time, value });
        keys.sort((a, b) => a.time - b.time);
        return { ...t, graphic: keys };
      });
      if (!found) next.push({ id: trackId, type: 'player', side, index, graphic: [{ time, value }] });
      return next;
    });
  }, [playheadTime, keyframes, nameUndo]);

  // ─────────────────────────────────────────────────────────────
  // ITEM TRACKS (ball / cone / target / zone)
  // Any selectable item can carry its own keyframe tracks. The
  // ball is special: instead of an Action track it has an
  // Attachment track — keys attach it to a player ('home:3') or
  // detach it ('none'), which drives its position and path arrows.
  // ─────────────────────────────────────────────────────────────

  /** Generic upsert for any track type (position / action / attach / graphic). */
  const upsertTrackKey = useCallback((trackId, trackType, keyObj) => {
    const time = keyObj.time;
    // V3: a sub-branch's first key is inherited — never overwritten.
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    // The undo label is a DICTIONARY KEY, never a template. `` `${trackType}
    // key` `` put bare English ("attach key") inside a Portuguese sentence —
    // the same bug class as an untranslated string, and the ↩ button read it
    // back to the coach. The noun also matches the lane's own label
    // (Attachment track / Faixa de anexação / Pista de anexión), so the
    // sentence and the strip name one thing.
    const LANE_UNDO = {
      graphic: 'undoVerb.arrowKey',
      position: 'undoVerb.positionKey',
      action: 'undoVerb.actionKey',
      attach: 'undoVerb.attachKey',
      visual: 'undoVerb.visualKey',
    };
    nameUndo(LANE_UNDO[trackType] || 'undoVerb.edit');
    setKeyframes(prev => {
      let found = false;
      const next = prev.map(t => {
        if (t.id !== trackId) return t;
        found = true;
        const keys = (t[trackType] || []).filter(k => k.time !== time);
        keys.push(keyObj);
        keys.sort((a, b) => a.time - b.time);
        return { ...t, [trackType]: keys };
      });
      if (!found) next.push({ id: trackId, [trackType]: [keyObj] });
      return next;
    });
  }, [nameUndo]);

  /** Recover the item descriptor from a track id ('ball:ball-01' → { type:'ball', id:'ball-01' }). */
  const itemFromTrackId = useCallback((trackId) => {
    if (trackId.startsWith('ball:')) return { type: 'ball', id: trackId.slice(5) };
    if (trackId.startsWith('cone:')) return { type: 'cone', id: trackId.slice(5) };
    if (trackId.startsWith('target:')) return { type: 'target', id: trackId.slice(7) };
    if (trackId.startsWith('zone:')) return { type: 'zone', id: trackId.slice(5) };
    return null;
  }, []);

  /** Human-friendly item name: 'Ball 01', 'Cone 02', ... (positional within its type). */
  const getItemDisplayName = useCallback((item) => {
    if (!item) return '';
    // THE name of an item, in the coach's language. Dozens of toasts read it
    // from here, so the word-list that used to sit in this function decided the
    // language of every one of them: a Portuguese coach was told "Cone 03
    // deleted" because a map of four English nouns lived at the source.
    const key = { ball: 'itm.ball', cone: 'itm.cone', target: 'itm.target', zone: 'itm.zone' }[item.type];
    const label = key ? t(key) : item.type;
    let list = [];
    if (item.type === 'ball') list = pitchState?.balls || [];
    else if (item.type === 'cone') list = pitchState?.cones || [];
    else if (item.type === 'target') list = pitchState?.targets || [];
    else if (item.type === 'zone') list = pitchState?.zones || [];
    const idx = list.findIndex(x => x.id === item.id);
    return `${label} ${String(idx >= 0 ? idx + 1 : 1).padStart(2, '0')}`;
  }, [pitchState, t]);

  /** Interpolated position for ANY track at the playhead (players and items). */
  const interpolatedPosition = useCallback((trackId, fallbackItem, atTime = null) => {
    const track = keyframes.find(k => k.id === trackId);
    const keys = track?.position || [];
    const fallback = fallbackItem ? itemCurrentPosition(fallbackItem) : null;
    if (keys.length === 0) return fallback;
    if (keys.length === 1) return keys[0].position;
    const t = atTime === null ? playheadTime : atTime;
    let k0 = null, k1 = null;
    for (const k of keys) {
      if (k.time <= t) k0 = k; else { k1 = k; break; }
    }
    if (!k0) k0 = keys[0];
    if (!k1) k1 = keys[keys.length - 1];
    const span = k1.time - k0.time;
    if (span <= 0) return k1.position;
    const f = (t - k0.time) / span;
    // Follow the segment trajectory (curve + arch) so keyed positions land on the path.
    return interpolateWithSegment(k0, k1, f, findSegment(track, k0.time, k1.time));
  }, [keyframes, playheadTime, itemCurrentPosition]);

  /**
   * Interpolated position for ANY track at an ARBITRARY time (the playhead
   * sampler above is not enough for passes: the release spot is at the drop
   * second, the catch spot one second later). Falls back to the item/player
   * base position when there is no movement track.
   */
  const positionAtTime = useCallback((trackId, t) => {
    const track = keyframes.find(k => k.id === trackId);
    const keys = track?.position || [];
    if (keys.length === 0) {
      if (/^(home|away):\d+$/.test(trackId)) {
        const [side, idx] = trackId.split(':');
        const players = side === 'home' ? pitchState?.home?.players : pitchState?.away?.players;
        return players?.[Number(idx)]?.position || null;
      }
      return itemCurrentPosition(itemFromTrackId(trackId));
    }
    if (keys.length === 1) return keys[0].position;
    let k0 = null, k1 = null;
    for (const k of keys) {
      if (k.time <= t) k0 = k; else { k1 = k; break; }
    }
    if (!k0) k0 = keys[0];
    if (!k1) k1 = keys[keys.length - 1];
    const span = k1.time - k0.time;
    if (span <= 0) return k1.position;
    const f = (t - k0.time) / span;
    return interpolateWithSegment(k0, k1, f, findSegment(track, k0.time, k1.time));
  }, [keyframes, pitchState, itemCurrentPosition, itemFromTrackId]);

  /** KEY MOVEMENT for an item track — keys its current interpolated position at the playhead.
   *  atTime: the lane hold writes at the second the finger touched instead. */
  const handleItemPositionKey = useCallback((trackId, atTime = null) => {
    // Same rule as handleKeyPress: the instant comes from the grid, so the attach
    // probe below and the position interpolated here are asked about the SAME
    // moment the key is finally written at (line: time: snapKeyTime(at)). Snapping
    // only at the write left the probe reading one side of a half-step and the
    // write landing on the other.
    const at = snapKeyTime(atTime === null ? playheadTime : atTime);
    const item = itemFromTrackId(trackId);
    // ATTACHED items FOLLOW — a position key at this instant is a dead spot
    // the carrier overrides (the ball dribbles, the zone stays glued). The
    // refusal lives HERE, not in the K key path, so the track row's key
    // button — the mouse twin — cannot bypass it either.
    const pTrack = keyframes.find(t => t.id === trackId);
    const pAct = [...(pTrack?.attach || [])].sort((x, y) => x.time - y.time)
      .filter(k => k.time <= at + 1e-6).pop();
    const pCarrier = pAct && pAct.value !== 'none' ? pAct.value
      : (item?.type === 'ball' ? pitchState?.balls?.find(b => b.id === item.id)?.attached : null);
    if (pCarrier) {
      // getItemDisplayName / describeAttachRef are defined BELOW this callback;
      // safe via closure at call time, deliberately NOT in deps (TDZ).
      setToast({ message: t('toast.followsCarrier', { name: getItemDisplayName(item), who: describeAttachRef(pCarrier) }), id: Date.now() });
      return false;
    }
    const pos = interpolatedPosition(trackId, item, at);
    if (!pos) {
      setToast({ message: t('toast.noPosItem'), id: Date.now() });
      return false;
    }
    upsertTrackKey(trackId, 'position', { time: snapKeyTime(at), position: pos });
    return true;
  }, [itemFromTrackId, interpolatedPosition, upsertTrackKey, playheadTime, keyframes, pitchState, t]);

  /** KEY ACTION for an item track (cone / target / zone). */
  const handleItemActionKey = useCallback((trackId, value) => {
    upsertTrackKey(trackId, 'action', { time: snapKeyTime(playheadTime), value });
  }, [upsertTrackKey, playheadTime]);

  /** KEY GRAPHIC (on/off) for an item track. */
  const handleItemGraphicKey = useCallback((trackId, value) => {
    upsertTrackKey(trackId, 'graphic', { time: snapKeyTime(playheadTime), value });
  }, [upsertTrackKey, playheadTime]);

  /**
   * A PLAYER MARKING ANOTHER PLAYER — the same gesture as attaching a ball, with the
   * opposite rule once it lands. A ball riding a player has no path of its own; a
   * marking player holds a BEARING AND A GAP: their own keys are parked, and the
   * sampler keeps them at the chosen compass point and distance from the marked
   * player for as long as the mark key is in force. That is what makes it usable for
   * coaching — the shape the two were in when you asked is preserved, and whatever the
   * marked player does, the marker answers it at that same gap.
   *
   * The maths lives in sampling.js (markPosition, reached from
   * sampleEffectivePosition, which checks a player's mark keys BEFORE the attachment
   * branch because the rules are deliberately different). This is only the asking and
   * the key.
   *
   * (This block used to describe the same verb as "a player FOLLOWING another player"
   * and to name a function — followPosition — that has never existed in sampling.js.
   * The word "follow" survives in the item-attach toast, where it means riding a
   * carrier; for a player the verb is MARK, and the difference is not cosmetic: a
   * follower would keep their own height, a marker does not, which is why the card
   * shuts ↑↓ for a player whose mark key is in force.)
   *
   * Two doors, one prompt: drag a player ONTO another player (the marker is already
   * chosen), or open their Action menu and pick one on the pitch.
   *
   * describeAttachRef is DECLARED BELOW these callbacks, so it is called in the
   * body and deliberately left out of the dependency arrays — naming it there
   * would read a not-yet-initialised binding at render time (the whole app dies
   * on the TDZ error, which is exactly what App.test.js caught the first time).
   */
  const commitMark = useCallback((trackId, value, tKey, dir, dist) => {
    const prior = markKeyAt(keyframes, trackId, tKey);
    const priorDir = Number.isFinite(prior?.dir) ? prior.dir : 0;
    const priorGap = Number.isFinite(prior?.dist) ? prior.dist : MARK_DEFAULT_DIST;
    const sameInstant = !!prior && prior.value === value && Math.abs(prior.time - tKey) < 1e-6;
    const turning = sameInstant && (priorDir !== dir || priorGap !== dist);
    // ANNOUNCE, DON'T FAKE — the rule the arrows lane keeps. The compass opens
    // on the spot they ALREADY hold (that is what makes marking one tap), so a
    // coach who taps "Mark from here" without turning anything has asked for the
    // key that is already there. Writing it burns an undo step for nothing and
    // toasts a change that did not happen. Refuse, leave the dial open, and name
    // the two knobs that would do something.
    if (sameInstant && !turning) {
      setToast({
        message: t('mark.sameSpot', {
          leader: describeAttachRef(value), t: fmtTime(tKey),
          where: t(`mark.dir.${MARK_DIRS[Math.round(dir) % 8] ?? 'front'}`), d: dist.toFixed(1),
        }), id: Date.now(),
      });
      return false;
    }
    handleDragStart('undoVerb.markPlayer');
    const spotAt = (tt, d, g) => {
      // The timeline dot goes where the MARK puts them, not where their own path
      // happened to be. A coach who marks a player and then looks at the strip
      // should see the spot they chose — and on release the run resumes from it.
      const leader = keyframes.find(tr => tr.id === value);
      const at = leader ? sampleEffectivePosition(leader, tt, keyframes) : null;
      const off = at ? markOffset(d, g, sideOfAttachRef(value)) : null;
      return at && off ? [at[0] + off[0], at[1] ?? 0, at[2] + off[2]] : interpolatedPosition(trackId, null);
    };
    // CHANGING SIDES IS A MOVE, NOT A JUMP. A mark key already sitting on this
    // instant would simply be overwritten, and the marker would appear on the
    // far side of the marked player in one frame — the teleport a coach feels as
    // wrong. So the old spot is kept and the new one lands a jog later: the
    // sampler sweeps the arc between them, at a pace their legs can run at.
    // (prior / sameInstant / turning are settled above, before the undo step.)
    let turnEnd = null;
    if (turning) {
      const track = keyframes.find(tr => tr.id === trackId);
      const next = (track?.attach || [])
        .filter(k => k && typeof k.time === 'number' && k.time > tKey + 1e-6)
        .sort((a, b) => a.time - b.time)[0];
      const want = tKey + markTurnSeconds(priorDir, dir, Math.max(priorGap, dist));
      if (!next || next.time > want + 1e-6) turnEnd = snapKeyTime(want);
    }
    // CHANGING WHO YOU MARK IS ALSO A MOVE, NOT A JUMP. The rule above only covers
    // re-aiming at the SAME leader: switching to a different leader is not
    // sameInstant, so turning is false and this used to fall through to the else
    // branch, which wrote a position key AT tKey at the new marking spot. The marker
    // appeared on the far side of the pitch in a single frame - the teleport a coach
    // feels as wrong. Same treatment now: leave them where they actually are at tKey
    // and land the new spot a jog later, at a pace their legs can cover.
    //
    // Gated on the LEADER actually changing, not merely on "not turning". A player who
    // already marks this person and is only re-aiming keeps the existing path, including
    // the case where a release lands half a second later and there is no room to invent
    // time - that is pinned deliberately elsewhere and must stay untouched.
    let retargetEnd = null;
    let hereNow = null;
    if (!turning && prior && prior.value && prior.value !== 'none' && prior.value !== value) {
      const track = keyframes.find(tr => tr.id === trackId);
      hereNow = track ? sampleEffectivePosition(track, tKey, keyframes) : null;
      const there = spotAt(tKey, dir, dist);
      const gapM = hereNow && there ? Math.hypot(hereNow[0] - there[0], hereNow[2] - there[2]) : 0;
      if (gapM > 1.0) {
        const next = (track?.attach || [])
          .filter(k => k && typeof k.time === 'number' && k.time > tKey + 1e-6)
          .sort((a, b) => a.time - b.time)[0];
        const want = tKey + markTravelSeconds(gapM);
        if (!next || next.time > want + 1e-6) retargetEnd = snapKeyTime(want);
      }
    }
    if (turning && turnEnd) {
      const from = spotAt(tKey, priorDir, priorGap);
      if (from) upsertTrackKey(trackId, 'position', { time: tKey, position: from });
      const to = spotAt(turnEnd, dir, dist);
      if (to) upsertTrackKey(trackId, 'position', { time: turnEnd, position: to });
      upsertTrackKey(trackId, 'attach', { time: turnEnd, value, dir, dist });
    } else if (retargetEnd && hereNow) {
      upsertTrackKey(trackId, 'position', { time: tKey, position: hereNow });
      const to = spotAt(retargetEnd, dir, dist);
      if (to) upsertTrackKey(trackId, 'position', { time: retargetEnd, position: to });
      upsertTrackKey(trackId, 'attach', { time: retargetEnd, value, dir, dist });
    } else {
      const spot = spotAt(tKey, dir, dist);
      if (spot) upsertTrackKey(trackId, 'position', { time: tKey, position: spot });
      upsertTrackKey(trackId, 'attach', { time: tKey, value, dir, dist });
    }
    if (Math.abs(tKey - refPlayheadTime.current) > 1e-9) setPlayheadTime(tKey);
    setAttachPickMode(null);
    setMarkPicker(null);
    setPromptState(null);
    setSelectedItem(itemFromTrackId(trackId)
      || { type: 'player', side: trackId.split(':')[0], index: +trackId.split(':')[1] });
    setToast({
      message: (turning && turnEnd
        ? t('mark.turning', {
          who: describeAttachRef(trackId), leader: describeAttachRef(value),
          from: t(`mark.dir.${MARK_DIRS[Math.round(priorDir) % 8]}`), where: t(`mark.dir.${MARK_DIRS[Math.round(dir) % 8]}`),
          t: fmtTime(turnEnd),
        })
        : t('mark.started', {
          who: describeAttachRef(trackId), leader: describeAttachRef(value), t: fmtTime(tKey),
          where: t(`mark.dir.${MARK_DIRS[Math.round(dir) % 8]}`), d: dist.toFixed(1),
        })) + t('toast.undoSuffix'),
      id: Date.now(),
    });
    handleDragEnd();
  }, [handleDragStart, handleDragEnd, keyframes, interpolatedPosition, upsertTrackKey, itemFromTrackId, setSelectedItem, setToast, t]);

  const commitUnmark = useCallback((trackId, tKey) => {
    handleDragStart('undoVerb.markPlayer');
    // Keep them where the MARK left them: without a position key at the release
    // instant they would snap back to a path laid down before the mark ever
    // happened. The effective position — not their own keys — is the truth here.
    const track = keyframes.find(tr => tr.id === trackId);
    const here = (track ? sampleEffectivePosition(track, tKey, keyframes) : null)
      || interpolatedPosition(trackId, null);
    if (here) upsertTrackKey(trackId, 'position', { time: tKey, position: here });
    upsertTrackKey(trackId, 'attach', { time: tKey, value: 'none' });
    if (Math.abs(tKey - refPlayheadTime.current) > 1e-9) setPlayheadTime(tKey);
    setAttachPickMode(null);
    setPromptState(null);
    setToast({
      message: t('mark.stopped', { who: describeAttachRef(trackId), t: fmtTime(tKey) })
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
    handleDragEnd();
  }, [handleDragStart, handleDragEnd, keyframes, interpolatedPosition, upsertTrackKey, setToast, t]);

  /**
   * STOP MARKING for a whole GROUP, in one tap. The group MARK was the answer
   * to "24 taps to set four markers"; this is the other half of it — getting
   * them out used to cost four menu trips, each naming a different leader.
   * Every member keeps the gap they were holding at the release instant (the
   * same rule a single release follows: without a position key there they would
   * snap back to a path laid down before the mark happened), and the whole
   * group comes out in ONE undo step.
   */
  const handleGroupUnmark = useCallback((members, tKey) => {
    const marking = (members || []).filter(m => m && m.type === 'player'
      && attachRefAt(keyframes, `${m.side}:${m.index}`, tKey));
    if (!marking.length) {
      // A refusal, not a silence: the entry only appears when someone is
      // marking, so getting here means the scene changed under the menu.
      setToast({ message: t('mark.groupNoneStop'), id: Date.now() });
      return;
    }
    // THE SEAM. At a child branch's 00:00 the mark key is inherited and read
    // only, and every write below would be refused one at a time — after an
    // undo step had been burned, and under a toast claiming the marks stopped.
    // Refuse once, up front, and name the way in.
    if (isLockedKeyTime(tKey)) { refuseLockedKey(); return; }
    handleDragStart('undoVerb.unmarkGroup');
    for (const m of marking) {
      const trackId = `${m.side}:${m.index}`;
      const track = keyframes.find(tr => tr.id === trackId);
      const here = (track ? sampleEffectivePosition(track, tKey, keyframes) : null)
        || interpolatedPosition(trackId, null);
      if (here) upsertTrackKey(trackId, 'position', { time: tKey, position: here });
      upsertTrackKey(trackId, 'attach', { time: tKey, value: 'none' });
    }
    if (Math.abs(tKey - refPlayheadTime.current) > 1e-9) setPlayheadTime(tKey);
    setAttachPickMode(null);
    setPromptState(null);
    setToast({
      message: t('mark.groupStopped', { n: marking.length, t: fmtTime(tKey) })
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
    handleDragEnd();
  }, [keyframes, interpolatedPosition, upsertTrackKey, handleDragStart,
    handleDragEnd, setPlayheadTime, setAttachPickMode, setPromptState, setToast, t]);
    // isLockedKeyTime / refuseLockedKey are deliberately NOT in that array: both
    // read refs (see the inherited-key guard above the seam check), so they
    // cannot go stale, and they are plain consts re-created every render —
    // naming them would rebuild this verb on every render for no safety.

  /**
   * Write the SAME bearing and gap to every marker in a group, in ONE undo step.
   * "All press goal-side, a metre and a half out" is one instruction on a
   * training pitch; it used to be one dial per marker, because each marker's
   * dir/dist lives on their own attach key. The bearing is relative to the
   * player EACH of them marks, which is what the instruction means.
   */
  const commitMarkGroup = useCallback((tracks, tKey, dir, dist) => {
    // The dial may already be open when the seam moves under it (a branch change,
    // or the playhead stepping to 00:00). The write is the last line: refuse
    // here, before the undo step, rather than let each upsert refuse and then
    // toast a gap that was never set.
    if (isLockedKeyTime(tKey)) { refuseLockedKey(); return 0; }
    const plan = [];
    let skipped = 0;
    for (const trackId of tracks || []) {
      const held = markKeyAt(keyframes, trackId, tKey);
      if (!held || !held.value || held.value === 'none') { skipped++; continue; }
      plan.push({ trackId, leader: held.value });
    }
    if (!plan.length) {
      setToast({ message: t('mark.groupNoneStop'), id: Date.now() });
      return 0;
    }
    handleDragStart('undoVerb.markGroupGap');
    for (const p of plan) {
      upsertTrackKey(p.trackId, 'attach', { time: tKey, value: p.leader, dir, dist });
    }
    handleDragEnd();
    setToast({
      message: t('mark.gapSetMany', {
        n: plan.length,
        where: t(`mark.dir.${MARK_DIRS[Math.round(dir) % 8] ?? 'front'}`),
        d: dist.toFixed(1),
      })
        + (skipped ? t('mark.gapSkipped', { n: skipped }) : '')
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
    return plan.length;
  }, [keyframes, upsertTrackKey, handleDragStart, handleDragEnd, setToast, t]);
    // The seam helpers stay out of the array — ref-backed, re-created every
    // render, and naming them would churn this callback for no safety.

  /**
   * THE COMPASS FOR A WHOLE GROUP of markers. Marking four attackers became one
   * drag; pressing all four the same way is still four dials. This opens ONE —
   * on the gap the pressed marker is actually holding — and the pitch shows a
   * ghost for EVERY leader in the group, so the preview cannot promise a spot
   * only one of them will take. "Not him, the other one" is deliberately absent:
   * who each man marks is a per-player decision, and a group dial that let you
   * re-pick one leader would be four dials wearing one panel.
   */
  const handleGroupMarkDial = useCallback((members) => {
    const tNow = snapKeyTime(refPlayheadTime.current);
    // Do not open a compass that can only refuse. The single-player dial has
    // always checked this; the group half was added later and never asked.
    if (isLockedKeyTime(tNow)) { refuseLockedKey(); return 0; }
    const marking = (members || []).filter(m => m && m.type === 'player'
      && attachRefAt(keyframes, `${m.side}:${m.index}`, tNow));
    if (marking.length < 2) {
      setToast({ message: t('mark.gapFew'), id: Date.now() });
      return 0;
    }
    const tracks = marking.map(m => `${m.side}:${m.index}`);
    const heldOf = (tid) => markKeyAt(keyframes, tid, tNow);
    const first = heldOf(tracks[0]);
    const leaders = [...new Set(tracks
      .map(tid => heldOf(tid)?.value)
      .filter(v => v && v !== 'none'))];
    refMarkRepick.current = null;
    // The way OUT of the group is the other half of the same door: one tap, and
    // every marker keeps the ground they gained (a door that opens in one move
    // has to close in one move too).
    refMarkRelease.current = () => handleGroupUnmark(marking, tNow);
    setMarkPicker({
      group: tracks, leaders, value: leaders[0] || 'none', time: tNow,
      dir: first && Number.isFinite(first.dir) ? first.dir : 0,
      dist: first && Number.isFinite(first.dist) ? first.dist : MARK_DEFAULT_DIST,
      keyHere: false,
    });
    setPromptState(null);
    return marking.length;
  }, [keyframes, handleGroupUnmark, setToast, t]);
    // Same as the commit: the seam helpers read refs and are re-created each
    // render, so they stay out of the array.

  /**
   * TAP WHERE THEY SHOULD STAND. The compass is open and its ghost is already
   * drawn on the pitch, so the answer to "back left, 2 m" is visible — but the
   * coach still had to find the right arrow among eight and drag a slider to the
   * distance. A tap on open turf moves the dial AND the ghost to that spot:
   * bearing and gap in one gesture, read off the same inverse the compass opens
   * on (markBearing is markOffset the other way round, and round-trips in the
   * sampling tests). It writes nothing: the panel's own button still commits, and
   * the commit is still one undo step. And it stops a tap on open turf from
   * deselecting the very player the dial is about to mark.
   */
  const handleMarkSpotPick = useCallback((spot) => {
    setMarkPicker(prev => {
      if (!prev) return prev;
      const leader = prev.value;
      if (!leader || leader === 'none') return prev;
      const leaderPos = interpolatedPosition(leader, itemFromTrackId(leader), prev.time);
      if (!leaderPos) return prev;
      const geo = markBearing([spot.gx, 0, spot.gz], leaderPos, sideOfAttachRef(leader));
      if (!geo || !Number.isFinite(geo.dist) || !Number.isFinite(geo.dir)) return prev;
      return { ...prev, dir: geo.dir, dist: geo.dist };
    });
  }, [interpolatedPosition, itemFromTrackId]);

  /**
   * True when `candidate` is already marking (directly or through a chain)
   * `trackId`. Making A mark B while B already marks A would send the sampler
   * round a loop, and the two would drift to wherever the recursion stopped —
   * so the ask is refused by name instead.
   */
  const marksTransitively = useCallback((candidate, trackId) => {
    let guard = 0, at = candidate;
    while (at && guard++ < 32) {
      const ref = attachRefAt(keyframes, at, refPlayheadTime.current);
      if (!ref) return false;
      if (ref === trackId) return true;
      at = ref;
    }
    return true;   // a chain longer than the guard is treated as a cycle
  }, [keyframes]);

  /**
   * Is there an attach key sitting on THIS instant for this track?
   *
   * Not the same question as "are they marking at this instant": the value in
   * force may have been written seconds earlier and simply never released, and
   * a key at this instant may be a hollow 'none' release. The compass needs the
   * second question, because "✕ Remove this mark key" is only an offer when
   * there is a key here to remove.
   */
  const hasAttachKey = useCallback((trackId, tKey) => (
    (keyframes.find(tr => tr && tr.id === trackId)?.attach || [])
      .some(k => k && k.time === tKey)
  ), [keyframes]);

  const handleMarkPrompt = useCallback((trackId, chosenRef = null) => {
    refMarkRepick.current = null;
    refMarkRelease.current = null;
    const tNow = snapKeyTime(refPlayheadTime.current);
    if (isLockedKeyTime(tNow)) { refuseLockedKey(); return; }
    const leader = attachRefAt(keyframes, trackId, tNow);
    const who = describeAttachRef(trackId);
    const mySide = trackId.split(':')[0];
    const ask = (value) => {
      if (value === trackId) {
        setToast({ message: t('mark.self', { who }), id: Date.now() });
        return;
      }
      // MARKING CROSSES THE DIVIDE. A player marking a teammate is not a
      // marking drill — it is two players welded together, and the drill the
      // coach is building loses both of them. The refusal says what to do
      // instead of just declining.
      if (value.split(':')[0] === mySide) {
        setToast({ message: t('mark.teammate', { who: describeAttachRef(value) }), id: Date.now() });
        return;
      }
      if (marksTransitively(value, trackId)) {
        setToast({
          message: t('mark.cycle', { who: describeAttachRef(value), leader: who }),
          id: Date.now(),
        });
        return;
      }
      // The WHO is settled; the WHERE is the compass. Eight bearings and a gap
      // are not things a drag can express, so the picker opens here — one panel,
      // then the key is written.
      //
      // Re-marking the player they ALREADY mark opens the dial on the spot they
      // are standing at now, so changing sides is one arrow over — and because a
      // new key lands at the playhead, the sampler sweeps the marker around the
      // marked player instead of dropping them on the other side.
      const held = leader === value ? markKeyAt(keyframes, trackId, tNow) : null;
      // The dial opens on the geometry the coach is ALREADY looking at, not on
      // a placeholder. Mark an opponent who happens to be standing 3 m off
      // your right shoulder and that is what the compass shows: the eight
      // arrows and the slider are still there to move, but the answer is
      // already on the dial and marking is one tap on Set instead of a
      // bearing hunt. Only when there is no key to hand back — re-marking the
      // player they already mark still opens on the spot they hold now.
      const geo = held ? null : markBearing(
        interpolatedPosition(trackId, itemFromTrackId(trackId), tNow),
        interpolatedPosition(value, itemFromTrackId(value), tNow),
        sideOfAttachRef(value),
      );
      setMarkPicker({
        trackId, value, time: tNow,
        dir: held && Number.isFinite(held.dir) ? held.dir : (geo ? geo.dir : 0),
        dist: held && Number.isFinite(held.dist) ? held.dist : (geo ? geo.dist : MARK_DEFAULT_DIST),
        keyHere: hasAttachKey(trackId, tNow),
      });
      setAttachPickMode(null);
      setPromptState(null);
    };
    setSegmentEdit(null);
    setGraphicEdit(null);
    if (chosenRef) {
      // THE DRAG ALREADY CHOSE THE WHO — and the compass IS the confirmation.
      // It names the marked player in its own title, opens on the spot the two
      // of them are actually standing at, and writes nothing until the coach
      // taps "Mark from here". The panel that used to sit here asked "Costa
      // marks Silva?" and its only other button released a mark the new key was
      // about to replace at this instant anyway: two panels and three taps for
      // what the dial now does in one and two.
      //
      // A drag that landed on a teammate, on themselves, or on a player who
      // already marks this one never reaches a panel at all — ask() refuses with
      // the toast that names the way in, exactly as it does for the pitch pick.
      ask(chosenRef);
      return;
    }
    const openPick = () => {
      setMarkPicker(null);
      setAttachPickMode(trackId);
      setPromptState({
        title: leader
          ? t('mark.changeTitle', { who, leader: describeAttachRef(leader) })
          : t('mark.title', { who }),
        description: t('mark.pickHint', { who }),
        mainAction: (target) => ask(`${target.side}:${target.index}`),
        subActions: leader ? [{
          label: t('mark.stop', { t: fmtTime(tNow) }),
          action: () => commitUnmark(trackId, tNow),
        }] : [],
        cancelAction: () => { setAttachPickMode(null); setPromptState(null); },
      });
    };
    if (leader) {
      // They ALREADY mark someone. The edit a coach makes over and over is the
      // SPOT, not the player, so the dial opens straight away: menu, arrow,
      // done. The rarer "not him, the other one" is one button inside the dial.
      const held = markKeyAt(keyframes, trackId, tNow);
      refMarkRepick.current = openPick;
      refMarkRelease.current = () => commitUnmark(trackId, tNow);
      setMarkPicker({
        trackId, value: leader, time: tNow,
        dir: held && Number.isFinite(held.dir) ? held.dir : 0,
        dist: held && Number.isFinite(held.dist) ? held.dist : MARK_DEFAULT_DIST,
        keyHere: hasAttachKey(trackId, tNow),
      });
      setPromptState(null);
      return;
    }
    refMarkRepick.current = null;
    openPick();
  }, [keyframes, marksTransitively, commitMark, commitUnmark, hasAttachKey, setToast, t, interpolatedPosition, itemFromTrackId]);

  /** Fired by Viewport3D when a player is dropped ONTO another player. */
  /**
   * MARK A GROUP IN ONE DROP.
   *
   * Marking four defenders on an attacker meant four round trips through the
   * menu — select, Mark, tap the leader, turn the dial, Set — twenty-four taps
   * for one drill. A group drag that ENDS on an attacker is the same gesture a
   * single player already uses, so it does the same thing for everyone in the
   * group: each of them marks that attacker FROM WHERE THEY ARE ACTUALLY
   * STANDING, the bearing and gap read off the geometry by the same markBearing
   * the compass opens on. No dial, because nothing is left to choose — the coach
   * put them there with their own hand, and the mark keeps them there.
   *
   * Members that cannot take the mark are counted, not silently dropped: a
   * teammate (marking crosses the divide), a chain that would close a cycle, or
   * a player standing on top of the leader, where no bearing exists to read.
   */
  const handleGroupMarkDrop = useCallback((members, leaderRef) => {
    const tKey = snapKeyTime(refPlayheadTime.current);
    if (isLockedKeyTime(tKey)) { refuseLockedKey(); return; }
    const leader = keyframes.find(tr => tr && tr.id === leaderRef);
    if (!leader) return;
    const leaderSide = sideOfAttachRef(leaderRef);
    const eligible = members.filter(m => m && m.side !== leaderSide
      && !marksTransitively(leaderRef, `${m.side}:${m.index}`));
    const blocked = members.length - eligible.length;
    const at = sampleEffectivePosition(leader, tKey, keyframes);
    const set = [];
    let standing = 0;
    for (const m of eligible) {
      const trackId = `${m.side}:${m.index}`;
      const here = interpolatedPosition(trackId, itemFromTrackId(trackId), tKey);
      // The compass reading, taken by the compass: the same inverse the dial
      // opens on, so a mark set by a drag and a mark set by the dial are the
      // same key, not two dialects of one idea.
      const geo = here && at ? markBearing(here, at, leaderSide) : null;
      if (!geo) { standing++; continue; }
      set.push({ trackId, dir: geo.dir, dist: geo.dist });
    }
    const skipped = blocked + standing;
    if (!set.length) {
      setToast({ message: t('mark.groupNone', { leader: describeAttachRef(leaderRef) }), id: Date.now() });
      return;
    }
    handleDragStart('undoVerb.markGroup');
    for (const s of set) {
      upsertTrackKey(s.trackId, 'attach', { time: tKey, value: leaderRef, dir: s.dir, dist: s.dist });
    }
    handleDragEnd();
    setToast({
      message: t(skipped ? 'mark.groupSetSome' : 'mark.groupSet', {
        n: set.length, leader: describeAttachRef(leaderRef), skipped,
      }) + t('toast.undoSuffix'),
      id: Date.now(),
    });
  }, [keyframes, refPlayheadTime, isLockedKeyTime, refuseLockedKey,
    marksTransitively, interpolatedPosition,
    itemFromTrackId, upsertTrackKey, handleDragStart, handleDragEnd, setToast, t]);
    // (describeAttachRef is DECLARED BELOW this callback. Calling it inside the
    // body is fine — the body runs after the render pass. Naming it in the
    // dependency array is not: that array is evaluated during the render, and
    // the const has not been initialised yet.)

  const handleMarkPlayerDrop = useCallback((side, index, receiverRef) => {
    // A group drag carries the whole group. Dropping it on an attacker marks
    // every player in it, not just the one under the finger — the gesture the
    // single player already uses, speaking for the group it is dragging.
    const players = (groupItems || []).filter(g => g && g.type === 'player');
    if (players.length > 1 && players.some(g => g.side === side && g.index === index)) {
      handleGroupMarkDrop(players, receiverRef);
      return;
    }
    handleMarkPrompt(`${side}:${index}`, receiverRef);
  }, [groupItems, handleGroupMarkDrop, handleMarkPrompt]);

  /**
   * MARK an opponent for a GROUP from the MENU — the twin of dropping the group
   * on him. The drag is the fast path, but a verb that exists ONLY as a drag is
   * invisible until somebody guesses it, so the menu has to carry it too, with
   * the count in its label. Pick mode is handed every member, so nobody in the
   * group can be picked as their own marker, and the commit is the drag's own
   * body: each member marks from where they are already standing.
   */
  const handleGroupMarkPrompt = useCallback((members) => {
    const players = (members || []).filter(m => m && m.type === 'player');
    if (players.length < 2) {
      // The menu entry only appears for a group, so getting here means the
      // selection changed under it. Name the door that does exist.
      setToast({ message: t('mark.groupOne'), id: Date.now() });
      return;
    }
    const tNow = snapKeyTime(refPlayheadTime.current);
    setAttachPickMode(players.map(m => `${m.side}:${m.index}`));
    setPromptState({
      title: t('mark.groupAskMany', { n: players.length }),
      description: t('mark.groupPickHint', { t: fmtTime(tNow) }),
      mainAction: (target) => {
        const leaderRef = target && (target.type === 'player' || target.side !== undefined)
          ? `${target.side}:${target.index}` : null;
        setAttachPickMode(null);
        setPromptState(null);
        if (!leaderRef) return;
        handleGroupMarkDrop(players, leaderRef);
      },
      cancelAction: () => { setAttachPickMode(null); setPromptState(null); },
    });
  }, [handleGroupMarkDrop, setAttachPickMode, setPromptState, setToast, t]);

  /**
   * KEY ATTACHMENT for the ball or a zone — opens the REUSABLE central prompt
   * and enters pick mode. The prompt's mainAction fires when the user clicks a
   * player OR the ball on the pitch; subAction "Release here" creates a detach
   * key — and only appears when a hold is actually in force at the playhead;
   * Cancel dismisses everything without setting any key.
   */
  /**
   * What a placement type is CALLED, in the coach's language. The arming toasts
   * used to carry their own English word-list beside the digit map — and the
   * ⇧9 twin printed the raw type ("cone armed again"), a data value speaking to
   * a person. The item menus already name these six; the toasts read from them.
   *
   * (And capWord beside it: a translated stem with an English tail is the same
   * bug wearing a different hat — "mandante #3" wants "Mandante #3".)
   * Both sit up here because describeAttachRef, the first reader of either,
   * runs earlier in the file than they used to.
   */
  const placeName = useCallback((type) => t(
    { cone: 'itm.cone', target: 'itm.target', zone: 'itm.zone', ball: 'itm.ball',
      'zone-circle': 'itm.circleZone', 'zone-dynamic': 'itm.dynamicZone' }[type]
  ) || type, [t]);

  /**
   * Capitalise a dictionary word for a heading or a label ("mandante" →
   * "Mandante"). Defined before trackName, its first reader — a useCallback's
   * dependency array is evaluated during the render pass, so a helper named
   * there must already be initialised.
   */
  const capWord = useCallback((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s), []);

  /**
   * A readable name for a TRACK ID, in the coach's language.
   *
   * trackDisplayLabel used to carry its own English word-list beside the id
   * parsing — a second, worse copy of the six nouns the dictionary already
   * owns. Every noun in a sentence has to come from the same place as the noun
   * on the button, or a Portuguese coach reads a sentence about "Cone 3" while
   * the button beside it says "Cono".
   *
   * The words are INJECTED into the id parser rather than copied into it, so
   * data.js stays free of React and the nouns stay in one place.
   */
  const trackName = useCallback((tid) => trackDisplayLabel(tid, {
    home: capWord(t('side.home')),
    away: capWord(t('side.away')),
    ball: capWord(t('itm.ball')),
    cone: capWord(t('itm.cone')),
    target: capWord(t('itm.target')),
    zone: capWord(t('itm.zone')),
    other: t('edit.otherItem'),
  }), [t, capWord]);

  /**
   * Human label for an attach reference: 'side:index' → player name,
   * 'ball:id' → 'Ball'. Prompts and toasts say "Pass to Silva?" instead of
   * bare ids — the state change should read like the action it performs.
   * (Defined HERE because the attach prompt below names carriers with it.)
   */
  const describeAttachRef = useCallback((ref) => {
    if (!ref || ref === 'none') return null;
    // placeName is how this app already spells an item type in the coach's
    // language; a second copy of "Ball" here is how a prompt ends up half
    // English in a Portuguese sentence. Zones, cones and targets can carry too
    // — the sampler walks to any track — so all four are named, and none of
    // them is ever reported as a raw 'cone:c7' inside someone's sentence.
    const kind = ref.split(':')[0];
    if (['ball', 'zone', 'cone', 'target'].includes(kind)) return placeName(kind);
    const m = /^(home|away):(\d+)$/.exec(ref);
    if (!m) return ref;
    const teamInfo = m[1] === 'home' ? homeTeam : awayTeam;
    // A player with no name on the team sheet is still "Home #3", not
    // "home #3" and not the raw id — the side word comes from the dictionary
    // and is capitalised the same way every other label is.
    return teamInfo?.playerInfo?.[+m[2]]?.name
      || `${capWord(t(m[1] === 'home' ? 'side.home' : 'side.away'))} #${+m[2] + 1}`;
  }, [homeTeam, awayTeam, placeName, capWord, t]);

  const handleBallAttachPrompt = useCallback((trackId) => {
    // A player track asks the FOLLOW question instead: same door, different rule.
    if (/^(home|away):\d+$/.test(trackId)) { handleMarkPrompt(trackId); return; }
    const isZone = trackId.startsWith('zone:');
    const who = itemFromTrackId(trackId);
    // WHAT IT IS DOING RIGHT NOW. The prompt used to ask "Attach?" whether or
    // not the item was attached, and offered Detach to something already
    // standing on its own — a no-op that then toasted a release. The title now
    // names the carrier the playhead is under, and Release only appears when
    // there is a hold to break.
    const tNow = snapKeyTime(refPlayheadTime.current);
    const carrier = attachRefAt(keyframes, trackId, tNow);
    setSegmentEdit(null); // dismiss any open transition editor first
    setGraphicEdit(null);
    setAttachPickMode(trackId);
    setPromptState({
      title: who
        ? (carrier
          ? t('attach.changeTitle', { name: getItemDisplayName(who), who: describeAttachRef(carrier) })
          : t('attach.title', { name: getItemDisplayName(who) }))
        : t('attach.title', { name: '' }).trim(),
      description: carrier
        ? t('attach.pickHint', { t: fmtTime(tNow) })
        : t(isZone ? 'attach.pickAny' : 'attach.pickPlayer'),
      mainAction: (target) => {
        // A player, or any item — the pitch only offers what the sampler can
        // walk to, and both spell the same reference. (The same "is it a
        // player?" test groupKeyOf uses, so a descriptor that carries a side
        // never falls through to an 'undefined:undefined' key.)
        const value = target.type === 'player' || target.side !== undefined
          ? `${target.side}:${target.index}` : `${target.type}:${target.id}`;
        // Keys always land on the 0.25s grid — snap the playhead there so
        // the scene reflects the attach/detach IMMEDIATELY. Without this a
        // mid-second playhead (e.g. 5.4 → key at 6) leaves the ball attached
        // on screen and drags get overridden by the hold/follow logic.
        const tKey = snapKeyTime(refPlayheadTime.current);
        if (isLockedKeyTime(tKey)) {
          // Seam: the write would be refused — say so, don't toast a lie.
          refuseLockedKey();
          setAttachPickMode(null);
          setPromptState(null);
          return;
        }
        upsertTrackKey(trackId, 'attach', { time: tKey, value });
        if (Math.abs(tKey - refPlayheadTime.current) > 1e-9) setPlayheadTime(tKey);
        setAttachPickMode(null);
        setPromptState(null);
        // The pick happened on the pitch — the prompt vanishing was the only
        // feedback. Name the new carrier out loud (state change you can hear).
        // And name the REQUESTER by its own menu word: this prompt serves balls,
        // zones, cones and targets, and a cone that started following someone
        // used to be announced as a ball.
        setToast({ message: t('attach.follows', {
          what: placeName(trackId.split(':')[0]),
          who: describeAttachRef(value), t: fmtTime(tKey),
        }), id: Date.now() });
      },
      subActions: [
        ...(carrier ? [{
          label: t('attach.release', { t: fmtTime(tNow) }),
          action: () => {
            const tKey = snapKeyTime(refPlayheadTime.current);
            if (isLockedKeyTime(tKey)) {
              refuseLockedKey();
              setAttachPickMode(null);
              setPromptState(null);
              return;
            }
            upsertTrackKey(trackId, 'attach', { time: tKey, value: 'none' });
            if (Math.abs(tKey - refPlayheadTime.current) > 1e-9) setPlayheadTime(tKey);
            setAttachPickMode(null);
            setPromptState(null);
            setToast({ message: t('attach.released', {
              what: placeName(trackId.split(':')[0]), t: fmtTime(tKey),
            }), id: Date.now() });
          },
        }] : []),
        // ✕ REMOVE THIS ATTACH KEY — the twin of the mark compass's exit, and the
        // same gap: Release writes a hollow 'none' key, so a key laid down by
        // mistake could only be deleted by double-clicking its dot, which a phone
        // does not do. One verb, one implementation, every lane.
        //
        // Gated on a key ACTUALLY WRITTEN here, not on a hold borrowed from an
        // earlier instant: deleting a borrowed value would remove nothing and
        // toast that it removed something.
        ...(hasAttachKey(trackId, tNow) ? [{
          label: t('attach.deleteKey', { t: fmtTime(tNow) }),
          utility: true,
          danger: true,
          action: () => {
            const tKey = snapKeyTime(refPlayheadTime.current);
            if (isLockedKeyTime(tKey)) {
              refuseLockedKey();
              setAttachPickMode(null);
              setPromptState(null);
              return;
            }
            setAttachPickMode(null);
            setPromptState(null);
            // handleKeyframeDelete announces a whole MOMENT going (arrival key +
            // claim key are one physical event); it must not be talked over.
            const moment = (keyframes.find(tr => tr && tr.id === trackId)?.position || [])
              .some(k => k && k.time === tKey);
            handleKeyframeDelete(trackId, 'attach', tKey);
            if (!moment) {
              setToast({
                message: t('attach.keyDeleted', {
                  what: placeName(trackId.split(':')[0]), t: fmtTime(tKey),
                }) + t('toast.undoSuffix'),
                id: Date.now(),
              });
            }
          },
        }] : []),
      ],
      cancelAction: () => {
        setAttachPickMode(null);
        setPromptState(null);
      },
    });
  }, [upsertTrackKey, itemFromTrackId, getItemDisplayName, describeAttachRef, keyframes, handleMarkPrompt, hasAttachKey, handleKeyframeDelete, setPlayheadTime, placeName, t]);

  /**
   * ATTACH a GROUP of items to ONE carrier, in one gesture. Long-press five
   * cones, open the menu, tap the ball: every cone rides it. The single-item
   * prompt above is the same door with one member — one body per verb, so the
   * group rule can never drift away from it. Pick mode is handed the WHOLE
   * list, so no member can be picked as its own carrier or as another
   * member's, and the pitch's glow shows that.
   */
  const handleGroupAttachPrompt = useCallback((members) => {
    const ids = (members || [])
      .filter(g => g && g.type !== 'player' && ['ball', 'zone', 'cone', 'target'].includes(g.type))
      .map(g => `${g.type}:${g.id}`);
    if (!ids.length) {
      // A refusal, not a silence — and it names the door that does exist.
      setToast({ message: t('attach.groupNone'), id: Date.now() });
      return;
    }
    const tNow = snapKeyTime(refPlayheadTime.current);
    // Who is RIDING right now. The release writes for these members only: a
    // 'none' key on something already standing on its own is a dead key, and
    // the count in the label has to be the count of what the tap will change.
    const ridingIds = ids.filter(id => attachRefAt(keyframes, id, tNow));
    const riding = ridingIds.length;
    setSegmentEdit(null); // dismiss any open transition editor first
    setGraphicEdit(null);
    setAttachPickMode(ids);
    setPromptState({
      title: riding
        ? t('attach.groupChangeTitle', { n: ids.length })
        : t('attach.groupTitle', { n: ids.length }),
      description: t('attach.pickHint', { t: fmtTime(tNow) }),
      mainAction: (target) => {
        const value = target.type === 'player' || target.side !== undefined
          ? `${target.side}:${target.index}` : `${target.type}:${target.id}`;
        const tKey = snapKeyTime(refPlayheadTime.current);
        if (isLockedKeyTime(tKey)) {
          refuseLockedKey();
          setAttachPickMode(null);
          setPromptState(null);
          return;
        }
        handleDragStart('undoVerb.attachGroup');
        for (const id of ids) upsertTrackKey(id, 'attach', { time: tKey, value });
        if (Math.abs(tKey - refPlayheadTime.current) > 1e-9) setPlayheadTime(tKey);
        setAttachPickMode(null);
        setPromptState(null);
        setToast({
          message: t('attach.groupFollows', {
            n: ids.length, who: describeAttachRef(value), t: fmtTime(tKey),
          }),
          id: Date.now(),
        });
        handleDragEnd();
      },
      subActions: [
        // Release for the whole group, in one tap. It appears only when at
        // least one member is actually riding something — a release that
        // releases nothing is a button that lies.
        ...(riding ? [{
          label: t('attach.groupRelease', { n: riding, t: fmtTime(tNow) }),
          action: () => {
            const tKey = snapKeyTime(refPlayheadTime.current);
            if (isLockedKeyTime(tKey)) {
              refuseLockedKey();
              setAttachPickMode(null);
              setPromptState(null);
              return;
            }
            handleDragStart('undoVerb.attachGroup');
            for (const id of ridingIds) upsertTrackKey(id, 'attach', { time: tKey, value: 'none' });
            if (Math.abs(tKey - refPlayheadTime.current) > 1e-9) setPlayheadTime(tKey);
            setAttachPickMode(null);
            setPromptState(null);
            setToast({
              message: t('attach.groupReleased', { n: riding, t: fmtTime(tKey) }),
              id: Date.now(),
            });
            handleDragEnd();
          },
        }] : []),
      ],
      cancelAction: () => { setAttachPickMode(null); setPromptState(null); },
    });
  }, [keyframes, upsertTrackKey, describeAttachRef, handleDragStart, handleDragEnd,
    setPlayheadTime, t]);

  /** Fired by Viewport3D when a player or ball is clicked while in attach-pick mode. */
  const handleAttachPlayerSelect = useCallback((target) => {
    if (promptState?.mainAction) promptState.mainAction(target);
  }, [promptState]);

  /** The red time/play control is scoped to the open branch only. */
  const handlePlayToggle = useCallback((e) => {
    // ⇧click the play button = the mouse twin of Space-from-the-tree: play
    // the WHOLE chosen path (walk), not just this branch. handleMasterPlay
    // toggles, so ⇧click while walking pauses the walk instead.
    if (e && e.shiftKey) { handleMasterPlay(); return; }
    const wasBranchPlaying = isPlaying && !masterPlayActive;
    const wasWalking = !!masterPlayActive;
    refMasterPlay.current = null;
    setMasterPlay(null);
    if (wasWalking) {
      // A path walk just downgraded to THIS branch only — say so, the pitch
      // alone cannot show that the walk was dropped.
      setToast({ message: t('branch.onlyNow'), id: Date.now() });
    }
    if (wasBranchPlaying) {
      setIsPlaying(false);
      return;
    }
    if (refPlayheadTime.current >= timelineDuration - 0.01) {
      refPlayheadTime.current = 0;
      setPlayheadTime(0);
    }
    setIsPlaying(true);
  }, [isPlaying, masterPlayActive, timelineDuration, handleMasterPlay]);

  /** Space bar toggles play/pause (ignored while typing in a field). */
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== 'Space') return;
      // Modified Space belongs to the main keyboard effect's trio
      // (⌥Space branch-only, ⇧Space to next fork). Firing here too made
      // ⌥Space from the branches view START a walk (this routing) and
      // instantly cancel it (handlePlayToggle) — a false "Path play" flash.
      if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return;
      const el = e.target;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
        || el.isContentEditable || el.getAttribute && el.getAttribute('contenteditable') === 'true');
      if (typing) return;
      e.preventDefault();
      handlePlayToggleRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const handlePlayToggleRef = useRef(handlePlayToggle);
  useEffect(() => { handlePlayToggleRef.current = masterPlay?.stopAtNextBranch ? handleNextBranchPlay : masterPlayActive || viewMode === 'branches' ? handleMasterPlay : handlePlayToggle; });

  /** Manual timeline scrub (ruler click / drag / wheel) — stops playback:
   *  the user is taking control of time, so the play button must let go, and any
   *  tree walk is abandoned rather than resumed from somewhere it never reached. */
  const handleScrub = useCallback((t) => {
    setIsPlaying(false);
    setMasterPlay(null);
    setPlayheadTime(t);
  }, []);

  /** User edited the timeline length — clamp the playhead if it's beyond the new end. */
  const handleDurationChange = useCallback((secs) => {
    if (Number(secs) === Number(refTimelineDurationState.current)) return; // same length: no undo burn
    pushUndo('undoVerb.branchDuration');
    setTimelineDuration(secs);
    if (refPlayheadTime.current > secs) {
      refPlayheadTime.current = secs;
      setPlayheadTime(secs);
    }
  }, [pushUndo]);

  /**
   * KEY ACTION prompt (players + non-ball items) — opens the reusable central
   * modal listing every V1 action as a button. target is either
   * { side, index } (player) or { trackId } (item track).
   *
   * The menu also carries the item's EDIT verbs (copy / paste / duplicate /
   * mirror / select-all-of-kind / delete-the-group), all declared
   * FURTHER DOWN the component
   * body. A useCallback naming them as dependencies would read those bindings
   * before they exist (TDZ), so the menu reaches them through this one table —
   * filled in as each verb is defined below.
   */
  const refItemVerbs = useRef({ copy: null, paste: null, mirror: null, duplicate: null, selectKind: null, remove: null, line: null, arrow: null, editSeg: null, mark: null, markGroup: null, unmarkGroup: null, markGapGroup: null, loft: null, loftGroup: null, bend: null, bendGroup: null, flight: null, height: null, attach: null, attachGroup: null, zoneScene: null, repeat: null, ballVisible: null });
  /**
   * True when EVERY one of these tracks ALREADY holds this exact action key at
   * this exact instant — writing it again changes nothing. ONE rule for the
   * action menu (which refuses the tap) and for ⇧X (which refuses the repeat),
   * so neither can toast a change that did not happen.
   */
  const actionAlreadyKeyedHere = useCallback((trackIds, value, t) => (
    trackIds.length > 0 && trackIds.every(id => {
      const f = actionKeyInForce(keyframes, id, t);
      return !!f && f.value === value && Math.abs(f.time - t) < 0.001;
    })
  ), [keyframes]);

  const handleActionPrompt = useCallback((target, keysOverride) => {
    // The menu's labels READ the scene: how high this key sits, whether the
    // arrows are on, which way the path bends. An entry that changes one of
    // those re-opens itself from the state the verb just wrote (keysOverride),
    // so the next tap is a tap on the same button, not a re-press of the item.
    const keys = keysOverride || keyframes;
    // A step, a flip, a cycle: an entry whose LABEL describes a state re-opens
    // the menu from the scene the verb just wrote, so the next tap lands on the
    // same button with the new number on it. Pressing the item buys the first
    // step; every step after that is one tap, not two.
    const stayAndRemeasure = () => handleActionPrompt(target, refKeyframesState.current);
    // LAYING A ROW IS ONE CARD OPEN AND N TAPS. The line verb places a marker and
    // selects it, so the card follows that selection instead of closing: the next
    // tap continues the line from the NEWEST marker — the one the pitch is already
    // tinting — and every other entry in the card now speaks for it too. Until now
    // each step cost two taps (open the card, tap the entry), so a row of six cones
    // was eleven taps for five of them. If the verb placed nothing — no trail, or
    // the next step lands on a spot that is taken — it has already said why in a
    // toast, and the card simply re-measures.
    const lineAndStay = (mode) => {
      const placed = refItemVerbs.current.line ? refItemVerbs.current.line(mode) : null;
      if (!placed) { stayAndRemeasure(); return; }
      // Queued, not opened now: the cone the verb just placed is not in the scene
      // this render can see yet, and its name is positional.
      setPendingPrompt({ item: placed });
    };
    setSegmentEdit(null); // dismiss any open transition editor first
    setGraphicEdit(null);
    // WHO the verbs belong to: the item whose menu this is — never whatever
    // happens to be selected. The strip's action button opens this menu without
    // selecting, so an ambient-selection verb would flip the wrong player.
    const verbMembers = (target.targets || [target])
      .map(tt => (tt.side !== undefined
        ? { type: 'player', side: tt.side, index: tt.index }
        : itemFromTrackId(tt.trackId)))
      .filter(Boolean);
    // Grouping has to mean something on touch: long-press several items, then
    // long-press one of them again, and the menu that opens speaks for the
    // WHOLE group — the same rule the chords already follow. A target that is
    // not in the current group (the strip's action button, for instance) speaks
    // only for itself, so the group the user left behind is never touched.
    const speaksForGroup = groupItems.length >= 2
      && verbMembers.every(m => groupItems.some(g => isSameGroupMember(g, m)));
    const verbTargets = speaksForGroup ? groupItems : verbMembers;
    // Duplicate only makes sense for items — squad slots are fixed, so a player
    // target gets no dead-end entry (the verb itself still refuses in words).
    const canDuplicate = verbTargets.some(g => g.type !== 'player');
    // ⇧⌥S — gather every item of this kind into ONE group, because the group is
    // what mirror / duplicate / delete already act on: it multiplies those verbs
    // rather than adding another one. sceneMembersOfKind is declared below
    // (TDZ), so it is read here in the body and left out of the deps on purpose.
    const kindBase = verbTargets[0] || null;
    const kindMembers = kindBase ? sceneMembersOfKind(pitchState, kindBase) : [];
    const kindLabel = kindBase
      ? (kindBase.type === 'player'
        ? t(kindBase.side === 'away' ? 'kindMany.playersAway' : 'kindMany.playersHome')
        : t(`kindMany.${kindBase.type}s`))
      : '';
    // Does this card ALREADY speak for every member of the kind? If so the
    // "select all of this kind" entry re-selects what is already selected and
    // re-opens the same card: a door that opens onto nothing. That entry is a door
    // INTO the group verbs, so it belongs on a card that has not reached them yet.
    const speaksForWholeKind = kindMembers.length >= 2
      && kindMembers.every(m => verbTargets.some(v => isSameGroupMember(v, m)));
    // THE LINE THIS PLAYER IS STANDING IN. A coach animating a back four wants
    // the four, not the eleven — and until now the only group door on a player's
    // card selected the whole side, so reaching those four meant long-pressing
    // each one on the pitch. The band is read off the POSITIONS, not the
    // formation key: players get moved by hand, drilled out of shape, and set up
    // without ever picking a shape, and the line the pitch shows is the line the
    // coach means.
    //
    // A GROUP CARD MAY USE IT TOO — two defenders long-pressed want the four —
    // but only when the line is a STRICT superset of who this card already speaks
    // for, and every member of the card is IN that line. A mixed card (two
    // defenders and a midfielder) must not be quietly narrowed to one line: the
    // coach would lose a member they never deselected.
    const lineMembers = (() => {
      if (!kindBase || kindBase.type !== 'player') return [];
      if (!verbTargets.every(v => v.type === 'player' && v.side === kindBase.side)) return [];
      const band = bandOfPlayer(kindBase.side, pitchState[kindBase.side]?.players, kindBase.index);
      const members = band.map(b => ({ type: 'player', side: kindBase.side, index: b.index }));
      if (members.length <= verbTargets.length) return [];
      if (!verbTargets.every(v => members.some(m => m.index === v.index))) return [];
      return members;
    })();
    // TWO GROUPS, ONE DOOR — the rule the mirror family already lives by: with
    // only one live there is nothing to choose, so it stays inline and costs no
    // extra tap. The line is offered only when it is a PROPER part of the side,
    // or it would be the same selection twice.
    const selectSideOption = kindMembers.length >= 2 && !speaksForWholeKind ? {
      label: t('itm.selectAllKind', { kind: kindLabel, n: kindMembers.length }), iconKey: 'group',
      action: () => {
        const members = refItemVerbs.current.selectKind
          ? refItemVerbs.current.selectKind(kindBase) : null;
        if (!members || members.length < 2) { setPromptState(null); return; }
        // The new selection is state the tap cannot see yet (setGroupItems lands
        // on the next render), so the re-open is QUEUED and handed the members the
        // verb returned — the same route ⧉ Duplicate already takes.
        setPendingPrompt({ item: kindBase, items: members });
      },
    } : null;
    const selectLineOption = lineMembers.length >= 2 && kindMembers.length > lineMembers.length ? {
      label: t('itm.selectLine', { n: lineMembers.length }), iconKey: 'line',
      action: () => {
        setPromptState(null);
        setPendingPrompt({ item: kindBase, items: lineMembers });
      },
    } : null;
    const selectGroupOptions = [selectLineOption, selectSideOption].filter(Boolean);
    const selectGroupPanel = selectGroupOptions.length > 1 ? {
      title: t('itm.selectGroupTitle'),
      description: t('itm.selectGroupHint'),
      subActions: selectGroupOptions,
      cancelAction: () => setPromptState(null),
    } : null;
    // COPY / PASTE — the route-reuse pair (copy Silva's run, paste it onto
    // Pires; copy a cone's size and place, paste it onto another cone). The
    // paste entry appears ONLY when it would land HERE: the clipboard falls
    // back to its own slot, so offering a paste that moves something else is a
    // trap, not a shortcut.
    const copyTarget = verbMembers[0] || null;
    // The whole-pitch flip (⌥M) is the coach's "move this drill to the other
    // flank" — one press instead of gathering every kind one at a time. Its
    // SCOPE is not the item the menu belongs to, so the count is spelled out in
    // the label: the blast radius has to be obvious before the tap.
    const sceneCount = ['home', 'away'].reduce((n, side) =>
      n + ((pitchState?.[side]?.players || []).filter(Boolean).length), 0)
      + (pitchState?.balls?.length || 0) + (pitchState?.cones?.length || 0)
      + (pitchState?.targets?.length || 0) + (pitchState?.zones?.length || 0);
    // LINE LAYOUT — repeat the spacing the last two placements set (D / ⌥D /
    // ⇧D / ⌥⇧D were the only way). The trail is a ref, and lineStepFromTrail is
    // declared BELOW this callback, so it is called in the body and
    // deliberately NOT named in the deps (TDZ). Only offered for the type that
    // is actually being lined up, so "next in line" never drops an item of a
    // different kind than the one whose menu you are in.
    const trail = refPlaceTrail.current || [];
    const lastPlaced = trail[trail.length - 1] || null;
    const rawStep = lastPlaced && copyTarget && lastPlaced.type === copyTarget.type
      // A BALL is excluded here, not just in the verb: handlePlaceInLine refuses a
      // ball for EVERY mode ("a second ball is not a drill marker"), so a ball's
      // card was offering ⧉ Next in line and ⧉ Turn the line onto a refusal. Two
      // dead entries in the card the ceiling test already calls the app's worst.
      && lastPlaced.type !== 'ball'
      ? lineStepFromTrail() : null;
    const lineStepM = rawStep ? Math.round(Math.hypot(rawStep.dx, rawStep.dz) * 100) / 100 : null;
    // A MIRROR needs one placement, not two (it reflects the last item across
    // the pitch rather than repeating a spacing). Balls are excluded because a
    // second ball is not a drill marker — the verb itself refuses in words.
    const canMirrorHere = !!(lastPlaced && copyTarget && lastPlaced.type === copyTarget.type
      && lastPlaced.type !== 'ball');
    // THE MIRROR FAMILY, built once so the menu can decide whether to show the
    // five of them or one door into them. Order is the order a coach reaches for
    // them: copy across the halfway line, copy across the touchline, then the
    // two flips of what is already there, then the whole scene.
    //
    // NOT FOR PLAYERS. A squad slot is not a position on the pitch: mirroring a
    // player either lands them on top of a teammate who already owns that spot
    // or benches someone by accident, and every coach who tried it said "that's
    // weird". The verbs stay for the things a drill is built out of — cones,
    // targets, zones, and the ball's copy-across — and the whole-pitch flip is
    // still one tap from any of those menus.
    // THE MIRROR DOOR, built once and opened twice — from the card, and from an
    // option that has just been tapped. Mirroring is a SHAPE verb: laying both
    // halves of a drill means tapping the same door several times in a row, and
    // until now every tap closed the whole card, so the second mirror cost three
    // taps (card, door, option) where it should cost one. The mirror verbs do not
    // move the selection — they place a copy or flip in place — so the door
    // re-opens on the same item it was opened for.
    //
    // The five choices inside are NOT the same size, and the door used to pretend
    // they were. "Copy other half/side" lays down ONE copy of the item the card
    // was opened on; "Mirror flank" and "Mirror end ↔ end" move EVERY member the
    // card speaks for. In a card speaking for three cones that meant two entries
    // touching one cone and two moving three, with nothing on the screen saying
    // which was which — the same one-member mistake 📋 Copy and the bend panel
    // already had to confess to. Each entry now counts what its own verb does.
    const mirrorAgain = () => { if (mirrorPanel) setPromptState(mirrorPanel); else setPromptState(null); };
    const mirrorMany = verbTargets.length > 1;
    const mirrorOptions = (copyTarget && copyTarget.type === 'player') ? [] : [
      ...(canMirrorHere ? [
        {
          // "Copy to", not "Mirror": this lays a NEW item down on the other
          // side. The ⇄ entries MOVE what is already there.
          label: t('itm.mirrorHalf') + (mirrorMany ? t('itm.onlyThis') : ''), iconKey: 'mirror',
          action: () => { if (refItemVerbs.current.line) refItemVerbs.current.line('mirrorX'); mirrorAgain(); },
        },
        {
          label: t('itm.mirrorSide') + (mirrorMany ? t('itm.onlyThis') : ''), iconKey: 'mirror',
          action: () => { if (refItemVerbs.current.line) refItemVerbs.current.line('mirrorZ'); mirrorAgain(); },
        },
      ] : []),
      {
        label: t('itm.mirrorFlank') + (mirrorMany ? t('itm.allOfThese', { n: verbTargets.length }) : ''), iconKey: 'mirror',
        action: () => { if (refItemVerbs.current.mirror) refItemVerbs.current.mirror(2, false, verbTargets); mirrorAgain(); },
      },
      {
        label: t('itm.mirrorEnd') + (mirrorMany ? t('itm.allOfThese', { n: verbTargets.length }) : ''), iconKey: 'mirror',
        action: () => { if (refItemVerbs.current.mirror) refItemVerbs.current.mirror(0, false, verbTargets); mirrorAgain(); },
      },
      ...(sceneCount >= 2 ? [{
        label: t('itm.mirrorPitch', { n: sceneCount }), iconKey: 'mirror',
        // whole = true: every item AND every track in the branch, not the
        // menu's target. The label already said so.
        action: () => { if (refItemVerbs.current.mirror) refItemVerbs.current.mirror(2, true); mirrorAgain(); },
      }] : []),
    ];
    // The door's own panel, built once so the card and a tapped option open the
    // SAME thing. Null when there is nothing behind the door.
    const mirrorPanel = mirrorOptions.length ? {
      title: t('itm.mirrorMenuTitle'),
      description: t('itm.mirrorMenuHint'),
      subActions: mirrorOptions,
      cancelAction: () => setPromptState(null),
    } : null;
    // ARROWS — the label must state which way the toggle will go, so the menu
    // asks the same question the G chord asks, through the same helper. For a
    // GROUP the question is asked of EVERY member this menu speaks for: the verb
    // already acts on all of them, so a label read off the selected player alone
    // could say "off" while turning two others ON.
    const arrowAt = snapKeyTime(refPlayheadTime.current);
    const arrowTrackId = copyTarget
      ? (copyTarget.type === 'player' ? `${copyTarget.side}:${copyTarget.index}` : `${copyTarget.type}:${copyTarget.id}`)
      : null;
    const arrowTrackOf = groupTrackId;
    // A ZONE HAS NO ARROW LANE. The strip gives a zone position / attach / visual
    // and never a graphic one, and the scene-wide ➜ already refuses to write a
    // graphic key for a zone for exactly that reason. This door never asked: it
    // offered "➜ Arrows on" for a zone, wrote a key the strip cannot show and the
    // scene verb refuses to make, and drew nothing. A zone that ALREADY has one —
    // older data — keeps the entry so it can be cleared, the same rule the scene
    // door follows.
    const arrowTargets = (verbTargets.length > 1 ? verbTargets : (copyTarget ? [copyTarget] : []))
      .filter(g => g && (g.type !== 'zone' || arrowOnAt(keys, arrowTrackOf(g), arrowAt)));
    const arrowOnCount = arrowTargets.filter(g => arrowOnAt(keys, arrowTrackOf(g), arrowAt)).length;
    const arrowGroup = arrowTargets.length > 1;
    const arrowOn = arrowGroup
      ? arrowOnCount === arrowTargets.length
      : !!(arrowTrackId && arrowOnAt(keys, arrowTrackId, arrowAt));
    // The COLOUR editor opens on the selected member's own arrow band, so its
    // gate asks about that member alone — not about the group's unanimity. It also
    // cannot fire for a zone by accident: a zone has no band unless older data
    // left one behind, and then clearing it is exactly what the entry is for.
    const arrowOnSelf = !!(arrowTrackId && arrowOnAt(keys, arrowTrackId, arrowAt));
    // Every OTHER member of this card whose arrow is ON at the same instant, with
    // the window each one is on in. A coach who turned arrows on for the back four
    // wants them the SAME colour, and until now that meant four menus and four
    // swatches, because the colour panel only ever knew one track.
    const arrowSiblings = verbTargets.length > 1 && arrowTrackId
      ? arrowTargets
        .map(g => arrowTrackOf(g))
        .filter(tid => tid && tid !== arrowTrackId && arrowOnAt(keys, tid, arrowAt))
        .map(tid => ({ trackId: tid,
          start: (graphicKeyAt(keyframes, tid, arrowAt) || { time: arrowAt }).time }))
      : [];
    // WHO THIS PLAYER IS MARKING right now, if anyone — the mark entry has to say
    // so, because "🎯 Mark an opponent…" on a player who already marks one reads
    // like nothing happened when the marked player changes.
    const markLeader = copyTarget && copyTarget.type === 'player'
      ? attachRefAt(keys, `${copyTarget.side}:${copyTarget.index}`, snapKeyTime(refPlayheadTime.current))
      : null;
    // HOW MANY OF THE MEMBERS THIS MENU SPEAKS FOR ARE MARKING right now. The
    // group MARK is one drag; the way out has to be one tap, or the gesture
    // only ever opens doors. The count goes in the label, so the entry says who
    // it will let go instead of promising a verb it may not have.
    const groupMarking = verbTargets.length > 1 && copyTarget && copyTarget.type === 'player'
      ? verbTargets.filter(g => g && g.type === 'player'
        && attachRefAt(keys, `${g.side}:${g.index}`, snapKeyTime(refPlayheadTime.current)))
      : [];
    // HOW MANY MEMBERS CAN BE ATTACHED — everything in the group that is not a
    // player. Players ride players through FOLLOW; an item rides anything the
    // sampler can walk to. A group of cones has five; a group of players has
    // none, and the entry does not appear.
    const groupAttachable = verbTargets.length > 1 && copyTarget && copyTarget.type !== 'player'
      ? verbTargets.filter(g => g && g.type !== 'player'
        && ['ball', 'zone', 'cone', 'target'].includes(g.type))
      : [];
    // …and HOW MANY OF THEM ARE PLAYERS, for the group MARK. Dropping the group
    // on an attacker already marks everyone in it; the menu has to carry the
    // same verb or it only exists for whoever happens to guess the drag.
    const groupMarkable = verbTargets.length > 1 && copyTarget && copyTarget.type === 'player'
      ? verbTargets.filter(g => g && g.type === 'player')
      : [];
    // WHAT AN ITEM IS RIDING RIGHT NOW — the same question, asked of the non-player
    // half of the scene. The attach entry has to answer it before it can promise
    // anything: "Attach…" over something already attached reads like nothing
    // happened when the carrier changes.
    const attachCarrier = copyTarget && copyTarget.type !== 'player'
      ? attachRefAt(keys, `${copyTarget.type}:${copyTarget.id}`, snapKeyTime(refPlayheadTime.current))
      : null;
    // WHAT THE PLAYHEAD IS SITTING ON, for this item's own track: a position
    // KEY (the key editor) or a SEGMENT between two keys (the transition
    // editor). Enter does this from the keyboard; on a phone the only way in
    // was to open the timeline and tap the bar in the lane. The entry names the
    // span it will open, so it is obvious which moment you are editing.
    const editTrackId = copyTarget
      ? (copyTarget.type === 'player' ? `${copyTarget.side}:${copyTarget.index}` : `${copyTarget.type}:${copyTarget.id}`)
      : null;
    const editTrack = editTrackId ? (keys.find(t => t.id === editTrackId) || null) : null;
    const editT = snapKeyTime(refPlayheadTime.current);
    // THE FLIGHT UNDER THE PLAYHEAD on any track: the span between two position
    // keys. One helper, because the pressed item's flight and every other member's
    // flight are the same question — and the bend panel now answers it for the
    // whole group at once.
    const segUnder = (tid) => {
      // THE SAME SCAN the group verbs use, so the flight this menu sees and the
      // flights the group verbs reach cannot drift apart. flightUnderPlayhead is
      // declared BELOW this callback; safe via closure at call time, deliberately
      // NOT in deps (TDZ).
      const f = flightUnderPlayhead(tid);
      return f ? { from: f.from, to: f.to } : null;
    };
    const editSegSpan = segUnder(editTrackId);
    const editFrom = editSegSpan ? editSegSpan.from : null;
    const editTo = editSegSpan ? editSegSpan.to : null;
    // …and whether that span is the item's OWN movement. The sampler is blunt
    // about it — "while it is attached it does not follow its own keys at all, it
    // rides the thing it is attached to", and for a marker "their own keys are
    // parked" — and ✚ below already skips exactly these members for exactly that
    // reason ("a position key under a carrier is a dead spot"). The FLIGHT verbs
    // never asked. A ball carried from 00:01 wrote ⤴ and ↝ entries for a flight
    // nothing draws: doors onto nothing, and the coach arches a pass they cannot
    // see arch. A span is NOT the item's own when it rides a carrier at BOTH ends
    // with no release key in between — a hand-off or a let-go inside means it
    // really is travelling, and the doors stay.
    const ridesUnderFlight = (tid, from, to) => {
      if (!tid || from === null || to === null || from === to) return false;
      if (attachRefAt(keys, tid, from) === null || attachRefAt(keys, tid, to) === null) return false;
      const tr = keys.find(k => k.id === tid);
      return !(tr?.attach || []).some(k => k && typeof k.time === 'number'
        && k.time > from + 1e-6 && k.time < to - 1e-6 && (!k.value || k.value === 'none'));
    };
    const ridesNow = (tid) => !!tid && attachRefAt(keys, tid, editT) !== null;
    // Does the GROUP have a flight under the playhead, even where the pressed
    // item has none? The chord checks this before its single-member refusal;
    // the menu has to as well, or the entry vanishes on the one member that
    // cannot bend while the other two could.
    const flightTid = (g) => (g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`);
    const groupFlights = verbTargets.length > 1 && refItemVerbs.current.flight
      ? verbTargets
        .map(g => ({ fl: refItemVerbs.current.flight(flightTid(g)) }))
        .filter(m => !!m.fl && !ridesUnderFlight(m.fl.tid, m.fl.from, m.fl.to))
        .map(m => m.fl)
      : [];
    // LOFT is ball-only (a ball is the only thing a drill throws), so the GROUP
    // count is the balls in the group with a flight at the playhead — not every
    // member with a flight, which is what BEND counts. And the same rule as
    // above: a ball riding a carrier has no flight of its own to loft.
    const groupLofts = verbTargets.length > 1 && refItemVerbs.current.flight
      ? verbTargets
        .filter(g => g && g.type === 'ball')
        .map(g => ({ fl: refItemVerbs.current.flight(`ball:${g.id}`) }))
        .filter(m => !!m.fl && !ridesUnderFlight(m.fl.tid, m.fl.from, m.fl.to))
        .map(m => m.fl)
      : [];
    // How many members are BALLS at all. The "no segment here" note is only true
    // of a ball that has no flight: a cone in the same card was never a candidate
    // for a verb that throws nothing, and counting it would blame the wrong thing.
    const ballMembers = verbTargets.filter(g => g && g.type === 'ball').length;
    const loftSkipped = Math.max(0, ballMembers - groupLofts.length);
    // …and how many members have a POSITION KEY at or before the playhead, the
    // one the height dial answers to. Without this the lift/lower entries vanish
    // on the one member that has no key while the other five could be lifted —
    // the same trap the flight probe fixed for bend and loft.
    // A member RIDING a carrier is not lifted: the sampler returns the CARRIER's
    // position (height included) while it rides, and for a marker the y comes
    // from the marked player — so the number would be written and never drawn.
    const groupRiders = verbTargets.length > 1
      ? verbTargets.filter(g => ridesNow(flightTid(g))).length : 0;
    const groupHeights = verbTargets.length > 1
      ? verbTargets.map(g => {
        const tid = g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`;
        // A zone is drawn flat (the renderer pins its y to 0), so it has no height for
        // the group verb to step — counted out here so the label's "n of m" is the
        // number of members the step will actually move.
        if (g.type === 'zone' || ridesNow(tid)) return null;
        return [...((keys.find(k => k.id === tid)?.position) || [])]
          .sort((a, b) => a.time - b.time).filter(kk => kk.time <= editT + 1e-6).pop() || null;
      }).filter(Boolean)
      : [];
    // When EVERY member the group verb will step stands at the same height, the
    // label can say it — the number is as true as the count. When they differ it
    // stays silent rather than quoting the selected one, which is the mistake the
    // ➜ entry used to make.
    const groupHeightSame = groupHeights.length > 1 && groupHeights.length === verbTargets.length
      && groupHeights.every(k => (k.position?.[1] ?? 0) === (groupHeights[0].position?.[1] ?? 0))
      ? (groupHeights[0].position?.[1] ?? 0) : null;
    // WHICH ACTION IS LIVE RIGHT NOW, for everything this menu speaks for. The
    // action list used to be twelve identical-looking verbs with no hint of
    // which one the character is already doing — and tapping the one that is
    // already keyed at this exact instant re-wrote the same key and toasted a
    // change that had not happened.
    const actT = snapKeyTime(refPlayheadTime.current);
    // WHO HAS AN ACTION LANE AT ALL. The strip decides it, and the strip is the only
    // place these keys are ever shown: its item row reads
    //     midKeys = attachable ? attach : action
    // and a ball or a zone IS attachable — their middle lane is ATTACHMENT, so an
    // action key on one is never drawn, never scrubbable, never hold-deletable. The
    // pitch draws an action key only through sampleCharacterAction, which is
    // characters-only. And ↻ Again refuses a ball in the app's own words: "balls have
    // no action lane". A key with no reader, no renderer and no way back is not a
    // verb — the card was offering eight doors onto nothing on a ball's card and
    // eight more on a zone's.
    const actableMembers = verbTargets.filter(g => g.type !== 'ball' && g.type !== 'zone');
    const actTrackIds = actableMembers.map(g => (g.type === 'player'
      ? `${g.side}:${g.index}` : `${g.type}:${g.id}`));
    const forceOf = (id, value) => {
      const f = actionKeyInForce(keys, id, actT);
      return !!f && f.value === value;
    };
    const pasteTarget = clipboard && copyTarget ? resolvePasteTarget(clipboard, copyTarget) : null;
    const canPasteHere = !!pasteTarget && !!copyTarget && isSameGroupMember(pasteTarget, copyTarget);
    const clipName = clipboard
      ? (clipboard.type === 'player'
        ? `${capWord(t(clipboard.side === 'home' ? 'side.home' : 'side.away'))} #${(clipboard.index ?? 0) + 1}`
        : getItemDisplayName(clipboard))
      : '';
    // THE FLIGHT UNDER THE PLAYHEAD, if there is one: the segment between two
    // position keys. Loft and bend write onto it, and the menu has to read it
    // first to promise the right thing — an entry that opens a refusal is a
    // door that leads nowhere.
    const editSeg = (editTrackId && editFrom !== null && editTo !== null && editFrom !== editTo)
      ? findSegment(editTrack, editFrom, editTo) : null;
    // The segment the entry will write, but only when the flight is the item's
    // OWN movement — see ridesUnderFlight above. A carried ball has a segment in
    // the data and no segment on the pitch. `ownFlight` on its own, because the
    // panel door opens for a flight with NO authored shape yet, too.
    const ownFlight = editFrom !== null && editFrom !== editTo
      && !ridesUnderFlight(editTrackId, editFrom, editTo);
    const ownSeg = ownFlight ? editSeg : null;
    // Every OTHER member of this card with a flight of its OWN under the playhead.
    // Bending a group's runs alike is ONE decision — four players making the same
    // curved run — and until now the panel only ever knew the member whose menu was
    // opened, so the coach bent the first and walked the menu three more times.
    // (arrowTrackOf is the one place that already spells a member's trackId; the
    // lane it was named for does not change the answer.) Members riding something
    // else are not swept in: their flight is not theirs to bend, and the pitch does
    // not draw one.
    const groupSegs = verbTargets.length > 1 && editTrackId
      ? verbTargets
        .filter(g => g && g.type !== 'player')
        .map(g => arrowTrackOf(g))
        .filter(tid => tid && tid !== editTrackId)
        .map(tid => ({ tid, span: segUnder(tid) }))
        .filter(x => x.span && x.span.from !== x.span.to
          && !ridesUnderFlight(x.tid, x.span.from, x.span.to))
        .map(x => ({ trackId: x.tid, from: x.span.from, to: x.span.to }))
      : [];
    // THE KEY THE HEIGHT DIAL WILL WRITE: the position key at or before the
    // playhead, exactly the one ⌥↑/⌥↓ answers to.
    const editHKey = editTrackId
      ? [...(editTrack?.position || [])].sort((a, b) => a.time - b.time)
        .filter(kk => kk.time <= editT + 1e-6).pop() || null
      : null;
    // …the key the dial will write, but only if the item is standing on its own
    // at this instant. While it rides a carrier the sampler returns the CARRIER's
    // position, height included, and for a marker the y comes from the marked
    // player — so ↑↓ would write a number nothing draws, exactly the door ⤴ and ↝
    // just stopped being. A ZONE is the same door from the other side: the renderer
    // builds it with the y pinned to zero — centerP = [mposP[0], 0, mposP[2]] and
    // o.position.set(mposP[0], 0, mposP[2]) — because a zone is an area on the turf.
    // The key would be written, shown in the strip, and never drawn.
    const ownHeightKey = (editHKey && !ridesNow(editTrackId) && copyTarget?.type !== 'zone')
      ? editHKey : null;
    // WHICH WAY the height pair can actually go, and how many members it moves
    // that way. nextHeight clamps at 0 and 10 and the verb refuses a step that
    // would not move — "at the ground a lift writes the same number" is the
    // sentence the keyboard path already says — so a card standing on the turf was
    // offering "↓ all 2 −0.5 m (0.0 m)" onto a refusal. The pair now asks the verb
    // its own question, over the same members it is about to hand it: the group
    // probe ORed with the pressed member's own key, exactly as bend and loft do.
    // The COUNT in the label is the members that will MOVE, not the members the
    // card happens to speak for.
    const heightKeys = verbTargets.length > 1 ? groupHeights : (ownHeightKey ? [ownHeightKey] : []);
    const heightMoves = (dir) => heightKeys
      .filter(k => nextHeight(k.position?.[1] ?? 0, dir, 0.5) !== (k.position?.[1] ?? 0)).length;
    const liftCount = heightMoves(1);
    const lowerCount = heightMoves(-1);
    // The denominator is the members this verb can reach at all — the card's
    // members minus the ones riding a carrier, which the 📎 note already names.
    // So "2 of 3" plus "1 follows a carrier" adds up, instead of leaving the coach
    // to work out which member the sentence left out.
    const heightPool = verbTargets.length > 1 ? verbTargets.length - groupRiders : (ownHeightKey ? 1 : 0);
    // One label for one direction: "all n" when every reachable member moves that
    // way, "n of m" when the clamp leaves some of them where they are.
    const heightLabel = (manyKey, someKey, n) => (n === heightPool
      ? t(manyKey, { n })
      : t(someKey, { n, m: heightPool }));
    // The item's own EDIT verbs, at the top for the eye. They are UTILITY
    // options: no number key, no Enter focus, so 1 is still the action you
    // reach for most. Every one of them used to be keyboard-only (Ctrl+D,
    // M / ⇧M, L, ⇧⌥S) while the tooltips around them assumed a keyboard.
    // WHO CAN BE KEYED at this instant. Two exclusions, and both are about the
    // GROUP rather than the item under the finger: a member that FOLLOWS a
    // carrier gets no entry (a position key under a carrier is a dead spot), and
    // a member that already HAS a position key here gets none — AUTO keys a move
    // where it stops, so ✚ on top of a key that is already there is a second
    // button for a thing that happens by itself. That is the exact reason this
    // entry was removed once before; the gate is what keeps it from coming back
    // as that same duplicate.
    const keyAtT = snapKeyTime(refPlayheadTime.current);
    const tidOfMember = (g) => (g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`);
    const keyable = verbTargets.filter(g => {
      const tid = tidOfMember(g);
      if (g.type !== 'player' && attachRefAt(keys, tid, keyAtT) !== null) return false;
      const pos = (keys.find(k => k && k.id === tid) || {}).position || [];
      return !pos.some(k => k && Math.abs(k.time - keyAtT) < 0.001);
    });
    // How many of the members this menu speaks for RIDE a carrier at this instant:
    // the key verb skips them, so the label has to subtract them.
    const keyFollowers = verbTargets.filter(g => g && g.type !== 'player'
      && attachRefAt(keys, g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`, keyAtT) !== null).length;
    const utilityOptions = [
      // ✚ KEY HERE — back at the top of this menu, and gated so it is not the
      // duplicate it used to be. It was removed on the grounds that AUTO keying
      // is on, so dragging an item already keys where it stopped. But AUTO is a
      // toggle, and eight of this app's own sentences ("✚ in the item menu keys
      // a position first") send a coach to a door the menu stopped carrying —
      // and they fire precisely when there is NO key to auto, which is when the
      // entry is the only way in. Same verb as the strip's ✚, same group
      // fan-out, one undo step — not a second implementation.
      ...(keyable.length ? [{
        // The group form counts what the verb will ACTUALLY key. Members riding a
        // carrier are skipped by it (a position key under a carrier is a dead
        // spot), so "all 4" over a verb that writes 3 is the same overstatement
        // the ➜ and ↝ labels just stopped making.
        label: verbTargets.length > 1
          ? t('itm.keyMany', { n: verbTargets.length - keyFollowers, t: fmtTime(keyAtT) })
            + (keyFollowers ? t(pluralKey('itm.keyFollow', keyFollowers), { n: keyFollowers }) : '')
          : t('itm.keyHere', { t: fmtTime(keyAtT) }), utility: true, iconKey: 'key',
        action: () => {
          handleKeyAtPlayhead(verbTargets);
          // THE CARD STAYS. Keying an item at several instants is ONE job, not
          // four: drag the playhead to the next moment, tap ✚ again. Closing here
          // made every extra instant cost two more taps — find the item on the
          // pitch again, then open the card again — and on a crowded pitch the
          // first of those is a hunt. The card re-measures, so the entry's own
          // time moves with the playhead and says what the next tap will do.
          stayAndRemeasure();
        },
      }] : []),
      // DELETE THIS ACTION — only when the menu was opened by tapping a key.
      // The strip's diamond answers "double-click to delete" with nothing on a
      // phone: preventDefault() on pointerdown suppresses the click event in
      // Safari, so the gesture the tooltip promised never arrived. The verb
      // lives in the panel the tap already opened, tinted red, and names the
      // instant it cuts.
      ...(target.deleteKey && arrowTrackId ? [{
        label: t('kf.deleteActionHere', { t: fmtTime(target.deleteKey.time) }),
        utility: true, danger: true, iconKey: 'delete',
        action: () => {
          setPromptState(null);
          handleKeyframeDelete(arrowTrackId, 'action', target.deleteKey.time);
        },
      }] : []),
      // The label reads the item's CURRENT arrow state at the playhead, so the
      // entry says which way it will go instead of being a blind toggle. For a
      // group it counts the members and names the mixed case: the verb turns
      // EVERY member the same way, so "on for all 5 — 2 already on" is the
      // honest sentence, and the one direction it implies is the one it takes.
      // When the menu speaks for members it will NOT write, the wording says so —
      // a zone has no arrow lane, so "on for all 2" over a verb that touches one
      // is the overstatement this menu has already stopped making three times.
      ...(arrowTrackId && arrowTargets.length ? [{
        label: (arrowGroup
          ? (arrowOn
            ? t('itm.arrowsHideMany', { n: arrowTargets.length, t: fmtTime(arrowAt) })
            : t('itm.arrowsShowMany', { n: arrowTargets.length, t: fmtTime(arrowAt) })
              + (arrowOnCount > 0 ? t('itm.arrowsSomeOn', { k: arrowOnCount }) : ''))
          : (arrowOn ? t('itm.arrowsHide', { t: fmtTime(arrowAt) })
            : t('itm.arrowsShow', { t: fmtTime(arrowAt) })))
            // …and when the menu speaks for members it will NOT write, it says so.
            + (verbTargets.length > arrowTargets.length
              ? t('itm.arrowsNoLane', { n: verbTargets.length - arrowTargets.length }) : ''), utility: true, iconKey: arrowOn ? 'arrowsOff' : 'arrowsOn',
        action: () => { if (refItemVerbs.current.arrow) refItemVerbs.current.arrow(arrowOn ? 'off' : 'on', arrowOn ? verbTargets : arrowTargets); stayAndRemeasure(); },
      }] : []),
      // THE ARROW'S COLOUR, from the same menu that turns arrows on. Until now the
      // only way to recolour a run was to hit the arrow BAND in the strip — a door
      // that exists, is easy to miss, and is thinner than a thumb. It appears only
      // while arrows are ON for this item at this instant: a colour entry sitting
      // over arrows that are off opens onto nothing.
      ...(arrowOnSelf && arrowTrackId ? [{
        label: arrowSiblings.length
          ? t('itm.arrowColourAll', { n: arrowSiblings.length + 1 })
          : t('itm.arrowColour'), utility: true, iconKey: 'colour',
        action: () => {
          const at = snapKeyTime(refPlayheadTime.current);
          const active = graphicKeyAt(keyframes, arrowTrackId, at);
          setGraphicEdit({
            trackId: arrowTrackId,
            start: active ? active.time : at,
            // …and every other member of this card whose arrow is on at the same
            // instant: one colour, one undo step, no walking the menu four times.
            ...(arrowSiblings.length
              ? { also: arrowSiblings }
              : {}),
          });
          setPromptState(null);
        },
      }] : []),
      // LOFT AND BEND, on the touch path at last. Both were chords (⇧C, C) or
      // the violet handle in the timeline: on a phone with no keyboard, the
      // flight of a pass could not be touched at all. The entry names the span
      // it writes, the way the arrow entry names its instant, and it only
      // appears when a flight is actually under the playhead — for the item
      // pressed, or for any member of the group this menu speaks for.
      // LOFT stays ball-only (a ball is the only thing a drill throws); BEND
      // fans out to the group, because bending a line of cones is one decision.
      //
      // AND NEITHER DOOR OPENS FOR A PLAYER. The entries that WRITE a run's shape
      // are already gated on type !== 'player' (see the ↝ Bend this run/pass entry
      // below) — "removed from the player menu on request". This block asked only
      // "is there a flight under the playhead?", and a player has one, so the CYCLE
      // entry — the door to the bend panel — was still standing on a player's card,
      // one entry over from the doors that were shut. The gate belongs on the block,
      // because both doors inside it are flight-shape doors for the same reason.
      ...(copyTarget && copyTarget.type !== 'player' && (ownSeg || groupFlights.length) ? [
        ...(copyTarget.type === 'ball' && (ownSeg || groupLofts.length) ? [{
          label: groupLofts.length > 1
            ? t('itm.loftMany', { n: groupLofts.length })
              + (loftSkipped ? t('bend.skipped', { n: loftSkipped }) : '')
            : groupLofts.length === 1 && !ownSeg
              // The card speaks for several balls and exactly ONE of them has a
              // flight here. The old label printed "⤴ Loft (1 balls)" — a count
              // dressed up as a group verb — and the span form could not be used
              // either, because the pressed ball has no flight to name. Say which
              // it is: the one that can.
              ? t('itm.loftSome', { n: 1 })
                + (loftSkipped ? t('bend.skipped', { n: loftSkipped }) : '')
              : t((ownSeg.arch ?? 0) > 0 ? 'itm.loftOff' : 'itm.loftOn',
                { from: fmtTime(editFrom), to: fmtTime(editTo) }), utility: true, iconKey: 'loft',
          action: () => {
            // The label and the verb are decided by the same probe, so they cannot
            // drift: the group verb runs when the group probe found more than one
            // flight (or one flight that is not the pressed ball's); otherwise the
            // single verb writes the span the label just named.
            if (groupLofts.length > 1 || (!ownSeg && groupLofts.length === 1)) {
              if (refItemVerbs.current.loftGroup) refItemVerbs.current.loftGroup(verbTargets);
            } else if (refItemVerbs.current.loft) {
              refItemVerbs.current.loft(editTrackId, editFrom, editTo);
            }
            stayAndRemeasure();
          },
        }] : []),
        {
          // The group label counts the members this verb can actually bend, not
          // everyone in the group: a member with no flight under the playhead is
          // skipped by the verb and announced as skipped, so a label saying
          // "all 3" over a verb that moves 2 is the same one-member mistake the
          // ➜ entry used to make, pointed the other way.
          //
          // And the GROUP entry is gated on the group PROBE, not just on the card
          // speaking for several members. When the probe finds nothing the label
          // used to fall through to "↝ Bend all 1" — a group of one invented out of
          // Math.max — while the verb it called would have bent nothing at all. A
          // door that opens onto nothing is worse than no door, and a number that
          // is manufactured is worse than a lie: the card now says which single
          // flight it is cycling, which is exactly what the verb will do.
          label: verbTargets.length > 1 && groupFlights.length
            ? (groupFlights.length < verbTargets.length
              ? t('itm.bendCycleSome', { n: groupFlights.length })
                + t('bend.skipped', { n: verbTargets.length - groupFlights.length })
              : t('itm.bendCycleMany', { n: groupFlights.length }))
            : t('itm.bendCycle', {
              from: fmtTime(editFrom), to: fmtTime(editTo),
              now: bendName((ownSeg && ownSeg.style) || 'straight'),
            }), utility: true, iconKey: 'bend',
          action: () => {
            if (verbTargets.length > 1 && groupFlights.length) {
              if (refItemVerbs.current.bendGroup) refItemVerbs.current.bendGroup(verbTargets);
            } else if (refItemVerbs.current.bend) {
              refItemVerbs.current.bend(editTrackId, editFrom, editTo);
            }
            stayAndRemeasure();
          },
        },
      ] : []),
      // HIDE / SHOW, for the ball only, and only for the ONE ball this menu
      // speaks for. It used to be ⇧V or a trip to the Ball panel: on a phone the
      // chord does not exist, and the panel is a step away from the ball you are
      // already holding. A hidden ball is not drawn, so the pitch cannot pick it
      // up again — which is why the entry FLIPS in place (the menu re-measures
      // after the verb) and why the toast names the strip as the other way back.
      ...(copyTarget && copyTarget.type === 'ball' && verbTargets.length === 1
        && (pitchState?.balls || []).some(b => b.id === copyTarget.id) ? [{
        label: ballIsVisible((pitchState?.balls || []).find(b => b.id === copyTarget.id))
          ? t('itm.ballHide') : t('itm.ballShow'), utility: true, iconKey: ballIsVisible((pitchState?.balls || []).find(b => b.id === copyTarget.id)) ? 'hidden' : 'visible',
        action: () => {
          const b = (pitchState?.balls || []).find(x => x.id === copyTarget.id);
          if (!b) {
            setToast({ message: t('toast.hideGone'), id: Date.now() });
            return;
          }
          const was = ballIsVisible(b);
          if (refItemVerbs.current.ballVisible) {
            refItemVerbs.current.ballVisible(copyTarget.id, { visible: !was });
          }
          setToast({ message: was ? t('view.ballHidden') : t('view.ballShown'), id: Date.now() });
          stayAndRemeasure();
        },
      }] : []),
      // LIFT and LOWER, the height of the key the playhead sits on — a KEY, not
      // a flight, so they stand on their own: an item parked at one moment still
      // has a height to set. The label carries the CURRENT height, so the pair
      // says where the item stands right now, not just that a dial exists in a
      // panel somewhere. And because the label carries a number that just
      // changed, the entry re-opens the menu on the new number: three taps lift
      // 1.5 m, where closing it made each step cost a long-press and a tap.
      // When the menu speaks for a GROUP the entry says so and steps every
      // member — five cones onto a platform is one entry, not five menus.
      ...(liftCount || lowerCount ? [
        ...(liftCount ? [{
          label: !ownHeightKey || verbTargets.length > 1
            ? heightLabel('itm.liftMany', 'itm.liftSome', liftCount)
              + (groupHeightSame !== null ? t('itm.manyAt', { h: groupHeightSame.toFixed(1) }) : '')
              + (groupRiders ? t(pluralKey('itm.keyFollow', groupRiders), { n: groupRiders }) : '')
            : t('itm.lift', { t: fmtTime(ownHeightKey.time), h: (ownHeightKey.position?.[1] ?? 0).toFixed(1) }), utility: true, iconKey: 'up',
          action: () => {
            if (!ownHeightKey || verbTargets.length > 1) {
              if (refItemVerbs.current.height) refItemVerbs.current.height(editTrackId, 1, 0.5, verbTargets);
            } else if (refItemVerbs.current.height) {
              refItemVerbs.current.height(editTrackId, 1, 0.5, null);
            }
            stayAndRemeasure();
          },
        }] : []),
        ...(lowerCount ? [{
          label: !ownHeightKey || verbTargets.length > 1
            ? heightLabel('itm.lowerMany', 'itm.lowerSome', lowerCount)
              + (groupHeightSame !== null ? t('itm.manyAt', { h: groupHeightSame.toFixed(1) }) : '')
              + (groupRiders ? t(pluralKey('itm.keyFollow', groupRiders), { n: groupRiders }) : '')
            : t('itm.lower', { t: fmtTime(ownHeightKey.time), h: (ownHeightKey.position?.[1] ?? 0).toFixed(1) }), utility: true, iconKey: 'down',
          action: () => {
            if (!ownHeightKey || verbTargets.length > 1) {
              if (refItemVerbs.current.height) refItemVerbs.current.height(editTrackId, -1, 0.5, verbTargets);
            } else if (refItemVerbs.current.height) {
              refItemVerbs.current.height(editTrackId, -1, 0.5, null);
            }
            stayAndRemeasure();
          },
        }] : []),
      ] : []),
      // MARK PLAYER, for a player only, and only ever ONE at a time — the entry
      // says who they are marking right now, so the menu is the complete list of
      // what this item supports. A GROUP of markers is the entry below it: they
      // share ONE marked opponent, each marking from where they stand, which is
      // what the group drag already does and what the chord has always done.
      ...(copyTarget && copyTarget.type === 'player' && verbTargets.length === 1 ? [{
        label: markLeader
          ? t('itm.markChange', { who: describeAttachRef(markLeader) })
          : t('itm.markAsk'), utility: true, iconKey: 'mark',
        action: () => {
          setPromptState(null);
          if (refItemVerbs.current.mark) {
            refItemVerbs.current.mark(`${copyTarget.side}:${copyTarget.index}`);
          }
        },
      }] : []),
      // MARK for the GROUP this menu speaks for — the menu twin of dropping the
      // group on an attacker. One entry, one tap on the pitch, and every member
      // marks that opponent from where they are already standing. The single
      // entry above stays put for a lone selection, so the menu never offers
      // the same verb twice under two different words.
      ...(groupMarkable.length > 1 ? [{
        label: t('itm.markAskMany', { n: groupMarkable.length }), utility: true, iconKey: 'mark',
        action: () => {
          setPromptState(null);
          if (refItemVerbs.current.markGroup) refItemVerbs.current.markGroup(verbTargets);
        },
      }] : []),
      // STOP MARKING, as its own entry. Until now the only way out was inside
      // the marking prompt — a coach who wanted OFF had to go through the door
      // that asks WHO, find a secondary button, and hope the words matched.
      // "There is no clear way to stop a player from marking another" was the
      // complaint; this is the answer. It names who will be let go and the
      // second it happens at, and it does one thing.
      ...(copyTarget && copyTarget.type === 'player' && verbTargets.length === 1 && markLeader ? [{
        label: t('mark.stopMenu', {
          who: describeAttachRef(markLeader), t: fmtTime(refPlayheadTime.current),
        }), utility: true, iconKey: 'unmark',
        action: () => {
          setPromptState(null);
          commitUnmark(`${copyTarget.side}:${copyTarget.index}`, snapKeyTime(refPlayheadTime.current));
        },
      }] : []),
      // THE SAME PRESS, SET FOR ALL OF THEM at once. Marking four attackers is
      // one drag; pressing them all "goal-side, a metre and a half out" was
      // still four dials, because each marker's bearing and gap live on their
      // own attach key. One panel, one ghost per marked player so the preview
      // shows every spot the release will take, one undo step — and the way OUT
      // of the group is the button inside the same panel.
      ...(groupMarking.length > 1 ? [{
        label: t('itm.markGapMany', { n: groupMarking.length }), utility: true, iconKey: 'mark',
        action: () => {
          setPromptState(null);
          if (refItemVerbs.current.markGapGroup) refItemVerbs.current.markGapGroup(verbTargets);
        },
      }] : []),
      // STOP MARKING for the GROUP this menu speaks for — one entry, one undo
      // step, one sentence. The single-player entry above stays put for a lone
      // selection; a group gets the count instead of one leader's name, because
      // four markers are usually marking four DIFFERENT players and naming one
      // of them would be a label that lies about the other three.
      ...(groupMarking.length ? [{
        label: t('mark.stopMenuMany', {
          n: groupMarking.length, t: fmtTime(refPlayheadTime.current),
        }), utility: true, iconKey: 'unmark',
        action: () => {
          setPromptState(null);
          if (refItemVerbs.current.unmarkGroup) {
            refItemVerbs.current.unmarkGroup(verbTargets, snapKeyTime(refPlayheadTime.current));
          }
        },
      }] : []),
      // ATTACH, for everything that is NOT a player — a cone, a target, a zone,
      // a ball. The item rides another thing's movement instead of drawing its
      // own. Players get 🎯 Mark above: the same lane, a different rule — a rider
      // has no path of its own, a marker's keys are parked and they hold a bearing
      // and a gap. The entry names the carrier it is under right now, and the
      // prompt it opens is where "release" lives.
      ...(copyTarget && copyTarget.type !== 'player' && verbTargets.length === 1 ? [{
        label: attachCarrier
          ? t('itm.attachChange', { who: describeAttachRef(attachCarrier) })
          : t('itm.attachAsk'), utility: true, iconKey: 'attach',
        action: () => {
          setPromptState(null);
          if (refItemVerbs.current.attach) refItemVerbs.current.attach(editTrackId);
        },
      }] : []),
      // ATTACH for the GROUP this menu speaks for. Five cones onto the ball used
      // to be five menus and five picks; this is one entry and one pick, and the
      // way out is one tap as well. The count is in the label, so the entry
      // cannot promise a verb the selection does not have.
      ...(groupAttachable.length ? [{
        label: t('itm.attachMany', { n: groupAttachable.length }), utility: true, iconKey: 'attach',
        action: () => {
          setPromptState(null);
          if (refItemVerbs.current.attachGroup) refItemVerbs.current.attachGroup(verbTargets);
        },
      }] : []),
      // REPEAT — the ⇧X verb on the touch path. "Do what I just did, here" is
      // the biggest step-saver in the app: mark five players with one tap each
      // instead of opening the action picker five times. Until now its only door
      // was a chord. The entry NAMES the action it will lay down, because "Do it
      // again" without a verb is a riddle, and it does not appear for a ball OR a zone,
      // neither of which has an action lane to repeat into. The gate is the SAME probe
      // the action block above asks — one shared answer per verb, so a card can never
      // offer a repeat for a member the repeat then refuses.
      ...(refLastAction.current && actTrackIds.length ? [{
        label: t('itm.repeat', { action: refLastAction.current.label }), utility: true, iconKey: 'repeat',
        action: () => {
          if (refItemVerbs.current.repeat) refItemVerbs.current.repeat(verbTargets);
          // THE CARD STAYS, exactly as it does after ✚. ↻ is a repeat verb by
          // definition — "do what I just did, here" — so the next tap is the most
          // likely one. Closing it made each extra instant cost finding the item
          // on the pitch again and opening the card again.
          stayAndRemeasure();
        },
      }] : []),
      // BEND — for the items whose PATH is the point. A coach bends a pass so it
      // curves around a defender; a player's run is drawn by where they go, not
      // by the shape between two of their keys, and the entry read as a riddle on
      // a player. Removed from the player menu on request; the ball, cones,
      // targets and zones keep it.
      //
      // This is the SLOW half of the verb above it, and it has to read that way:
      // ↝ Bend 2–4: straight cycles the shape in one tap and stays open, this one
      // opens the panel with the sliders. Both start with ↝ and both named the
      // same two instants, so a coach saw "↝ Bend 2–4: straight" and "↝ Bend this
      // pass (2–4)" and could not tell that one is a tap and one is a panel. The
      // ellipsis is this app's own mark for "a panel follows" (🎯 Mark an
      // opponent… · 📎 Attach to… · ⇄ Mirror…), and the instants go: the entry
      // above already names the same flight, from the same two variables.
      // In a GROUP card this door used to open the panel for the ONE member whose
      // menu it is, and said so — "this item only" — because the card read "all 2"
      // everywhere else. That was honest and still slow: bending four cones' runs
      // to the same shape was four menus and four slider drags. The door now
      // carries the other members' flights with it ("↝ Bend all 4 runs alike…")
      // and the panel writes them together, each onto its OWN flight bounds. When
      // the card speaks for members this door cannot bend — one cone and a player
      // it was removed from, or members riding something else — it falls back to
      // the singular line and keeps saying which one it touches.
      ...(ownFlight && copyTarget && copyTarget.type !== 'player' ? [{
        label: groupSegs.length
          ? t('itm.bendAll', { n: groupSegs.length + 1 })
          : (copyTarget.type === 'ball'
            ? t('itm.bendPass')
            : t('itm.bendRun'))
            + (verbTargets.length > 1 ? t('itm.onlyThis') : ''), utility: true, iconKey: 'bend',
        action: () => {
          setPromptState(null);
          if (refItemVerbs.current.editSeg) {
            refItemVerbs.current.editSeg(editTrackId, editFrom, editTo,
              groupSegs.length ? groupSegs : undefined);
          }
        },
      }] : []),
      ...(copyTarget ? [{
        // In a card that speaks for several members, this one touches ONE: the
        // clipboard holds a single item, and paste puts that one item down. Every
        // other line in the same card says "all 2", so the exception has to name
        // itself — otherwise a coach copying a three-cone shape pastes one cone
        // and wonders where the other two went.
        label: t('itm.copyIt')
          + (verbTargets.length > 1 ? t('itm.onlyThis') : ''), utility: true, iconKey: 'copy',
        action: () => { setPromptState(null); if (refItemVerbs.current.copy) refItemVerbs.current.copy(copyTarget); },
      }] : []),
      ...(canPasteHere ? [{
        label: t('itm.pasteHere', { name: clipName }), utility: true, iconKey: 'paste',
        action: () => { setPromptState(null); if (refItemVerbs.current.paste) refItemVerbs.current.paste(copyTarget); },
      }] : []),
      ...(canDuplicate ? [{
        label: t('itm.duplicateIt'), utility: true, iconKey: 'duplicate',
        // DUPLICATE KEEPS THE CARD, the way the line verbs do: duplicating a
        // shape is how a coach repeats a drill structure, and the second copy is
        // as likely as the first. The verb selects what it made (and groups the
        // copies), so the card re-opens on THOSE — queued, because the scene this
        // render can see does not contain them yet. 📋 Copy and 🗑 Delete still
        // close: copy is a two-place verb (you go somewhere else to paste), and a
        // card that stays open over items it just destroyed is how one accident
        // becomes two.
        action: () => {
          const created = refItemVerbs.current.duplicate
            ? refItemVerbs.current.duplicate(verbTargets) : null;
          if (!created || !created.length) { setPromptState(null); return; }
          setPendingPrompt({ item: created[created.length - 1], items: created });
        },
      }] : []),
      ...(lineStepM ? [{
        label: t('itm.nextLineStep', { m: lineStepM }), utility: true, iconKey: 'line',
        action: () => lineAndStay('step'),
      }] : []),
      // THE LINE VERBS LIVE IN THE ITEM'S OWN MENU, not in the + toolbar. They
      // used to sit in the toolbar greyed-or-not by whatever was placed LAST, so
      // with a cone in the trail and a player selected the mirror entries still
      // looked live — and tapping one laid a cone down while you thought you
      // were working on the player. Here the menu IS the target: the entry only
      // appears in the menu of an item that is actually in the line.
      ...(lineStepM ? [{
        label: t('itm.turnLine', { m: lineStepM }), utility: true, iconKey: 'line',
        action: () => lineAndStay('turn'),
      }] : []),
      // THE MIRROR FAMILY, behind one door. Five ways to turn a thing over —
      // copy to the other half / the other side, flip end-for-end, flip
      // flank-for-flank, turn the WHOLE scene — is five buttons in a menu that a
      // phone user has to scroll, and the difference between "mirror" and "copy
      // across" is the kind of thing a label cannot settle. One entry opens them
      // as their own question, where each name gets room. With only one option
      // live there is nothing to choose, so it stays inline and costs no extra
      // tap.
      ...(mirrorOptions.length === 1 ? mirrorOptions.map(sa => ({ ...sa, utility: true }))
        : mirrorOptions.length > 1 ? [{
          label: t('itm.mirrorMenu', { n: mirrorOptions.length }), utility: true, iconKey: 'mirror',
          // Inside the door the options ARE the answers, so they take the number
          // keys and the Enter focus — a utility entry never does.
          action: () => mirrorAgain(),
        }] : []),
      // THE GROUP DOOR — the side, the line, or one door into both. Built above so
      // the same option objects serve inline and inside the panel. THE CARD
      // RE-OPENS ON THE GROUP IT JUST MADE: selecting a group is not an end in
      // itself — it is how a coach reaches the group verbs (↑ all 4 · ↝ all 4 ·
      // 🗑 all 4) without long-pressing every member on the pitch. Closing the card
      // here sent them back to the pitch to tap one member and open a card again:
      // the very step this entry exists to remove.
      ...(selectGroupOptions.length === 1
        ? [{ ...selectGroupOptions[0], utility: true }]
        : selectGroupOptions.length > 1 ? [{
          label: t('itm.selectGroupMenu', { n: selectGroupOptions.length }), utility: true, iconKey: 'group',
          // Inside the door the options ARE the answers, so they take the number
          // keys and the Enter focus — a utility entry never does.
          action: () => { if (selectGroupPanel) setPromptState(selectGroupPanel); else setPromptState(null); },
        }] : []),
      // Destructive verbs LAST, and only when the menu really speaks for a
      // group: an ungrouped item is deleted by the toolbar − (or plain Del), and
      // offering "delete all 1" here would blur that deliberate split.
      ...(speaksForGroup ? [{
        label: t('itm.deleteAll', { n: verbTargets.length }), utility: true, danger: true, iconKey: 'delete',
        action: () => { setPromptState(null); if (refItemVerbs.current.remove) refItemVerbs.current.remove(verbTargets); },
      }] : []),
    ];
    // The item this card was opened ON, when it was opened on one by id alone.
    const selfItem = itemFromTrackId(target.trackId || '');
    const selfName = selfItem ? getItemDisplayName(selfItem) : '';
    // WHOSE CARD THIS IS — and how many more it is speaking for. A card opened on
    // a three-cone group was headed "Cone 01" while every line inside it said
    // "all 3": the header named one member and the verbs described three, so the
    // only way to learn you were in a group card was to read the entries. The
    // count is the card's own verb list's count — verbTargets, the same array
    // every "all n" below is measured from.
    const cardTitle = target.title || (target.side !== undefined
      ? t('act.title', { what: `${capWord(t(target.side === 'home' ? 'side.home' : 'side.away'))} #${target.index + 1}` })
      // A card opened on an item nobody named — the re-open after a placement,
      // or any caller that has a trackId but no name to hand — names the item
      // itself, through the one word-list the rest of the app already reads.
      // Deriving it HERE (not at the call site) is what keeps it right for an
      // item placed a moment ago: the name is positional, so it has to come
      // from the scene the card is being built against.
      : (selfName || t('act.plain')));
    setPromptState({
      title: cardTitle + (verbTargets.length > 1
        ? t('act.groupMore', { n: verbTargets.length - 1 }) : ''),
      description: utilityOptions.length ? t('itm.menuHint') : undefined,
      iconOnly: true,
      // Most-used actions of THIS branch first — number keys then hit the
      // action you actually reach for (1 = favourite).
       subActions: [
        ...utilityOptions.map(sa => ({ ...sa, section: ['key', 'delete', 'repeat'].includes(sa.iconKey) ? 'time' : 'visual' })),
        // The action block appears only when at least one member this card speaks for
        // has an action lane to write into (see actableMembers above). A card speaking
        // for a ball, or a zone, shows NO action verbs at all — and a mixed card keeps
        // them, counts only the lanes that exist, and says who was skipped.
        ...(actTrackIds.length ? rankActions(ACTION_TYPES, keys).map(at => {
        // The live one says so — the menu now answers "what is it doing here?"
        // For a GROUP the answer is rarely all-or-nothing: four players running the
        // same route usually have two waving. The marker used to go silent then, so
        // the card answered the question for one member and refused it for four —
        // and the coach had to key the action to find out. The count is the same
        // forceOf the solo marker asks, over the same tracks this entry will write.
        const liveCount = actTrackIds.reduce((n, id) => n + (forceOf(id, at.value) ? 1 : 0), 0);
        const live = actTrackIds.length > 0 && liveCount === actTrackIds.length;
        // A label must count what the VERB will do. On a mixed card the verb writes
        // the members that have an action lane and skips the rest, so the entry says
        // so before the tap — the same parenthetical the bend entries already use.
        const laneSkip = verbTargets.length - actTrackIds.length;
        return {
        label: (live ? t('act.now', { what: at.label })
          : (liveCount > 0 ? t('act.nowSome', { what: at.label, n: liveCount, m: actTrackIds.length })
            : at.label))
          + (laneSkip > 0 ? t('act.noLaneSkipped', { n: laneSkip }) : ''),
        icon: at.icon,
        section: 'actions',
        action: () => {
          // One prompt can key a whole group (target.targets) — one undo step.
          const list = target.targets || [target];
          // Only members WITH an action lane are written. The rest are counted out
          // loud, never silently dropped — the rule ↻ Again and the bend verbs already
          // keep. A prompt target does not always carry a type, so the lane is read off
          // the track id when the type is missing.
          const laneType = (tt) => tt.type || (itemFromTrackId(tt.trackId || '') || {}).type;
          const writable = list.filter(tt => {
            const ty = laneType(tt);
            return ty !== 'ball' && ty !== 'zone';
          });
          const actSkip = list.length - writable.length;
          // The identical key at the identical instant is a NO-OP: refuse it,
          // leave the menu open, say so. A DIFFERENT instant is a real
          // re-trigger (action keys are one-shot), so only "exact" blocks.
          if (actionAlreadyKeyedHere(actTrackIds, at.value, actT)) {
            setToast({ message: actTrackIds.length > 1
              ? t('toast.alreadyActionMany', { n: actTrackIds.length, action: at.label, t: fmtTime(actT) })
              : t('toast.alreadyActionOne', { action: at.label, t: fmtTime(actT) }), id: Date.now() });
            return;
          }
          handleDragStart('undoVerb.setActions');
          for (const tt of writable) {
            if (tt.side !== undefined && tt.index !== undefined) {
              handleActionKey(tt.side, tt.index, at.value);
            } else if (tt.trackId) {
              handleItemActionKey(tt.trackId, at.value);
            }
          }
          handleDragEnd();
          // THE CARD STAYS, and re-opens on the NEXT render through the queue.
          // Keying the same action at several instants is one job — "Shout at 2 s,
          // 4 s, 6 s" — and closing here charged a hunt for the item and a re-open
          // for every instant after the first. The QUEUE, not an immediate
          // re-measure: the key just written is not in the keyframes this render
          // can see, so measured now the entry that says which action is live would
          // name the one from before the tap.
          setPendingPrompt({ target });
          // REMEMBER it — ⇧X repeats this action on the next item without
          // walking the menu again (the branch's favourites already float to
          // the top of that menu, so this is the last mile of that ranking).
          refLastAction.current = { value: at.value, label: at.label };
          // One action or many — the commit speaks. (handleActionKey never
          // refuses: action keys are one-shot triggers, never inherited.)
          // The suffix is the SHARED key, not a literal typed here. A bare
          // "· Ctrl+Z undoes" inside a template put English in front of a
          // Portuguese coach and named a chord a phone does not have; the key
          // behind it names the ↩ button as well, and every other commit
          // toast already reads the same one.
          setToast({ message: (writable.length > 1
            ? `${writable.length} × ${at.label} at ${fmtTime(refPlayheadTime.current)}`
            : `${at.label} at ${fmtTime(refPlayheadTime.current)}`)
            + (actSkip ? t('act.noLaneSkipped', { n: actSkip }) : '')
            + t('toast.undoSuffix'), id: Date.now() });
        },
        };
        }) : []),
      ],
      cancelAction: () => setPromptState(null),
    });
  }, [handleActionKey, handleItemActionKey, keyframes, pitchState, groupItems, clipboard, getItemDisplayName, handleDragStart, handleDragEnd, itemFromTrackId, handleKeyframeClick, commitUnmark, handleKeyframeDelete, actionAlreadyKeyedHere, t]);
  // The queued re-open (see pendingPrompt above). It runs on the render that
  // already contains the item the verb placed, and it builds the header from
  // getItemDisplayName AT THAT MOMENT — the name of a cone placed on the previous
  // tick is only positional inside the scene, so a title written any earlier would
  // read "Cone 01" over the seventh cone.
  useEffect(() => {
    if (!pendingPrompt) return;
    setPendingPrompt(null);
    // A caller that already HAS the card's target (keying an action, where the
    // target carries the key it was opened on) hands it over as it stands; the
    // item/items shape below is the convenience for callers that only have a group
    // member — a placement, a copy, a new selection.
    if (pendingPrompt.target) {
      handleActionPrompt(pendingPrompt.target, refKeyframesState.current);
      return;
    }
    const g = pendingPrompt.item;
    const items = pendingPrompt.items && pendingPrompt.items.length > 1
      ? pendingPrompt.items : null;
    handleActionPrompt({
      ...promptTargetOf(g),
      // A player's name comes from the side/index the target already carries;
      // only items need the positional word-list, and asking for a player's name
      // there returns nothing, which used to head a squad card "Action".
      ...(g.type === 'player' ? {} : { title: getItemDisplayName(g) }),
      ...(items ? { targets: items.map(promptTargetOf) } : {}),
    }, refKeyframesState.current);
  }, [pendingPrompt, handleActionPrompt, getItemDisplayName]);

  /**
   * REPEAT THE LAST ACTION — ⇧X.
   *
   * Running a set of players through the same beat (all four wave, both
   * fullbacks drop) means opening the same menu over and over. That menu is
   * already ranked by this branch's favourites, so X→1 is two presses; ⇧X is
   * one, and it names what it did so the state change is unmistakable.
   *
   * SINGLE TARGET on purpose: the group fan-out stays an explicit X, which
   * confirms WHO gets the action before writing to five tracks.
   */
  const handleRepeatAction = useCallback((explicit = null) => {
    // `explicit` = the members the CALLER named. The item menu repeats on the item
    // whose menu it is — and the strip opens that menu WITHOUT selecting, so this
    // used to read the ambient selection and key the action on whoever happened to
    // be picked instead. The keyboard path (⇧X) passes nothing and keeps its
    // selection-based behaviour exactly.
    const sel = refSelectedItem.current;
    const named = explicit && explicit.length ? explicit : null;
    if (!named && !sel) {
      setToast({ message: t('toast.needItemRepeat'), id: Date.now() });
      return;
    }
    if (!named && (sel.type === 'ball' || sel.type === 'zone')) {
      // The strip gives a ball AND a zone an ATTACHMENT lane where a cone or target has
      // the Action lane, and the pitch reads action keys for characters only. So a
      // repeated action on either writes a key nothing shows, nothing scrubs and — now
      // the card closed too — nothing can delete.
      setToast({ message: t('act.ballsNoRepeat'), id: Date.now() });
      return;
    }
    const last = refLastAction.current;
    if (!last) {
      setToast({ message: t('act.noActionYet'), id: Date.now() });
      return;
    }
    // GROUP-AWARE, like X already is: one prompt keys the back four 'Shout', so
    // repeating that action at the next moment must key all FOUR — not make the
    // user re-select each player for one press. Balls have no action lane:
    // they are skipped and COUNTED up front, never silently dropped.
    const inGroup = !named && groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, sel));
    const pool = named || (inGroup ? groupItems : [sel]);
    const targets = pool.filter(g => g.type !== 'ball' && g.type !== 'zone');
    if (!targets.length) {
      setToast({ message: t('toast.ballNoActionShift'), id: Date.now() });
      return;
    }
    // Repeating at the SAME instant for the SAME tracks is a no-op: refuse it
    // rather than toast "again" for a key that was already there. ⇧X is for the
    // NEXT moment — step the playhead on, or select someone else.
    const repIds = targets.map(g => (g.type === 'player'
      ? `${g.side}:${g.index}` : `${g.type}:${g.id}`));
    if (actionAlreadyKeyedHere(repIds, last.value, snapKeyTime(refPlayheadTime.current))) {
      setToast({ message: t('toast.repeatSameInstant', { action: last.label, t: fmtTime(refPlayheadTime.current) }), id: Date.now() });
      return;
    }
    handleDragStart('undoVerb.setAction');
    for (const g of targets) {
      if (g.type === 'player') handleActionKey(g.side, g.index, last.value);
      else handleItemActionKey(`${g.type}:${g.id}`, last.value);
    }
    handleDragEnd();
    // The skip count comes from the POOL the verb was handed, not from the ambient
    // group: a card speaking for three members with a ball among them has to count
    // the ball it skipped, whoever made the card.
    const skipped = pool.length - targets.length;
    setToast({
      message: t('act.againAt', { action: last.label, t: fmtTime(refPlayheadTime.current) })
        + (targets.length > 1
          ? (skipped
            ? t('act.againGroupBalls', { n: targets.length, m: pool.length })
            : t('act.againGroupAll', { n: targets.length }))
          : '')
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
  }, [handleActionKey, handleItemActionKey, handleDragStart, handleDragEnd, groupItems, isSameGroupMember, setToast, actionAlreadyKeyedHere, t]);

  /**
   * KEY THIS SPOT NOW — K on the keyboard, ✚ in an item's long-press menu.
   *
   * Keying the position a thing ALREADY holds is the single most common
   * timeline action (mark where he stands at 0 s, mark that he is STILL there
   * at 3 s), yet the pitch had no touch path to it: you either dragged — which
   * MOVES the item — or went looking for the key button on the track row in
   * the timeline. One entry in the menu the item already opens fixes that.
   *
   * `explicit` is the menu's own target list (it speaks for its group); with no
   * argument the chord's selection + group rules apply. Returns whether a key
   * actually landed, so callers never claim success the handler refused.
   */
  const handleKeyAtPlayhead = useCallback((explicit = null) => {
    const members = explicit && explicit.length
      ? explicit
      : (groupItems.length >= 2 && refSelectedItem.current
        && groupItems.some(g => isSameGroupMember(g, refSelectedItem.current)))
        ? groupItems
        : (refSelectedItem.current ? [refSelectedItem.current] : []);
    if (!members.length) {
      setToast({ message: t('toast.keyNeedItem'), id: Date.now() });
      return false;
    }
    const at = refPlayheadTime.current;
    // ATTACHED members FOLLOW — a position key under a carrier is a dead spot.
    // Skip them, count them, name the release verb. PLAN BEFORE BURNING UNDO:
    // the refusal happens before handleDragStart, so nothing is recorded.
    const followers = members.filter(g => g.type !== 'player'
      && attachRefAt(keyframes, `${g.type}:${g.id}`, at) !== null);
    if (followers.length === members.length) {
      setToast({ message: t(pluralKey('toast.keyAllFollow', members.length), { n: members.length }), id: Date.now() });
      return false;
    }
    if (members.length > 1) {
      // One undo step for the whole group key, and a COUNT so you know all N
      // members got keyed — a silent loop feels like nothing happened.
      handleDragStart('undoVerb.groupPositionKeys');
      for (const g of members) {
        if (followers.includes(g)) continue;
        if (g.type === 'player') handleKeyPress(g.side, g.index);
        else handleItemPositionKey(`${g.type}:${g.id}`);
      }
      handleDragEnd();
      setToast({ message: t('toast.keyMany', { n: members.length - followers.length, t: fmtTime(at) })
        + (followers.length ? t(pluralKey('toast.keyFollowSuffix', followers.length), { n: followers.length }) : '')
        // A position key written where the item ALREADY stands changes nothing you
        // can see — the pitch looks untouched. The ↩ is the only way back the
        // sentence can offer, because the card has no ✕ for a key it did not open
        // on, and AUTO will not remove what you put there by hand.
        + t('toast.undoSuffix'), id: Date.now() });
      return true;
    }
    const only = members[0];
    if (only.type === 'player') {
      handleKeyPress(only.side, only.index);
      setToast({ message: t('toast.keyOne', { name: getItemDisplayName(only), t: fmtTime(at) })
        + t('toast.undoSuffix'), id: Date.now() });
      return true;
    }
    // The dead-spot refusal lives inside handleItemPositionKey itself (shared
    // with the track row's key button), so this reports success only when the
    // handler says a key landed.
    const landed = handleItemPositionKey(`${only.type}:${only.id}`);
    if (landed) setToast({ message: t('toast.keyOne', { name: getItemDisplayName(only), t: fmtTime(at) })
      + t('toast.undoSuffix'), id: Date.now() });
    return landed;
  }, [keyframes, groupItems, isSameGroupMember, handleKeyPress, handleItemPositionKey, handleDragStart, handleDragEnd, getItemDisplayName, setToast, t]);
  // (The item menu used to reach this verb through refItemVerbs.current.key.
  // The "✚ Key here" entry is gone — AUTO keys a move where it stops — so the
  // chord is the only caller and the ref would be a door to an empty room.)

  // The KEY GRAPHIC prompt is gone. It asked "Arrows ON at 4 — change?" with
  // On and Off in it, and exactly one of those two buttons could ever do
  // anything: writing the value already in force changes nothing, and the verb
  // refuses it with a toast. One live button behind a modal is a panel that
  // exists to hide a tap. handleGraphicFlip (below handleArrowToggle) presses
  // the live button directly, from the ✚ and from the diamond alike.

  /**
   * SHOW / HIDE THE ARROWS — the pitch-side twin of G.
   *
   * The arrow window is how you READ a drill at a glance, yet switching one
   * meant the G key or tapping the graphic lane INSIDE the timeline. The menu
   * an item already opens carries it, and the label states the CURRENT state
   * at the playhead, so the tap is a decision you can see — not a toggle you
   * have to test.
   *
   * The VALUE comes from the menu (decided by the item you long-pressed) and is
   * shared by every member that menu speaks for: one decision, each member's
   * own state — the same rule the group bend follows.
   */
  const handleArrowToggle = useCallback((value, explicit = null) => {
    const members = explicit && explicit.length
      ? explicit
      : (refSelectedItem.current ? [refSelectedItem.current] : []);
    if (!members.length) {
      setToast({ message: t('toast.arrowsNeedItem'), id: Date.now() });
      return false;
    }
    const t0 = snapKeyTime(refPlayheadTime.current);
    const tidOf = (g) => (g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`);
    // SEAM: a graphic key at a child branch's locked 00:00 cannot be written.
    // Filter BEFORE burning an undo step — the guard already toasted.
    const movable = members.filter(g => !refuseLockedDrag(tidOf(g), t0, ['graphic']));
    if (!movable.length) return false;
    // Announce, don't fake: writing an ON key over an ON window changes
    // nothing, and a toast claiming it did is worse than refusing. Each member
    // keeps its OWN state — the decision (on/off) is the shared one.
    const states = movable.map(g => ({ g, on: arrowOnAt(keyframes, tidOf(g), t0) }));
    const todo = states.filter(s => s.on !== (value === 'on'));
    if (!todo.length) {
      setToast({ message: value === 'on'
        ? t('toast.arrowsAlreadyOn', { t: fmtTime(t0) })
        : t('toast.arrowsAlreadyOff', { t: fmtTime(t0) }), id: Date.now() });
      return false;
    }
    if (todo.length > 1) {
      handleDragStart('undoVerb.groupArrows');
      for (const s of todo) {
        if (s.g.type === 'player') handleGraphicKey(s.g.side, s.g.index, value);
        else handleItemGraphicKey(tidOf(s.g), value);
      }
      handleDragEnd();
      setToast({ message: (value === 'on'
          ? t('toast.arrowsOnMany', { n: todo.length, t: fmtTime(t0) })
          : t('toast.arrowsOffMany', { n: todo.length, t: fmtTime(t0) }))
        + (todo.length < states.length ? t('toast.alreadyThatWay', { n: states.length - todo.length }) : '')
        + t('toast.undoSuffix'), id: Date.now() });
      return true;
    }
    const only = todo[0].g;
    if (only.type === 'player') handleGraphicKey(only.side, only.index, value);
    else handleItemGraphicKey(tidOf(only), value);
    // A handler that REFUSES (a locked seam) toasts over this one — last write
    // in the tick is the one you see.
    setToast({ message: (value === 'on'
        ? t('toast.arrowsOnOne', { name: getItemDisplayName(only), t: fmtTime(t0) })
        : t('toast.arrowsOffOne', { name: getItemDisplayName(only), t: fmtTime(t0) }))
      + t('toast.undoSuffix'), id: Date.now() });
    return true;
  }, [keyframes, refuseLockedDrag, handleGraphicKey, handleItemGraphicKey, handleDragStart, handleDragEnd, getItemDisplayName, setToast, t]);
  refItemVerbs.current.arrow = handleArrowToggle;

  /**
   * THE ARROWS LANE'S ONE VERB, WHATEVER OPENED THE DOOR.
   *
   * This lane has two values, and the window in force at an instant is
   * something the app already knows. So the useful action at any second is the
   * opposite of what is showing — and the other one is a key that changes
   * nothing, which the verb refuses with a toast saying so. A panel offering
   * On and Off therefore always contained exactly one live button and one dead
   * end; the tap now presses the live one.
   *
   * Same call as the item menu's ➜ entry and as tapping an existing diamond:
   * the seam guard, the "already that way" refusal and the announcement are
   * written once, in handleArrowToggle.
   */
  const handleGraphicFlip = useCallback((target) => {
    const tid = target.trackId
      || (target.side !== undefined ? `${target.side}:${target.index}` : null);
    if (!tid || !refItemVerbs.current.arrow) return;
    const member = target.trackId
      ? itemFromTrackId(target.trackId)
      : { type: 'player', side: target.side, index: target.index };
    const at = snapKeyTime(refPlayheadTime.current);
    refItemVerbs.current.arrow(arrowOnAt(keyframes, tid, at) ? 'off' : 'on', member ? [member] : null);
  }, [keyframes, itemFromTrackId]);

  /**
   * HOLD AN EMPTY SPOT on the pitch (touch) → the VIEW menu.
   *
   * The camera presets were 1 / 2 / 3 keys and nothing else. On a phone you
   * could orbit and pinch, but you could never SNAP to the view you wanted —
   * and the two that matter are the ones you change constantly: straight down
   * to ARRANGE a drill, down at the touchline to READ where a pass goes. This
   * is the same long-press the items already use, on the pitch itself.
   */
  const handlePitchViewPrompt = useCallback(() => {
    setSegmentEdit(null); // dismiss any open editor first (same rule as the other prompts)
    setGraphicEdit(null);
    setKeyframeEdit(null);
    const ask = (kind) => {
      setCameraRequest(c => ({ kind, n: (c?.n || 0) + 1 }));
      setPromptState(null);
    };
    // ARROWS, BOTH WAYS, FOR THE WHOLE SCENE. The per-item verb (G, and the ➜
    // entry in an item's menu) is state-aware already; what was missing was the
    // scene-wide half: "show me every run at once" when you are reviewing a
    // drill, "clear them all" when you are arranging one. The entry reads how
    // many arrows are ON right now and offers the opposite of that, so the
    // label says which way it will go instead of being a blind toggle.
    const t0 = snapKeyTime(refPlayheadTime.current);
    const tidOf = (g) => (g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`);
    const everyone = [];
    for (const side of ['home', 'away']) {
      ((pitchState?.[side]?.players) || []).forEach((p, i) => { if (p) everyone.push({ type: 'player', side, index: i }); });
    }
    for (const kind of ['balls', 'cones', 'targets', 'zones']) {
      for (const it of (pitchState?.[kind] || [])) {
        everyone.push({ type: kind === 'balls' ? 'ball' : kind.slice(0, -1), id: it.id });
      }
    }
    const arrowsOnCount = everyone.filter(g => arrowOnAt(keyframes, tidOf(g), t0)).length;
    // A ZONE's lane is its SHAPE, not an arrow (the strip gives a zone a visual
    // lane where every other item gets a graphic one). Turning arrows ON for a
    // zone writes a key no lane can show or clear — the same dead-key noise the
    // per-item verb already refuses. So ON skips zones; OFF still counts them,
    // because clearing a zone's stray arrow key is exactly what OFF is for.
    const arrowable = everyone.filter(g => g.type !== 'zone');
    // WHO IS MARKING RIGHT NOW. Clearing a press is the most common cleanup
    // after a pressing drill — the moment the ball is won, every marker's gap
    // and direction is stale. The group verb already exists (the menu's "stop
    // marking" for a group); what was missing was the scene half, because on a
    // phone the only way out was one menu trip PER player.
    const markers = everyone.filter(g => g.type === 'player'
      && attachRefAt(keyframes, `${g.side}:${g.index}`, t0));
    // At a child branch's 00:00 the mark key is INHERITED from the branch before
    // it — read only. Both mark entries would open a compass, or burn an undo
    // step, to write nothing, so the pitch stops offering them there. The mark
    // is edited in the branch that owns the instant.
    const seamHere = isLockedKeyTime(t0);
    // Which balls are in the air HERE, and which way the whole pitch should go.
    const sceneLoft = loftPlanFor(everyone);
    const zoneTracks = (pitchState?.zones || []).map(z => `zone:${z.id}`);
    const cutT = (refPlayheadTime.current ?? 0) + 0.001;
    // ONE answer to "which window is showing on this zone right now" — the count
    // in the label and the verb below must not drift apart.
    const zoneActive = (id) => {
      const tr = keyframes.find(k => k.id === id);
      return [...(tr?.visual || [])].sort((a, b) => a.time - b.time)
        .filter(k => k.time <= cutT).pop() || null;
    };
    const windowsOnCount = zoneTracks.filter(id => !!zoneActive(id)).length;
    // A window welded to the branch seam (an inherited key at a child's 00:00) is
    // skipped by the verb, which counts the skip in its own toast. The label has
    // to subtract the same zones or it promises a tap that will not land. Same
    // predicate as the verb — and a pure one, because a menu built during render
    // must never toast.
    const t0z = snapKeyTime(refPlayheadTime.current);
    const seamLockedAt = (id) => {
      const tr = keyframes.find(k => k.id === id);
      return (tr?.visual || []).some(k => Math.abs(k.time - t0z) < 1e-6 && isLockedKeyTime(k.time));
    };
    const shapesOnCount = zoneTracks.filter(id => !seamLockedAt(id)).length;
    const shapesOffCount = zoneTracks.filter(id => {
      const act = zoneActive(id);
      return !!act && !isLockedKeyTime(act.time);
    }).length;
    // The skip note, built beside the probes rather than inside the panel: the
    // label stays a plain ternary over dictionary calls, which is what the
    // screen-reader sweep reads. Same fragment the verb toasts, so the label and
    // the announcement cannot drift.
    const seamNote = (n) => (n ? t('vis.lockedAtSeam', { n }) : '');
    setPromptState({
      title: t('view.title'),
      description: t('view.hint'),
      subActions: [
        { label: t('view.top'), action: () => ask('top') },
        { label: t('view.low'), action: () => ask('low') },
        { label: t('view.reset'), action: () => ask('reset') },
        ...(arrowsOnCount || arrowable.length ? [{
          label: arrowsOnCount
            ? t('view.arrowsOff', { n: arrowsOnCount })
            : t('view.arrowsOn', { n: arrowable.length }),
          utility: true,
          // ONE verb for the whole scene: handleArrowToggle already groups,
          // counts, skips locked seams and announces what actually changed.
          action: () => {
            setPromptState(null);
            handleArrowToggle(arrowsOnCount ? 'off' : 'on', arrowsOnCount ? everyone : arrowable);
          },
        }] : []),
        // THE SHAPE OVERLAY FOR EVERY ZONE, both ways. A drill's pressing shape
        // is usually drawn on six zones at once; the strip's Visual lane ✚ keys
        // one, and the keyboard's ⇧⌥S + ⇧T keys them all — a phone had no half
        // of that verb. The entry reads how many zones already have a window at
        // this instant and offers the opposite, so it says which way it goes.
        ...(shapesOnCount || shapesOffCount ? [{
          // The direction is chosen by what the tap can REACH, not by what
          // exists: if every open window is welded to the seam there is nothing
          // to hide, so the entry offers the other way round instead of a door
          // that can only refuse.
          label: shapesOffCount
            ? t('view.shapesOff', { n: shapesOffCount }) + seamNote(windowsOnCount - shapesOffCount)
            : t('view.shapesOn', { n: shapesOnCount }) + seamNote(zoneTracks.length - shapesOnCount),
          utility: true,
          action: () => {
            setPromptState(null);
            // Deliberately NOT in this callback's dependency array: the scene
            // verb is declared further down the body, and naming it here would
            // read a const before it initialises on the first render. The
            // prompt is rebuilt whenever keyframes change, which is the same
            // render that has the current verb.
            if (refItemVerbs.current.zoneScene) {
              refItemVerbs.current.zoneScene(shapesOffCount ? 'off' : 'on', zoneTracks);
            }
          },
        }] : []),
        // STOP EVERY MARK IN THE SCENE, one tap. The same verb the menu runs for
        // a group — every marker keeps the gap they were holding at the release
        // instant, and the whole scene comes out in ONE undo step.
        ...(markers.length && !seamHere ? [{
          label: t('view.unmarkAll', { n: markers.length }),
          utility: true,
          action: () => { setPromptState(null); handleGroupUnmark(markers, t0); },
        }] : []),
        // 📏 AND THE OTHER HALF: every marker's gap, set in one compass. The
        // group menu needs a group; the pitch prompt does not, because it asks
        // the squad who is pressing right now.
        ...(markers.length > 1 && !seamHere ? [{
          label: t('view.gapAll', { n: markers.length }),
          utility: true,
          action: () => { setPromptState(null); handleGroupMarkDial(markers); },
        }] : []),
        // ⤴ EVERY FLIGHT IN THE SCENE, both ways. "Make the whole thing loopy"
        // is a sentence coaches say out loud; it used to mean selecting the
        // balls one at a time. The SAME probe the group verb uses decides which
        // way the pitch should go, so the label cannot promise one thing while
        // the verb does another. loftPlanFor is declared further down the body
        // and is deliberately NOT in this callback's dependency array — the
        // array is evaluated during the render pass, before it initialises.
        ...(sceneLoft.plan.length ? [{
          label: sceneLoft.arch
            ? t('view.loftOn', { n: sceneLoft.plan.length })
            : t('view.loftOff', { n: sceneLoft.plan.length }),
          utility: true,
          action: () => {
            setPromptState(null);
            // Only the balls: handing the whole scene to the group verb would
            // make it count eleven players as "not balls in flight" and put that
            // noise in the coach's toast. The skip is a fact about a GROUP the
            // coach chose, not about the pitch.
            if (refItemVerbs.current.loftGroup) {
              refItemVerbs.current.loftGroup(everyone.filter(g => g.type === 'ball'));
            }
          },
        }] : []),
      ],
      cancelAction: () => setPromptState(null),
    });
  }, [t, pitchState, keyframes, handleArrowToggle, handleGroupUnmark, handleGroupMarkDial]);

  /**
   * TAP (no drag) on a timeline dot — or HOLD an empty stretch of its lane:
   * open/write THAT LANE's key at the instant touched. Retargeting a catch used
   * to mean select → scrub → A → pick (four steps); the dot knows track+instant,
   * so one click lands the playhead there and opens the same UI the keyboard
   * verb would — pick on the pitch, ⌥A for nearest, Esc drops it. The upsert at
   * the same time REPLACES the tapped key: no orphan, no extra delete. Lanes:
   *   attach → carrier prompt · action → action picker ·
   *   graphic → FLIP the arrows at that instant · visual → the zone window's
   *   shape editor.
   */
  const handleTimelineKeyTap = useCallback((trackId, trackType, time) => {
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    if (Math.abs(refPlayheadTime.current - time) > 1e-9) {
      handleScrub(time);
      refPlayheadTime.current = time; // in step NOW — the prompt reads it at open
    }
    const tr = keyframes.find(t => t.id === trackId);
    const isPlayerTrack = tr && tr.type === 'player';
    if (trackType === 'position') {
      // HOLD an empty stretch of the MOVEMENT lane. The playhead has already
      // moved to the second the finger touched, and the write is the SAME verb
      // as the ✚ in the name column — asked at that second, with the position
      // the player or item has THERE. Two gestures (scrub, then ✚) become one,
      // and the instant is the place the coach pressed.
      if (!tr) return;
      if (isPlayerTrack) handleKeyPress(tr.side, tr.index, time);
      else handleItemPositionKey(trackId, time);
      return;
    }
    if (trackType === 'attach') {
      // A PLAYER's attach lane is the MARK lane — tapping that diamond opens the
      // compass, not the ball's "ride someone" prompt. Same key, different verb,
      // because the two have never meant the same thing for a player.
      if (isPlayerTrack) handleMarkPrompt(trackId);
      else handleBallAttachPrompt(trackId);
    } else if (trackType === 'action') {
      if (isPlayerTrack) {
        handleActionPrompt({
          side: tr.side, index: tr.index, title: t('act.title', { what: tr.name || `${capWord(t(tr.side === 'home' ? 'side.home' : 'side.away'))} #${tr.index + 1}` }),
          deleteKey: { trackId, time },
        });
      } else {
        const item = itemFromTrackId(trackId);
        handleActionPrompt({ trackId, title: t('act.title', { what: item ? getItemDisplayName(item) : t('act.item') }), deleteKey: { trackId, time } });
      }
    } else if (trackType === 'graphic') {
      // THE ARROWS LANE HAS ONE GESTURE AT EVERY END OF IT: tap the square to
      // flip it, hold an empty stretch to flip it at the second you touched,
      // tap the ✚ to flip it at the playhead. All three land in
      // handleGraphicFlip, which writes the OPPOSITE of the window in force at
      // that instant — the only button the old On/Off panel could ever offer.
      // Deleting a key stays a hold on its diamond, the ladder every other
      // lane already answers.
      handleGraphicFlip(isPlayerTrack ? { side: tr.side, index: tr.index } : { trackId });
    } else if (trackType === 'visual') {
      // Zone shape window: open its editor directly (no prompt) — the mouse
      // twin of landing on it with . / and pressing Enter.
      const vis = (tr?.visual || []).slice().sort((a, b) => a.time - b.time);
      const idx = vis.findIndex(k => Math.abs(k.time - time) < 1e-9);
      if (idx < 0) {
        // HOLD an empty stretch of the VISUAL lane: start a window HERE. The
        // playhead is already at the touched instant, and this is the SAME
        // verb as the ✚ on the zone's row — one implementation, asked where
        // the finger landed. A lane that answers three holds and not the
        // fourth is a rule with a door left open.
        // handleZoneVisualKey is declared BELOW this callback; safe via
        // closure at call time, deliberately NOT in deps (TDZ).
        handleZoneVisualKey(trackId);
        return;
      }
      _setKeyframeEdit(null);
      _setSegmentEdit(null);
      _setGraphicEdit(null);
      setZoneVisualEdit({ trackId, keyIndex: idx });
    }
  }, [handleScrub, handleBallAttachPrompt, handleMarkPrompt, handleActionPrompt, handleGraphicFlip, handleKeyPress, handleItemPositionKey, keyframes, itemFromTrackId, getItemDisplayName]);

  /**
   * SEGMENT TRANSITIONS — properties of the movement between two position keys.
   * Clicking a segment on the timeline opens the editor modal; the saved config
   * drives the interpolation (curve + arch) and the path arrows.
   */
  const upsertSegment = useCallback((trackId, from, to, config) => {
    // Name the verb: the ↩ button reads this label, and 'edit' told the user
    // nothing about what the next press would give back.
    nameUndo('undoVerb.transition');
    setKeyframes(prev => prev.map(t => {
      if (t.id !== trackId) return t;
      const segments = (t.segments || []).filter(s => !(s.from === from && s.to === to));
      segments.push({ from, to, ...config });
      segments.sort((a, b) => a.from - b.from);
      return { ...t, segments };
    }));
  }, [setKeyframes, nameUndo]);

  /** Clicked a timeline segment — open its transition editor. Any other open
   *  modal (attachment / action / graphic prompt, arrow-colour editor) is
   *  cancelled + dismissed first, so only one editor is ever open. */
  const handleSegmentClick = useCallback((trackId, from, to, also) => {
    if (attachPickMode) setAttachPickMode(null);
    if (promptState) setPromptState(null);
    setGraphicEdit(null);
    setKeyframeEdit(null);
    setZoneVisualEdit(null);
    // `also` is what the item menu hands over when the card speaks for a group:
    // every other member's OWN flight under the playhead. The strip never passes
    // it — a segment tapped in the timeline is one segment, and the coach said
    // which one by touching it.
    setSegmentEdit({ trackId, from, to, ...(also && also.length ? { also } : {}) });
  }, [attachPickMode, promptState]);
  refItemVerbs.current.editSeg = handleSegmentClick;
  // FOLLOW: the Action menu's player entry opens the same prompt the drag does.
  refItemVerbs.current.mark = handleMarkPrompt;
  // STOP MARKING for a group: the menu's entry and the pitch's release run the
  // same body, so the group rule can never drift from the single-player one.
  refItemVerbs.current.unmarkGroup = handleGroupUnmark;
  // The group compass: one dial, every marker's bearing and gap.
  refItemVerbs.current.markGapGroup = handleGroupMarkDial;
  // MARK for a group: the menu's counted entry and the pitch's group drop share
  // one body, so the drag and the tap cannot drift apart.
  refItemVerbs.current.markGroup = handleGroupMarkPrompt;
  // ATTACH: the item menu and the ⇧⌥A chord open the SAME prompt, which is where
  // release lives. One implementation, one set of seam guards.
  refItemVerbs.current.attach = handleBallAttachPrompt;
  // ATTACH for a group: the menu's counted entry, same pick mode, same release
  // rule — every member at once.
  refItemVerbs.current.attachGroup = handleGroupAttachPrompt;
  // REPEAT: the menu and ⇧X run the same group-aware, already-keyed-here body.
  refItemVerbs.current.repeat = handleRepeatAction;

  /** Live update of the edited segment config (no save needed). */
  const handleSegmentChange = useCallback((config) => {
    if (!segmentEdit) return;
    upsertSegment(segmentEdit.trackId, segmentEdit.from, segmentEdit.to, config);
    // …and every other flight the card brought with it. Each keeps its OWN
    // bounds: four cones' runs rarely start at the same instant, and writing the
    // primary's span onto all of them would bend paths the coach never touched.
    for (const w of segmentEdit.also || []) {
      if (w && w.trackId && w.trackId !== segmentEdit.trackId
        && w.from !== null && w.from !== w.to) {
        upsertSegment(w.trackId, w.from, w.to, config);
      }
    }
  }, [segmentEdit, upsertSegment]);

  /** Live bend-drag on the 3D apex handle: style follows the pull side,
   *  curve follows the distance. Kept out of the modal — the drag IS the edit. */
  const handleSegmentBend = useCallback((trackId, from, to, style, curve) => {
    const track = keyframes.find(t => t.id === trackId);
    upsertSegment(trackId, from, to, { ...DEFAULT_SEGMENT, ...findSegment(track, from, to), style, curve });
  }, [keyframes, upsertSegment]);

  /** Live arch-drag on the violet flight handle: distance from the chord IS
   *  the arch height in metres (same 0–10 range as the modal slider). */
  const handleSegmentArch = useCallback((trackId, from, to, arch) => {
    const track = keyframes.find(t => t.id === trackId);
    upsertSegment(trackId, from, to, { ...DEFAULT_SEGMENT, ...findSegment(track, from, to), arch: Math.round(arch * 10) / 10 });
  }, [keyframes, upsertSegment]);

  /** Clicked an arrow window between graphic keys — open the colour editor. */
  const handleGraphicSegClick = useCallback((trackId, start, end) => {
    if (attachPickMode) setAttachPickMode(null);
    if (promptState) setPromptState(null);
    setSegmentEdit(null);
    setKeyframeEdit(null);
    setZoneVisualEdit(null);
    setGraphicEdit({ trackId, start, end });
  }, [attachPickMode, promptState]);

  /** Live update of the window's arrow colour (writes onto the ON key). */
  const handleGraphicColorChange = useCallback((config) => {
    if (!graphicEdit) return;
    const { trackId, start } = graphicEdit;
    const color = (!config.color || config.color === 'default') ? null : config.color;
    nameUndo('undoVerb.arrowColour');
    // The windows this edit owns: the one the panel is open on, plus every member
    // of the group the card spoke for. One colour for the lot, ONE undo step —
    // "the back four's arrows went red" is one thing the ↩ button takes back.
    const windows = [{ trackId, start },
      ...(graphicEdit.also || []).filter(w => w && w.trackId && w.trackId !== trackId)];
    const wanted = new Set(windows.map(w => w.trackId));
    setKeyframes(prev => prev.map(t => {
      if (!wanted.has(t.id)) return t;
      const win = windows.find(w => w.trackId === t.id);
      const graphic = (t.graphic || []).map(k => {
        if (k.time !== win.start) return k;
        if (!color) { const c = { ...k }; delete c.color; return c; }
        return { ...k, color };
      });
      return { ...t, graphic };
    }));
  }, [graphicEdit, setKeyframes, nameUndo]);

  /** ZONE VISUAL key at the playhead — starts a visuals window. The key's value
   *  is a snapshot of the zone's CURRENT visual state (radius / shape points /
   *  vertex refs), which the user then edits via the window editor. */
  const handleZoneVisualKey = useCallback((trackId) => {
    const zoneId = trackId.replace(/^zone:/, '');
    const zone = pitchState?.zones?.find(z => z.id === zoneId);
    // Snapshot the DISPLAYED shape at the playhead. The displayed shape is
    // the ACTIVE visual key's points anchored at its captured centre, drawn
    // at the movement track's interpolated position — so capture exactly
    // that: active points + (interpolated − active centre), centred on the
    // interpolated position. Without a visual key the base points play that
    // role (anchored at the base position).
    const track = keyframes.find(k => k.id === trackId);
    const now = refPlayheadTime.current ?? 0;
    // Interpolated movement position at the playhead (falls back to base).
    let ipos = zone?.position ? [zone.position[0], 0, zone.position[2]] : [0, 0, 0];
    if (track?.position?.length) {
      const keys = track.position.slice().sort((a, b) => a.time - b.time);
      let k0 = null, k1 = null;
      for (const k of keys) { if (k.time <= now) k0 = k; else { k1 = k; break; } }
      if (!k0) k0 = keys[0];
      if (!k1) k1 = keys[keys.length - 1];
      const span = k1.time - k0.time;
      ipos = span <= 0 ? k1.position : [
        k0.position[0] + (k1.position[0] - k0.position[0]) * ((now - k0.time) / span),
        0,
        k0.position[2] + (k1.position[2] - k0.position[2]) * ((now - k0.time) / span),
      ];
    }
    // Active visual window at the playhead (last key ≤ now).
    const vk = (track?.visual || []).slice().sort((a, b) => a.time - b.time);
    let active = null;
    for (const k of vk) { if (k.time <= now) active = k; else break; }
    let snapPts, snapRefs;
    if (active?.value?.points?.length) {
      const ax = active.value.center?.[0] ?? 0, az = active.value.center?.[2] ?? 0;
      const sx = ipos[0] - ax, sz = ipos[2] - az;
      snapPts = active.value.points.map(p => [p[0] + sx, p[1] ?? 0, p[2] + sz]);
      snapRefs = active.value.pointRefs ? active.value.pointRefs.map(r => ({ ref: r?.ref ?? null })) : (zone?.pointRefs ? zone.pointRefs.map(r => ({ ref: r?.ref ?? null })) : []);
    } else if (zone?.position) {
      const sx = ipos[0] - zone.position[0], sz = ipos[2] - zone.position[2];
      snapPts = (zone.points || []).map(p => [p[0] + sx, p[1] ?? 0, p[2] + sz]);
      snapRefs = zone.pointRefs ? zone.pointRefs.map(r => ({ ref: r?.ref ?? null })) : [];
    } else {
      snapPts = [];
      snapRefs = [];
    }
    // Inherit the LOOK of the previous visual key when one exists (the window
    // the playhead is currently in) — see buildZoneVisualKeyValue.
    const value = buildZoneVisualKeyValue({
      inherited: active?.value || null,
      zone,
      points: snapPts,
      pointRefs: snapRefs,
      // The centre this snapshot was captured against — the anchor the points
      // are drawn relative to, so a later move/interpolation translates the
      // whole shape instead of re-anchoring it (polygon stays glued to cross).
      center: [ipos[0], 0, ipos[2]],
    });
    upsertTrackKey(trackId, 'visual', { time: snapKeyTime(refPlayheadTime.current), value });
    // Open the editor on the new window: resolve the key's real index in the
    // sorted visual array (a fresh key at the playhead, or an existing one).
    const t = snapKeyTime(refPlayheadTime.current);
    const tk = keyframes.find(k => k.id === trackId);
    const vis = (tk?.visual || []).slice().sort((a, b) => a.time - b.time);
    const exact = vis.findIndex(k => k.time === t);
    const idx = exact >= 0 ? exact : vis.filter(k => k.time < t).length;
    setZoneVisualEdit({ trackId, keyIndex: idx });
    setZoneVisualTarget({ trackId, keyIndex: idx });
  }, [upsertTrackKey, pitchState, keyframes]);

  /**
   * EVERY ZONE, ONE TAP — the scene half of the shape overlay. The keyboard has
   * had this for a group all along (⇧⌥S picks every zone, ⇧T keys them all in
   * one press); on a phone the only door was the strip's Visual lane ✚, which
   * keys ONE zone, so showing the pressing shape for a six-zone drill was six
   * walks to the strip. The pitch prompt that already carries the scene-wide
   * arrow verb carries this one. Locked seams are skipped and COUNTED, and the
   * whole scene is ONE undo step.
   */
  const handleZoneVisualScene = useCallback((mode, ids) => {
    const tracks = ids || [];
    if (!tracks.length) {
      setToast({ message: t('vis.noZonesScene'), id: Date.now() });
      return 0;
    }
    const t0 = snapKeyTime(refPlayheadTime.current);
    const cut = (refPlayheadTime.current ?? 0) + 0.001;
    const activeOf = (id) => {
      const tr = keyframes.find(k => k.id === id);
      return [...(tr?.visual || [])].sort((a, b) => a.time - b.time)
        .filter(k => k.time <= cut).pop() || null;
    };
    if (mode === 'off') {
      const targets = [], locked = [];
      for (const id of tracks) {
        const act = activeOf(id);
        if (!act) continue;
        if (isLockedKeyTime(act.time)) { locked.push(id); continue; }
        targets.push({ id, time: act.time });
      }
      if (!targets.length) {
        setToast({ message: locked.length
          ? t('seam.windowsReadOnly')
          : t('vertex.noWindow'), id: Date.now() });
        return 0;
      }
      handleDragStart('undoVerb.deleteZoneWindows');
      for (const tg of targets) handleKeyframeDelete(tg.id, 'visual', tg.time);
      handleDragEnd();
      // The scene verb is not an edit of one shape — no editor left open.
      setZoneVisualEdit(null);
      setToast({
        message: t(targets.length === 1 ? 'vis.windowsDeletedOne' : 'vis.windowsDeletedN', { n: targets.length })
          + (locked.length ? t('vis.lockedAtSeam', { n: locked.length }) : '')
          + t('toast.undoSuffix'),
        id: Date.now(),
      });
      return targets.length;
    }
    const seamLocked = (id) => {
      const tr = keyframes.find(k => k.id === id);
      return (tr?.visual || []).some(k => Math.abs(k.time - t0) < 1e-6 && isLockedKeyTime(k.time));
    };
    // A zone that ALREADY has a window here is not skipped: re-keying it
    // re-captures the shape where the zone stands NOW, which is exactly what
    // ⇧T does for a group. One verb, one behaviour — the scene half may not
    // invent a second rule for the same key. Only a window LOCKED at the seam
    // (the one this branch inherited) is skipped, and counted.
    const locked = tracks.filter(id => seamLocked(id));
    const can = tracks.filter(id => !locked.includes(id));
    if (!can.length) {
      setToast({ message: t('seam.windowsReadOnly'), id: Date.now() });
      return 0;
    }
    handleDragStart('undoVerb.zoneVisualScene');
    for (const id of can) handleZoneVisualKey(id);
    handleDragEnd();
    setZoneVisualEdit(null);
    setToast({
      message: t('vis.windowsOpenedScene', { n: can.length, t: fmtTime(t0) })
        + (locked.length ? t('vis.lockedAtSeam', { n: locked.length }) : '')
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
    return can.length;
  }, [keyframes, handleZoneVisualKey, handleKeyframeDelete, handleDragStart,
    handleDragEnd, setZoneVisualEdit, setToast, t]);

  /**
   * MOUSE double-click a ZONE on the pitch = its PRIMARY verb: the shape /
   * visual editor (the T verb's mouse twin — a zone at rest has no flight to
   * edit, so the action picker was the wrong card to land on). Double-click
   * AGAIN while the editor is open grabs VERTEX 1 for Ctrl+arrow nudging,
   * exactly like T twice. The action picker stays one X away.
   */
  const handleZoneShapePrompt = useCallback((zoneId) => {
    const zTrackId = `zone:${zoneId}`;
    if (zoneVisualEdit && zoneVisualEdit.trackId === zTrackId) {
      setZoneEditRequest({ zoneId, ts: Date.now(), selectPoint: 0 });
      setToast({ message: t('vertex.grabbed'), id: Date.now() });
      return;
    }
    // SEAM: same guard as T — an inherited visual window can never be keyed.
    if (refuseLockedDrag(zTrackId, snapKeyTime(refPlayheadTime.current), ['visual'])) return;
    handleZoneVisualKey(zTrackId);
    setToast({ message: t('vis.windowOpenGrab', { t: fmtTime(snapKeyTime(refPlayheadTime.current)) }), id: Date.now() });
  }, [zoneVisualEdit, handleZoneVisualKey, setToast, t]);

  /** Clicked a zone-visuals window — open the visual editor for that window. */
  const handleZoneVisualSegClick = useCallback((trackId, keyIndex) => {
    if (attachPickMode) setAttachPickMode(null);
    if (promptState) setPromptState(null);
    setSegmentEdit(null);
    setGraphicEdit(null);
    setKeyframeEdit(null);
    setZoneVisualEdit({ trackId, keyIndex });
    setZoneVisualTarget({ trackId, keyIndex });
  }, [attachPickMode, promptState]);

  /** Dismissing polygon edit mode returns to the visuals editor that led into it. */
  const handleZoneEditExit = useCallback(() => {
    if (zoneVisualTarget) setZoneVisualEdit(zoneVisualTarget);
  }, [zoneVisualTarget]);

  /** Live update of a zone visual key's value (radius / shape / refs / visuals). */
  /**
   * The zone editor's Add / Remove tools, used from the PITCH (touch) rather
   * than from the keyboard. The keyboard twins already said what they did; the
   * touch path wrote the shape change silently, so on a phone a vertex appeared
   * or vanished with no word about which one, how many were left, or how to get
   * it back. Same state change, same announcement — different door named: the
   * pitch strings point at dragging the vertex and the on-screen ↩ button, not
   * at Ctrl+Z a phone cannot press.
   */
  /**
   * The name of a bend, in the coach's language. The segment editor already
   * labels these three styles (edit.straight / edit.left / edit.right); the C
   * verb used to toast the raw data value — "0–4: curveLeft" — which is a
   * source-code identifier speaking to a coach. ONE map, both places.
   */
  const bendName = useCallback((style) => t(
    { straight: 'edit.straight', curveLeft: 'edit.left', curveRight: 'edit.right' }[style] || 'edit.straight'
  ), [t]);

  /**
   * An arrow colour by NAME, in the coach's language. The palette used to carry
   * English words next to the hex codes, so a Spanish coach cycling ⇧G read
   * "Arrow red from 3" — the colour they were shown, named in a language they
   * had not chosen. Unknown hexes still fall back to the value itself.
   */
  const colourName = useCallback((hex) => (hex
    ? t({ '#ef4444': 'color.red', '#f97316': 'color.orange', '#eab308': 'color.yellow',
      '#22c55e': 'color.green', '#3b82f6': 'color.blue', '#a78bfa': 'color.purple' }[hex] || hex)
    : t('color.default')), [t]);

  /**
   * LOFT, one implementation for two doors. ⇧C has always arched the flight
   * under the playhead; on a phone the only ways were to find the violet handle
   * in the timeline and drag it, or to open the segment editor. The item menu
   * calls this same function, so the menu and the chord cannot drift apart —
   * the toast, the undo label and the arch height are decided once.
   */
  const handleLoftToggle = useCallback((trackId, from, to) => {
    const track = keyframes.find(k => k.id === trackId);
    const cur = findSegment(track, from, to) || {};
    const arch = (cur.arch ?? 0) > 0 ? 0 : 3;
    handleDragStart('undoVerb.loftToggle');
    upsertSegment(trackId, from, to, { ...DEFAULT_SEGMENT, ...cur, arch });
    handleDragEnd();
    setToast({ message: (arch
      ? t('loft.on', { m: arch, from: fmtTime(from), to: fmtTime(to) })
      : t('loft.off', { from: fmtTime(from), to: fmtTime(to) })) + t('toast.undoSuffix'), id: Date.now() });
    return arch;
  }, [keyframes, upsertSegment, handleDragStart, handleDragEnd, setToast, t]);

  /**
   * BEND, the same door twice: straight → left → right → straight, whichever
   * way it was reached. The group version plans its own segments and counts
   * what it skipped; this is the single path both the chord and the menu walk.
   */
  const handleBendCycle = useCallback((trackId, from, to) => {
    const track = keyframes.find(k => k.id === trackId);
    const cur = findSegment(track, from, to) || {};
    const next = nextBendStyle(cur.style || 'straight');
    handleDragStart('undoVerb.bendPath');
    upsertSegment(trackId, from, to, { ...DEFAULT_SEGMENT, ...cur, style: next });
    handleDragEnd();
    setToast({ message: t('bend.cycledTo', { from: fmtTime(from), to: fmtTime(to), name: bendName(next) })
      + t('toast.undoSuffix'), id: Date.now() });
    return next;
  }, [keyframes, upsertSegment, handleDragStart, handleDragEnd, setToast, bendName, t]);

  // The flight a track is standing IN at the playhead — its own segment, from
  // and to. The group verb needs one per member, and the menu needs to know
  // whether a group has any flight at all before it offers the entry.
  const flightUnderPlayhead = useCallback((tid) => {
    const tr = keyframes.find(k => k.id === tid);
    const pk = [...(tr?.position || [])].sort((a, b) => a.time - b.time);
    // Snapped, because every key time in this app is snapped and the playhead is
    // allowed to sit between two of them. Probing at the raw instant made the
    // group verbs reach a slightly different flight than the menu that promises
    // them — one scan, one answer.
    const t0 = snapKeyTime(refPlayheadTime.current);
    for (let i = 0; i < pk.length - 1; i++) {
      if (pk[i].time <= t0 + 0.001 && pk[i + 1].time >= t0 - 0.001 && pk[i].time !== pk[i + 1].time) {
        return { tid, tr, from: pk[i].time, to: pk[i + 1].time };
      }
    }
    return null;
  }, [keyframes, refPlayheadTime]);

  /**
   * GROUP BEND: one decision for the whole group, like G/X/K. Each member
   * cycles the segment UNDER ITS OWN PLAYHEAD position; members with no segment
   * there are skipped and counted — the shape each follows is its own, the
   * DECISION is shared. Reached from the C chord AND from the item menu, which
   * is the only door a phone has.
   */
  const handleBendCycleGroup = useCallback((members) => {
    const list = members || [];
    const plan = list.map(g => flightUnderPlayhead(
      g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`,
    )).filter(Boolean);
    if (!plan.length) {
      setToast({ message: t('bend.noneGroup'), id: Date.now() });
      return 0;
    }
    handleDragStart('undoVerb.groupBendPath');
    for (const s of plan) {
      const cur = findSegment(s.tr, s.from, s.to);
      upsertSegment(s.tid, s.from, s.to, {
        ...DEFAULT_SEGMENT, ...cur, style: nextBendStyle(cur.style || 'straight'),
      });
    }
    handleDragEnd();
    const skipped = list.length - plan.length;
    setToast({ message: t('bend.cycled', {
      n: plan.length, skipped: skipped ? t('bend.skipped', { n: skipped }) : '',
    }) + t('toast.undoSuffix'), id: Date.now() });
    return plan.length;
  }, [flightUnderPlayhead, upsertSegment, handleDragStart, handleDragEnd, setToast, t]);

  /**
   * The shared probe behind EVERY group and scene LOFT door: which members have
   * a flight under the playhead, and which way the group should go. Two copies
   * of "is any flight still on the ground" is how a menu label starts promising
   * one thing while the verb does another.
   */
  const loftPlanFor = useCallback((members) => {
    const list = members || [];
    const plan = list.filter(g => g && g.type === 'ball')
      .map(g => flightUnderPlayhead(`ball:${g.id}`)).filter(Boolean);
    const segOf = (s) => findSegment(s.tr, s.from, s.to) || {};
    return {
      plan,
      arch: plan.some(s => (segOf(s).arch ?? 0) <= 0) ? 3 : 0,
      skipped: list.length - plan.length,
    };
  }, [flightUnderPlayhead]);

  /**
   * GROUP LOFT: one decision for the whole group, the twin of GROUP BEND. A
   * drill with six balls in the air is one tap, not six menus. Members that are
   * not balls, or have no flight under the playhead, are skipped and COUNTED —
   * a ball is the only thing a drill throws, so the skip is a fact about the
   * group, not an error. The group decides ONCE: if any flight is still on the
   * ground the group lofts, and if every one is already arched they all come
   * back down. Six toggles from six separate taps would leave the drill in a
   * state nobody chose.
   */
  const handleLoftToggleGroup = useCallback((members) => {
    const { plan, arch, skipped } = loftPlanFor(members);
    if (!plan.length) {
      setToast({ message: t('loft.noneGroup'), id: Date.now() });
      return 0;
    }
    const segOf = (s) => findSegment(s.tr, s.from, s.to) || {};
    handleDragStart('undoVerb.loftGroup');
    for (const s of plan) {
      upsertSegment(s.tid, s.from, s.to, { ...DEFAULT_SEGMENT, ...segOf(s), arch });
    }
    handleDragEnd();
    setToast({ message: t(arch ? 'loft.onGroup' : 'loft.offGroup', {
      n: plan.length, m: arch, skipped: skipped ? t('loft.skipped', { n: skipped }) : '',
    }) + t('toast.undoSuffix'), id: Date.now() });
    return plan.length;
  }, [loftPlanFor, upsertSegment, handleDragStart, handleDragEnd, setToast, t]);

  // The flight verbs the menu offers ARE the chord's own functions — same toast,
  // same undo step, same arch height, decided once. Registered here, right after
  // them: the ref table fills in declaration order, and reading a useCallback
  // before its const initialises is a TDZ crash on the first render.
  refItemVerbs.current.loft = handleLoftToggle;
  refItemVerbs.current.loftGroup = handleLoftToggleGroup;
  // The scene-wide zone shape verb, reached from the pitch prompt (which is
  // declared earlier in the body and therefore reaches it through this table).
  refItemVerbs.current.zoneScene = handleZoneVisualScene;
  refItemVerbs.current.bend = handleBendCycle;
  refItemVerbs.current.bendGroup = handleBendCycleGroup;
  // The menu builder is declared further up the body, so it reaches the flight
  // probe through the same ref table it already uses for the verbs.
  refItemVerbs.current.flight = flightUnderPlayhead;

  /**
   * HEIGHT, one implementation for two doors. ⌥↑/⌥↓ dials the height of the
   * position key at or before the playhead — 0.5 m, 0.1 m with Shift, clamped to
   * 0–10. The item menu steps the same key with the same function.
   *
   * The clamp used to announce a change that had not happened: at ground level,
   * ⌥↓ wrote 0.0 m again and toasted "Height 0.0 m". A number that did not move
   * is not an announcement, it is a lie — so the floor and the ceiling now say
   * which way the door is.
   */
  // THE STEP lives in data.js (nextHeight): the single path and the group path
  // below both call it, so the clamp cannot drift between the two doors.
  const handleHeightStep = useCallback((trackId, dir, step = 0.5, explicit = null) => {
    const tidOf = (g) => (g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`);
    const activeKeyOf = (tid) => {
      const track = keyframes.find(k => k.id === tid);
      return [...(track?.position || [])].sort((a, b) => a.time - b.time)
        .filter(kk => kk.time <= refPlayheadTime.current + 1e-6).pop() || null;
    };
    // THE GROUP. A menu that speaks for a group speaks for the height too: five
    // cones onto a half-metre platform is one entry, not five walks through the
    // menu. The DECISION (which way, how far) is shared; each member keeps its
    // OWN key and its own current height — the same rule the group arrows and
    // the group bend follow.
    const members = explicit && explicit.length > 1 ? explicit : null;
    if (members) {
      const t0 = refPlayheadTime.current;
      // SEAM: filter before burning an undo step — the guard already toasted.
      const movable = members.filter(g => !refuseLockedDrag(tidOf(g), t0, ['position']));
      if (!movable.length) return null;
      const states = movable.map(g => {
        const tid = tidOf(g);
        const active = activeKeyOf(tid);
        // A member RIDING a carrier is not lifted — the sampler returns the
        // carrier's position, height included, and for a marker the y comes from
        // the marked player. Same rule as ✚, same skip-and-count as ↝. The key is
        // still read, so a group of nothing but riders is reported as riding a
        // carrier by the skip sentence, not as having no key at all.
        const rides = attachRefAt(keyframes, tid, t0) !== null;
        const cur = active ? (active.position?.[1] ?? 0) : null;
        const h = cur === null ? null : nextHeight(cur, dir, step);
        return { g, tid, active, h, atLimit: !rides && cur !== null && h === cur, can: !rides && !!active && h !== cur };
      });
      const todo = states.filter(s => s.can);
      if (!todo.length) {
        // Announce, don't fake: at the ground a lift writes the same number.
        setToast({ message: states.every(s => !s.active) ? t('kf.noPosKey')
          : t(dir > 0 ? 'height.ceilingMany' : 'height.floorMany'), id: Date.now() });
        return null;
      }
      handleDragStart('undoVerb.groupHeight');
      for (const s of todo) handleKeyframeHeightChange(s.tid, s.active.time, s.h);
      handleDragEnd();
      setToast({
        // The keys for this sentence are spelled …Many / …One, so the count picks
        // the whole key rather than pluralKey's suffix — same rule, one expression.
        message: t(todo.length === 1
          ? (dir > 0 ? 'toast.heightUpOne' : 'toast.heightDownOne')
          : (dir > 0 ? 'toast.heightUpMany' : 'toast.heightDownMany'), { n: todo.length, m: step })
          + (todo.length < states.length ? t('toast.heightSkipped', { n: states.length - todo.length }) : '')
          + t('toast.undoSuffix'),
        id: Date.now(),
      });
      return todo.length;
    }
    const track = keyframes.find(k => k.id === trackId);
    const active = [...(track?.position || [])].sort((a, b) => a.time - b.time)
      .filter(kk => kk.time <= refPlayheadTime.current + 1e-6).pop();
    if (!active) { setToast({ message: t('kf.noPosKey'), id: Date.now() }); return null; }
    // The same rule the menu already applies, now in the verb: while the item
    // follows another one — carried, or marking — the sampler draws the OTHER
    // item's height, so a lift here writes a number nothing renders.
    if (attachRefAt(keyframes, trackId, refPlayheadTime.current) !== null) {
      setToast({ message: t('height.follows'), id: Date.now() });
      return null;
    }
    const cur = active.position?.[1] ?? 0;
    const h = nextHeight(cur, dir, step);
    if (h === cur) {
      setToast({ message: t(dir > 0 ? 'height.ceiling' : 'height.floor', { h: cur.toFixed(1) }), id: Date.now() });
      return null;
    }
    handleKeyframeHeightChange(trackId, active.time, h);
    setToast({ message: t('key.heightAt', {
      t: fmtTime(active.time), h: h.toFixed(1),
      hint: h > 0 ? t('key.heightHint') : '',
    }) + t('toast.undoSuffix'), id: Date.now() });
    return h;
  }, [keyframes, refPlayheadTime, handleKeyframeHeightChange, refuseLockedDrag, handleDragStart, handleDragEnd, setToast, t]);

  refItemVerbs.current.height = handleHeightStep;

  const announceZoneVertex = useCallback((info) => {
    if (!info) return;
    const message = info.kind === 'added'
      ? t('vertex.addedTouch', { n: info.n, count: info.count })
      : info.kind === 'removed'
        ? t('vertex.removedTouch', { n: info.n, count: info.count })
        : t('vertex.minTwo');
    setToast({ message, id: Date.now() });
  }, [setToast, t]);

  const handleZoneVisualChange = useCallback((trackId, keyIndex, patch) => {
    nameUndo('undoVerb.zoneShape');
    setKeyframes(prev => prev.map(t => {
      if (t.id !== trackId) return t;
      const visual = (t.visual || []).map((k, i) => {
        if (i !== keyIndex) return k;
        return { ...k, value: { ...(k.value || {}), ...patch } };
      });
      return { ...t, visual };
    }));
  }, [setKeyframes, nameUndo]);

  /** Current visual snapshot for a zone (applied at the playhead by the viewport). */
  const zoneVisualAt = useCallback((trackId, t) => {
    const track = keyframes.find(k => k.id === trackId);
    const vk = (track?.visual || []).slice().sort((a, b) => a.time - b.time);
    if (vk.length === 0) return null;
    let cur = null;
    for (const k of vk) { if (k.time <= t) cur = k; else break; }
    return cur ? { keyIndex: vk.indexOf(cur), value: cur.value } : null;
  }, [keyframes]);

  // ── Callbacks: place / move / look / remove ──
  const handlePlayerPlace = useCallback((side, playerIndex, position) => {
    if (refuseLockedDrag(`${side}:${playerIndex}`, snapKeyTime(playheadTime))) return;
    const trackId = `${side}:${playerIndex}`;
    const tKey = snapKeyTime(refPlayheadTime.current);
    const track = keyframes.find(t => t.id === trackId);
    // A player who is MARKING cannot be dragged to a spot of their own — the
    // mark IS their position, so a drag on one of them looked dead: the key
    // changed under the finger and the player never moved. The drop re-anchors
    // the mark instead. The spot under the finger becomes the bearing and gap
    // they hold from the player they mark, and because the new key lands at the
    // playhead the sampler walks them there rather than dropping them on it.
    const held = markKeyAt(keyframes, trackId, tKey);
    if (held) {
      const leader = keyframes.find(t => t.id === held.value);
      const at = leader ? sampleEffectivePosition(leader, tKey, keyframes) : null;
      const geo = at ? markBearing(position, at, sideOfAttachRef(held.value)) : null;
      if (!geo) {
        // Dropped on top of the player they mark: there is no bearing out of
        // float noise, and inventing one would move them somewhere neither the
        // finger nor the compass asked for.
        setToast({ message: t('mark.dragNowhere', { leader: describeAttachRef(held.value) }), id: Date.now() });
        return;
      }
      // A drag gesture opened its own undo step at pointerdown and closes it at
      // release; a discrete click-place has no gesture, so it opens one here.
      // Same for the toast: mid-drag the coach is watching the player move under
      // their finger, and a sentence per pointermove is not an announcement, it
      // is a strobe — and each one would burn an undo step.
      const midGesture = !!refDragging.current;
      if (!midGesture) handleDragStart('undoVerb.markDrag');
      upsertTrackKey(trackId, 'attach', { time: tKey, value: held.value, dir: geo.dir, dist: geo.dist });
      if (!midGesture) {
        handleDragEnd();
        setToast({
          message: t('mark.dragged', {
            where: t(`mark.dir.${MARK_DIRS[Math.round(geo.dir) % 8] ?? 'front'}`),
            d: geo.dist.toFixed(1),
            leader: describeAttachRef(held.value),
          }) + t('toast.undoSuffix'),
          id: Date.now(),
        });
      }
      return;
    }
    // A released mark KEEPS the gap it had already caused — that is what makes
    // a release continuous instead of snapping a player back to a path laid
    // down before the mark ever happened. The renderer shows own + that gap, so
    // writing the raw drop point landed them a whole mark-gap away from where
    // the finger let go, and every drag slid them further: "they stopped
    // marking and I still can't move them".
    //
    // The gap is measured off the AUTHORED path, and inserting a key moves the
    // authored path — so there is no formula for the own value that lands them
    // on the drop. The answer is to ask the sampler instead of guessing how it
    // works: write a candidate key, read back where the player would actually
    // be, correct, repeat. A player with no mark on converges on the first pass
    // (own already IS where they are), so an ordinary drag costs nothing.
    const ownForSpot = (tt, drop) => {
      if (!track) return drop;
      const sortedKeys = (p) => [...(track.position || [])
        .filter(k => k && k.time !== tt), { time: tt, position: p }]
        // The sampler walks keys in ARRAY order and assumes they are sorted —
        // an unsorted trial reads the wrong segment and the solve chases a
        // position that was never sampled.
        .sort((a, b) => a.time - b.time);
      const effAt = (p) => {
        const trial = { ...track, position: sortedKeys(p) };
        return sampleEffectivePosition(trial, tt,
          keyframes.map(tr => (tr && tr.id === trackId ? trial : tr)));
      };
      const at = effAt(drop);
      if (!at) return drop;
      const ex = drop[0] - at[0], ez = drop[2] - at[2];
      if (Math.abs(ex) < 1e-6 && Math.abs(ez) < 1e-6) return drop;
      // The mark rules are piecewise LINEAR in the key value, so measure the
      // response with two probes and solve, rather than walking toward the
      // answer a step at a time (a step-only loop converges at a rate that
      // depends on where the release landed between two keys — sometimes
      // almost not at all).
      const r1 = effAt([drop[0] + 1, drop[1] ?? 0, drop[2]]);
      const r2 = effAt([drop[0], drop[1] ?? 0, drop[2] + 1]);
      let p = null;
      if (r1 && r2) {
        const a11 = r1[0] - at[0], a12 = r2[0] - at[0];
        const a21 = r1[2] - at[2], a22 = r2[2] - at[2];
        const det = a11 * a22 - a12 * a21;
        if (Math.abs(det) > 1e-9) {
          p = [(ex * a22 - ez * a12) / det + drop[0], drop[1] ?? 0, (a11 * ez - a21 * ex) / det + drop[2]];
        }
      }
      if (!p) p = [drop[0] + ex, drop[1] ?? 0, drop[2] + ez];   // one honest step
      // Verify against the sampler, because "piecewise" is the operative word:
      // a probe can cross into another segment, and a mark bearing can clamp.
      // If the solve missed, fall back to correcting steps from where it got to.
      let best = p, bestErr = Math.hypot(ex, ez);
      for (let i = 0; i < 6; i++) {
        const got = effAt(p);
        if (!got) break;
        const err = Math.hypot(drop[0] - got[0], drop[2] - got[2]);
        if (err < bestErr) { bestErr = err; best = p; }
        if (err < 1e-6) break;
        const dx = drop[0] - got[0], dz = drop[2] - got[2];
        if (Math.hypot(dx, dz) < 1e-9) break;                   // the key cannot move them
        p = [p[0] + dx, p[1] ?? 0, p[2] + dz];
      }
      // When no value of the key puts them on the drop — a mark still holding at
      // that instant, a spot the compass clamps away — keep the honest authored
      // value. A key the solve invented out of a degenerate system would move
      // them somewhere the finger never pointed.
      return bestErr <= 1e-3 ? best : drop;
    };
    const atKey = ownForSpot(tKey, position);
    const atBase = ownForSpot(0, position);
    // Pre-placement snapshot of the roster (refs still hold the old state here):
    // was this player OFF the pitch, and did the armed menu selection name them?
    const prevPlayers = refCurrentScenario.current?.phases?.[0]?.start?.[side]?.players || [];
    const wasBench = !prevPlayers[playerIndex]?.position;
    const sel = refSelectedItem.current;
    const armedFromMenu = !!sel && sel.type === 'player' && sel.side === side && sel.index === playerIndex;
    setScenario(prev => {
      if (!prev) {
        const newScen = createEmptyScenario('New Scenario');
        const players = [];
        while (players.length <= playerIndex) players.push({ position: null });
        players[playerIndex] = { position: atBase };
        newScen.phases[0].start = { ...createEmptyPitchState(), [side]: { players } };
        return newScen;
      }
      const phases = [...prev.phases];
      // V1-LEGACY: the multi-phase update loop is retired — only the layout
      // phase (0) mirrors item positions.
      const phase = phases[0];
      const start = { ...(phase.start || createEmptyPitchState()) };
      const players = [...(start[side]?.players || [])];
      while (players.length <= playerIndex) players.push({ position: null });
      players[playerIndex] = { ...players[playerIndex], position: atBase };
      start[side] = { players };
      phases[0] = { ...phase, start };
      return { ...prev, phases };
    });
    // AUTO-key: record a position keyframe for the moved/placed player at the rounded second.
    recordAutoKey(trackId, { type: 'player', side, index: playerIndex }, atKey);
    warnUnrecordedMove(trackId);
    // STICKY SQUAD LOOP: placing a BENCHED player armed from the ADD PLAYER
    // menu (or ⇧7/⇧8) auto-arms the NEXT benched player on that side — build a
    // whole XI with one arm and N clicks, Esc out. The selection (and with it
    // the toolbar glow + menu ✓) moves to the next player; the toast names them.
    if (armedFromMenu && wasBench) {
      const team = side === 'home' ? homeTeam : awayTeam;
      const nextIdx = (team?.playerInfo || []).findIndex((p, i) =>
        p && i !== playerIndex && !(prevPlayers[i]?.position));
      if (nextIdx >= 0) {
        setSelectedItem({ type: 'player', side, index: nextIdx });
        setActionMode('place');
        const p = team.playerInfo[nextIdx];
        setToast({ message: t('bench.armedNext', {
          who: p.name || t('bench.playerN', { n: nextIdx + 1 }),
        }), id: Date.now() });
      } else {
        setActionMode('none');
        setToast({ message: t('bench.squadComplete'), id: Date.now() });
      }
    }
  }, [setScenario, recordAutoKey, warnUnrecordedMove, keyframes, playheadTime, homeTeam, awayTeam, refCurrentScenario, refSelectedItem, setSelectedItem, setActionMode, setToast, t, upsertTrackKey, handleDragStart, handleDragEnd, describeAttachRef, refDragging]);

  /**
   * PLACE A FORMATION — the shape row at the top of the player menu.
   *
   * Building a team used to mean arming one player, tapping the pitch, arming
   * the next, tapping again… eleven times, each one aiming by eye. One tap puts
   * the whole squad into a real shape on the right half of the pitch, and the
   * player list underneath the shapes is still there for the two or three
   * tweaks a template always needs.
   *
   * The squad fills the shape from the GOALKEEPER UP: index 0 is the keeper,
   * then each line from the team's own goal outward. A side with seven players
   * gets the keeper, the back four and the first two of the next line — the
   * shape is incomplete in the way a short team sheet is incomplete, not in a
   * random scatter.
   */
  const handlePlaceFormation = useCallback((side, key) => {
    const team = side === 'home' ? homeTeam : awayTeam;
    const roster = (team?.playerInfo || []).filter(Boolean);
    if (!roster.length) {
      // A refusal is a door: say what to do instead of just declining.
      setToast({ message: t('form.none', { team: side === 'home' ? t('side.home') : t('side.away') }), id: Date.now() });
      return;
    }
    const count = Math.min(11, roster.length);
    const plan = planFormation(key, side, count);
    const prevPlayers = refCurrentScenario.current?.phases?.[0]?.start?.[side]?.players || [];
    const same = plan.every(p => {
      const at = prevPlayers[p.index]?.position;
      return Array.isArray(at) && at[0] === p.position[0] && at[1] === p.position[1] && at[2] === p.position[2];
    });
    if (same) {
      // Nothing moves, so nothing burns an undo step and nothing brags.
      setToast({ message: t('form.already', { name: formationLabel(key) }), id: Date.now() });
      return;
    }
    pushUndo('undoVerb.placeFormation');
    setScenario(prev => {
      const base = prev || (() => {
        const s = createEmptyScenario('New Scenario');
        s.phases[0].start = { ...createEmptyPitchState(), [side]: { players: [] } };
        return s;
      })();
      const phases = [...base.phases];
      const phase = phases[0];
      const start = { ...(phase.start || createEmptyPitchState()) };
      const players = [...(start[side]?.players || [])];
      for (const p of plan) {
        while (players.length <= p.index) players.push({ position: null });
        players[p.index] = { ...players[p.index], position: p.position };
      }
      start[side] = { players };
      phases[0] = { ...phase, start };
      return { ...base, phases };
    });
    // AUTO keys, one per placed player, so the shape is the START of the drill
    // and not just a picture: scrubbing forward still shows everyone moving.
    //
    // That promise was false, and measurement caught it: after applying a formation the
    // live scene held 22 tracks with EXACTLY ONE position key each and zero able to
    // animate, so scrubbing showed a still picture. One key cannot interpolate. A player
    // already standing somewhere needs a FROM as well as a TO: keep their current spot at
    // the playhead and put the shape a beat later. A player with no prior position (an
    // empty pitch) still gets the single key - there is nowhere to run from, and inventing
    // a start would fabricate a movement the coach never set up.
    const tFrom = snapKeyTime(playheadTime);
    const tTo = snapKeyTime(playheadTime + 0.6);
    for (const placed of plan) {
      const tid = `${side}:${placed.index}`;
      const meta = { type: 'player', side, index: placed.index };
      const priorPos = prevPlayers[placed.index] && prevPlayers[placed.index].position;
      if (Array.isArray(priorPos) && priorPos.length >= 3 && tTo > tFrom) {
        setKeyframes(prev => upsertPositionKey(prev, tid, meta, priorPos, tFrom));
        setKeyframes(prev => upsertPositionKey(prev, tid, meta, placed.position, tTo));
        if (Math.abs(tTo - playheadTime) > 1e-9) setPlayheadTime(tTo);
      } else {
        recordAutoKey(tid, meta, placed.position);
      }
    }
    const empty = 11 - count;
    setToast({
      message: empty > 0
        ? t('form.placedShort', { name: formationLabel(key), n: count, empty })
        : t('form.placed', { name: formationLabel(key), n: count }),
      id: Date.now(),
    });
  }, [homeTeam, awayTeam, refCurrentScenario, setScenario, recordAutoKey, pushUndo, setToast, t, playheadTime, setKeyframes, upsertPositionKey, setPlayheadTime]);

  /**
   * THE PLACEMENT TRAIL — the last two placements of ONE type.
   * cones.ejs says "place a line of cones": the tool stays armed, but every
   * click is a fresh aim and the spacing is eyeballed. Two clicks define the
   * vector; D repeats it exactly.
   */
  const refPlaceTrail = useRef([]); // [{ type, coords }] — never more than 2, all one type
  const rememberPlacement = useCallback((type, coords) => {
    const same = refPlaceTrail.current.filter(t => t.type === type);
    refPlaceTrail.current = [...same.slice(-1), { type, coords }];
  }, []);
  const lineStepFromTrail = useCallback(() => {
    const [a, b] = refPlaceTrail.current;
    if (!a || !b) return null;
    const dx = b.coords[0] - a.coords[0];
    const dz = b.coords[2] - a.coords[2];
    // Two clicks on the SAME spot define no direction — repeating would stack
    // items invisibly on one another, which reads as "D did nothing".
    if (Math.hypot(dx, dz) < 0.05) return null;
    return { type: b.type, dx, dz };
  }, []);

  /**
   * What the LINE-LAYOUT verbs can do right now (D / ⌥D / ⇧D / ⌥⇧D were the
   * only way to repeat a measured spacing). Read at render: the toolbar menu is
   * built when it opens, and the placement trail is a ref, not state.
   */
  const lineLayoutState = useCallback(() => {
    const trail = refPlaceTrail.current || [];
    const last = trail[trail.length - 1] || null;
    const step = lineStepFromTrail();
    return {
      last,
      step: step ? Math.round(Math.hypot(step.dx, step.dz) * 100) / 100 : null,
      // A ball is placed one at a time (it is handed to a player, not lined
      // up), so the mirror verbs have nothing to mirror for it.
      canMirror: !!last && last.type !== 'ball',
    };
  }, [lineStepFromTrail]);

  const handleObjectPlace = useCallback((type, coords, opts = {}) => {
    // Human-friendly sequential id per type: 'ball-01', 'cone-02', ...
    refLastPlaceType.current = type; // Shift+9 re-arms this type later
    const keepArmed = !!opts.keepArmed && type !== 'ball' && type !== 'zone' && !type.startsWith('zone');
    // CONES, TARGETS and ZONES stay armed after ANY click-place: arm once,
    // click N times, Esc — the drill-building loop. Touch already places sticky
    // (every tap keepArmed); a mouse click that disarmed after ONE marker
    // forced a re-arm (⇧9) per cone. Zones join because a shape/block drill is
    // several zones, and staying armed is SAFE: placement only fires on EMPTY
    // pitch, so clicking the zone you just dropped selects it instead of
    // stacking another. Balls still disarm — the next thing you do with a ball
    // is animate it, not place another.
    const sticky = keepArmed || type === 'cone' || type === 'target'
      || type === 'zone' || type.startsWith('zone');
    const newItemId = nextItemId(type, [
      ...(currentScenario?.phases || []),
      ...flattenTree(refScenarioTree.current).flatMap(({ branch }) => branch.phases || []),
    ]);
    if (opts.remember !== false) rememberPlacement(type, coords); // feeds D — the spacing of the last two
    setScenario(prev => {
      if (!prev) {
        const newScen = createEmptyScenario('New Scenario');
        newScen.phases[0].start = updatePhaseStartForObjectPlace(createEmptyPitchState(), type, coords, true, newItemId);
        return newScen;
      }
      const phases = [...prev.phases];
      // V1-LEGACY: items were added to all phases — now only the layout phase (0).
      const phase = phases[0];
      const start = { ...(phase.start || createEmptyPitchState()) };
      phases[0] = { ...phase, start: updatePhaseStartForObjectPlace(start, type, coords, true, newItemId) };
      return { ...prev, phases };
    });
    if (type === 'ball') {
      setSelectedItem({ type: 'ball', id: newItemId });
      setPendingObjectType(null);
      // Keep place mode so the next click MOVES the placed ball instead of
      // deselecting it (players already behave this way).
      setActionMode('place');
      // Placing a ball right AT a player's feet means "give it to them" —
      // open the SAME Attach? prompt the drag-onto-player path opens, so
      // placement + hand-off is one gesture instead of place → select →
      // drag → prompt. Nearest player within 2 m; anything else stays a
      // plain free ball. (handleBallDrop is defined below — referenced at
      // click time, deliberately NOT in deps: TDZ during render.)
      let bReceiver = null, bBestD = 2.0;
      const bStart = currentScenario?.phases?.[0]?.start;
      for (const side of ['home', 'away']) {
        (bStart?.[side]?.players || []).forEach((p, i) => {
          if (!p?.position) return;
          const d = Math.hypot(p.position[0] - coords[0], p.position[2] - coords[2]);
          if (d < bBestD) { bBestD = d; bReceiver = `${side}:${i}`; }
        });
      }
      if (bReceiver) {
        // The prompt takes this gesture over — leaving place mode armed would
        // let the NEXT pitch click move the ball behind the open card.
        setActionMode('none');
        handleBallDrop(newItemId, coords, bReceiver);
      }
    } else if (type === 'target' || type === 'cone' || type === 'zone' || type.startsWith('zone')) {
      let zPrompted = false;   // an Attach card took this gesture over
      if (!sticky) setPendingObjectType(null);
      // Zones: default VISUAL key at t=0 — same idea as players/balls getting
      // a default graphic key. Snapshots the zone's PLACEMENT state (built
      // from the same type/coords as updatePhaseStartForObjectPlace, since
      // the scenario state update above is async), so the visual track starts
      // with a key and edit windows behave consistently from the start.
      if (type === 'zone' || type.startsWith('zone')) {
        const zoneMode = type.startsWith('zone-') ? type.slice(5) : 'static';
        const cx = coords[0], cz = coords[2];
        const value = zoneMode === 'circle'
          ? { mode: 'circle', radius: 3 }
          : (() => {
              let points;
              if (zoneMode === 'dynamic') {
                const n = 5;
                points = [];
                for (let i = 0; i < n; i++) {
                  const a = (i / n) * Math.PI * 2;
                  points.push([cx + Math.cos(a) * 3, 0, cz + Math.sin(a) * 3]);
                }
              } else {
                const s = 3;
                points = [
                  [cx - s, 0, cz - s], [cx + s, 0, cz - s],
                  [cx + s, 0, cz + s], [cx - s, 0, cz + s],
                ];
              }
              return { mode: zoneMode, points, center: [cx, 0, cz] };
            })();
        setKeyframes(kPrev => {
          let found = false;
          const next = kPrev.map(t => {
            if (t.id !== `zone:${newItemId}`) return t;
            found = true;
            return { ...t, visual: [...(t.visual || []), { time: 0, value }] };
          });
          if (!found) next.push({ id: `zone:${newItemId}`, type: 'zone', zoneId: newItemId, visual: [{ time: 0, value }] });
          return next;
        });
        // A zone placed OVER a player means "mark this player" — zones SNAP
        // to their carrier (no flight, by design), so offer the same
        // Attach? prompt the drag path opens (handleZoneDrop is defined
        // below — referenced at click time, not in deps: TDZ pattern).
        if (!keepArmed) {
          let zReceiver = null, zBestD = 3.0;
          const zStart = currentScenario?.phases?.[0]?.start;
          for (const side of ['home', 'away']) {
            (zStart?.[side]?.players || []).forEach((p, i) => {
              if (!p?.position) return;
              const d = Math.hypot(p.position[0] - coords[0], p.position[2] - coords[2]);
              if (d < zBestD) { zBestD = d; zReceiver = `${side}:${i}`; }
            });
          }
          if (zReceiver) {
            // The CARD owns the next click: an armed zone tool behind an open
            // Attach prompt would drop a SECOND zone while the card is about
            // the first — the same rule the ball path follows above.
            zPrompted = true;
            setPendingObjectType(null);
            setActionMode('none');
            handleZoneDrop(newItemId, zReceiver);
          }
        }
      }
      if (sticky && !zPrompted) setToast({ message: t('toast.placedMore', {
        type: ['cone', 'target', 'zone', 'ball'].includes(type) ? t(`kindOne.${type}`) : type,
        hint: t(lineStepFromTrail() ? 'toast.placedHintLine' : 'toast.placedHintMirror'),
      }), id: Date.now() });
    }
    // The caller may want to know WHAT was just placed (D selects it so the
    // newest marker wears the selection tint instead of being one more dot).
    return newItemId;
  }, [currentScenario, setScenario, setKeyframes, rememberPlacement, lineStepFromTrail, t]);

  /**
   * D — drop another item the SAME STEP further along the line.
   * ⌥D — turn 90° keeping that step: a row becomes a grid, and again walks a
   *        square (one spacing, set once, drives the whole layout).
   * ⇧D / ⌥⇧D — mirror the last placement instead: negating x flips it to the
   * other HALF (the M axis, end↔end), negating z flips it to the other SIDE
   * (the ⇧M axis, flank↔flank). "Make the drill symmetric" is one keypress per
   * side instead of six more aimed clicks.
   */
  const handlePlaceInLine = useCallback((mode = 'step') => {
    const trail = refPlaceTrail.current;
    const last = trail[trail.length - 1];
    if (!last) {
      setToast({ message: t('toast.placeNeedArm'), id: Date.now() });
      return;
    }
    if (last.type === 'ball') {
      setToast({ message: t('toast.placeBallOne'), id: Date.now() });
      return;
    }
    const label = last.type.charAt(0).toUpperCase() + last.type.slice(1);
    const select = (id) => { if (id) setSelectedItem({ type: last.type.startsWith('zone') ? 'zone' : last.type, id }); };
    if (mode === 'mirrorX' || mode === 'mirrorZ') {
      const coords = mode === 'mirrorZ'
        ? [last.coords[0], last.coords[1] ?? 0, -last.coords[2]]
        : [-last.coords[0], last.coords[1] ?? 0, last.coords[2]];
      // remember:false — a mirror is not a STEP. If it joined the trail, the
      // next D would stride original→mirror (twice the offset) and the row
      // would jump off the pitch.
      const newId = handleObjectPlace(last.type, coords, { keepArmed: true, remember: false });
      select(newId);
      setToast({
        message: t(mode === 'mirrorZ' ? 'toast.mirrorPlacedSide' : 'toast.mirrorPlacedHalf', { label }),
        id: Date.now(),
      });
      return;
    }
    const step = lineStepFromTrail();
    if (!step) {
      setToast({
        message: trail.length < 2
          ? t('toast.lineNeedTrail')
          : t('toast.lineSameSpot'),
        id: Date.now(),
      });
      return;
    }
    // A TURN keeps the step and ROTATES it 90°. Unlike a mirror it joins the
    // trail, so D continues along the new line and ⌥D again walks a square.
    const vec = mode === 'turn' ? { dx: -step.dz, dz: step.dx } : step;
    const newId = handleObjectPlace(last.type, [last.coords[0] + vec.dx, last.coords[1] ?? 0, last.coords[2] + vec.dz], { keepArmed: true });
    // Select what was just dropped: the newest marker wears the selection
    // tint, so a chained D is legible as a line GROWING rather than a line of
    // identical dots — and Ctrl+←→ can nudge it straight away.
    select(newId);
    setToast({
      message: mode === 'turn'
        ? t('toast.lineTurned', { label })
        : t('toast.lineStepped', { label, m: Math.round(Math.hypot(step.dx, step.dz) * 100) / 100 }),
      id: Date.now(),
    });
    // WHAT IT JUST PLACED, back to the caller. The item menu needs it to keep the
    // card open on the marker that is now selected — see the ⧉ entries there.
    return { type: last.type, id: newId };
  }, [lineStepFromTrail, handleObjectPlace, setSelectedItem, setToast, t]);
  refItemVerbs.current.line = handlePlaceInLine;

  const handleObjectMove = useCallback((type, id, position) => {
    // A drag at the locked 00:00 of a child branch must not touch ANYTHING:
    // not the AUTO key (recordAutoKey guards too), not the base position,
    // not the visual-window translation — the inherited start state is the
    // parent's end, and a partial write corrupts what future branches inherit.
    if (refuseLockedDrag(`${type}:${id}`, snapKeyTime(playheadTime))) return;
    // Zones: translate visual-key shapes BEFORE the phase-state update (top
    // level — nested state updates inside a setScenario updater would run
    // twice under StrictMode). A visual window snapshots the shape (points +
    // the centre it was captured against); moving the zone translates BOTH,
    // preserving the captured form — dragging never resets/re-forms it.
    if (type === 'zone') {
      const zone = currentScenario?.phases?.[0]?.start?.zones?.find(z => z.id === id);
      if (zone?.position) {
        const dx = position[0] - zone.position[0];
        const dz = position[2] - zone.position[2];
        if (dx !== 0 || dz !== 0) {
          setKeyframes(kPrev => kPrev.map(t => {
            if (t.id !== `zone:${id}` || !t.visual || t.visual.length === 0) return t;
            return {
              ...t,
              visual: t.visual.map(k => {
                const v = k.value || {};
                if (!Array.isArray(v.points) || v.points.length === 0) {
                  // Circle (radius-only) window: no points to translate —
                  // carry the window's CENTRE with the drag instead, so the
                  // circle keeps its position like polygon windows do.
                  const c0 = v.center?.[0] ?? zone.position[0];
                  const c2 = v.center?.[2] ?? zone.position[2];
                  return { ...k, value: { ...v, center: [c0 + dx, 0, c2 + dz] } };
                }
                return {
                  ...k,
                  value: {
                    ...v,
                    points: v.points.map(p => [p[0] + dx, p[1] ?? 0, p[2] + dz]),
                    center: [((v.center?.[0] ?? zone.position[0]) + dx), 0, ((v.center?.[2] ?? zone.position[2]) + dz)],
                  },
                };
              }),
            };
          }));
        }
      }
    }
    updatePhaseState(prev => {
      if (type === 'ball') return { ...prev, balls: (prev.balls || []).map(b => b.id === id ? { ...b, position } : b) };
      if (type === 'cone') return { ...prev, cones: (prev.cones || []).map(c => c.id === id ? { ...c, position } : c) };
      if (type === 'target') return { ...prev, targets: (prev.targets || []).map(t => t.id === id ? { ...t, position } : t) };
      if (type === 'zone') {
        const zone = (prev.zones || []).find(z => z.id === id);
        if (!zone?.position) return prev;
        const dx = position[0] - zone.position[0];
        const dz = position[2] - zone.position[2];
        // Base points translate with the zone; visual keys were already
        // translated above (top-level, outside this updater).
        return { ...prev, zones: (prev.zones || []).map(z => z.id === id ? {
          ...z, position,
          points: (z.points || []).map(p => [p[0] + dx, p[1] ?? 0, p[2] + dz])
        } : z) };
      }
      return prev;
    });
    // AUTO-key: record a position keyframe for the moved object at the rounded second.
    recordAutoKey(`${type}:${id}`, { type, [type === 'ball' ? 'ballId' : type === 'cone' ? 'coneId' : type === 'target' ? 'targetId' : 'zoneId']: id }, position);
    warnUnrecordedMove(`${type}:${id}`);
  }, [updatePhaseState, recordAutoKey, warnUnrecordedMove, currentScenario, setKeyframes, keyframes, playheadTime]);

  const handleBallDrop = useCallback((ballId, position, receiver) => {
    const trackId = `ball:${ballId}`;
    const time = snapKeyTime(refPlayheadTime.current);
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    const track = keyframes.find(t => t.id === trackId);
    const active = [...(track?.attach || [])].sort((a, b) => a.time - b.time)
      .filter(k => k.time <= refPlayheadTime.current).pop();
    const carrier = active ? (active.value === 'none' ? null : active.value)
      : pitchState?.balls?.find(b => b.id === ballId)?.attached;
    const attachment = receiver || (carrier ? 'none' : null);
    // A drop on a DIFFERENT player than the current carrier is a PASS: the
    // ball is released at the drop second and the attachment COMPLETES one
    // second later, so the flight between the two keys IS the pass.
    const PASS_SECONDS = 1;
    const isPass = !!receiver && !!carrier && receiver !== carrier;
    const commit = (value) => {
      handleDragStart('undoVerb.attachBall');
      handleObjectMove('ball', ballId, position);
      if (value) {
        upsertTrackKey(trackId, 'attach', { time, value });
        // The release point must exist even when AUTO-key is off.
        upsertTrackKey(trackId, 'position', { time, position });
        setPlayheadTime(time);
      }
      handleDragEnd();
      setPromptState(null);
      if (value) {
        setToast({
          message: (value === 'none' ? t('attach.ballReleased') : t('attach.ballTo', { who: describeAttachRef(value) })) + t('toast.undoSuffix'),
          id: Date.now(),
        });
      }
    };
    const commitPass = (flight = PASS_SECONDS, loft = false) => {
      handleDragStart('undoVerb.pass');
      const releasePos = positionAtTime(carrier, time) || position;
      // A pass NEEDS its flight. Clamping the catch to the branch end used to
      // collapse release+catch onto one instant (a zero-flight teleport) or
      // shorten the flight the prompt had promised. Instead the branch stretches
      // — one undo step reverts ball AND length together.
      const catchTime = snapKeyTime(time + flight);
      const stretched = catchTime > timelineDuration;
      if (stretched) setTimelineDuration(catchTime);
      const catchPos = positionAtTime(receiver, catchTime) || position;
      // Release: the ball leaves the carrier's feet at the drop second…
      upsertTrackKey(trackId, 'attach', { time, value: 'none' });
      upsertTrackKey(trackId, 'position', { time, position: releasePos });
      // …and lands with the receiver one second later, attached from there on.
      upsertTrackKey(trackId, 'attach', { time: catchTime, value: receiver });
      upsertTrackKey(trackId, 'position', { time: catchTime, position: catchPos });
      // "Lofted" is not a slower pass. The button says the ball goes UP, so the
      // segment between release and catch carries the same arch the LOFT verb
      // writes from the menu and the chord — one height, decided once.
      if (loft) upsertSegment(trackId, time, catchTime, { ...DEFAULT_SEGMENT, arch: 3 });
      handleDragEnd();
      setPromptState(null);
      // Land ON the catch with the ball selected, so Alt+←/→ immediately
      // re-times the catch — the one tuning most passes need after the drop.
      setPlayheadTime(catchTime);
      setSelectedItem({ type: 'ball', id: ballId });
      setToast({ message: t('pass.to', {
        who: describeAttachRef(receiver), flight, how: loft ? t('pass.loftSuffix') : '',
      })
        + (stretched ? t('pass.stretched', { t: fmtTime(catchTime) }) : '') + t('toast.undoSuffix'), id: Date.now() });
    };
    // Dropped back ON ITS OWN CARRIER: the ball already lives at that
    // player's feet — a "move only" commit would write a base-position move
    // and an AUTO position key that the follow logic immediately overrides
    // (dead keys, silent). Refuse and name the two real options instead.
    if (receiver && receiver === carrier) {
      setToast({ message: t('pass.sameCarrier', { who: describeAttachRef(receiver) }), id: Date.now() });
      return;
    }
    if (!attachment) { commit(null); return; }
    setAttachPickMode(null);
    setPromptState({
      title: isPass ? t('pass.title', { who: describeAttachRef(receiver) })
        : receiver ? t('pass.attachTitle', { who: describeAttachRef(receiver) }) : t('pass.detachTitle'),
      // The flight used to be fixed at one second, and changing it meant the
      // timeline (or a keyboard chord). Pick it HERE instead: one tap per pass,
      // at the speed the play needs — no keys, nothing to remember.
      description: isPass ? t('pass.flightHint') : undefined,
      subActions: isPass ? [
        { label: t('pass.quick'), action: () => commitPass(0.5) },
        { label: t('pass.normal'), action: () => commitPass(PASS_SECONDS) },
        { label: t('pass.lofted'), action: () => commitPass(2, true) },
      ] : [
        { label: receiver ? t('itm.attach') : t('itm.detach'), action: () => commit(attachment) },
        ...(!carrier && receiver ? [{ label: t('pass.moveOnly'), action: () => commit(null) }] : []),
      ],
      cancelAction: () => setPromptState(null),
    });
  }, [handleObjectMove, handleDragStart, handleDragEnd, keyframes, pitchState, upsertTrackKey, upsertSegment, positionAtTime, timelineDuration, setTimelineDuration, describeAttachRef, setSelectedItem, t]);

  /**
   * Fired by Viewport3D when a player is dragged and dropped ONTO a ball.
   * Mirrors the ball→player gesture: the prompt hands the ball over to the
   * player (attach key at the playhead second), so dribbles, give-and-gos and
   * tackles are built without leaving the pitch.
   */
  const handlePlayerBallDrop = useCallback((side, index, ballId) => {
    const trackId = `ball:${ballId}`;
    const time = snapKeyTime(refPlayheadTime.current);
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    const value = `${side}:${index}`;
    const track = keyframes.find(t => t.id === trackId);
    const active = [...(track?.attach || [])].sort((a, b) => a.time - b.time)
      .filter(k => k.time <= refPlayheadTime.current).pop();
    const carrier = active ? (active.value === 'none' ? null : active.value)
      : pitchState?.balls?.find(b => b.id === ballId)?.attached;
    if (carrier === value) {
      // Silent refusal was a dead gesture — say why nothing happened.
      setToast({ message: t('pass.hasBall', { who: describeAttachRef(value) }), id: Date.now() });
      return;
    }
    const pos = interpolatedPosition(trackId, { type: 'ball', id: ballId });
    const takerName = describeAttachRef(value);
    const carrierName = describeAttachRef(carrier);
    const isPass = !!carrier && carrier !== value;
    // The twin of the ball→player drop, and it now asks the SAME question: how
    // long is the flight? One tap per pass, chosen at the moment of the pass,
    // instead of a fixed second plus a trip to the timeline afterwards.
    const commitGive = (flight = 1, loft = false) => {
      handleDragStart('undoVerb.pass');
      // Same PASS semantics as dropping the ball on a player: release key at
      // the drop second, attachment COMPLETES one flight later — the travel
      // between the keys IS the pass, no teleport.
      const releasePos = positionAtTime(carrier, time) || pos;
      // Same stretch rule as the ball→player twin: the pass always gets the
      // flight it promised, even at the branch end.
      const catchTime = snapKeyTime(time + flight);
      const stretched = catchTime > timelineDuration;
      if (stretched) setTimelineDuration(catchTime);
      const catchPos = positionAtTime(value, catchTime) || pos;
      upsertTrackKey(trackId, 'attach', { time, value: 'none' });
      upsertTrackKey(trackId, 'position', { time, position: releasePos });
      upsertTrackKey(trackId, 'attach', { time: catchTime, value });
      upsertTrackKey(trackId, 'position', { time: catchTime, position: catchPos });
      // The twin writes the same loft, so the two doors cannot drift: "Lofted"
      // means the ball goes up, at the height the LOFT verb itself uses.
      if (loft) upsertSegment(trackId, time, catchTime, { ...DEFAULT_SEGMENT, arch: 3 });
      setPlayheadTime(catchTime);
      setSelectedItem({ type: 'ball', id: ballId });
      setToast({ message: t('pass.to', {
        who: takerName, flight, how: loft ? t('pass.loftSuffix') : '',
      })
        + (stretched ? t('pass.stretched', { t: fmtTime(catchTime) }) : '') + t('toast.undoSuffix'), id: Date.now() });
      handleDragEnd();
      setPromptState(null);
    };
    const commitTake = () => {
      handleDragStart('undoVerb.pass');
      upsertTrackKey(trackId, 'attach', { time, value });
      // The ball's spot at the handover second must exist even with AUTO off.
      if (pos) upsertTrackKey(trackId, 'position', { time, position: pos });
      setPlayheadTime(time);
      handleDragEnd();
      setPromptState(null);
    };
    setPromptState({
      title: t('pass.giveTitle', { who: takerName }),
      description: isPass
        ? t('pass.giveHint', { carrier: carrierName })
        : t('pass.takeHint', { who: takerName }),
      subActions: isPass ? [
        { label: t('pass.quick'), action: () => commitGive(0.5) },
        { label: t('pass.normal'), action: () => commitGive(1) },
        { label: t('pass.lofted'), action: () => commitGive(2, true) },
      ] : [{ label: t('itm.attach'), action: commitTake }],
      cancelAction: () => setPromptState(null),
    });
  }, [keyframes, pitchState, interpolatedPosition, upsertTrackKey, upsertSegment, handleDragStart, handleDragEnd, describeAttachRef, positionAtTime, timelineDuration, setTimelineDuration, setSelectedItem, setToast, t]);

  /**
   * Fired by Viewport3D when a zone is dropped ONTO a player/ball (attach the
   * whole zone) or onto open pitch while it is carried (detach). The drag IS the
   * decision: a zone has no flight to pick, so unlike the ball there is nothing
   * left to ask — it commits, and the toast says what happened.
   */
  /**
   * A CARRIED ITEM, DROPPED — zones, cones and targets in ONE place.
   *
   * The release answers one question whatever the shape: is this a real change,
   * or the carrier it is already under? The confirm panel this used to open had
   * ONE live button and a Cancel — it asked the coach to confirm the decision
   * their thumb had just made. The no-op drops refuse; everything else lands,
   * and the toast names it and points at ↩.
   *
   * (Zones keep their own sentences rather than a {what} parameter: "Zone
   * already follows Silva" reads better in three languages than a noun dropped
   * into an article-taking slot. The CODE is shared; only the copy differs.)
   */
  const handleCarriedDrop = useCallback((kind, itemId, receiverRef) => {
    const isZone = kind === 'zone';
    const trackId = `${kind}:${itemId}`;
    const time = snapKeyTime(refPlayheadTime.current);
    if (isLockedKeyTime(time)) { refuseLockedKey(); return; }
    const track = keyframes.find(t => t.id === trackId);
    const active = [...(track?.attach || [])].sort((a, b) => a.time - b.time)
      .filter(k => k.time <= refPlayheadTime.current).pop();
    const carrier = active && active.value !== 'none' ? active.value : null;
    const value = receiverRef || 'none';
    // The subject word is the one the item's OWN menu uses, so the sentence
    // names the thing the way the button does.
    const what = capWord(placeName(kind));
    const who = describeAttachRef(receiverRef);
    if (value === (carrier || 'none')) {
      // Silent refusal was a dead gesture — say WHY nothing happened.
      // (These are rigid shapes: they SNAP to their carrier and follow it —
      // unlike balls they never get release/catch flight, by design.)
      setToast({
        message: isZone
          ? t(receiverRef ? 'attach.zoneAlready' : 'attach.zoneNobody', receiverRef ? { who } : {})
          : t(receiverRef ? 'attach.objAlready' : 'attach.objNobody', receiverRef ? { what, who } : { what }),
        id: Date.now(),
      });
      return;
    }
    handleDragStart(isZone ? 'undoVerb.attachZone' : 'undoVerb.attachObj');
    upsertTrackKey(trackId, 'attach', { time, value });
    setPlayheadTime(time);
    handleDragEnd();
    setToast({
      message: (isZone
        ? t(receiverRef ? 'attach.zoneTo' : 'attach.zoneOff', receiverRef ? { who } : {})
        : t(receiverRef ? 'attach.objTo' : 'attach.objOff', receiverRef ? { what, who } : { what }))
        + t('toast.undoSuffix'),
      id: Date.now(),
    });
  }, [keyframes, upsertTrackKey, handleDragStart, handleDragEnd, describeAttachRef,
    placeName, capWord, setPlayheadTime, t]);

  const handleZoneDrop = useCallback((zoneId, receiverRef) => {
    handleCarriedDrop('zone', zoneId, receiverRef);
  }, [handleCarriedDrop]);

  /**
   * A CONE OR TARGET DROPPED ON A PLAYER FOLLOWS THAT PLAYER.
   *
   * Until now the only door was the item menu: select the cone, open the menu,
   * 📎 Attach, then tap the carrier — four taps for a decision the hand had
   * already made by carrying the cone across the pitch. Zones already answered
   * the same drop. Cones and targets were the two shapes left out, and the
   * sampler then ignored the key they were given: the door opened onto nothing
   * and the cone sat still while the midfielder it was pinned to ran away.
   */
  const handleObjectDrop = useCallback((kind, itemId, receiverRef) => {
    handleCarriedDrop(kind, itemId, receiverRef);
  }, [handleCarriedDrop]);

  // ── The look-at TARGET feature was removed (the red aim pin, the L / ⇧L gaze
  // lock, and the three callbacks that backed them). Players face the way they
  // travel — see playerFacing.js — and a second, invisible aim system on top of
  // that only created confusion: a pinned head that only moved during playback,
  // a red pin nobody could find, and saved aims that silently overrode each
  // other. Old scenarios may still carry an aim point or a look mode on their
  // player state; nothing reads those fields, so they load and play exactly
  // like any other extra property. ──

  // ── Player overlay actions (wave, kick, etc.) ──
  // (removed V1 legacy handler: handleActionAdd)

  // (removed V1 legacy handler: handleActionDelete)

  // (removed V1 legacy handler: handleActionUpdate)

  // ── Player Highlight handlers (color + dual-knob range, same pattern as actions) ──
  // (removed V1 legacy handler: handleHighlightAdd)

  // (removed V1 legacy handler: handleHighlightDelete)

  // (removed V1 legacy handler: handleHighlightUpdate)

  const handlePlayerRemove = useCallback((side, index) => {
    const carrierRef = `${side}:${index}`;
    // Dropping the carrier drops what it carries. A ball or zone whose
    // ACTIVE attach at the playhead is this player gets a release key +
    // a position key at the carrier's interpolated spot: it FALLS where it
    // was held instead of dangling on a benched slot (the timeline would
    // otherwise still read "Attach: Silva" for a Silva on the bench, and
    // the ball would teleport back to its base position).
    const dTime = snapKeyTime(refPlayheadTime.current);
    let dropped = 0, followers = 0;
    if (!isLockedKeyTime(dTime)) {
      for (const tr of keyframes) {
        if (tr.id === carrierRef) continue;
        const acts = [...(tr.attach || [])].sort((a, b) => a.time - b.time)
          .filter(k => k.time <= dTime + 1e-9).pop();
        if (!acts || acts.value !== carrierRef) continue;
        const pos = positionAtTime(carrierRef, dTime) || [0, 0, 0];
        upsertTrackKey(tr.id, 'attach', { time: dTime, value: 'none' });
        upsertTrackKey(tr.id, 'position', { time: dTime, position: pos });
        // A FOLLOWER is released the same way — they keep the ground they gained
        // and stand on their own — but saying "carried item" about a person is
        // the sort of wording that makes a feature sound like a toy.
        if (isPlayerTrackId(tr.id)) followers++; else dropped++;
      }
    }
    setScenario(prev => {
      if (!prev) return prev;
      const phases = [...prev.phases];
      const phase = phases[0];
      const start = { ...(phase.start || createEmptyPitchState()) };
      const players = [...(start[side]?.players || [])];
      if (index >= 0 && index < players.length) players[index] = { ...players[index], position: null };
      start[side] = { players };
      phases[0] = { ...phase, start };
      return { ...prev, phases };
    });
    setSelectedItem(null);
    if (dropped || followers) {
      const parts = [];
      if (followers) parts.push(t(followers === 1 ? 'bench.markerOne' : 'bench.markerMany', { n: followers }));
      if (dropped) parts.push(t(dropped === 1 ? 'bench.carriedOne' : 'bench.carriedMany', { n: dropped }));
      setToast({
        message: t('bench.benched', {
          who: describeAttachRef(carrierRef), what: parts.join(', '), t: fmtTime(dTime),
        }),
        id: Date.now(),
      });
    }
  }, [setScenario, keyframes, upsertTrackKey, positionAtTime, describeAttachRef, setToast, t]);

  const handleObjectRemove = useCallback((type, id) => {
    setScenario(prev => {
      if (!prev) return prev;
      const phases = [...prev.phases];
      const phase = phases[0];
      const start = { ...(phase.start || createEmptyPitchState()) };
      if (type === 'ball') start.balls = (start.balls || []).filter(b => b.id !== id);
      else if (type === 'cone') start.cones = (start.cones || []).filter(c => c.id !== id);
      else if (type === 'target') start.targets = (start.targets || []).filter(t => t.id !== id);
      else if (type === 'zone') start.zones = (start.zones || []).filter(z => z.id !== id);
      phases[0] = { ...phase, start };
      return { ...prev, phases };
    });
    setSelectedItem(null);
  }, [setScenario]);

  /**
   * Toolbar '−' button: 100% remove the selected item from the scenario —
   * the object itself AND its keyframe tracks (position / graphic / attach /
   * visual / action keys), so nothing stale survives a save + reload.
   */
  const handleRemoveSelectedItem = useCallback(() => {
    if (!selectedItem) return;
    if (selectedItem.type === 'player') {
      handlePlayerRemove(selectedItem.side, selectedItem.index);
      setKeyframes(prev => prev.filter(t => t.id !== `${selectedItem.side}:${selectedItem.index}`));
    } else {
      handleObjectRemove(selectedItem.type, selectedItem.id);
      setKeyframes(prev => prev.filter(t => t.id !== `${selectedItem.type}:${selectedItem.id}`));
    }
    // Also close any editors that target the removed item
    setSegmentEdit(null);
    setGraphicEdit(null);
    setZoneVisualTarget(null);
    // GROUP HONESTY: the removed member must leave the group with the item.
    // A stale descriptor made ⇧Del count a ghost ('3 deleted' with two on the
    // pitch) and sent mirror / duplicate hunting a dead id. The toast also
    // teaches the verb the user most likely meant: plain Del took ONE member.
    if (groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem))) {
      const rest = groupItems.filter(g => !isSameGroupMember(g, selectedItem));
      setGroupItems(rest);
      setToast({
        message: t('group.deletedStill', {
          name: getItemDisplayName(selectedItem), n: rest.length,
        }),
        id: Date.now(),
      });
    }
  }, [selectedItem, groupItems, handlePlayerRemove, handleObjectRemove, setKeyframes, setGroupItems, getItemDisplayName, setToast, t]);

  /**
   * DELETE THE WHOLE GROUP (⇧Del, and the 🗑 entry of a grouped item's menu):
   * players return to the bench, items are removed — ONE undo step for the
   * whole gesture, and a count toast so the number is never guessed.
   * `explicit` = the members the CALLER named (the menu speaks for the group it
   * was opened on, even when the strip opened it without selecting); without it
   * the current group is used.
   */
  const handleDeleteGroup = useCallback((explicit = null) => {
    const members = explicit && explicit.length ? explicit : groupItems;
    if (members.length < 2) {
      // The chord cannot get here (it checks the group first), so this only
      // fires if a menu was built against a group that has since gone. Name the
      // way out instead of silently deleting nothing.
      setToast({ message: t('toast.groupOneRemove'), id: Date.now() });
      return;
    }
    const n = members.length;
    handleDragStart('undoVerb.deleteGroup');
    // TWO PASSES — players first: benching a carrier writes drop keys onto
    // carried tracks, and the drop scan reads the (stale within this tick)
    // keyframes closure. Removing a carried ball BEFORE its carrier would let
    // that scan re-create the deleted track. Players first makes every drop
    // land on a track that still gets removed after — or survives correctly
    // when the carrier leaves the group.
    for (const g of members) {
      if (g.type !== 'player') continue;
      handlePlayerRemove(g.side, g.index);
      setKeyframes(prev => prev.filter(t => t.id !== `${g.side}:${g.index}`));
    }
    for (const g of members) {
      if (g.type === 'player') continue;
      // Balls included (b904524 doctrine): the old '!== ball' copy came from X,
      // where it is correct (no ACTION lane there) — but ⇧Del means DELETE, and
      // single Del removes a ball just fine. A grouped ball vanishing from the
      // count toast but staying on the pitch was a lie.
      handleObjectRemove(g.type, g.id);
      setKeyframes(prev => prev.filter(t => t.id !== `${g.type}:${g.id}`));
    }
    handleDragEnd();
    setGroupItems([]);
    setSelectedItem(null);
    setSegmentEdit(null); setGraphicEdit(null); setZoneVisualTarget(null);
    setToast({ message: t('clip.deletedN', { n }), id: Date.now() });
  }, [groupItems, handlePlayerRemove, handleObjectRemove, handleDragStart, handleDragEnd,
      setKeyframes, setGroupItems, setSelectedItem, setToast, t]);
  refItemVerbs.current.remove = handleDeleteGroup;

  /**
   * Every item of ONE KIND on the pitch: all the cones, all the zones, one
   * team's players. ⇧⌥S feeds the GROUP, and the group is what M, Ctrl+D,
   * ⌥←/→ and ⇧Del already act on — so "select all cones" is a MULTIPLIER for
   * every group-aware verb rather than a verb of its own. (Plain Del stays
   * ONE item: the split is deliberate, so the strings must never blur it.)
   */
  const sceneMembersOfKind = useCallback((state, base) => {
    const out = [];
    if (!state || !base) return out;
    if (base.type === 'player') {
      ((state[base.side]?.players) || []).forEach((p, i) => { if (p) out.push({ type: 'player', side: base.side, index: i }); });
      return out;
    }
    // PLURAL in phase state, SINGULAR on the item — the trap that once made a
    // mirror silently do nothing, spelled out here so it cannot recur.
    const bucket = base.type === 'ball' ? 'balls' : `${base.type}s`;
    for (const it of (state[bucket] || [])) out.push({ type: base.type, id: it.id });
    return out;
  }, []);

  const handleSelectKind = useCallback((explicitBase = null) => {
    // `explicit` = the item the CALLER named (the item menu gathers the kind of
    // the item whose menu it is, which the strip opens WITHOUT selecting).
    const base = explicitBase
      || (groupItems.length >= 2 ? groupItems[0] : (selectedItem ? groupMemberOf(selectedItem) : null));
    if (!base) {
      setToast({ message: t('toast.needItemSelectAll'), id: Date.now() });
      return;
    }
    const members = sceneMembersOfKind(pitchState, base);
    const label = base.type === 'player'
      ? t(base.side === 'home' ? 'kindMany.playersHome' : 'kindMany.playersAway')
      : t(`kindMany.${base.type}s`);
    if (members.length === 0) {
      setToast({
        message: t(base.type === 'player' ? 'select.nonePlayers' : 'select.noneItems', {
          who: label, what: label,
        }),
        id: Date.now(),
      });
      return;
    }
    if (members.length === 1) {
      // Grouping one item would change nothing — say so instead of pretending.
      setToast({ message: t('clip.onlyOne', {
        what: base.type === 'player'
          ? t('clip.whatPlayer', { side: t(`side.${base.side === 'home' ? 'home' : 'away'}`) })
          : t(`itm.${base.type}`, {}).toLowerCase(),
      }), id: Date.now() });
      return;
    }
    setGroupItems(members);
    setSelectedItem(base);
    setToast({
      message: t('group.allSelected', { n: members.length, label }),
      id: Date.now(),
    });
    // RETURN the group it made. The caller that opened this from an item's card
    // cannot see the new selection in the render that made the tap — setGroupItems
    // lands on the next one — so the card that re-opens has to be handed the
    // members directly, through the queued re-open.
    return members;
  }, [groupItems, selectedItem, pitchState, sceneMembersOfKind, setGroupItems, setSelectedItem, setToast, t]);
  refItemVerbs.current.selectKind = handleSelectKind;

  /**
   * MIRROR (M / ⇧M · ⌥M / ⇧⌥M) — flip items across the pitch, RUN INCLUDED.
   *
   * Building the other side of a drill meant dragging every player and cone to
   * a mirrored spot by hand, key by key. One press flips the whole selection:
   * the base position, every position key on the track, the zone's shape, and
   * the movement bends swap left↔right so an arc still bows the
   * same physical way. Group-aware like C: ⇧-click the players, press
   * M, and the pattern turns over.
   *
   * axis 2 (z) = flank ↔ flank (the coach's usual ask); axis 0 (x) = end ↔ end
   * across the halfway line. The pitch is centred on the origin, so negating
   * the coordinate IS the mirror.
   *
   * `whole` (⌥) turns the 2×2 into four chords without a new idea: the same
   * mirror applied to EVERY item in the branch — flipping a whole scenario is
   * one press instead of select-all-then-M (there is no select-all).
   */
  const handleMirrorSelection = useCallback((axis, whole = false, explicit = null) => {
    // `explicit` = the members the CALLER named (the action menu mirrors the
    // item whose menu it is, not whatever is selected). Without it the verb
    // follows the selection/group, which is what the M chord means.
    const members = explicit
      ? explicit
      : whole ? [] : (groupItems.length >= 2
        ? groupItems
        : (selectedItem ? [groupMemberOf(selectedItem)] : []));
    if (whole) {
      for (const side of ['home', 'away']) {
        ((pitchState?.[side]?.players) || []).forEach((p, i) => { if (p) members.push({ type: 'player', side, index: i }); });
      }
      for (const kind of ['balls', 'cones', 'targets', 'zones']) {
        for (const it of (pitchState?.[kind] || [])) members.push({ type: kind === 'balls' ? 'ball' : kind.slice(0, -1), id: it.id });
      }
    }
    if (!members.length) {
      setToast({ message: whole
        ? t('toast.mirrorNothingPitch')
        : t('toast.mirrorNeedSelect'), id: Date.now() });
      return;
    }
    const idOf = (g) => (g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`);
    // Whole-branch: every track in the branch, including one whose item is not
    // in the current phase state (a benched player still has a run).
    const ids = whole
      ? Array.from(new Set([...members.map(idOf), ...(keyframes || []).map(t => t && t.id)]))
      : members.map(idOf);
    const flip = (p) => (Array.isArray(p) && p.length > axis
      ? p.map((v, i) => (i === axis ? (v === 0 ? 0 : -v) : v)) // never -0
      : p);
    handleDragStart(axis === 2 ? 'undoVerb.mirrorFlank' : 'undoVerb.mirrorEnd');
    setKeyframes(prev => mirrorTracks(prev, ids, axis));
    updatePhaseState(prev => {
      const next = { ...prev };
      for (const g of members) {
        if (g.type === 'player') {
          const players = [...((next[g.side] || {}).players || [])];
          const p = players[g.index];
          if (p) {
            players[g.index] = { ...p, position: flip(p.position) };
          }
          next[g.side] = { ...(next[g.side] || {}), players };
        } else if (g.type === 'zone') {
          next.zones = (next.zones || []).map(z => (z.id === g.id
            ? { ...z, position: flip(z.position), points: (z.points || []).map(flip) } : z));
        } else if (g.type === 'ball') {
          next.balls = (next.balls || []).map(b => (b.id === g.id ? { ...b, position: flip(b.position) } : b));
        } else {
          // Phase state keeps these arrays PLURAL while items are typed
          // singular ('cone' → cones[]): writing next.cone would silently
          // mirror nothing at all.
          const bucket = g.type === 'ball' ? 'balls' : `${g.type}s`;
          next[bucket] = (next[bucket] || []).map(o => (o.id === g.id ? { ...o, position: flip(o.position) } : o));
        }
      }
      return next;
    });
    handleDragEnd();
    const n = members.length;
    setToast({
      message: whole
        ? t('toast.mirrorWhole', { n, axis: t(axis === 2 ? 'toast.mirrorAxisFlank' : 'toast.mirrorAxisEnd') })
          + t('toast.undoSuffix')
        : t(n === 1 ? 'toast.mirrorOne' : 'toast.mirrorMany',
          { n, axis: t(axis === 2 ? 'toast.mirrorAxisFlank' : 'toast.mirrorAxisEnd') })
          + t(axis === 2 ? 'toast.mirrorFlankHint' : 'toast.mirrorEndHint')
          + t('toast.undoSuffix'),
      id: Date.now(),
    });
  }, [groupItems, selectedItem, pitchState, keyframes, setKeyframes, updatePhaseState, handleDragStart, handleDragEnd, setToast, t]);
  // The action menu (declared ABOVE this callback) reaches the verb through a
  // ref: naming handleMirrorSelection in that useCallback's deps would read the
  // binding before it exists (TDZ).
  refItemVerbs.current.mirror = handleMirrorSelection;

  // ── Height change: update Y of selected item in real-time ──
  const handleItemHeightChange = useCallback((height) => {
    if (!selectedItem) return;
    pushUndo('undoVerb.itemHeight');
    updatePhaseState(prev => {
      if (selectedItem.type === 'player') {
        const players = [...(prev[selectedItem.side]?.players || [])];
        const p = players[selectedItem.index];
        if (p?.position) players[selectedItem.index] = { ...p, position: [p.position[0], height, p.position[2]] };
        return { ...prev, [selectedItem.side]: { players } };
      }
      if (selectedItem.type === 'ball') {
        const ball = (prev.balls || []).find(b => b.id === selectedItem.id);
        const pos = ball?.position;
        if (pos) return { ...prev, balls: (prev.balls || []).map(b => b.id === selectedItem.id ? { ...b, position: [pos[0], height, pos[2]] } : b) };
        return prev;
      }
      if (selectedItem.type === 'cone') {
        return { ...prev, cones: (prev.cones || []).map(c => c.id === selectedItem.id && c.position ? { ...c, position: [c.position[0], height, c.position[2]] } : c) };
      }
      if (selectedItem.type === 'target') {
        return { ...prev, targets: (prev.targets || []).map(t => t.id === selectedItem.id && t.position ? { ...t, position: [t.position[0], height, t.position[2]] } : t) };
      }
      return prev;
    });
  }, [selectedItem, updatePhaseState, pushUndo]);

  // ── Ball attach/detach: the LEGACY base-state path (balls[].attached) is
  //    read-only now — it survives in old saves and as the carrier fallback
  //    in the drop handlers, but every NEW attach goes through the keyframe
  //    attach lane (⌥A / drag-prompt / pick-mode), which is what playback
  //    and the timeline show. The old panel handlers were removed together
  //    with Viewport3D's never-armed ballFollowPlayerMode. ──

  // ── Move item in next phase (for manual path end point drag) ──
  // (removed V1 legacy handler: handleMoveItemInNextPhase)

  // ── Zone points change (edit mode: add/move/remove polygon vertices) ──
  const handleZonePointsChange = useCallback((zoneId, pointsIn, newPointRefs) => {
    // While a zone-visuals window is being edited (editor open OR polygon edit
    // mode active), shape edits belong to that window's key (the keyframes),
    // not the base phase state.
    if (zoneVisualTarget && zoneVisualTarget.trackId === `zone:${zoneId}` && zoneVisualTarget.keyIndex >= 0) {
      const patch = { points: pointsIn.map(p => [...p]) };
      if (newPointRefs) patch.pointRefs = newPointRefs.map(r => ({ ref: r?.ref ?? null }));
      handleZoneVisualChange(zoneVisualTarget.trackId, zoneVisualTarget.keyIndex, patch);
      return;
    }
    pushUndo('undoVerb.zoneShape');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => {
        if (z.id !== zoneId) return z;
        // Recenter reverted: the zone position is a FIXED anchor (set at
        // placement, moved by dragging the zone). Vertex edits do NOT move it.
        const points = pointsIn.map(p => [...p]);
        const pointRefs = newPointRefs ? newPointRefs.map(r => ({ ref: r?.ref ?? null })) : (z.pointRefs || []);
        return { ...z, points, pointRefs };
      }) };
    });
  }, [updatePhaseState, zoneVisualTarget, handleZoneVisualChange, pushUndo]);

  // ── Zone color change (current phase only) ──
  const handleZoneColorChange = useCallback((zoneId, color) => {
    const zc = (refCurrentScenario.current?.phases?.[0]?.start?.zones || []).find(z => z.id === zoneId);
    if (zc && zc.color === color) return; // same colour: no undo burn
    pushUndo('undoVerb.zoneColour');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => z.id === zoneId ? { ...z, color } : z) };
    });
  }, [updatePhaseState, pushUndo]);

  // ── Ball properties (colour / visible) — current phase, by ball id. A ball
  //    hidden here is not drawn in the viewport and cannot be click-selected
  //    there either (Viewport3D's picking skips hidden objects), so the change
  //    goes on the undo stack: Ctrl/Cmd+Z brings a hidden ball back even after
  //    the selection is dropped. ──
  const handleBallPropertyChange = useCallback((ballId, patch) => {
    // Same-value commit (a colour input re-firing, or the visible checkbox
    // set to what it already was) must not burn an undo step.
    const cb = (refCurrentScenario.current?.phases?.[0]?.start?.balls || []).find(b => b.id === ballId);
    if (cb && Object.keys(patch).every(k => cb[k] === patch[k])) return;
    pushUndo('undoVerb.ballProperties');
    updatePhaseState(prev => {
      return { ...prev, balls: (prev.balls || []).map(b => b.id === ballId ? { ...b, ...patch } : b) };
    });
  }, [updatePhaseState, pushUndo]);

  // The menu's 👻/👁 entry writes through the SAME commit the Ball panel's
  // checkbox and ⇧V use — one undo step, one rule about what "hidden" means.
  // Assigned HERE, not up with the other verbs: the ref table is filled in
  // declaration order, and naming a const before its line is a render-time
  // ReferenceError, not a warning.
  refItemVerbs.current.ballVisible = handleBallPropertyChange;
  const handleZoneOpacityChange = useCallback((zoneId, opacity) => {
    pushUndo('undoVerb.zoneOpacity');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => z.id === zoneId ? { ...z, opacity } : z) };
    });
  }, [updatePhaseState, pushUndo]);

  // ── Zone mode change (current phase only) — also initialises the
  //    mode-specific fields (circle → radius, dynamic → vertices + refs). ──
  const handleZoneModeChange = useCallback((zoneId, mode) => {
    const zm = (refCurrentScenario.current?.phases?.[0]?.start?.zones || []).find(z => z.id === zoneId);
    if (zm && zm.mode === mode) return; // same mode: no undo burn
    pushUndo('undoVerb.zoneMode');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => {
        if (z.id !== zoneId) return z;
        const cx = z.position?.[0] ?? 0, cz = z.position?.[2] ?? 0;
        if (mode === 'circle') {
          return { ...z, mode, radius: z.radius ?? 3, points: [] };
        }
        if (mode === 'dynamic') {
          if ((z.points || []).length >= 3) return { ...z, mode }; // keep existing vertices
          const n = 5;
          const points = [];
          for (let i = 0; i < n; i++) {
            const a = (i / n) * Math.PI * 2;
            points.push([cx + Math.cos(a) * 3, 0, cz + Math.sin(a) * 3]);
          }
          return { ...z, mode, points, pointRefs: points.map(() => ({ ref: null })) };
        }
        // static polygon
        if ((z.points || []).length >= 2) return { ...z, mode };
        const s = 3;
        return { ...z, mode, points: [
          [cx - s, 0, cz - s], [cx + s, 0, cz - s],
          [cx + s, 0, cz + s], [cx - s, 0, cz + s],
        ], pointRefs: [] };
      }) };
    });
  }, [updatePhaseState, pushUndo]);

  // ── Zone radius change (circle zones, live update in view) ──
  const handleZoneRadiusChange = useCallback((zoneId, radius) => {
    pushUndo('undoVerb.zoneRadius');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => z.id === zoneId ? { ...z, radius: Math.max(0.5, radius) } : z) };
    });
  }, [updatePhaseState, pushUndo]);

  // ── Zone closed change (current phase only) ──
  const handleZoneClosedChange = useCallback((zoneId, closed) => {
    pushUndo('undoVerb.zoneOpenClosed');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => z.id === zoneId ? { ...z, closed } : z) };
    });
  }, [updatePhaseState, pushUndo]);

  // ── Zone thickness change (current phase only) ──
  const handleZoneThicknessChange = useCallback((zoneId, thickness) => {
    pushUndo('undoVerb.zoneThickness');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => z.id === zoneId ? { ...z, thickness } : z) };
    });
  }, [updatePhaseState, pushUndo]);

  // ── Zone point count change (current phase only) ──
  const handleZonePointCountChange = useCallback((zoneId, count) => {
    pushUndo('undoVerb.zoneVertices');
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => {
        if (z.id !== zoneId) return z;
        const oldPoints = z.points || [];
        const oldRefs = z.pointRefs || [];
        let newPoints, newRefs;
        if (count > oldPoints.length) {
          const cx = oldPoints.reduce((s, p) => s + (p[0] || 0), 0) / Math.max(1, oldPoints.length);
          const cz = oldPoints.reduce((s, p) => s + (p[2] || 0), 0) / Math.max(1, oldPoints.length);
          newPoints = [...oldPoints];
          newRefs = [...oldRefs];
          while (newPoints.length < count) {
            newPoints.push([cx + (Math.random() - 0.5) * 2, 0, cz + (Math.random() - 0.5) * 2]);
            newRefs.push({ ref: null });
          }
        } else {
          newPoints = oldPoints.slice(0, count);
          newRefs = oldRefs.slice(0, count);
        }
        return { ...z, points: newPoints, pointRefs: newRefs };
      }) };
    });
  }, [updatePhaseState, pushUndo]);

  // ── Zone point ref change (current phase only; or the open visual window) ──
  const handleZonePointRefChange = useCallback((zoneId, pointIndex, ref) => {
    // The bind is INVISIBLE until playback deforms the zone, so the toast has
    // to carry the meaning — which vertex, what happens, and that it undoes.
    setToast({ message: (ref
      ? t('zoneVertex.bound', { n: pointIndex + 1, who: describeAttachRef(ref) })
      : t('zoneVertex.freed', { n: pointIndex + 1 })) + t('toast.undoSuffix'), id: Date.now() });
    pushUndo('undoVerb.zoneVertexLink');
    if (zoneVisualTarget && zoneVisualTarget.trackId === `zone:${zoneId}` && zoneVisualTarget.keyIndex >= 0) {
      const tk = zoneVisualTarget.trackId;
      const ki = zoneVisualTarget.keyIndex;
      setKeyframes(prev => prev.map(t => {
        if (t.id !== tk) return t;
        const visual = (t.visual || []).map((k, i) => {
          if (i !== ki) return k;
          const pointRefs = [...((k.value || {}).pointRefs || [])];
          const nPts = ((k.value || {}).points || []).length;
          while (pointRefs.length < nPts) pointRefs.push({ ref: null });
          pointRefs[pointIndex] = { ref };
          return { ...k, value: { ...(k.value || {}), pointRefs } };
        });
        return { ...t, visual };
      }));
      return;
    }
    updatePhaseState(prev => {
      return { ...prev, zones: (prev.zones || []).map(z => {
        if (z.id !== zoneId) return z;
        const pointRefs = [...(z.pointRefs || [])];
        while (pointRefs.length < (z.points || []).length) pointRefs.push({ ref: null });
        pointRefs[pointIndex] = { ref };
        return { ...z, pointRefs };
      }) };
    });
  }, [updatePhaseState, zoneVisualTarget, setKeyframes, describeAttachRef, pushUndo, t]);

  // ── Read current arc settings of selected item ──


  // ── Read current height of selected item ──
  const selectedItemHeight = (() => {
    if (!selectedItem || !pitchState) return 0;
    if (selectedItem.type === 'player') {
      const players = selectedItem.side === 'home' ? pitchState?.home?.players : pitchState?.away?.players;
      return players?.[selectedItem.index]?.position?.[1] ?? 0;
    }
    if (selectedItem.type === 'ball') return pitchState?.balls?.find(b => b.id === selectedItem.id)?.position?.[1] ?? 0;
    if (selectedItem.type === 'cone') return pitchState?.cones?.find(c => c.id === selectedItem.id)?.position?.[1] ?? 0;
    if (selectedItem.type === 'target') return pitchState?.targets?.find(t => t.id === selectedItem.id)?.position?.[1] ?? 0;
    return 0;
  })();

  // ── Action mode ──
  const handleActionModeChange = useCallback((mode) => {
    setActionMode(prev => prev === mode ? 'none' : mode);
    if (mode !== 'place') setPendingObjectType(null);
  }, []);

  const handleFieldObjectClick = useCallback((type) => {
    if (pendingObjectType === type) {
      setPendingObjectType(null);
      setActionMode('none');
    } else {
      setPendingObjectType(type);
      setActionMode('place');
      setSelectedItem(null);
    }
  }, [pendingObjectType]);

  // ── Player-press (Add Home/Away menu row) → set selectedItem ──
  function handlePlayerPressTop(player, playerKey, index) {
    // Toggle: if already selected, deselect
    if (selectedItem?.type === 'player' && selectedItem.side === 'home' && selectedItem.index === index) {
      setSelectedItem(null);
      return;
    }
    setSelectedItem({ type: 'player', side: 'home', index });
    setPendingObjectType(null);
    setActionMode('place');
  }

  function handlePlayerPressBottom(player, playerKey, index) {
    if (selectedItem?.type === 'player' && selectedItem.side === 'away' && selectedItem.index === index) {
      setSelectedItem(null);
      return;
    }
    setSelectedItem({ type: 'player', side: 'away', index });
    setPendingObjectType(null);
    setActionMode('place');
  }


  // Shared pacing for explicit actions; native interstitials and inline web ads.
  const showAdGate = ({ slot, onContinue }) => {
    if (isPlaying || masterPlay?.active) { onContinue?.(); return; }
    if (isMobileBuild) { Promise.resolve(getRuntimeAdapter().scenarioAd?.()).catch(() => {}).finally(() => onContinue?.()); return; }
    const eligible = navigator.onLine && adsConfig?.enabled && adsConfig?.client && (slot === 'save' ? adsConfig.slotSave : adsConfig.slotLoad) && (!account || account.tier !== 'premium');
    if (eligible) adFrequency.current.opportunity();
    const proceed = () => { onContinue?.(); };
    if (eligible && !scenarioAd && adFrequency.current.ready()) {
      adFrequency.current.record();
      setScenarioAd(slot);
    }
    proceed();
  };

  const handleSaveScenario = async () => {
    if (!account) {
      // Saving requires an account — prompt to sign in (free has all features)
      setShowLoginModal(true);
      return;
    }
    if (!currentScenario) return;
    const doSave = async () => {
      // V3: persist the whole BRANCH TREE (labelled sceneVersion 3.0), plus the
      // root branch's timeline mirrored into phases/keyframes so a V2 frontend
      // can still open the scenario. The open branch is committed first.
      const tree = committedTree();
      setScenarioTree(tree);
      const isDbItem = currentScenario.id && !currentScenario.id.startsWith('local-');
      try {
        const payload = {
          ...scenarioForSave({ name: currentScenario.name, tree }),

        };
        if (isMobileBuild) {
          const saved = await getRuntimeAdapter().saveDraft(currentScenario.id, payload);
          setScenario(prev => ({ ...prev, id: saved._id }));
          const data = await authAPI.session();
          setScenarios((data.scenarios || []).map(dbScenarioToMenuItem));
        } else if (isDbItem) {
          await scenariosAPI.update(currentScenario.id, payload);
        } else {
          const created = await accountsAPI.createScenario(account._id, payload);
          const dbItem = dbScenarioToMenuItem(created);
          setScenario(prev => ({ ...prev, id: dbItem.id }));
          const data = await authAPI.session();
          setScenarios((data.scenarios || []).map(dbScenarioToMenuItem));
        }
        showAdGate({ slot: 'save', messageKey: 'modal.ad.saveMsg' });
      } catch (err) {
        console.error('Save failed:', err);
        // No silent failures — surface the error so the user knows the save
        // did not reach the server.
        setToast({ message: t('scenario.saveFailed', { why: err.message || t('scenario.unknownError') }), id: Date.now() });
      }
    };
    // Persist first; only a successful explicit save creates an ad opportunity.
    void doSave();
  };

  const handleSaveAs = async (name) => {
    if (!account) {
      setShowLoginModal(true);
      return;
    }
    if (!currentScenario) return;
    try {
      // V3: same payload shape as Save — the whole tree, labelled 3.0.
      const tree = committedTree();
      setScenarioTree(tree);
      const created = await accountsAPI.createScenario(account._id, {
        ...scenarioForSave({ name, tree }),

      });
      const dbItem = dbScenarioToMenuItem(created);
      setScenario(prev => ({ ...prev, id: dbItem.id, name }));
      const data = await authAPI.session();
      setScenarios((data.scenarios || []).map(dbScenarioToMenuItem));
      showAdGate({ slot: 'save', messageKey: 'modal.ad.saveMsg' });
    } catch (err) {
      console.error('Save As failed:', err);
      // No silent failures — surface the error so the user knows the save
      // did not reach the server.
      setToast({ message: t('scenario.saveAsFailed', { why: err.message || t('scenario.unknownError') }), id: Date.now() });
    }
  };

  // ── Copy: store the current selection's full data (from the layout phase) ──
  const handleCopy = useCallback((explicit = null) => {
    if (!currentScenario) return;
    const item = explicit || selectedItem;
    if (!item) return;
    const currentStart = currentScenario.phases?.[0]?.start;
    if (!currentStart) return;

    let data = null;

    if (item.type === 'player') {
      const p = currentStart[item.side]?.players?.[item.index];
      if (p) data = JSON.parse(JSON.stringify(p));
    } else if (item.type === 'ball') {
      const ball = currentStart.balls?.find(b => b.id === item.id);
      if (ball) data = JSON.parse(JSON.stringify(ball));
    } else if (item.type === 'cone') {
      const c = currentStart.cones?.find(x => x.id === item.id);
      if (c) data = JSON.parse(JSON.stringify(c));
    } else if (item.type === 'target') {
      const t = currentStart.targets?.find(x => x.id === item.id);
      if (t) data = JSON.parse(JSON.stringify(t));
    } else if (item.type === 'zone') {
      const z = currentStart.zones?.find(x => x.id === item.id);
      if (z) data = JSON.parse(JSON.stringify(z));
    }

    // ROUTE: the item's movement keys + segment styles in the CURRENT branch
    // (players AND items — a ball's run or a zone's sweep can be replayed on
    // another item of the same type, shifted to start from its own spot).
    if (data) {
      const tm = itemTrackMeta(item);
      const track = tm ? keyframes.find(k => k.id === tm.id) : null;
      if (track?.position?.length) {
        data.route = {
          // The item's base spot — the reference the paste-time shift uses.
          anchor: data.position || data.points?.[0] || null,
          position: JSON.parse(JSON.stringify(track.position)),
          segments: JSON.parse(JSON.stringify(track.segments || [])),
        };
      }
    }

    if (data) {
      setClipboard({ ...item, data });
      // Build readable name for toast — the same words the pitch and the menus
      // use, not a second English list kept beside them.
      const sideWord = capWord(t(item.side === 'home' ? 'side.home' : 'side.away'));
      let name = item.type;
      if (item.type === 'player') {
        const teamInfo = item.side === 'home' ? homeTeam : awayTeam;
        const pInfo = teamInfo?.playerInfo?.[item.index];
        name = pInfo?.name ? `${pInfo.name} (${sideWord})` : `${sideWord} #${item.index + 1}`;
      } else {
        name = getItemDisplayName(item);
      }
      const toastId = Date.now();
      // Announce the copy AND where it goes next: the clipboard is invisible, so
      // the toast is the only place that says a paste is now possible.
      setToast({ message: t('clip.copied', {
        name, route: data.route ? t('clip.route') : '',
        type: t(`itm.${item.type}`, {}).toLowerCase(),
      }), id: toastId });
      setTimeout(() => setToast(t => t?.id === toastId ? null : t), 2000);
    }
  }, [currentScenario, selectedItem, homeTeam, awayTeam, keyframes, itemTrackMeta, getItemDisplayName, t, capWord]);
  refItemVerbs.current.copy = handleCopy;

  // ── Paste: apply clipboard data to the layout phase (0) ──
  // TARGET-AWARE: with an item of the same type selected, the copied state
  // lands on the SELECTED item (the target keeps its identity — id / roster
  // slot); with no same-type selection it falls back to the copied item's own
  // slot. V1-LEGACY: paste targeted previous/current/next PHASES — with the
  // timeline model there is a single layout phase, so paste always applies there.
  const handlePaste = useCallback((explicit = null) => {
    if (!currentScenario || !clipboard) return;
    const phases = currentScenario.phases;
    if (!phases || phases.length === 0) return;
    // `explicit` = the item the CALLER named (the item menu pastes onto the
    // item whose menu it is). The chord keeps following the selection.
    const target = resolvePasteTarget(clipboard, explicit || selectedItem);
    if (!target) return;
    const label = target.type === 'player'
      ? `${capWord(t(target.side === 'home' ? 'side.home' : 'side.away'))} #${target.index + 1}`
      : getItemDisplayName(target);

    // ROUTE PASTE: a copied item WITH a movement route, pasted onto another
    // item of the SAME type, replays that run on the target — shifted so it
    // keeps the same relationship to the target's own spot (the intent is the
    // MOVEMENT, not the placement). Inherited locked keys are preserved.
    const route = clipboard.data?.route && target.type === clipboard.type ? clipboard.data.route : null;
    if (route) {
      // GROUP PASTE: if the target is part of a group, the run lands on EVERY
      // member of the same type — each shifted to its own spot. One copied
      // drill run, replicated across the whole unit in one paste.
      const inGroup = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, target));
      const targets = inGroup
        ? [target, ...groupItems.filter(g => g.type === target.type && !isSameGroupMember(g, target))]
        : [target];
      const plan = [];
      for (const tg of targets) {
        const tm = itemTrackMeta(tg);
        if (!tm) continue;
        const track = keyframes.find(t => t.id === tm.id);
        const lockedKeys = (track?.position || []).filter(k => isLockedKeyTime(k.time));
        const start = phases[0]?.start || {};
        let anchor = null;
        if (tg.type === 'player') {
          anchor = start[tg.side]?.players?.[tg.index]?.position || null;
        } else {
          const listKey = { ball: 'balls', cone: 'cones', target: 'targets', zone: 'zones' }[tg.type];
          const it = start[listKey]?.find(x => x.id === tg.id);
          anchor = it?.position || it?.points?.[0] || null;
        }
        if (!anchor && lockedKeys[0]?.position) anchor = lockedKeys[0].position;
        const shifted = shiftRoute(route, anchor, lockedKeys);
        plan.push({ tm, shifted, track });
      }
      // NO-OP ROUTE: pasting a run back onto the item it came from shifts to
      // the SAME anchor and lands every key exactly where it already is —
      // nothing changes, so nothing burns and nothing brags.
      if (plan.length && plan.every(p => p.track
        && JSON.stringify([p.shifted.position, p.shifted.segments || []])
          === JSON.stringify([p.track.position || [], p.track.segments || []]))) {
        setToast({ message: t('clip.routeSame', { label }), id: Date.now() });
        return;
      }
      pushUndo('undoVerb.pasteRoute');
      for (const p of plan) {
        setKeyframes(prev => {
          const exists = prev.find(t => t.id === p.tm.id);
          const updated = {
            ...(exists || { id: p.tm.id, ...p.tm.meta }),
            position: p.shifted.position,
            segments: p.shifted.segments,
          };
          return exists ? prev.map(t => (t.id === p.tm.id ? updated : t)) : [...prev, updated];
        });
      }
      setToast({ message: (targets.length > 1
        ? t('route.pastedMany', { n: targets.length })
        : t('route.pastedOne', { label }))
        // A route paste writes position keys on every member of the group, at
        // offsets — the one commit in the app whose result is a SET of keys rather
        // than one, and no entry in any card removes them together.
        + t('toast.undoSuffix'), id: Date.now() });
      return;
    }

    const prevStart = phases[0]?.start || createEmptyPitchState();
    const newStart = applyPasteToStart(
      JSON.parse(JSON.stringify(prevStart)),
      clipboard, target,
    );
    if (JSON.stringify(newStart) === JSON.stringify(prevStart)) {
      // Same position, same look, same everything — the paste changes
      // NOTHING, so it must not burn an undo step or claim credit.
      setToast({ message: t('clip.stateSame', { label }), id: Date.now() });
      return;
    }
    pushUndo('undoVerb.pasteItem');
    setScenario(prev => {
      if (!prev) return prev;
      const newPhases = [...prev.phases];
      const phase = newPhases[0];
      newPhases[0] = { ...phase, start: newStart };
      return { ...prev, phases: newPhases };
    });
    setToast({ message: t('clip.pasted', { label }) + t('toast.undoSuffix'), id: Date.now() });
  }, [currentScenario, clipboard, selectedItem, groupItems, setScenario, pushUndo, getItemDisplayName, keyframes, itemTrackMeta, t, capWord]);
  refItemVerbs.current.paste = handlePaste;

  /**
   * DUPLICATE (Ctrl+D): clone the selected ball / cone / target / zone in the
   * layout phase with a fresh sequential id, nudged clear of the source, and
   * select the copy. Players are not duplicable — copy + paste between roster
   * slots is the way to reuse a player's setup.
   */
  const handleDuplicateItem = useCallback((explicit = null) => {
    if (!currentScenario) return;
    if (!explicit && !selectedItem) return;
    // GROUP-AWARE: ⇧-select a cone shape (or a zone/target block) and Ctrl+D
    // copies the WHOLE thing at the offset — repeated drill structures in one
    // press instead of duplicating item by item. Players still cannot be
    // duplicated (slots are fixed); they are skipped and counted, not fatal.
    // `explicit` = the members the CALLER named (the item menu duplicates the
    // item whose menu it is — and the strip opens that menu WITHOUT selecting).
    const inGroup = !explicit && groupItems.length >= 2;
    const pool = explicit || (inGroup ? groupItems : [groupMemberOf(selectedItem)]);
    const members = pool.filter(g => g.type !== 'player');
    const skipped = pool.length - members.length;
    if (!members.length) {
      setToast({ message: t('toast.dupPlayerNo'), id: Date.now() });
      return;
    }
    const phases = currentScenario.phases;
    if (!phases || phases.length === 0) return;
    // Chain the clones: each one sees the previous result, so the ids count up
    // (Cone 04, 05, 06) instead of every copy landing on the same fresh id.
    let start = phases[0]?.start || createEmptyPitchState();
    const created = [];
    for (const g of members) {
      const result = duplicateItemInStart(start, g);
      if (!result) continue;
      start = result.start;
      created.push(result.item);
    }
    if (!created.length) return;
    pushUndo(created.length >= 2 ? 'undoVerb.duplicateGroup' : 'undoVerb.duplicateItem');
    setScenario(prev => {
      if (!prev) return prev;
      const newPhases = [...prev.phases];
      newPhases[0] = { ...newPhases[0], start };
      return { ...prev, phases: newPhases };
    });
    setSelectedItem(created[created.length - 1]);
    if (created.length >= 2) {
      // The copies arrive GROUPED, ready to nudge into place as one block —
      // which is what you almost always want right after duplicating a shape.
      setGroupItems(created);
    }
    const names = created.map(c => getItemDisplayName(c)).join(', ');
    setToast({
      message: (created.length >= 2
        ? t('clip.duplicatedN', { n: created.length, names })
        : t('clip.duplicatedOne', { names, d: DUPLICATE_OFFSET }))
        + (skipped ? t('clip.skippedPlayers', { n: skipped }) : ''),
      id: Date.now(),
    });
    // WHAT IT MADE, back to the caller — the item menu re-opens its card on the
    // copies instead of closing (see the ⧉ entry there).
    return created;
  }, [currentScenario, selectedItem, groupItems, setScenario, setGroupItems, pushUndo, setSelectedItem, getItemDisplayName, setToast, t]);
  // The item menu (declared above) reaches this verb through a ref — naming it
  // in that useCallback's deps would read the binding before it exists (TDZ).
  refItemVerbs.current.duplicate = handleDuplicateItem;

  // ═══════════════════════════════════════════════════════════════════
  // Keyboard shortcuts — the fast path for the whole editing loop.
  //   Ctrl/Cmd+Z undo · Ctrl/Cmd+Shift+Z (or Ctrl+Y) redo
  //   Ctrl/Cmd+C copy selected item · Ctrl/Cmd+V paste (onto the selected
  //   same-type item, else the source's own slot) · Ctrl/Cmd+D duplicate
  //   Delete / Backspace remove the selected item
  //   ← / → step the playhead one 0.25 s key-grid step (Shift = 1 s)
  //   Home / End jump to the branch start / end
  //   Escape dismisses the open prompt/editor, cancels a pending placement
  //   or a mode, or deselects
  //   While a central prompt is open: 1–9 choose its option, 0 cancels
  //   K key position · X action picker · G arrow · C segment shape ·
  //   T zone visuals · A auto-key · , / . walk keys · Alt+←/→ re-time ·
  //   1/2/3/F camera · V/H panels · Space play · +/− speed
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Keyboard branch jumps narrate their landings (the card row may be
    // off-screen): name where you landed and every way onward from here.
    const announceJump = (landed) => {
      const kids = sortedChildren(landed);
      const parent = findParent(committedTree(), landed.id);
      const sibs = (parent?.children || []).length;
      // Did landing here CHANGE the master path? Selecting a branch chooses it
      // for its sibling set (and every set above it) — the tutorial's rule, and
      // the one users trip on when a reloaded scenario "plays the wrong way".
      // Say so at the moment it happens, and name the way the path used to go.
      let pathNote = null;
      if (parent) {
        const set = sortedChildren(parent);
        if (set.length >= 2) {
          const prev = (refBranchChoices.current || {})[parent.id];
          const eff = prev || set[0]?.id; // no pick yet: the path followed order 1
          if (eff && eff !== landed.id) {
            const was = findBranch(committedTree(), eff)?.name;
            pathNote = was ? `⑂ path was ${was}` : '⑂ path re-routed';
          }
        }
      }
      setToast({ message: (pathNote
          ? t('toast.landedPath', { name: landed.name, path: pathNote })
          : t('toast.landed', { name: landed.name }))
        + (pathNote ? '' : (kids.length ? t('toast.landedKids', { n: kids.length }) : t('toast.landedAddBranch')))
        + (parent ? (sibs >= 2 ? t('toast.landedUpMany') : t('toast.landedUpOne')) : t('toast.landedWalkIn')),
        id: Date.now() });
    };
    const onKey = (e) => {
      // Named, not `t`: the whole keyboard handler is one function body, and a
      // local called `t` would shadow the i18n translator for every chord toast
      // in it (t('toast.attachedTo', …) would call e.target instead).
      const evTarget = e.target;
      if (evTarget && (evTarget.tagName === 'INPUT' || evTarget.tagName === 'TEXTAREA' || evTarget.isContentEditable)) {
        // Escape is never typing: a focused SLIDER (SegmentModal auto-focuses
        // its first one) must still be able to close its editor via the
        // cascade below. Text inputs keep the full guard.
        if (!(e.key === 'Escape' && evTarget.tagName === 'INPUT' && evTarget.type === 'range')) return;
      }
      // A full-screen modal owns the keyboard — don't edit the scene behind it.
      // Escape closes it (innermost first).
      if (showLoginModal || showScenarioModal || showPublicModal || teamModalTarget || showAccountModal || showTeamEditor || showHelpModal || infoModal || confirmState || nameState) {
        if (e.key === 'Escape') {
          if (infoModal) setInfoModal(null);
          else if (showHelpModal) setShowHelpModal(false);
          else if (showTeamEditor) setShowTeamEditor(false);
          else if (showAccountModal) setShowAccountModal(false);
          else if (teamModalTarget) setTeamModalTarget(null);
          else if (showPublicModal) setShowPublicModal(false);
          else if (showScenarioModal) setShowScenarioModal(false);
          else if (showLoginModal) setShowLoginModal(false);
        }
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      // VERTEX TWIN: if the pitch zone-editor has a polygon vertex grabbed,
      // Ctrl+arrows nudge that vertex by step metres (dx, dz) instead of
      // dialing radius/height. Works with or without the visual modal open —
      // the window is found at the playhead, exactly like the radius dial.
      // Returns true when it consumed the key.
      const nudgeZoneVertex = (dirX, dirZ, step, trackId) => {
        const vIdx = refZonePointSelected.current;
        if (vIdx === null || !selectedItem || selectedItem.type !== 'zone') return false;
        const vTrack = keyframes.find(t => t.id === trackId);
        const vActive = [...(vTrack?.visual || [])].sort((a, b) => a.time - b.time)
          .filter(kk => kk.time <= refPlayheadTime.current + 1e-6).pop();
        const vPts = vActive?.value?.points;
        if (!Array.isArray(vPts) || !Array.isArray(vPts[vIdx])) return false;
        const vKeyIndex = (vTrack.visual || []).findIndex(kk => kk.time === vActive.time);
        if (vKeyIndex < 0) return false;
        const vNew = vPts.map((p, i) => i === vIdx
          ? [p[0] + dirX * step, p[1] ?? 0, p[2] + dirZ * step]
          : p);
        handleDragStart('undoVerb.zoneShape');
        handleZoneVisualChange(trackId, vKeyIndex, { points: vNew });
        handleDragEnd();
        setToast({ message: t('vertex.nudged', { n: vIdx + 1, step }) + t('toast.undoSuffix'), id: Date.now() });
        return true;
      };
      // VERTEX TWIN (2): the EDIT ZONE panel's "+ Add" and "− Remove" tools, on
      // the keyboard. The panel makes you switch TOOL and then click the pitch;
      // a GRABBED vertex is already the thing you mean, so ⇧A inserts the next
      // vertex at its edge midpoint and Del removes it — reshape without ever
      // leaving the keyboard. Refs travel with the shape (insert a free one,
      // remove the one at the same index) exactly like the pitch tools do.
      const editZoneVertex = (mode, trackId) => {
        const vIdx = refZonePointSelected.current;
        if (vIdx === null || !selectedItem || selectedItem.type !== 'zone') return false;
        const zt = keyframes.find(t => t.id === trackId);
        const zAct = [...(zt?.visual || [])].sort((a, b) => a.time - b.time)
          .filter(kk => kk.time <= refPlayheadTime.current + 1e-6).pop();
        if (!zAct) { setToast({ message: t('vertex.noWindow'), id: Date.now() }); return true; }
        if (isLockedKeyTime(zAct.time)) { refuseLockedKey(); return true; }
        const zKeyIndex = (zt.visual || []).findIndex(kk => kk.time === zAct.time);
        if (zKeyIndex < 0) return false;
        const pts = Array.isArray(zAct.value?.points) ? zAct.value.points : [];
        const refs = Array.isArray(zAct.value?.pointRefs) ? zAct.value.pointRefs : pts.map(() => ({ ref: null }));
        if (mode === 'remove') {
          if (pts.length <= 2) {
            setToast({ message: t('vertex.minTwo'), id: Date.now() });
            return true;
          }
          const np = pts.filter((_, i) => i !== vIdx);
          const nr = refs.filter((_, i) => i !== vIdx);
          handleDragStart('undoVerb.zoneVertexRemove');
          handleZoneVisualChange(trackId, zKeyIndex, { points: np, pointRefs: nr });
          handleDragEnd();
          // Keep the edit hot: the vertex that TOOK its place stays grabbed, so
          // Del can chain down the shape without re-grabbing anything.
          setZoneEditRequest({ zoneId: selectedItem.id, ts: Date.now(), selectPoint: Math.min(vIdx, np.length - 1) });
          // NOT the shared ↩ sentence: this is the KEYBOARD half of a deliberate
          // twin. announceZoneVertex (the panel's own buttons) says "the ↩ button
          // puts it back" through vertex.removedTouch, and this one — fired from
          // ⇧A / Del — names the chord the hand that pressed it actually has.
          // Pinning it both ways is what keeps a phone from being told about Ctrl
          // and a keyboard from being told to hunt for a button it never reaches
          // for.
          setToast({ message: t('vertex.removed', { n: vIdx + 1, count: np.length }), id: Date.now() });
          return true;
        }
        if (!pts.length) return false;
        // Insert at the MIDPOINT of the edge from the grabbed vertex to the
        // next one — deterministic, no pitch click needed, and the new vertex
        // arrives GRABBED so Ctrl+arrows push it straight out to the spot you
        // actually want.
        const j = (vIdx + 1) % pts.length;
        const a = pts[vIdx], b = pts[j];
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        const mid = [(a[0] + b[0]) / 2, a[1] ?? 0, (a[2] + b[2]) / 2];
        const np = [...pts.slice(0, j), mid, ...pts.slice(j)];
        const nr = [...refs.slice(0, j), { ref: null }, ...refs.slice(j)];
        handleDragStart('undoVerb.zoneVertexAdd');
        handleZoneVisualChange(trackId, zKeyIndex, { points: np, pointRefs: nr });
        handleDragEnd();
        setZoneEditRequest({ zoneId: selectedItem.id, ts: Date.now(), selectPoint: j });
        setToast({ message: t('vertex.added', { n: j + 1 }), id: Date.now() });
        return true;
      };
      if (mod) {
        if (k === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
        else if (k === 'z' && e.shiftKey) { e.preventDefault(); handleRedo(); }
        else if (k === 'y') { e.preventDefault(); handleRedo(); }
        // Shift variants FIRST: Ctrl+Shift+C/V is the BRANCH-timeline pair —
        // it must win over the plain item-copy/paste bindings below, or a
        // selected item would silently steal it.
        else if (e.shiftKey && k === 'c') { e.preventDefault(); handleCopyBranchTimeline(currentBranchId); }
        else if (e.shiftKey && k === 'v') { e.preventDefault(); handlePasteBranchTimeline(currentBranchId); }
        else if (k === 'c' && selectedItem) { e.preventDefault(); handleCopy(); }
        else if (k === 'v' && clipboard) { e.preventDefault(); handlePaste(); }
        else if (k === 'd' && selectedItem) { e.preventDefault(); handleDuplicateItem(); }
        // Ctrl/Cmd+S quick-save (beats the browser's Save page dialog);
        // Ctrl/Cmd+O opens the scenario browser.
        else if (k === 's') { e.preventDefault(); handleSaveScenario(); }
        // Ctrl+N: new scenario — File menu parity. handleNewScenario is
        // defined below this effect (referenced via closure, NOT in deps — TDZ).
        else if (k === 'n') { e.preventDefault(); handleNewScenario(); }
        else if (k === 'o') { e.preventDefault(); handleLoadScenarios(); }
        // Ctrl+↑ / Ctrl+↓: raise / lower the HEIGHT of the selected item's
        // position key at (or just before) the playhead — the height editor's
        // slider on the keyboard. 0.5 m steps, 0.1 m with Shift, 0–10 m clamp.
        // CONTEXT SWAP: while a polygon-zone VERTEX is grabbed on the pitch,
        // these nudge THAT VERTEX instead (the vertex-drag keyboard twin).
        else if (k === 'arrowup' || k === 'arrowdown') {
          e.preventDefault();
          if (!selectedItem) { setToast({ message: t('toast.needItemHeight'), id: Date.now() }); return; }
          const hTrackId = selectedItem.type === 'player'
            ? `${selectedItem.side}:${selectedItem.index}`
            : `${selectedItem.type}:${selectedItem.id}`;
          if (nudgeZoneVertex(0, k === 'arrowup' ? 1 : -1, e.shiftKey ? 0.05 : 0.25, hTrackId)) return;
          handleHeightStep(hTrackId, k === 'arrowup' ? 1 : -1, e.shiftKey ? 0.1 : 0.5);
          return;
        }
        // Ctrl+[ / Ctrl+]: fine-tune the BEND AMOUNT of the movement segment
        // under the playhead (±0.1, Shift ±0.02) — the segment editor's curve
        // slider on the keyboard. C flips the side, these dial the depth.
        // With ⌥ added (⌥⌃[ / ⌥⌃]) they dial the ARCH PEAK instead — where
        // along the flight the highest point happens. Three dials, one key
        // family: bend depth, loft height (⌥[ ]), peak position (⌥⌃[ ]).
        // Matched by CODE as well as key: some layouts type something else
        // while Alt is held, and the physical key is what the user pressed.
        else if (k === '[' || k === ']' || e.code === 'BracketLeft' || e.code === 'BracketRight') {
          e.preventDefault();
          if (!selectedItem) { setToast({ message: t('toast.needItemBend'), id: Date.now() }); return; }
          const bTrackId = selectedItem.type === 'player'
            ? `${selectedItem.side}:${selectedItem.index}`
            : `${selectedItem.type}:${selectedItem.id}`;
          const bTrack = keyframes.find(t => t.id === bTrackId);
          const bPos = [...(bTrack?.position || [])].sort((a, b) => a.time - b.time);
          const bT0 = refPlayheadTime.current;
          let bFrom = null, bTo = null;
          for (let i = 0; i < bPos.length - 1; i++) {
            if (bPos[i].time <= bT0 + 0.001 && bPos[i + 1].time >= bT0 - 0.001) { bFrom = bPos[i].time; bTo = bPos[i + 1].time; break; }
          }
          if (bFrom === null || bFrom === bTo) { setToast({ message: t('kf.noSegment'), id: Date.now() }); return; }
          const bCur = findSegment(bTrack, bFrom, bTo);
          const bDir = (e.code === 'BracketRight' || k === ']') ? 1 : -1;
          if (e.altKey) {
            // The peak only exists on a lofted segment — dialing it on a flat
            // one would change nothing visible, so say what to press first.
            if ((bCur.arch ?? 0) <= 0) {
              setToast({ message: t('loft.noLoft'), id: Date.now() });
              return;
            }
            const pStep = e.shiftKey ? 0.02 : 0.1;
            const pPeak = Math.max(0, Math.min(1, (bCur.archPeak ?? 0.5) + bDir * pStep));
            handleDragStart('undoVerb.archPeak');
            upsertSegment(bTrackId, bFrom, bTo, { ...DEFAULT_SEGMENT, ...bCur, archPeak: pPeak });
            handleDragEnd();
            setToast({ message: t('key.peakAt', {
              pct: Math.round(pPeak * 100), from: fmtTime(bFrom), to: fmtTime(bTo),
            }) + t('toast.undoSuffix'), id: Date.now() });
            return;
          }
          const bStep = e.shiftKey ? 0.02 : 0.1;
          const bAmount = Math.max(0.05, Math.min(1, (bCur.curve ?? 0) + bDir * bStep));
          const bStyle = bCur.style === 'straight' ? 'curveRight' : bCur.style;
          handleDragStart('undoVerb.bendAmount');
          upsertSegment(bTrackId, bFrom, bTo, { ...DEFAULT_SEGMENT, ...bCur, curve: bAmount, style: bStyle });
          handleDragEnd();
          setToast({ message: t('key.bendAt', {
            pct: Math.round(bAmount * 100), from: fmtTime(bFrom), to: fmtTime(bTo),
          }) + t('toast.undoSuffix'), id: Date.now() });
          return;
        }
        // Ctrl+← / Ctrl+→: dial the RADIUS of the circle-zone visual window
        // at (or just before) the playhead — the zone editor's radius slider
        // on the keyboard. 0.5 m steps, 0.1 m with Shift. CONTEXT SWAP: while
        // a polygon-zone VERTEX is grabbed, these nudge THAT VERTEX.
        else if (k === 'arrowleft' || k === 'arrowright') {
          e.preventDefault();
          if (!selectedItem) {
            setToast({ message: t('toast.needItemSlide'), id: Date.now() });
            return;
          }
          const zTrackId = `zone:${selectedItem.id}`;
          if (nudgeZoneVertex(k === 'arrowright' ? 1 : -1, 0, e.shiftKey ? 0.05 : 0.25, zTrackId)) return;
          if (selectedItem.type !== 'zone') {
            // NON-ZONE: these arrows used to just say 'Radius is for zones'.
            // That refusal was a dead end — the same chord now SLIDES the
            // item's spot end↔end, the keyboard twin of dragging it, at the
            // same 0.5 / 0.1 m steps as the height dial. With a group active
            // the WHOLE block slides, because dragging one member moves them
            // all.
            const xGroup = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
            const xItems = xGroup ? groupItems.filter(g => g.type !== 'zone') : [selectedItem];
            handleGroupXZNudge(xItems, (k === 'arrowright' ? 1 : -1) * (e.shiftKey ? 0.1 : 0.5), 0);
            return;
          }
          const zTrack = keyframes.find(t => t.id === zTrackId);
          const zActive = [...(zTrack?.visual || [])].sort((a, b) => a.time - b.time)
            .filter(kk => kk.time <= refPlayheadTime.current + 1e-6).pop();
          if (!zActive || !zActive.value || zActive.value.mode !== 'circle') {
            // POLYGON window with nothing grabbed: grab VERTEX 1 right now —
            // the next Ctrl+arrow nudges it. Discoverable without needing to
            // land a pixel-perfect pitch grab on the first try.
            const zPts = zActive?.value?.points;
            if (Array.isArray(zPts) && zPts.length) {
              e.preventDefault();
              setZoneEditRequest({ zoneId: selectedItem.id, ts: Date.now(), selectPoint: 0 });
              setToast({ message: t('vertex.grabbedOf', { count: zPts.length }), id: Date.now() });
              return;
            }
            setToast({ message: t('vis.noCircleWindow'), id: Date.now() });
            return;
          }
          const zStep = e.shiftKey ? 0.1 : 0.5;
          const zR = Math.max(0.5, Math.min(50, (zActive.value.radius ?? 3) + (k === 'arrowright' ? zStep : -zStep)));
          handleDragStart('undoVerb.zoneRadius');
          setKeyframes(prev => prev.map(t => t.id !== zTrackId ? t : {
            ...t,
            visual: (t.visual || []).map(kk => kk.time === zActive.time ? { ...kk, value: { ...kk.value, radius: zR } } : kk),
          }));
          handleDragEnd();
          setToast({ message: t('key.zoneRadius', { r: zR.toFixed(1), t: fmtTime(zActive.time) }) + t('toast.undoSuffix'), id: Date.now() });
          return;
        }
        return;
      }
      // While a prompt is open, BRANCH NAVIGATION, PLACEMENT ARMING and ALL
      // DELETES pause: the prompt's targets live on THIS branch's timeline, an
      // armed placement would eat the pitch click the prompt is waiting for,
      // and deleting (item, group, branch) can remove a target out from under
      // the confirm. Priority (Alt+Shift+↑↓) stays allowed — it re-orders the
      // fork without touching any target. Answer first: 1 accepts · 0/Esc cancels.
      // (Placed BEFORE every delete/navigation block — chain position is
      // semantics: this sits ahead of Delete, the sibling walk and the tree.)
      if (promptState && !mod && (
        e.key === 'Delete' || e.key === 'Backspace' ||
        (e.shiftKey && !e.altKey && (
          e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === '{' || e.key === '}'
          || /^Digit[1-9]$/.test(e.code || ''))) ||
        (e.altKey && !e.shiftKey && (e.key === 'ArrowUp' || /^[1-9]$/.test(e.key)))
      )) {
        e.preventDefault();
        setToast({ message: t('prompt.answerFirst'), id: Date.now() });
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // HEIGHT EDITOR open: Del deletes the KEY being edited, not the item.
        // Pressing Del inside a key editor clearly means "this key" — falling
        // through to item delete was a destructive surprise. (Locked keys
        // refuse inside handleKeyframeDelete, editor stays open.)
        if (!mod && !e.altKey && !e.shiftKey && keyframeEdit) {
          e.preventDefault();
          const delTime = keyframeEdit.time;
          if (isLockedKeyTime(delTime)) { refuseLockedKey(); return; } // editor stays open
          handleDragStart('undoVerb.deleteKey');
          // NO toast here any more. handleKeyframeDelete announces the delete
          // itself — it is the only place that knows whether it took one key
          // or the whole moment, and whether it took anything at all. A
          // pre-toast here announced "Key at X deleted" even when nothing sat
          // at X, and then the handler's own toast overwrote it: two
          // announcements, one of them a lie.
          handleKeyframeDelete(keyframeEdit.trackId, 'position', delTime);
          handleDragEnd();
          setKeyframeEdit(null);
          return;
        }
        // GRAPHIC editor open: Del deletes the window's ON key (the whole
        // arrow window goes — graphic keys had no keyboard delete before).
        if (!mod && !e.altKey && !e.shiftKey && graphicEdit) {
          e.preventDefault();
          const gStart = graphicEdit.start;
          if (isLockedKeyTime(gStart)) { refuseLockedKey(); return; }
          handleDragStart('undoVerb.deleteKey');
          handleKeyframeDelete(graphicEdit.trackId, 'graphic', gStart);
          handleDragEnd();
          setGraphicEdit(null);
          setToast({ message: t('key.arrowDeleted', { t: fmtTime(gStart) }), id: Date.now() });
          return;
        }
        // ZONE EDITOR open with a vertex GRABBED: Del removes THAT VERTEX, not
        // the zone — the same context swap as the height/graphic editors, and
        // the pitch panel's "− Remove" tool without the tool switch.
        if (!mod && !e.altKey && !e.shiftKey && selectedItem?.type === 'zone'
            && refZonePointSelected.current !== null) {
          e.preventDefault();
          editZoneVertex('remove', `zone:${selectedItem.id}`);
          return;
        }
        // Alt+Del: REMOVE the current BRANCH and everything under it (the
        // card's ✕) — checked FIRST so it never falls through to item delete.
        // The root branch is protected.
        if (e.altKey && !mod) {
          e.preventDefault();
          if (!findParent(committedTree(), currentBranchId)) {
            setToast({ message: t('toast.rootTrunk'), id: Date.now() });
            return;
          }
          handleRemoveBranch(currentBranchId);
          setToast({ message: t('toast.branchDeleted'), id: Date.now() });
          return;
        }
        // Shift+Del: delete the WHOLE group at once (players return to the
        // bench, items removed) — one undo step, count toast. The verb itself
        // lives in handleDeleteGroup so the item menu runs the SAME copy.
        if (e.shiftKey && !mod && !e.altKey && selectedItem
            && groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem))) {
          e.preventDefault();
          handleDeleteGroup();
          return;
        }
        if (selectedItem) { e.preventDefault(); handleRemoveSelectedItem(); }
        return;
      }
      // + / = faster · - / _ slower — the shared playback speed, no menu needed.
      if (!mod && !e.altKey && (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd')) {
        e.preventDefault();
        const sp = cyclePlaybackSpeed(1);
        setToast({ message: sp ? t('speed.at', { r: sp }) : t('speed.top'), id: Date.now() });
        return;
      }
      if (!mod && !e.altKey && (e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract')) {
        e.preventDefault();
        const sp = cyclePlaybackSpeed(-1);
        setToast({ message: sp ? t('speed.at', { r: sp }) : t('speed.floor'), id: Date.now() });
        return;
      }
      // 0 — back to NORMAL speed in one press. Skipped while a prompt is
      // open: 0 is the prompt's cancel key there.
      if (!mod && !e.altKey && !promptState && (e.key === '0' || e.code === 'Digit0')) {
        e.preventDefault();
        if (playbackSpeedRef.current === 1) {
          setToast({ message: t('play.normalAlready'), id: Date.now() });
          return;
        }
        changePlaybackSpeed(1);
        setToast({ message: t('play.atOneX'), id: Date.now() });
        return;
      }
      // [ / ]: RIPPLE — slide everything AFTER the playhead ±0.25 s. The
      // playhead is a cut; the sibling of ⌥+/− for local retiming, and ⌥drag
      // on a key is its mouse twin. Matched by CODE (⇧ types { } on a US
      // layout, and ⇧ [ ] belongs to the sibling-walk block below).
      // [ / ] — RIPPLE everything after the playhead (the ⌥drag twin). The
      // BIG step is ⌥⇧[ / ] , NOT ⇧[ ]: shift alone types { } on a US layout,
      // and ⇧ [ ] is the documented WALK-THE-FORK verb (sibling branches).
      // Taking it was a drift — the helpChords guard now pins this split.
      if (!mod && e.altKey && e.shiftKey && (e.code === 'BracketLeft' || e.code === 'BracketRight')) {
        e.preventDefault();
        handleRippleKeys((e.code === 'BracketRight' ? 1 : -1) * 1);
        return;
      }
      if (!mod && !e.altKey && !e.shiftKey && (e.code === 'BracketLeft' || e.code === 'BracketRight')) {
        e.preventDefault();
        handleRippleKeys((e.code === 'BracketRight' ? 1 : -1) * (e.shiftKey ? 1 : 0.25));
        return;
      }
      // ⌥⇧+ / ⌥⇧−: the branch LENGTH on the keyboard — the twin of the TIME
      // box and the ruler edge. This changes the WINDOW, not the keys: ⌥+/−
      // scales every key inside it. Padding is how you make room to key
      // something LATER without opening the timeline at all; trimming is the
      // same control in reverse (⇧K clips to the last key exactly).
      if (!mod && e.altKey && e.shiftKey && (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd'
          || e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract')) {
        const grow = e.key === '+' || e.key === '=' || e.code === 'NumpadAdd';
        const oldDur = refTimelineDurationState.current ?? DEFAULT_BRANCH_DURATION;
        const newDur = Math.round(Math.max(0, oldDur + (grow ? 1 : -1)) * 4) / 4;
        e.preventDefault();
        if (newDur < 1) {
          setToast({ message: t('branch.shortest'), id: Date.now() });
          return;
        }
        if (newDur > 600) {
          setToast({ message: t('branch.longest'), id: Date.now() });
          return;
        }
        const lastT = lastKeyTime(keyframes);
        if (!grow && lastT > newDur + 1e-9) {
          // Trimming past a key would DELETE work the user never asked to lose.
          setToast({ message: t('branch.lastKeyAt', { t: fmtTime(lastT) }), id: Date.now() });
          return;
        }
        handleDurationChange(newDur);
        setToast({ message: t('branch.nowDur', {
          t: fmtTime(newDur), grow: grow ? t('branch.growNote') : '',
        }), id: Date.now() });
        return;
      }
      // Alt + / Alt −: STRETCH or COMPRESS the whole branch timeline — every
      // key of every kind ×1.25 / ×0.8, snapped to the 0.25 s grid. "Same
      // move, played slower" without touching a single keyframe. The verb
      // itself lives in handleStretchKeys, so the ruler's timing menu runs the
      // SAME copy (long-press the ruler with a finger).
      if (!mod && e.altKey && (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd'
          || e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract')) {
        e.preventDefault();
        const stretch = e.key === '+' || e.key === '=' || e.code === 'NumpadAdd';
        handleStretchKeys(stretch ? 1.25 : 0.8);
        return;
      }
      // Alt+[ / Alt]: dial the LOFT (arch height) of the ball segment under
      // the playhead, ±0.5 m — the violet handle's keyboard twin. ⇧C toggles
      // a 3 m loft on/off; these dial it precisely.
      if (!mod && e.altKey && !e.shiftKey && (e.key === '[' || e.key === ']')) {
        e.preventDefault();
        if (!selectedItem || selectedItem.type !== 'ball') {
          setToast({ message: t('toast.needBallLoft'), id: Date.now() });
          return;
        }
        const aTrackId = `ball:${selectedItem.id}`;
        const aTrack = keyframes.find(t => t.id === aTrackId);
        const aPos = [...(aTrack?.position || [])].sort((a, b) => a.time - b.time);
        const aT0 = refPlayheadTime.current;
        let aFrom = null, aTo = null;
        for (let i = 0; i < aPos.length - 1; i++) {
          if (aPos[i].time <= aT0 + 0.001 && aPos[i + 1].time >= aT0 - 0.001) { aFrom = aPos[i].time; aTo = aPos[i + 1].time; break; }
        }
        if (aFrom === null || aFrom === aTo) { setToast({ message: t('kf.noBallSegment'), id: Date.now() }); return; }
        const aCur = findSegment(aTrack, aFrom, aTo);
        const aOld = aCur.arch ?? 0;
        const aArch = Math.max(0, Math.min(10, aOld + (e.key === ']' ? 0.5 : -0.5)));
        if (aArch === aOld) {
          // At the clamp edge the dial cannot move — refuse without burning
          // an undo step or toasting a change that never happened.
          setToast({ message: aOld <= 0
            ? t('loft.alreadyFlat')
            : t('loft.max'), id: Date.now() });
          return;
        }
        handleDragStart('undoVerb.loftHeight');
        upsertSegment(aTrackId, aFrom, aTo, { ...DEFAULT_SEGMENT, ...aCur, arch: aArch });
        handleDragEnd();
        setToast({ message: (aArch > 0
          ? t('loft.on', { m: aArch.toFixed(1), from: fmtTime(aFrom), to: fmtTime(aTo) })
          : t('loft.flatNow', { from: fmtTime(aFrom), to: fmtTime(aTo) })) + t('toast.undoSuffix'), id: Date.now() });
        return;
      }
      // S: LAND ON THE SAME SECOND — the selected item's key nearest the
      // playhead jumps to the nearest instant another track already keys.
      // targets.ejs' timing exercise in one press, no timeline strip. (Plain S
      // only: ⌥⇧S is select-the-kind, ⌘S/Ctrl+S is save — both matched above.)
      if (!mod && !e.altKey && !e.shiftKey && e.code === 'KeyS') {
        e.preventDefault();
        handleLandOnNearestKeyTime();
        return;
      }
      // Alt+←/→ nudge the selected item's nearest movement key in time
      // (Shift = 1 s) — timing edits without touching the timeline strip.
      if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        handleNudgeSelectedKey((e.shiftKey ? 1 : 0.25) * (e.key === 'ArrowLeft' ? -1 : 1));
        return;
      }
      // Shift+[ / Shift+] — WALK the fork: jump to the previous / next
      // SIBLING branch (same fork, side-by-side variants). Comparing two
      // finishes of the same build-up is two taps of Shift+], no menus,
      // no remembering branch numbers.
      if (!mod && !e.altKey && (e.key === '{' || e.key === '}')) {
        e.preventDefault();
        const dir = e.key === '{' ? -1 : 1;
        const tree = committedTree();
        const parent = findParent(tree, currentBranchId);
        if (!parent || (parent.children || []).length < 2) {
          setToast({ message: t('toast.noSiblings'), id: Date.now() });
          return;
        }
        const sibs = (parent.children || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const i = sibs.findIndex(b => b.id === currentBranchId);
        const j = i + dir;
        if (j < 0 || j >= sibs.length) {
          setToast({ message: dir > 0 ? t('tree.lastSibling') : t('tree.firstSibling'), id: Date.now() });
          return;
        }
        handleSelectBranch(sibs[j].id);
        announceJump(sibs[j]);
        return;
      }
      // , / . — jump the playhead to the PREVIOUS / NEXT keyframe. With a
      // selection it walks that item's own track; falling back to any track
      // when the selection has no key in that direction. The landing toast
      // names the LANE (move/arrow/shape/attach/action) and teaches Enter —
      // the core loop: walk, then edit what you landed on.
      // Brackets are NOT aliases here: plain [ / ] RIPPLES (bound above at the
      // handleRippleKeys block, and ⌥drag is its mouse twin). They used to be
      // duplicate jump keys; the earlier binding shadows them, so re-adding
      // them here would be dead code that reads as live.
      if (!mod && !e.altKey && (e.key === ',' || e.key === '.')) {
        const dir = (e.key === ',') ? -1 : 1;
        const trackId = selectedItem
          ? (selectedItem.type === 'player' ? `${selectedItem.side}:${selectedItem.index}` : `${selectedItem.type}:${selectedItem.id}`)
          : null;
        let target = nearestKeyTime(keyframes, refPlayheadTime.current, dir, trackId);
        if (target === null && trackId) target = nearestKeyTime(keyframes, refPlayheadTime.current, dir, null);
        if (target === null) {
          // End of the walk — explain instead of dying silently.
          setToast({ message: dir < 0
            ? t('kf.walkBackNone')
            : t('kf.walkFwdNone'), id: Date.now() });
          return;
        }
        e.preventDefault();
        const wTime = Math.max(0, Math.min(refTimelineDurationState.current, target));
        handleScrub(wTime);
        const wNames = {
          position: t('lane.move'), graphic: t('lane.arrow'), visual: t('lane.shape'),
          attach: t('lane.attach'), action: t('lane.action'),
        };
        let wLane = null;
        const wOrder = trackId
          ? [trackId, ...keyframes.map(t => t.id).filter(id => id !== trackId)]
          : keyframes.map(t => t.id);
        for (const id of wOrder) {
          const tr = keyframes.find(t => t.id === id);
          if (!tr) continue;
          for (const lane of ['position', 'graphic', 'visual', 'attach', 'action']) {
            if ((tr[lane] || []).some(k => Math.abs(k.time - target) < 1e-6)) { wLane = lane; break; }
          }
          if (wLane) break;
        }
        if (wLane) setToast({ message: t('lane.keyAt', { lane: wNames[wLane], t: fmtTime(wTime) }), id: Date.now() });
        return;
      }
      // Shift+K: CLIP the current branch's timeline to its last keyframe —
      // the tree card's ✂ button, reachable from the pitch. Must come BEFORE
      // plain K because Shift makes e.key 'K'.
      if (!mod && !e.altKey && e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        handleClipBranchTime();
        return;
      }
      // K: key the selected item/player's CURRENT position at the playhead —
      // the single most common timeline action, now one keystroke on the pitch.
      // With a drag group active, K keys the WHOLE group at once. Thin wrapper:
      // the rules live in handleKeyAtPlayhead — one copy of the group fan-out
      // and the carrier refusal, whether K or AUTO wrote the key.
      if (!mod && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        if (!selectedItem) return;
        e.preventDefault();
        handleKeyAtPlayhead();
        return;
      }
      // Enter: open the EDITOR for the selected item's context at the playhead —
      // the position KEY if the playhead sits on one (keyframe editor, the
      // double-click-diamond target), otherwise the SEGMENT under it (the
      // transition editor). Pair with , / . to walk keys, Enter to edit.
      if (!mod && !e.altKey && !e.shiftKey && e.key === 'Enter') {
        // A focused control eats Enter natively (prompt button = answer, text
        // field = commit) — same yield rule as Space.
        const ae = document.activeElement;
        if (ae && ['BUTTON', 'SELECT', 'A', 'INPUT', 'TEXTAREA'].includes(ae.tagName)) return;
        if (!selectedItem) return;
        e.preventDefault();
        const eTrackId = selectedItem.type === 'player'
          ? `${selectedItem.side}:${selectedItem.index}`
          : `${selectedItem.type}:${selectedItem.id}`;
        const eTrack = keyframes.find(t => t.id === eTrackId);
        const eT0 = refPlayheadTime.current;
        const ePos = [...(eTrack?.position || [])].sort((a, b) => a.time - b.time);
        const eActive = ePos.filter(kk => kk.time <= eT0 + 1e-6).pop();
        if (eActive && Math.abs(eActive.time - eT0) < 0.001) {
          handleKeyframeClick(eTrackId, eActive.time);
          return;
        }
        let eFrom = null, eTo = null;
        for (let i = 0; i < ePos.length - 1; i++) {
          if (ePos[i].time <= eT0 + 0.001 && ePos[i + 1].time >= eT0 - 0.001) { eFrom = ePos[i].time; eTo = ePos[i + 1].time; break; }
        }
        if (eFrom !== null && eFrom !== eTo) handleSegmentClick(eTrackId, eFrom, eTo);
        else {
          // No position key/segment there — but an ARROW WINDOW may be: the
          // graphic-lane bar's Enter twin (ON key → next graphic key or the
          // timeline end) opening the arrow-colour editor.
          const eGfx = [...(eTrack?.graphic || [])].sort((a, b) => a.time - b.time);
          const gOn = eGfx.filter(kk => kk.time <= eT0 + 1e-6 && kk.value === 'on').pop();
          const gEnd = gOn ? (eGfx.find(kk => kk.time > gOn.time + 1e-6)?.time ?? (refTimelineDurationState.current ?? 0)) : 0;
          if (gOn && eT0 < gEnd - 1e-6) {
            handleGraphicSegClick(eTrackId, gOn.time, gEnd);
            return;
          }
          // ZONE twin: playhead inside a VISUALS window (zones are often
          // visual-only — no position keys at all) — the strip's window-bar
          // Enter twin, opening the radius/shape editor for that window.
          if (selectedItem.type === 'zone') {
            const zVk = [...(eTrack?.visual || [])].sort((a, b) => a.time - b.time);
            const zIdx = zVk.reduce((acc, kk, i) => (kk.time <= eT0 + 1e-6 ? i : acc), -1);
            if (zIdx >= 0) {
              handleZoneVisualSegClick(eTrackId, zIdx);
              return;
            }
          }
          // Guide honestly: if the item HAS keys, the fix is WALKING to them
          // (, .) — not keying a new one at the playhead with K.
          const eAnyKey = ['position', 'graphic', 'visual', 'attach']
            .reduce((n, lane) => n + (eTrack?.[lane]?.length || 0), 0);
          setToast({ message: eAnyKey
            ? t('kf.nothingExact')
            : t('kf.noneYet'), id: Date.now() });
        }
        return;
      }
      // C: cycle the movement shape of the segment UNDER the playhead for the
      // selected item — straight → curve left → curve right. The transition
      // modal's core use (bending a run or a pass) without opening anything.
      // Shift+C on a BALL segment toggles the ARCH (3 m loft) — the violet
      // flight handle appears immediately for pulling the height by hand.
      if (!mod && !e.altKey && (e.key === 'c' || e.key === 'C')) {
        if (!selectedItem) return;
        const trackId = selectedItem.type === 'player'
          ? `${selectedItem.side}:${selectedItem.index}`
          : `${selectedItem.type}:${selectedItem.id}`;
        const track = keyframes.find(t => t.id === trackId);
        const posKeys = [...(track?.position || [])].sort((a, b) => a.time - b.time);
        const t0 = refPlayheadTime.current;
        let from = null, to = null;
        for (let i = 0; i < posKeys.length - 1; i++) {
          if (posKeys[i].time <= t0 + 0.001 && posKeys[i + 1].time >= t0 - 0.001) {
            from = posKeys[i].time; to = posKeys[i + 1].time; break;
          }
        }
        const inGroupSeg = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
        if (inGroupSeg && !e.shiftKey) {
          // GROUP BEND, through the SAME verb the item menu calls: the chord and
          // the menu are two doors, not two implementations. Checked BEFORE the
          // single-member refusal — the group may have segments even when the
          // selected member has none.
          e.preventDefault();
          handleBendCycleGroup(groupItems);
          return;
        }
        if (from === null || to === null || from === to) {
          // Silent refusal was a dead key — explain and point at the fix.
          e.preventDefault();
          setToast({ message: t('bend.noneHere'), id: Date.now() });
          return;
        }
        e.preventDefault();
        if (e.shiftKey) {
          if (selectedItem.type !== 'ball') {
            setToast({ message: t('toast.needBallLoft'), id: Date.now() });
            return;
          }
          handleLoftToggle(trackId, from, to);
          return;
        }
        handleBendCycle(trackId, from, to);
        return;
      }
      // G: toggle the movement arrow (graphic ON/OFF key) at the playhead for
      // the selected item — the Key Graphic button as one keystroke.
      // Shift+G: cycle the COLOUR of the arrow window under the playhead —
      // default → red → orange → yellow → green → blue → purple → default.
      if (!mod && !e.altKey && (e.key === 'g' || e.key === 'G')) {
        if (!selectedItem) return;
        const trackId = selectedItem.type === 'player'
          ? `${selectedItem.side}:${selectedItem.index}`
          : `${selectedItem.type}:${selectedItem.id}`;
        const t0 = snapKeyTime(refPlayheadTime.current);
        // ONE helper answers "which arrow window is in force here" — the item
        // menu's ➜ entry and its label ask the same question through it.
        const active = graphicKeyAt(keyframes, trackId, t0);
        if (e.shiftKey) {
          e.preventDefault();
          const PALETTE = [null, '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a78bfa'];
          // The colour NAMES used to live here as English words beside the hex
          // codes. They are dictionary keys now (color.*), read through
          // colourName, so the cycle announces in the coach's language.
          const inGroupC = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
          if (inGroupC) {
            // GROUP COLOUR: one decision for the whole group, like G and X
            // already do. The palette step derives from the SELECTED member's
            // active window and lands on EVERY member with an arrow ON at t0.
            // Members with no visible arrow are skipped and counted — colouring
            // an invisible arrow is noise. The selected member still CREATES a
            // window when it has none (⇧G's one-press create+colour survives).
            const states = groupItems.map(g => {
              const tid = g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`;
              const tr = keyframes.find(t => t.id === tid);
              const act = [...(tr?.graphic || [])].sort((a, b) => a.time - b.time)
                .filter(k => k.time <= t0).pop();
              return { g, tid, act, on: !!act && act.value === 'on' };
            });
            const selState = states.find(s => isSameGroupMember(s.g, selectedItem)) || states[0];
            const creating = !selState.on;
            const nextColor = creating
              ? '#ef4444'
              : PALETTE[(PALETTE.indexOf(selState.act.color || null) + 1) % PALETTE.length];
            // SEAM: recolour guards the member's OWN active key time; creation
            // guards t0. Filter before burning an undo step.
            const movable = states.filter(s =>
              !refuseLockedDrag(s.tid, s.on ? s.act.time : t0, ['graphic']));
            if (!movable.length) return; // guard already toasted
            // PLAN BEFORE BURNING UNDO: colour needs at least one ON window,
            // or the selected member itself being movable while creating.
            const willCreate = creating && movable.some(s => isSameGroupMember(s.g, selectedItem));
            const toColour = movable.filter(s => s.on);
            if (!willCreate && !toColour.length) {
              setToast({ message: t('toast.noArrowOn'), id: Date.now() });
              return;
            }
            handleDragStart('undoVerb.groupArrowColour');
            let coloured = 0, skipped = 0;
            for (const s of movable) {
              if (!s.on && !isSameGroupMember(s.g, selectedItem)) { skipped += 1; continue; }
              if (!s.on) {
                if (s.g.type === 'player') handleGraphicKey(s.g.side, s.g.index, 'on');
                else handleItemGraphicKey(s.tid, 'on');
              }
              const kt = s.on ? s.act.time : t0;
              setKeyframes(prev => prev.map(t => t.id !== s.tid ? t : {
                ...t,
                graphic: (t.graphic || []).map(k => {
                  if (k.time !== kt) return k;
                  if (!nextColor) { const c = { ...k }; delete c.color; return c; }
                  return { ...k, color: nextColor };
                }),
              }));
              coloured += 1;
            }
            handleDragEnd();
            setToast({ message: t('arrow.groupColour', {
              c: colourName(nextColor),
              n: coloured,
              skipped: skipped ? t('arrow.skippedColour', { n: skipped }) : '',
            }) + t('toast.undoSuffix'), id: Date.now() });
            return;
          }
          // SEAM GUARD (single path): at a child branch's 00:00 an INHERITED
          // graphic key is read-only — without this, ⇧G-create still RECOLOURED
          // the inherited key after the guarded writer refused.
          if (refuseLockedDrag(trackId, t0, ['graphic'])) return;
          if (!active || active.value !== 'on') {
            // No window here: ⇧G CREATES one AND starts the colour cycle —
            // "coloured arrow here" in ONE press (was G, then ⇧G). Further
            // ⇧G presses cycle the palette as usual.
            handleDragStart('undoVerb.arrowColour');
            if (selectedItem.type === 'player') handleGraphicKey(selectedItem.side, selectedItem.index, 'on');
            else handleItemGraphicKey(trackId, 'on');
            setKeyframes(prev => prev.map(t => t.id !== trackId ? t : {
              ...t,
              graphic: (t.graphic || []).map(k => k.time === t0 ? { ...k, color: '#ef4444' } : k),
            }));
            handleDragEnd();
            setToast({ message: t('arrow.onRed', { t: fmtTime(t0) }) + t('toast.undoSuffix'), id: Date.now() });
            return;
          }
          const nextColor = PALETTE[(PALETTE.indexOf(active.color || null) + 1) % PALETTE.length];
          // The window may have OPENED at the seam (inherited key at 00:00,
          // playhead later) — recolouring that key re-forms the seam too.
          if (refuseLockedDrag(trackId, active.time, ['graphic'])) return;
          handleDragStart('undoVerb.arrowColour');
          setKeyframes(prev => prev.map(t => t.id !== trackId ? t : {
            ...t,
            graphic: (t.graphic || []).map(k => {
              if (k.time !== active.time) return k;
              if (!nextColor) { const c = { ...k }; delete c.color; return c; }
              return { ...k, color: nextColor };
            }),
          }));
          handleDragEnd();
          setToast({ message: t('arrow.colourSet', { c: colourName(nextColor), t: fmtTime(active.time) }) + t('toast.undoSuffix'), id: Date.now() });
          return;
        }
        const next = (active ? active.value : 'on') === 'on' ? 'off' : 'on';
        e.preventDefault();
        const inGroupG = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
        if (inGroupG) {
          // One decision for the whole group — every member gets the SAME
          // arrow state, one undo step, count toast. Balls INCLUDED (they
          // have a graphic lane; X's ball filter is different — no ACTION
          // lane there).
          // SEAM: members whose inherited graphic key sits at t0 hold their
          // formation — filter them BEFORE burning an undo step, and tell
          // the truth about who actually moved.
          const movable = groupItems.filter(g => !refuseLockedDrag(
            g.type === 'player' ? `${g.side}:${g.index}` : `${g.type}:${g.id}`, t0, ['graphic']));
          if (!movable.length) return; // guard already toasted
          handleDragStart('undoVerb.groupArrows');
          for (const g of movable) {
            if (g.type === 'player') handleGraphicKey(g.side, g.index, next);
            else handleItemGraphicKey(`${g.type}:${g.id}`, next);
          }
          handleDragEnd();
          const blocked = groupItems.length - movable.length;
          setToast({ message: t(next === 'on' ? 'arrow.groupOn' : 'arrow.groupOff', {
            t: fmtTime(t0), n: movable.length,
            blocked: blocked ? t('arrow.blockedSeam', { n: blocked }) : '',
          }) + t('toast.undoSuffix'), id: Date.now() });
        } else {
          // SEAM GUARD (single path): the group path filters per member.
          if (refuseLockedDrag(trackId, t0, ['graphic'])) return;
          if (selectedItem.type === 'player') handleGraphicKey(selectedItem.side, selectedItem.index, next);
          else handleItemGraphicKey(trackId, next);
          setToast({ message: t(next === 'on' ? 'arrow.on' : 'arrow.off', {
            t: fmtTime(t0), name: getItemDisplayName(selectedItem),
          }), id: Date.now() });
        }
        return;
      }
      // ⇧X: REPEAT the last action on the selected player/item — one press
      // where X→1 needs two. Matched by CODE first: ⇧ uppercases e.key, and
      // the plain X block below must not swallow it (shift guard added there
      // too, the ⇧V/⇧P lesson).
      if (!mod && !e.altKey && e.shiftKey && (e.code === 'KeyX' || e.key === 'X' || e.key === 'x')) {
        e.preventDefault();
        handleRepeatAction();
        return;
      }
      // X: open the ACTION picker for the selected player/item at the
      // playhead — the Key Action button as one keystroke (balls have no
      // action track, they have attachment instead). With a group active,
      // ONE prompt keys every member (e.g. the back four all "Shout").
      if (!mod && !e.altKey && !e.shiftKey && (e.key === 'x' || e.key === 'X')) {
        if (!selectedItem) return;
        e.preventDefault();
        const inGroup = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
        if (inGroup) {
          const targets = groupItems
            .filter(g => g.type !== 'ball' && g.type !== 'zone')
            .map(g => g.type === 'player' ? { side: g.side, index: g.index } : { trackId: `${g.type}:${g.id}` });
          if (!targets.length) {
            setToast({ message: t('toast.ballNoActionX'), id: Date.now() });
            return;
          }
          // Say UP FRONT who actually gets the action — a 5-group with 2 balls
          // answering 'Shout' should not discover '3 ×' only after the yes.
          const skipped = groupItems.length - targets.length;
          handleActionPrompt({
            targets,
            title: skipped
              ? t('act.titleSome', { n: targets.length, total: groupItems.length })
              : t('act.titleAll', { n: targets.length }),
          });
          return;
        }
        if (selectedItem.type === 'ball' || selectedItem.type === 'zone') {
          setToast({ message: t('act.ballsNoPick'), id: Date.now() });
          return;
        }
        if (selectedItem.type === 'player') {
          handleActionPrompt({ side: selectedItem.side, index: selectedItem.index, title: t('act.title', { what: getItemDisplayName(selectedItem) }) });
        } else {
          handleActionPrompt({ trackId: `${selectedItem.type}:${selectedItem.id}`, title: t('act.title', { what: getItemDisplayName(selectedItem) }) });
        }
        return;
      }
      // ⇧D / ⌥⇧D mirror the last placement across the pitch (x → −x = the
      // other HALF, the M axis; z → −z = the other SIDE) — a symmetric drill
      // without a single aimed click. Then plain D walks the row on.
      if (!mod && e.altKey && e.shiftKey && e.code === 'KeyD') {
        e.preventDefault();
        handlePlaceInLine('mirrorZ');
        return;
      }
      if (!mod && !e.altKey && e.shiftKey && e.code === 'KeyD') {
        e.preventDefault();
        handlePlaceInLine('mirrorX');
        return;
      }
      // ⌥D: turn the row 90° at the SAME spacing — a passing grid, then a
      // square, without a single measured click.
      if (!mod && e.altKey && !e.shiftKey && e.code === 'KeyD') {
        e.preventDefault();
        handlePlaceInLine('turn');
        return;
      }
      // D: place ANOTHER item one spacing further along the line the last two
      // placements made — cones.ejs "place a line of cones" without a fresh
      // aim per click. It CHAINS: D again drops the next one at the same step.
      if (!mod && !e.altKey && !e.shiftKey && e.code === 'KeyD') {
        e.preventDefault();
        handlePlaceInLine();
        return;
      }
      // T: start a ZONE VISUALS window at the playhead for the selected zone
      // (the Key Visual button as one keystroke) — opens the shape editor.
      // Shift+T: DELETE the visuals window under the playhead (undoable).
      if (!mod && !e.altKey && !e.shiftKey && (e.key === 't' || e.key === 'T')) {
        if (!selectedItem || selectedItem.type !== 'zone') {
          if (selectedItem) setToast({ message: t('toast.needZone'), id: Date.now() });
          return;
        }
        e.preventDefault();
        // T TWICE = grab: with this zone's shape editor already open, the
        // second T skips the ✎ button and grabs VERTEX 1 for Ctrl+arrow
        // nudging — the mouse path (editor → ✎ Edit Shape → grab a dot)
        // becomes one more keypress.
        const zTrackId = `zone:${selectedItem.id}`;
        if (zoneVisualEdit && zoneVisualEdit.trackId === zTrackId) {
          setZoneEditRequest({ zoneId: selectedItem.id, ts: Date.now(), selectPoint: 0 });
          setToast({ message: t('vertex.grabbed'), id: Date.now() });
          return;
        }
        // SEAM: a zone's visual key at a child branch's 00:00 is inherited —
        // T there would open an editor whose Apply can never land (guarded
        // writer) plus a toast claiming a window that was never keyed.
        const t0T = snapKeyTime(refPlayheadTime.current);
        // GROUP-AWARE: ⇧⌥S picks every zone, T then keys a visuals window for
        // ALL of them at this instant — the shape overlay for a whole drill in
        // one press, like G / X / C already treat a group. The EDITOR still
        // opens for the selected zone only (it edits one shape at a time), so
        // the others are keyed FIRST and the selection LAST. A locked member
        // is skipped and COUNTED, never fatal for the rest of the group.
        const inGroupT = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
        if (inGroupT) {
          const zoneTracks = groupItems.filter(g => g.type === 'zone').map(g => `zone:${g.id}`);
          if (!zoneTracks.length) {
            setToast({ message: t('vis.forZones'), id: Date.now() });
            return;
          }
          const seamLocked = (id) => {
            const tr = keyframes.find(k => k.id === id);
            return (tr?.visual || []).some(k => Math.abs(k.time - t0T) < 1e-6 && isLockedKeyTime(k.time));
          };
          const keyed = zoneTracks.filter(id => !seamLocked(id));
          if (!keyed.length) { refuseLockedKey(); return; }
          const ordered = [...keyed.filter(id => id !== zTrackId), ...(keyed.includes(zTrackId) ? [zTrackId] : [])];
          for (const id of ordered) handleZoneVisualKey(id);
          setToast({
            message: t('vis.windowGroup', { t: fmtTime(t0T), n: keyed.length })
              + (zoneTracks.length - keyed.length ? t('vis.lockedAtSeam', { n: zoneTracks.length - keyed.length }) : ''),
            id: Date.now(),
          });
          return;
        }
        if (refuseLockedDrag(zTrackId, t0T, ['visual'])) return;
        handleZoneVisualKey(zTrackId);
        setToast({ message: t('vis.windowOpen', { t: fmtTime(snapKeyTime(refPlayheadTime.current)) }), id: Date.now() });
        return;
      }
      if (!mod && !e.altKey && e.shiftKey && (e.key === 'T' || e.key === 't')) {
        if (!selectedItem || selectedItem.type !== 'zone') {
          if (selectedItem) setToast({ message: t('toast.needZone'), id: Date.now() });
          return;
        }
        const trackId = `zone:${selectedItem.id}`;
        // GROUP-AWARE, the pair of T: ⇧⌥S then ⇧T clears the shape overlays
        // from EVERY zone at this instant. Keying six windows in one press and
        // deleting them one at a time would be half a verb.
        const inGroupDT = groupItems.length >= 2 && groupItems.some(g => isSameGroupMember(g, selectedItem));
        if (inGroupDT) {
          const zoneTracks = groupItems.filter(g => g.type === 'zone').map(g => `zone:${g.id}`);
          if (!zoneTracks.length) {
            setToast({ message: t('vis.forZones'), id: Date.now() });
            return;
          }
          const cut = (refPlayheadTime.current ?? 0) + 0.001;
          const targets = [], locked = [];
          for (const id of zoneTracks) {
            const tr = keyframes.find(t => t.id === id);
            const act = [...(tr?.visual || [])].sort((a, b) => a.time - b.time).filter(k => k.time <= cut).pop();
            if (!act) continue;
            if (isLockedKeyTime(act.time)) { locked.push(id); continue; }
            targets.push({ id, time: act.time });
          }
          if (!targets.length) {
            setToast({ message: locked.length
              ? t('seam.windowsReadOnly')
              : t('vertex.noWindow'), id: Date.now() });
            return;
          }
          e.preventDefault();
          handleDragStart('undoVerb.deleteZoneWindows');
          for (const t of targets) handleKeyframeDelete(t.id, 'visual', t.time);
          handleDragEnd();
          setZoneVisualEdit(null);
          // Announced AFTER the deletes: locked members were filtered out up
          // front, so nothing can refuse behind this toast's back.
          setToast({
            // The "✚ in the Visual lane keys a new one" clause is the FORWARD door —
            // it makes a window, it does not bring back the ones that were just
            // deleted with their timings and shapes. N deletions are one undo step,
            // so the sentence names the one button that matches the commit.
            message: t(targets.length === 1 ? 'vis.windowsDeletedOne' : 'vis.windowsDeletedN', { n: targets.length })
              + (locked.length ? t('vis.lockedAtSeam', { n: locked.length }) : '')
              + t('toast.undoSuffix'),
            id: Date.now(),
          });
          return;
        }
        const track = keyframes.find(t => t.id === trackId);
        const active = [...(track?.visual || [])].sort((a, b) => a.time - b.time)
          .filter(k => k.time <= (refPlayheadTime.current ?? 0) + 0.001).pop();
        if (!active) { setToast({ message: t('vertex.noWindow'), id: Date.now() }); return; }
        e.preventDefault();
        if (isLockedKeyTime(active.time)) { refuseLockedKey(); return; } // no burn
        handleDragStart('undoVerb.deleteZoneWindow');
        // Toast BEFORE the delete: if the window opened at the inherited
        // 00:00 the handler refuses (locked) and its toast must WIN over
        // this one — not the other way round, which announced a lie.
        setToast({ message: t('vis.windowDeleted', { t: fmtTime(active.time) }) + t('toast.undoSuffix'), id: Date.now() });
        handleKeyframeDelete(trackId, 'visual', active.time);
        handleDragEnd();
        setZoneVisualEdit(null);
        return;
      }
      // While a central prompt is open, number keys ANSWER it: 1–9 pick the
      // listed option, 0 cancels (Escape also cancels). Camera presets pause.
      if (promptState && !mod && !e.altKey && /^[0-9]$/.test(e.key)) {
        const hit = promptKeyAction(promptState, e.key);
        e.preventDefault();
        if (hit) hit.run();
        return;
      }
      // L and ⇧L (the gaze lock / gaze cycle pair) were removed with the
      // look-at target feature: players face where they travel. The letters are
      // free again.
      // M / ⇧M: MIRROR the selection — or the whole ⇧-group — across the pitch.
      // The run and the zone shape come with it, and the movement
      // bends swap side. ⇧M flips the OTHER axis (end ↔ end across the halfway
      // line). ⌥M / ⇧⌥M do the same to EVERY item in the branch (there is no
      // select-all, so the whole-scenario flip needs its own chord). All four
      // matched by CODE first: ⌥ types µ and ⇧ types M, and a plain block that
      // also matched 'M' would swallow its own shifted twin.
      if (!mod && e.altKey && e.shiftKey && (e.code === 'KeyM' || e.key === 'M')) {
        e.preventDefault();
        handleMirrorSelection(0, true);
        return;
      }
      if (!mod && e.altKey && !e.shiftKey && (e.code === 'KeyM' || e.key === 'm')) {
        e.preventDefault();
        handleMirrorSelection(2, true);
        return;
      }
      if (!mod && !e.altKey && e.shiftKey && (e.code === 'KeyM' || e.key === 'M')) {
        e.preventDefault();
        handleMirrorSelection(0);
        return;
      }
      if (!mod && !e.altKey && !e.shiftKey && (e.code === 'KeyM' || e.key === 'm')) {
        e.preventDefault();
        handleMirrorSelection(2);
        return;
      }
      // ⇧⌥S — select every item of the selected item's KIND: all the cones,
      // all the zones, one team's players. Grouping by hand costs a ⇧-click per
      // item, and the GROUP is what M / Ctrl+D / ⌥←/→ / Del already act on, so
      // this one press multiplies every group-aware verb at once. Matched by
      // CODE: ⇧⌥ types Í on a Mac.
      if (!mod && e.altKey && e.shiftKey && (e.code === 'KeyS' || e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        handleSelectKind();
        return;
      }
      // Alt+1..9 — open the Nth POSSIBLE NEXT sub-branch of the current
      // branch (the branch cards from the keyboard; records the master-path
      // choice and hands over the normalised playhead). Alt+↑ = parent.
      if (!mod && e.altKey && /^[1-9]$/.test(e.key)) {
        const node = findBranch(refScenarioTree.current, currentBranchId);
        // Same ORDER the POSSIBLE NEXT cards use, so the digit matches the card.
        const kids = [...(node?.children || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        const child = kids[+e.key - 1];
        if (!child) {
          // A blind keyboard jump must say WHY it refused, not just sit there.
          e.preventDefault();
          setToast({ message: kids.length
            ? (kids.length === 1 ? t('tree.onlyOneKid') : t('tree.onlyNKids', { n: kids.length }))
            : t('tree.noKids'), id: Date.now() });
          return;
        }
        e.preventDefault();
        handleSelectBranch(child.id);
        // Narrate the jump: the card view may be off-screen, so name where
        // the user landed and the ways onward from there.
        announceJump(child);
        return;
      }
      // Alt+Shift+↑ / ↓: raise / lower this branch's PRIORITY among its
      // siblings — priority 1 is the default path through this fork, so these
      // keys re-route the master playback without touching any card.
      if (!mod && e.altKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const dir = e.key === 'ArrowUp' ? 'up' : 'down';
        // Explain what the move does to the MASTER PATH: with no explicit
        // choice at this fork, the path follows priority — so re-prioritising
        // re-routes playback. Once a branch has been SELECTED at the fork, the
        // choice is pinned and priority moves are ordering only.
        const pTree = committedTree();
        const pNode = findParent(pTree, currentBranchId);
        const explicitChoice = pNode && (refBranchChoices.current || {})[pNode.id];
        const pSibs0 = pNode ? sortedChildren(pNode) : [];
        const i0 = pSibs0.findIndex(s => s.id === currentBranchId);
        const newRank = dir === 'up' ? i0 : i0 + 2; // swap lands one step that way
        if (!handleMoveBranchPriority(currentBranchId, dir)) {
          // Refusal, out loud — and it never burned an undo step to get here.
          setToast({ message: !pNode
            ? t('tree.rootNoPriority')
            : dir === 'up'
              ? (explicitChoice ? t('tree.alreadyFirst')
                : t('tree.alreadyFirstPath'))
              : t('tree.alreadyLast'), id: Date.now() });
          return;
        }
        setToast({ message: t('branch.priority', {
          dir: t(dir === 'up' ? 'branch.dirUp' : 'branch.dirDown'),
          n: newRank, m: pSibs0.length,
          choice: t(explicitChoice ? 'branch.choiceKept' : 'branch.choiceFollows'),
        }), id: Date.now() });
        return;
      }
      if (!mod && e.altKey && !e.shiftKey && e.key === 'ArrowUp') {
        const parent = findParent(refScenarioTree.current, currentBranchId);
        if (!parent) {
          e.preventDefault();
          setToast({ message: t('tree.atRoot'), id: Date.now() });
          return;
        }
        e.preventDefault();
        handleSelectBranch(parent.id);
        announceJump(parent);
        return;
      }
      // Shift+↑ / Shift+↓: WALK the tree from the pitch — parent / FIRST CHILD
      // (in POSSIBLE NEXT card order). The card list needs no mouse: ⇧↑ up,
      // ⇧↓ down, ⇧[ / ⇧] between siblings. And because handleSelectBranch
      // CHOOSES each landed-on branch for its sibling set, walking the tree
      // with these keys BUILDS the master playback path as a side effect —
      // no separate path-picking keys needed.
      if (!mod && !e.altKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const wTree = committedTree();
        if (e.key === 'ArrowUp') {
          const wParent = findParent(wTree, currentBranchId);
          if (!wParent) { setToast({ message: t('tree.isRoot'), id: Date.now() }); return; }
          handleSelectBranch(wParent.id);
          announceJump(wParent);
        } else {
          const wNode = findBranch(wTree, currentBranchId);
          const wKids = sortedChildren(wNode);
          if (!wKids.length) { setToast({ message: t('tree.noKids'), id: Date.now() }); return; }
          handleSelectBranch(wKids[0].id);
          announceJump(wKids[0]);
        }
        return;
      }
      // ? — open HELP (shortcut cheat-sheet + tutorials). Escape closes it.
      if (!mod && !e.altKey && e.key === '?') {
        e.preventDefault();
        setShowHelpModal(true);
        return;
      }
      // P: SCREENSHOT from anywhere. The share sheet (or PNG download)
      // announces itself — no toast to stack on top of it.
      if (!mod && !e.altKey && !e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setScreenshotTick(t => t + 1);
        return;
      }
      // ⇧P: start/stop the video recording — the panel's Record/Stop button as
      // one key. CaptureControl decides which half and announces the START;
      // stopping lets the Saving/share flow speak for itself.
      if (!mod && !e.altKey && e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setRecordToggleTick(t => t + 1);
        return;
      }
      // Shift+R: RENAME the current branch — jumps to the tree view if needed
      // and focuses the selected card's name field with the text pre-selected,
      // so renaming is type-immediately. (Typing in that field never re-triggers
      // this key: the handler ignores INPUT targets.)
      if (!mod && !e.altKey && e.shiftKey && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault();
        if (viewMode !== 'branches') setViewMode('branches');
        setRenameNonce(n => n + 1);
        setToast({ message: t('tree.renaming'), id: Date.now() });
        return;
      }
      // B: add a sub-branch under the CURRENT branch (inherits its end
      // state) — the branch panel's + button as one keystroke, from anywhere.
      // Shift+B: DUPLICATE the current branch as a sibling, timeline included.
      if (!mod && !e.altKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        if (e.shiftKey) handleDuplicateBranch(currentBranchId);
        // B fans: after landing in a new branch, the next B still adds under
        // the ORIGINAL parent (refBranchParentForB) — B B B = three siblings.
        else handleAddBranch(refBranchParentForB.current ?? currentBranchId);
        return;
      }
      // Alt+B: add a SIBLING of the current branch — an empty branch continuing
      // from the same point (the card's "+ Add sibling branch" button).
      if (!mod && e.altKey && !e.shiftKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        if (!findParent(committedTree(), currentBranchId)) {
          setToast({ message: t('tree.rootNoSiblings'), id: Date.now() });
          return;
        }
        // Same creation-anchor rule as B: after landing in a sibling, the
        // next ⌥B adds BESIDE it (anchor = parent), never nested under it.
        handleAddSibling(refBranchParentForB.current ?? currentBranchId);
        return;
      }
      // Alt+E: OPEN the current branch's timeline (the card's ✎ button) —
      // the keyboard way back from the V tree view.
      if (!mod && e.altKey && !e.shiftKey && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault();
        handleEnterBranch(currentBranchId);
        return;
      }
      // Shift+1…Shift+6: ARM PLACEMENT straight from the keyboard — the ADD
      // menu's whole catalogue (cone, target, zone, ball, circle zone, dynamic
      // zone; press the same combo again to disarm). Then click the pitch to
      // place; Shift+click places and stays armed for a whole line.
      if (!mod && !e.altKey && e.shiftKey && /^Digit[1-6]$/.test(e.code || '')) {
        const PLACE_KEYS = {
          Digit1: 'cone', Digit2: 'target', Digit3: 'zone',
          Digit4: 'ball', Digit5: 'zone-circle', Digit6: 'zone-dynamic',
        };
        const type = PLACE_KEYS[e.code];
        e.preventDefault();
        const disarming = pendingObjectType === type;
        handleFieldObjectClick(type);
        setToast({ message: t(disarming ? 'place.cancelled' : 'place.armed', {
          name: placeName(type),
        }), id: Date.now() });
        return;
      }
      // Shift+7 / Shift+8: arm the NEXT BENCHED HOME / AWAY player — the ADD
      // PLAYER menu's usual first entry, without opening the menu. "Benched" =
      // on the roster but not placed on the pitch yet. Pressing the SAME combo
      // again cancels (the ⇧1-6 toggle twin) — loudly, instead of the silent
      // deselect the press-handler toggle would otherwise produce.
      if (!mod && !e.altKey && e.shiftKey && (e.code === 'Digit7' || e.code === 'Digit8')) {
        const side = e.code === 'Digit7' ? 'home' : 'away';
        e.preventDefault();
        const team = side === 'home' ? homeTeam : awayTeam;
        const onPitch = pitchState?.[side]?.players || [];
        const pName = (i) => (team?.playerInfo?.[i]?.name) || t('bench.playerN', { n: i + 1 });
        const selP = refSelectedItem.current;
        if (selP && selP.type === 'player' && selP.side === side && !onPitch[selP.index]?.position) {
          handlePlacePlayer(side, selP.index); // the press handler toggles OFF
          setToast({ message: t('bench.disarmed', {
            who: pName(selP.index), key: side === 'home' ? '7' : '8',
          }), id: Date.now() });
          return;
        }
        const idx = (team?.playerInfo || []).findIndex((p, i) => p && !onPitch[i]?.position);
        if (idx < 0) {
          setToast({ message: t(side === 'home' ? 'bench.noBenchHome' : 'bench.noBenchAway'), id: Date.now() });
          return;
        }
        handlePlacePlayer(side, idx);
        const p = team.playerInfo[idx];
        setToast({ message: t('bench.armedClick', {
          who: p.name || t('bench.playerN', { n: idx + 1 }), key: side === 'home' ? '7' : '8',
        }), id: Date.now() });
        return;
      }
      // Shift+9: re-arm the LAST object type placed on the pitch — build
      // another cone without remembering which digit armed the last one.
      if (!mod && !e.altKey && e.shiftKey && e.code === 'Digit9') {
        e.preventDefault();
        const last = refLastPlaceType.current;
        if (!last) {
          setToast({ message: t('toast.nothingPlaced'), id: Date.now() });
          return;
        }
        const disarming = pendingObjectType === last;
        handleFieldObjectClick(last);
        setToast({ message: t(disarming ? 'place.cancelled' : 'place.armedAgain', {
          name: placeName(last),
        }), id: Date.now() });
        return;
      }
      // N / Shift+N: cycle the selection through every item ON the pitch
      // (home players → away players → balls → cones → targets → zones).
      // The cheap way to walk a packed drill without hunting pixels: select,
      // nudge (Alt+←/→), key (K/X/G/T), move — all without touching the mouse.
      // Alt+N / Alt+Shift+N: same cycle, and the camera FOCUSES on the new
      // selection (one-shot zoom — the sticky follow is the viewport button).
      if (!mod && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        const list = [];
        for (const side of ['home', 'away']) {
          (pitchState?.[side]?.players || []).forEach((p, i) => {
            if (p?.position) list.push({ type: 'player', side, index: i });
          });
        }
        for (const key of ['balls', 'cones', 'targets', 'zones']) {
          (pitchState?.[key] || []).forEach(o => {
            if (o?.position && o.id) list.push({ type: key.slice(0, -1), id: o.id });
          });
        }
        if (!list.length) {
          setToast({ message: t('toast.nothingOnPitch'), id: Date.now() });
          return;
        }
        const isCur = it => selectedItem && it.type === selectedItem.type
          && (it.type !== 'player'
            ? it.id === selectedItem.id
            : it.side === selectedItem.side && it.index === selectedItem.index);
        const idx = list.findIndex(isCur);
        const next = e.shiftKey
          ? list[(idx - 1 + list.length) % list.length]
          : list[(idx + 1) % list.length];
        handleSelectItem(next);
        if (e.altKey) setCameraRequest(c => ({ kind: 'focus', n: (c?.n || 0) + 1 }));
        // When the cycle wraps (full lap done), teach the camera-focus twin —
        // a full lap is exactly when items started feeling off-screen.
        setToast({ message: getItemDisplayName(next) + t('select.cycleHint')
          + (e.altKey ? t('select.cameraFocused') : (idx === list.length - 1 ? t('select.cameraHint') : '')), id: Date.now() });
        return;
      }
      // 1 / 2 / 3 — camera presets: default angle, top-down, low sideline.
      if (!mod && !e.altKey && (e.key === '1' || e.key === '2' || e.key === '3')) {        e.preventDefault();
        setCameraRequest(c => ({
          kind: e.key === '1' ? 'reset' : e.key === '2' ? 'top' : 'low',
          n: (c?.n || 0) + 1,
        }));
        return;
      }
      // ⇧A — ADD a vertex to the zone shape you are editing (the EDIT ZONE
      // panel's "+ Add" tool, without switching tools or clicking the pitch).
      // The new vertex lands at the edge midpoint and arrives GRABBED, so
      // Ctrl+←→ pushes it out to where you actually want it.
      if (!mod && !e.altKey && e.shiftKey && (e.code === 'KeyA' || e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        if (selectedItem?.type === 'zone' && editZoneVertex('add', `zone:${selectedItem.id}`)) return;
        setToast({ message: t('vertex.grabFirst'), id: Date.now() });
        return;
      }
      // A — toggle AUTO key recording (the mode switch that changes how every
      // drag behaves; the toast confirms the new state). SHIFT EXCLUDED: ⇧
      // types a capital A, and this block matched 'A' — it was swallowing the
      // ⇧A vertex verb before it even existed.
      if (!mod && !e.altKey && !e.shiftKey && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        setAutoKeyOn(v => !v);
        setToast({ message: autoKeyOn
          ? t('view.autoOff')
          : t('view.autoOn'), id: Date.now() });
        return;
      }
      // ⌥A: ATTACH at the playhead — the keyboard twin of dragging a ball
      // onto a player. Ball/zone → NEAREST player; player → NEAREST ball to
      // itself ("give the ball to this player"). ⇧⌥A: detach (on a player,
      // detaches every ball currently attached to it). One undo step each.
      // With an attach PROMPT open, ⌥A ANSWERS it with the nearest target.
      if (!mod && e.altKey && (e.key === 'a' || e.key === 'A')) {
        if (!selectedItem || (selectedItem.type !== 'ball' && selectedItem.type !== 'zone' && selectedItem.type !== 'player')) {
          setToast({ message: t('toast.needBallAttach'), id: Date.now() });
          return;
        }
        e.preventDefault();
        const aTime = snapKeyTime(refPlayheadTime.current);
        // SEAM: every ⌥A sub-path (detach, attach, pass) writes AT aTime.
        // The guarded upsertTrackKey would refuse the seam key but the code
        // would keep going — a HALF pass: release refused, catch lands, the
        // ball teleports instead of flying. One honest refusal up front.
        if (isLockedKeyTime(aTime)) { refuseLockedKey(); return; }
        const isPlayerA = selectedItem.type === 'player';
        const aTrackId = isPlayerA ? null : `${selectedItem.type}:${selectedItem.id}`;
        if (e.shiftKey) {
          if (isPlayerA) {
            // Detach every ball whose ACTIVE attach value is this player.
            // Collect FIRST — opening the undo step before knowing there is
            // work to do burns a blank Ctrl+Z entry when nothing is attached.
            const owner = `${selectedItem.side}:${selectedItem.index}`;
            const aTargets = [];
            for (const tr of keyframes) {
              if (!tr.attach?.length) continue;
              const act = [...tr.attach].sort((x, y) => x.time - y.time)
                .filter(k => k.time <= refPlayheadTime.current + 1e-6).pop();
              if (act && act.value === owner) aTargets.push(tr.id);
            }
            if (!aTargets.length) {
              setToast({ message: t('attach.nothingAttached', { name: getItemDisplayName(selectedItem) }), id: Date.now() });
              return;
            }
            let aN = 0;
            handleDragStart('undoVerb.detach');
            for (const trId of aTargets) {
              upsertTrackKey(trId, 'attach', { time: aTime, value: 'none' });
              aN++;
            }
            handleDragEnd();
            // The next gesture after a detach is almost always "animate the
            // freed ball" — select it so drag/K/⌥←→ work immediately, no
            // re-click hunting through the crowd.
            if (aN === 1) {
              const oneM = /^(ball|zone):(.+)$/.exec(aTargets[0]);
              if (oneM) setSelectedItem({ type: oneM[1], id: oneM[2] });
            }
            setToast({ message: (aN === 1
              ? t('detach.fromOne', { who: getItemDisplayName(selectedItem) })
              : t('detach.fromMany', { n: aN, who: getItemDisplayName(selectedItem) })) + t('toast.undoSuffix'), id: Date.now() });
            return;
          }
          // Already free at this moment? Refuse — no undo burn, no key.
          const aTr = keyframes.find(t => t.id === aTrackId);
          const aAct = [...(aTr?.attach || [])].sort((x, y) => x.time - y.time)
            .filter(k => k.time <= refPlayheadTime.current + 1e-6).pop();
          const aCarrier = aAct ? (aAct.value === 'none' ? null : aAct.value)
            : (selectedItem.type === 'ball'
              ? pitchState?.balls?.find(b => b.id === selectedItem.id)?.attached
              : null);
          if (!aCarrier) {
            setToast({ message: t('toast.alreadyFree'), id: Date.now() });
            return;
          }
          handleDragStart('undoVerb.detach');
          upsertTrackKey(aTrackId, 'attach', { time: aTime, value: 'none' });
          handleDragEnd();
          setToast({ message: t('attach.detached', { t: fmtTime(aTime) }), id: Date.now() });
          return;
        }
        // Find the nearest partner (players for ball/zone; balls for player).
        let aBest = null, aBestD = Infinity, aBestTrack = null;
        if (!isPlayerA) {
          const aPos = positionAtTime(aTrackId, refPlayheadTime.current) || itemCurrentPosition(selectedItem);
          if (!aPos) { setToast({ message: t('toast.needPosKey'), id: Date.now() }); return; }
          for (const side of ['home', 'away']) {
            (pitchState?.[side]?.players || []).forEach((p, i) => {
              if (!p?.position) return;
              const pp = positionAtTime(`${side}:${i}`, refPlayheadTime.current) || p.position;
              const d = Math.hypot(pp[0] - aPos[0], pp[2] - aPos[2]);
              if (d < aBestD) { aBestD = d; aBest = { type: 'player', side, index: i }; }
            });
          }
          if (!aBest) { setToast({ message: t('attach.noPlayer'), id: Date.now() }); return; }
        } else {
          const pPos = positionAtTime(`${selectedItem.side}:${selectedItem.index}`, refPlayheadTime.current) || itemCurrentPosition(selectedItem);
          if (!pPos) { setToast({ message: t('toast.needPosKey'), id: Date.now() }); return; }
          for (const b of (pitchState?.balls || [])) {
            if (!b?.position || !b.id) continue;
            const bp = positionAtTime(`ball:${b.id}`, refPlayheadTime.current) || b.position;
            const d = Math.hypot(bp[0] - pPos[0], bp[2] - pPos[2]);
            if (d < aBestD) { aBestD = d; aBest = { type: 'ball', id: b.id }; aBestTrack = `ball:${b.id}`; }
          }
          if (!aBest) { setToast({ message: t('attach.noBall'), id: Date.now() }); return; }
        }
        // An open attach prompt? ⌥A ANSWERS it with the nearest partner —
        // the prompt's own accept path (snaps, closes, keys immediately).
        if (promptState?.mainAction) {
          promptState.mainAction(aBest);
          setToast({ message: t('prompt.answered', { type: placeName(aBest.type), d: aBestD.toFixed(1) }), id: Date.now() });
          return;
        }
        // A player taking a ball carried by ANOTHER player is a PASS — same
        // release/catch semantics as both drag twins, so no instant takeover.
        let aPassFrom = null;
        if (isPlayerA) {
          const aOwner = `${selectedItem.side}:${selectedItem.index}`;
          const aTr = keyframes.find(t => t.id === aBestTrack);
          const aAct = [...(aTr?.attach || [])].sort((x, y) => x.time - y.time)
            .filter(k => k.time <= refPlayheadTime.current + 1e-6).pop();
          const aCarrier = aAct ? (aAct.value === 'none' ? null : aAct.value)
            : (pitchState?.balls || []).find(b => b.id === aBest.id)?.attached;
          if (aCarrier && aCarrier !== aOwner) {
            const aCatch = snapKeyTime(aTime + 1);
            const aStretch = aCatch > timelineDuration;
            handleDragStart('undoVerb.pass');
            if (aStretch) setTimelineDuration(aCatch);
            upsertTrackKey(aBestTrack, 'attach', { time: aTime, value: 'none' });
            upsertTrackKey(aBestTrack, 'position', { time: aTime,
              position: positionAtTime(aCarrier, aTime) || positionAtTime(aBestTrack, refPlayheadTime.current) });
            upsertTrackKey(aBestTrack, 'attach', { time: aCatch, value: aOwner });
            upsertTrackKey(aBestTrack, 'position', { time: aCatch,
              position: positionAtTime(aOwner, aCatch) || positionAtTime(aBestTrack, refPlayheadTime.current) });
            handleDragEnd();
            setPlayheadTime(aCatch);
            setToast({ message: t('pass.tuneCatch', { name: getItemDisplayName(selectedItem) })
              + (aStretch ? t('pass.stretchedFor', { t: fmtTime(aCatch) }) : '') + t('toast.undoSuffix'), id: Date.now() });
            return;
          }
        }
        handleDragStart('undoVerb.attachBall');
        if (isPlayerA) upsertTrackKey(aBestTrack, 'attach', { time: aTime, value: `${selectedItem.side}:${selectedItem.index}` });
        else upsertTrackKey(aTrackId, 'attach', { time: aTime, value: `${aBest.side}:${aBest.index}` });
        handleDragEnd();
        if (Math.abs(aTime - refPlayheadTime.current) > 1e-9) setPlayheadTime(aTime);
        setToast({ message: t('toast.attachedTo', { name: getItemDisplayName(aBest), d: aBestD.toFixed(1), t: fmtTime(aTime) }) + t('toast.undoSuffix'), id: Date.now() });
        return;
      }
      // F — fly the camera to the selected item (close zoom, keeps angle).
      // With nothing selected it used to vanish silently — explain instead.
      if (!mod && !e.altKey && (e.key === 'f' || e.key === 'F')) {
        if (!selectedItem) {
          e.preventDefault();
          setToast({ message: t('toast.needSelectFly'), id: Date.now() });
          return;
        }
        e.preventDefault();
        setCameraRequest(c => ({ kind: 'focus', n: (c?.n || 0) + 1 }));
        return;
      }
      // V: flip the bottom panel between TIMELINE and BRANCHES (branches
      // only when the scenario actually has a tree). H: hide/show all panels
      // for a full-pitch view. (⇧V belongs to the ball-hide twin below —
      // shift makes e.key uppercase, so this match must exclude it.)
      if (!mod && !e.altKey && !e.shiftKey && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        // Name the view you landed on — the panel swap is visible but the
        // VERB is not, and 'branches' only exists when there's a tree.
        const nextV = viewMode === 'timeline'
          ? (countBranches(refScenarioTree.current) > 1 ? 'branches' : 'timeline')
          : 'timeline';
        if (nextV === viewMode) {
          setToast({ message: t('tree.oneBranch'), id: Date.now() });
        } else {
          setViewMode(nextV);
          setToast({ message: nextV === 'branches'
            ? t('view.branches')
            : t('view.timeline'), id: Date.now() });
        }
        return;
      }
      // ⇧V: hide / show the selected BALL — the BallPanel checkbox's keyboard
      // twin. Hidden balls are not drawn and cannot be clicked on the pitch,
      // so the way back must not depend on finding the checkbox again: N
      // cycles through hidden balls too, and ⇧V flips the flag from there.
      if (!mod && !e.altKey && e.shiftKey && (e.key === 'V' || e.key === 'v')) {
        e.preventDefault();
        if (!selectedItem) {
          setToast({ message: t('toast.needBallHide'), id: Date.now() });
          return;
        }
        if (selectedItem.type !== 'ball') {
          setToast({ message: t('toast.hideOnlyBall'), id: Date.now() });
          return;
        }
        const vBall = (pitchState?.balls || []).find(b => b.id === selectedItem.id);
        if (!vBall) return;
        const wasVisible = ballIsVisible(vBall);
        handleBallPropertyChange(selectedItem.id, { visible: !wasVisible });
        setToast({ message: wasVisible
          ? t('view.ballHidden')
          : t('view.ballShown'), id: Date.now() });
        return;
      }
      if (!mod && !e.altKey && (e.key === 'h' || e.key === 'H')) {
        e.preventDefault();
        setTimelineHidden(!timelineHidden);
        setToast({ message: timelineHidden
          ? t('view.panelsBack')
          : t('view.panelsHidden'), id: Date.now() });
        return;
      }
      // Shift+Space: play to the NEXT BRANCH POINT (review one segment at a
      // time). Plain Space plays the whole chosen path.
      // Alt+Space: play / PAUSE just the CURRENT branch — the timeline
      // strip's play button on the keyboard. (Space walks the whole
      // master path; ⇧Space walks it up to the next fork.)
      if (!mod && e.altKey && (e.key === ' ' || e.code === 'Space')) {
        e.preventDefault();
        handlePlayToggle();
        return;
      }
      if (!mod && !e.altKey && e.shiftKey && (e.key === ' ' || e.code === 'Space')) {
        // No activeElement guard: ⇧Space never activates a focused button
        // natively (only plain Space/Enter do), and the legacy effect no
        // longer covers modified Space — this must work right after a
        // mouse click on the play button.
        e.preventDefault();
        handleNextBranchPlay();
        return;
      }
      // Space: master play / pause (unless a focused control should eat it).
      if (!mod && !e.altKey && !e.shiftKey && (e.key === ' ' || e.code === 'Space')) {
        const ae = document.activeElement;
        if (ae && ['BUTTON', 'SELECT', 'A', 'INPUT', 'TEXTAREA'].includes(ae.tagName)) return;
        e.preventDefault();
        handleMasterPlay();
        return;
      }
      if (e.key === 'Escape') {
        if (promptState) { promptState.cancelAction?.(); return; }
        if (segmentEdit) { setSegmentEdit(null); return; }
        if (keyframeEdit) { setKeyframeEdit(null); return; }
        if (graphicEdit) { setGraphicEdit(null); return; }
        // A GRABBED pitch vertex means the user is mid-shape: Esc's first rung
        // drops the VERTEX (Viewport3D's handler owns that) and the editor
        // stays open. Closing the editor in the same press skipped a rung and
        // threw away the panel they were working in.
        if (refZonePointSelected.current !== null) return;
        if (zoneVisualEdit) { setZoneVisualEdit(null); return; }
        if (pendingObjectType) { setPendingObjectType(null); setActionMode('none'); return; }
        if (actionMode === 'remove') { setActionMode('none'); return; }
        if (groupItems.length) { setGroupItems([]); return; } // Escape drops the group first
        if (selectedItem) setSelectedItem(null);
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
        const dur = refTimelineDurationState.current;
        let next;
        if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = dur;
        else {
          const step = (e.shiftKey ? 1 : 0.25) * (e.key === 'ArrowLeft' ? -1 : 1);
          next = snapKeyTime(refPlayheadTime.current + step);
        }
        next = Math.max(0, Math.min(dur, next));
        if (Math.abs(next - refPlayheadTime.current) > 1e-9) {
          e.preventDefault();
          handleScrub(next);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    handleUndo, handleRedo, handleCopy, handlePaste, handleDuplicateItem, handleSaveScenario,
    // NOTE: handleLoadScenarios is defined below this effect — referenced via
    // closure (initialised before any key can fire), deliberately NOT listed
    // in deps (TDZ during render).
    handleRemoveSelectedItem, handlePlayerRemove, handleObjectRemove, handlePlaceInLine, handleLandOnNearestKeyTime, handleDurationChange, lastKeyTime, setKeyframes, handleScrub, handleNudgeSelectedKey, handleGroupXZNudge, cyclePlaybackSpeed, changePlaybackSpeed, handleCopyBranchTimeline, handlePasteBranchTimeline,
    handleKeyPress, handleItemPositionKey, handleMasterPlay, handleNextBranchPlay, handlePlayToggle,
    handleGraphicKey, handleItemGraphicKey, upsertSegment, upsertTrackKey, positionAtTime, itemCurrentPosition, handleDragStart, handleDragEnd, handleActionPrompt, handleZoneVisualKey, handleZoneVisualChange, handleKeyframeDelete, handleKeyframeHeightChange, pitchState, describeAttachRef, handleAddBranch, handleDuplicateBranch, handleMoveBranchPriority, handleClipBranchTime, handleStretchKeys, handleRippleKeys, handleRepeatAction, handleKeyAtPlayhead, handleMirrorSelection, handleSelectKind, handleBallPropertyChange, handleAddSibling, handleEnterBranch, handleRemoveBranch, handleKeyframeClick, handleSegmentClick, handleGraphicSegClick, handleZoneVisualSegClick, currentBranchId, handleSelectBranch, setTimelineDuration,
    keyframes, groupItems, autoKeyOn, handleFieldObjectClick, homeTeam, awayTeam, handleSelectItem, getItemDisplayName, committedTree, // handlePlacePlayer is defined BELOW this effect — naming it in deps would hit the TDZ; it is a stable useCallback, safe to read via closure
    selectedItem, clipboard, promptState, segmentEdit, keyframeEdit, graphicEdit,
    zoneVisualEdit, pendingObjectType, actionMode,
    showLoginModal, showScenarioModal, showPublicModal, teamModalTarget, showAccountModal, showTeamEditor, showHelpModal, infoModal, confirmState, nameState,
    viewMode, timelineHidden, // Shift+R reads viewMode to decide whether to jump to the tree view; H reads timelineHidden to narrate the flip
    t, // every chord toast reads the dictionary — re-subscribe when the language changes
    bendName, colourName, placeName, // the same dictionary, read through three helpers
  ]);

  const handleLoadScenarios = () => {
    if (!account) {
      setShowLoginModal(true);
      return;
    }
    // Explicit load creates a paced ad opportunity.
    showAdGate({
      slot: 'load',
      messageKey: 'modal.ad.loadMsg',
      onContinue: () => { refreshScenarios(); setShowScenarioModal(true); },
    });
  };

  const handleLoadFromPublic = () => {
    // Everyone can load public scenarios. Free/guest users see an ad first;
    // premium users go straight to the browser.
    showAdGate({
      slot: 'load',
      messageKey: 'modal.ad.loadPublicMsg',
      onContinue: () => setShowPublicModal(true),
    });
  };

  const handlePublicScenarioSelect = (scen) => {
    // Load as a local copy — strip the DB id so it won't overwrite the original on save
    loadScenarioAsTree({ ...migrateScenario(scen), id: `local-${Date.now()}` });
    // Teams are NOT part of a scenario (scenarioTeams.js strips them on save),
    // so loading someone else's scenario leaves our current kits alone.
    setShowPublicModal(false);
  };

  const [scenariosLoading, setScenariosLoading] = useState(false);
  const refreshScenarios = useCallback(async () => {
    setScenariosLoading(true);
    try {
      const data = await authAPI.session();
      setScenarios((data.scenarios || []).map(dbScenarioToMenuItem));
    } catch (err) { console.error('Refresh scenarios failed:', err); }
    finally { setScenariosLoading(false); }
  }, []);

  // ── Scenario management (rename / share / delete) — JSON twins of the
  //    /scenarios page actions, reachable from the Load modal itself. ──
  const handleScenarioRename = useCallback(async (scen, name) => {
    try { await scenariosAPI.update(scen.id, { name }); refreshScenarios(); }
    catch (err) { setToast({ message: t('scenario.renameFailed', { why: err.message || t('scenario.unknownError') }), id: Date.now() }); }
  }, [refreshScenarios, t]);
  const handleScenarioDelete = useCallback((scen) => {
    setConfirmState({
      title: t('confirm.deleteScenario'),
      message: t('confirm.deleteScenarioBody', { name: scen.name }),
      confirmLabel: t('confirm.deleteScenario'),
      danger: true,
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await scenariosAPI.delete(scen.id);
          refreshScenarios();
          setToast({ message: t('scenario.deletedNamed', { name: scen.name }), id: Date.now() });
        } catch (err) { setToast({ message: t('scenario.deleteFailed', { why: err.message || t('scenario.unknownError') }), id: Date.now() }); }
      },
      onCancel: () => setConfirmState(null),
    });
  }, [refreshScenarios, t]);
  const handleScenarioTogglePublic = useCallback(async (scen) => {
    try {
      await scenariosAPI.update(scen.id, { isPublic: !scen.isPublic });
      setToast({ message: t(scen.isPublic ? 'scenario.nowPrivate' : 'scenario.nowPublic', { name: scen.name }), id: Date.now() });
      refreshScenarios();
    } catch (err) { setToast({ message: t('scenario.shareFailed', { why: err.message || t('scenario.unknownError') }), id: Date.now() }); }
  }, [refreshScenarios, t]);

  const handleUpgrade = async (plan) => {
    if (!account) { setShowLoginModal(true); return; }
    const checkoutTab = window.open('about:blank', '_blank');
    if (!checkoutTab) { setToast({ message: t('scenario.checkoutPopupBlocked'), id: Date.now() }); return; }
    checkoutTab.opener = null;
    try {
      const data = await stripeAPI.checkout(plan || 'monthly');
      if (!data.url || new URL(data.url).protocol !== 'https:') throw new Error('Checkout link unavailable.');
      billingWindowPending.current = true;
      checkoutTab.location.replace(data.url);
    } catch (err) { checkoutTab.close(); setToast({ message: t('scenario.checkoutFailed', { why: err.message || t('scenario.unknownError') }), id: Date.now() }); }
  };

  // ── Account modal actions (JSON twins of the /account page forms) ──
  const handleManageSubscription = async () => {
    const portalTab = window.open('about:blank', '_blank');
    if (!portalTab) { setInfoModal({ title: t('account.subscription'), message: t('scenario.portalPopupBlocked'), type: 'error' }); return; }
    portalTab.opener = null;
    try {
      const data = await stripeAPI.portal();
      if (!data.url || new URL(data.url).protocol !== 'https:') throw new Error('Billing portal link unavailable.');
      billingWindowPending.current = true;
      portalTab.location.replace(data.url);
    } catch (err) {
      portalTab.close();
      console.error('Billing portal failed:', err);
      setInfoModal({ title: t('account.subscription'), message: t('scenario.portalFailed', { why: err.message || t('scenario.unknownError') }), type: 'error' });
    }
  };
  const handleAccountChangeName = async (name) => {
    await authAPI.changeName(name);
    setAccount(prev => (prev ? { ...prev, name } : prev));
  };
  const handleAccountChangePassword = (currentPassword, newPassword) =>
    authAPI.changePassword(currentPassword, newPassword);
  const handleNewScenario = () => {
    // A new scenario replaces the whole working state — snapshot it first so
    // Ctrl+Z can bring the old scenario back (the snapshot covers tree +
    // scenario + timeline, exactly what loadScenarioAsTree overwrites).
    pushUndo('undoVerb.newScenario');
    // V3: a new scenario is a single root branch; the user splits it into
    // sub-branches from the scenario tree view.
    const scen = createEmptyScenarioV3(account ? `Scenario ${scenarios.length + 1}` : 'New Scenario');
    if (account) setScenarios(prev => [...prev, scen]);
    loadScenarioAsTree(scen);
  };

  // ═══════════════════════════════════════════════════════════════════
  // V2 Toolbar & Timeline wiring
  // ═══════════════════════════════════════════════════════════════════

  /** Derived list of all on-pitch players (home + away) for the timeline editor. */
  const timelinePlayers = [
    ...(homeTeam?.playerInfo || []).map((p, i) => ({
      side: 'home', index: i,
      name: p.name || `Player ${i + 1}`,
      number: p.number || i + 1,
      // Side-identity colours (home blue / away red) — NOT the kit colour,
      // which is red for both default teams and made every badge red.
      color: '#2563eb',
    })),
    ...(awayTeam?.playerInfo || []).map((p, i) => ({
      side: 'away', index: i,
      name: p.name || `Player ${i + 1}`,
      number: p.number || i + 1,
      color: '#dc2626',
    })),
  ];

  /** FILE dropdown actions. */
  const handleFileAction = useCallback((action) => {
    if (action === 'new') { handleNewScenario(); return; }
    if (action === 'save') { handleSaveScenario(); return; }
    if (action === 'saveAs') {
      if (!account) { setShowLoginModal(true); return; }
      // In-app text entry: a native prompt() was a browser strip with an
      // untranslatable button and no room to say what the copy is for.
      setNameState({
        title: t('prompt.saveAsCopy'),
        label: t('name.saveAsHint'),
        defaultValue: (currentScenario?.name || 'Scenario') + ' copy',
        confirmLabel: t('name.saveCopy'),
        onSubmit: (name) => { setNameState(null); handleSaveAs(name); },
        onCancel: () => setNameState(null),
      });
      return;
    }
    if (action === 'load') { handleLoadScenarios(); return; }
    if (action === 'loadPublic') { handleLoadFromPublic(); return; }
    if (action === 'account') { if (!account) { setShowLoginModal(true); return; } setShowAccountModal(true); return; }
    if (action === 'teams') { if (!account) { setShowLoginModal(true); return; } setShowTeamEditor(true); return; }
    if (action === 'help') {
      if (isMobileBuild && getRuntimeAdapter()?.openHelp) getRuntimeAdapter().openHelp();
      else window.open(HELP_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    if (action === 'upgrade') { handleUpgrade('monthly'); return; }
  }, [account, isPremium, currentScenario, handleNewScenario, handleSaveScenario, handleSaveAs, handleLoadScenarios, handleLoadFromPublic, handleUpgrade, t]);

  /** ADD PLAYER HOME / AWAY: pick a player → enter placement mode. */
  const handlePlacePlayer = useCallback((side, index) => {
    const team = side === 'home' ? homeTeam : awayTeam;
    const player = team?.playerInfo?.[index];
    if (side === 'home') handlePlayerPressTop(player, null, index);
    else handlePlayerPressBottom(player, null, index);
  }, [homeTeam, awayTeam, handlePlayerPressTop, handlePlayerPressBottom]);

  /** ADD ITEM: pick ball/cone/target/zone → enter placement mode. */
  const handlePlaceItem = useCallback((type) => {
    handleFieldObjectClick(type);
  }, [handleFieldObjectClick]);

  // ═══════════════════════════════════════════════════════════════════
  // Viewport Resize Handling
  // ═══════════════════════════════════════════════════════════════════

  /** Measure the viewport div and update the 3D canvas dimensions. */
  function handleResize() {
    if (!refViewportDiv.current) return;
    setStateWidthViewport3D(refViewportDiv.current.clientWidth);
    setStateHeightViewport3D(refViewportDiv.current.clientHeight);
  }
  useLayoutEffect(() => {
    handleResize();
    // Re-measure after layout settles (fixes mobile first-load centering)
    const rafId = requestAnimationFrame(() => {
      handleResize();
      // Second pass — some mobile browsers need two frames
      requestAnimationFrame(handleResize);
    });
    // Extended retries: some mobile browsers settle layout slowly
    const timeout1 = setTimeout(handleResize, 100);
    const timeout2 = setTimeout(handleResize, 300);
    const timeout3 = setTimeout(handleResize, 1000);
    return () => { cancelAnimationFrame(rafId); clearTimeout(timeout1); clearTimeout(timeout2); clearTimeout(timeout3); };
  }, []);
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    const onOrientationChange = () => {
      // Delay to let browser settle after rotation
      setTimeout(handleResize, 100);
      setTimeout(handleResize, 300);
    };
    window.addEventListener('orientationchange', onOrientationChange);
    // ResizeObserver catches CSS grid layout changes that window resize misses
    let resizeObs;
    if (refViewportDiv.current && window.ResizeObserver) {
      resizeObs = new ResizeObserver(() => {
        // Deferred a frame: doing layout work inside the observer's own
        // delivery pass is what makes the browser abort with "ResizeObserver
        // loop completed with undelivered notifications".
        if (refResizeQueued.current) return;
        refResizeQueued.current = true;
        requestAnimationFrame(() => { refResizeQueued.current = false; handleResize(); });
      });
      resizeObs.observe(refViewportDiv.current);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      if (resizeObs) resizeObs.disconnect();
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════

  useEffect(() => { if (isPlaying || masterPlay?.active || account?.tier === 'premium') setScenarioAd(null); }, [isPlaying, masterPlay?.active, account?.tier]);
  useEffect(() => {
    if (!isMobileBuild) return;
    getRuntimeAdapter().setPlaybackActive?.(Boolean(isPlaying || masterPlay?.active));
    return () => getRuntimeAdapter().setPlaybackActive?.(false);
  }, [isPlaying, masterPlay?.active]);

  // Early return: still checking auth session (but only block briefly)
  if (!authChecked) return <div style={{ position: 'fixed', inset: 0, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Loading...</div>;

  // Error overlay: backend unreachable

  if (loadError) return (
    <div style={{ position: 'fixed', inset: 0, background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontFamily: 'system-ui, sans-serif', gap: '1rem' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Connection Error</div>
      <div style={{ color: '#94a3b8', fontSize: '0.95rem', textAlign: 'center', maxWidth: '400px' }}>{loadError}</div>
      <button onClick={() => window.location.reload()} style={{ padding: '0.6rem 1.5rem', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem' }}>Retry</button>
    </div>
  );

  // Guard: teams still loading from backend
  if (!homeTeam || !awayTeam) return <div style={{ position: 'fixed', inset: 0, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Loading teams...</div>;

  const webAdVisible = scenarioAd && !isMobileBuild && navigator.onLine && account?.tier !== 'premium' && !isPlaying && !masterPlay?.active;
  // The floating editors live between the top toolbar and the playback row.
  // Reserve the actual timeline height, including the master bar when shown.
  const contextBottom = (timelineHidden ? MASTER_H : panelHeight + (showMaster ? MASTER_H : 0)) + 52;
  return (
    <div className={`App${timelineHidden ? " timeline-hidden" : ""}`}
      style={{ '--kv-context-top': `${webAdVisible ? 236 : 56}px` }}>
      {webAdVisible && <aside aria-label={t('ad.label')} style={{ position:'absolute', top:0, left:0, right:0, height:180, padding:'8px 16px', boxSizing:'border-box', background:'#0f172a' }}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}><span>{t('ad.label')}</span><button onClick={() => setScenarioAd(null)} aria-label={t('ad.close')}>{t('fb.close')}</button></div>
        <AdUnit client={adsConfig.client} slot={scenarioAd === 'save' ? adsConfig.slotSave : adsConfig.slotLoad} format="horizontal" fixedHeight={90} />
      </aside>}
      <button className="controls-toggle" aria-label={timelineHidden ? t('view.panelsShowAria') : t('view.panelsHideAria')} aria-expanded={!timelineHidden} onClick={() => setTimelineHidden(v => !v)} style={{ bottom: timelineHidden ? MASTER_H : panelHeight + (showMaster ? MASTER_H : 0) }}>{timelineHidden ? "▲" : "▼"}</button>
      {/* ── 3D Viewport (Three.js canvas) — full area minus bottom strip ── */}
      <div className="viewport-div" ref={refViewportDiv} style={{ top: webAdVisible ? 180 : 0, bottom: timelineHidden ? MASTER_H : panelHeight + (showMaster ? MASTER_H : 0) }}>
        <Viewport3D
          playing={isPlaying}
          captureOpen={captureOpen}
          onCaptureOpenChange={setCaptureOpen}
          screenshotTick={screenshotTick}
          recordToggleTick={recordToggleTick}
          onCaptureNotify={(m) => setToast({ message: m, id: Date.now() })}
          width={stateWidthViewport3D}
          height={stateHeightViewport3D}
          pitchState={pitchState}
          homeTeamState={homeTeam}
          awayTeamState={awayTeam}
          actionMode={actionMode}
          onActionModeChange={handleActionModeChange}
          pendingObjectType={pendingObjectType}
          selectedItem={selectedItem}
          onSelectItem={handleSelectItem}
          groupItems={groupItems}
          cameraRequest={cameraRequest}
          onPitchLongPress={handlePitchViewPrompt}
          onSegmentBend={handleSegmentBend}
          onSegmentArch={handleSegmentArch}
          onPlayerPlace={handlePlayerPlace}
          onObjectPlace={handleObjectPlace}
          onObjectMove={handleObjectMove}
          onPlayerRemove={handlePlayerRemove}
          onObjectRemove={handleObjectRemove}
            onMasterPlay={handleMasterPlay}
          masterPlaying={isPlaying && masterPlayActive && !masterPlay?.stopAtNextBranch}
          onNextBranchPlay={handleNextBranchPlay}
          nextBranchPlaying={isPlaying && !!masterPlay?.stopAtNextBranch}
          playbackSpeed={playbackSpeed}
          onPlaybackSpeedChange={changePlaybackSpeed}
          onUndo={handleUndo}
          canUndo={undoCount > 0}
          // The stack stores a DICTIONARY KEY, so the tooltip is translated
          // HERE — at the point a person reads it — and not in the label that
          // travels on the stack.
          undoLabel={undoTopLabel ? t(undoTopLabel) : null}
          // How FAR back the button reaches — the question Ctrl+Z answers
          // differently once you know. At the limit the title says the older
          // work is gone instead of leaving you to discover it.
          undoCount={undoCount}
          onShareError={(message) => setToast({ message, id: Date.now() })}
          undoAtLimit={undoCount >= UNDO_LIMIT}
          onRedo={handleRedo}
          canRedo={redoCount > 0}
          redoLabel={redoTopLabel ? t(redoTopLabel) : null}
          redoCount={redoCount}
          onUndoStart={handleDragStart}
          onUndoEnd={handleDragEnd}
          onItemHeightChange={handleItemHeightChange}
          selectedItemHeight={selectedItemHeight}
          onZonePointsChange={handleZonePointsChange}
          onZoneColorChange={handleZoneColorChange}
          onZoneOpacityChange={handleZoneOpacityChange}
          onZoneModeChange={handleZoneModeChange}
          onZoneRadiusChange={handleZoneRadiusChange}
          zoneEditRequest={zoneEditRequest}
          zoneLinkRequest={zoneLinkRequest}
          onZoneEditExit={handleZoneEditExit}
          onZoneClosedChange={handleZoneClosedChange}
          onZonePointRefChange={handleZonePointRefChange}
          onZonePointSelected={(i) => { refZonePointSelected.current = i; }}
          onZoneThicknessChange={handleZoneThicknessChange}
          onZonePointCountChange={handleZonePointCountChange}
          attachMode={attachPickMode}
          onAttachPlayerSelect={handleAttachPlayerSelect}
          onActionPrompt={handleActionPrompt}
          onZoneShapePrompt={handleZoneShapePrompt}
          onBallDrop={handleBallDrop}
          onPlayerBallDrop={handlePlayerBallDrop}
        onMarkPlayerDrop={handleMarkPlayerDrop}
        markPreview={markPicker}
        onMarkSpotPick={handleMarkSpotPick}
        onZoneVertexCommit={announceZoneVertex}
          onZoneDrop={handleZoneDrop}
          onObjectDrop={handleObjectDrop}
          onCopy={handleCopy}
          onPaste={handlePaste}
          hasClipboard={!!clipboard}
          clipboardType={clipboard?.type || null}
          clipboardLabel={clipboard ? (clipboard.type === 'player' ? `${clipboard.side} #${clipboard.index + 1}` : clipboard.type === 'ball' ? 'ball' : clipboard.type === 'cone' ? 'cone' : clipboard.type === 'target' ? 'target' : clipboard.type === 'zone' ? 'zone' : '') : ''}
          clipboardSide={clipboard?.side || null}
          clipboardIndex={clipboard?.index ?? null}
          onAssetsLoaded={handleAssetsLoaded}
          keyframes={keyframes}
          timelineTime={playheadTime}
          duration={timelineDuration}
        />
      </div>
      <FeedbackButton open={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
      {/* ── V2 top-left toolbar (FILE / ADD PLAYER HOME / ADD PLAYER AWAY / ADD ITEM) ── */}
      <div className="kv-toolbar" style={{
        position: 'absolute', top: '10px', left: '10px', zIndex: 60,
      }}>
        <TopToolbar
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          account={account}
          isPremium={isPremium}
          monthlyPrice={pricing?.monthly}
          onFileAction={handleFileAction}
          onPlacePlayer={handlePlacePlayer}
          onPlaceFormation={handlePlaceFormation}
          onPlaceItem={handlePlaceItem}
          pendingType={pendingObjectType}
          placingSide={(actionMode === 'place' && !pendingObjectType && selectedItem?.type === 'player') ? selectedItem.side : null}
          placingIndex={(actionMode === 'place' && !pendingObjectType && selectedItem?.type === 'player') ? selectedItem.index : -1}
          onSwitchTeam={(side) => handleTeamButtonClick(side)}
          onRemoveSelectedItem={handleRemoveSelectedItem}
          hasSelectedItem={!!selectedItem}
          onDuplicateItem={handleDuplicateItem}
          canDuplicateItem={!!selectedItem && selectedItem.type !== 'player'}
          onPlaceInLine={handlePlaceInLine}
          lineState={lineLayoutState()}
          onLogin={() => setShowLoginModal(true)}
          onLogout={handleLogout}
          onFeedback={() => {
            const mobile = isMobileBuild ? getRuntimeAdapter() : null;
            if (mobile && !mobile.context?.isSignedIn?.()) {
              mobile.context?.promptSignIn?.('account', 'Sign in to send feedback.');
              return;
            }
            setShowFeedbackModal(true);
          }}
        />
      </div>

      {/* ── Current scenario name (centred at the top of the page) — shows
           which scenario is loaded / saved once the user has one open ── */}
      {currentScenario?.name && (
        <div
          className="kv-scenario-name"
          title={currentScenario.name}
          style={{
            position: 'absolute', top: '14px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 59, pointerEvents: 'none',
            background: 'rgba(15, 23, 42, 0.72)', border: '1px solid rgba(148, 163, 184, 0.28)',
            color: '#cbd5e1', fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em',
            padding: '4px 14px', borderRadius: '999px', whiteSpace: 'nowrap', overflow: 'hidden',
            textOverflow: 'ellipsis', maxWidth: 'min(46vw, 380px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
        >
          {currentScenario.name}
        </div>
      )}

      {/* ── V3: MASTER TIMELINE — one equal segment per branch on the master
           path. Not a clock: pulling the handle walks the scenario and moves the
           tree selection with it, normalising each branch to its own length. ── */}
      {showMaster && (
        <MasterTimeline
          chain={masterChain}
          forkAlts={masterForks}
          chosenIds={chosenIds}
          pos={masterPos}
          bottom={timelineHidden ? 0 : panelHeight}
          height={MASTER_H}
          playing={isPlaying}
          loadingLabel={masterPlay?.active ? t('branch.masterPlaying') : ''}
          onScrub={handleMasterScrub}
          onSegmentDoubleTap={(id, shift) => startTreeWalk(!!shift)}
          t={t}
        />
      )}

      {/* ── V3: the BOTTOM PANEL — one space, two views.
           It shows the scenario tree, and switches to that branch's timeline
           when a branch is opened (Edit / double-click). BACK in the timeline
           ruler brings the tree back. The 3D viewport above it never moves. ── */}
      <div className="timeline-panels">
      {viewMode === 'branches' ? (
        <BranchNavigator
          tree={scenarioTree}
          selectedId={currentBranchId}
          openId={currentBranchId}
          chosenIds={chosenIds}
          onSelect={handleSelectBranch}
          onEdit={handleEnterBranch}
          onAdd={handleAddBranch}
          onClip={handleClipBranchTime}
          onCopyTimeline={handleCopyBranchTimeline}
          onPasteTimeline={handlePasteBranchTimeline}
          onAddSibling={handleAddSibling}
          onDuplicate={handleDuplicateBranch}
          clipboardBranch={branchClipboard ? { id: branchClipboard.id, name: branchClipboard.name } : null}
          onMove={handleMoveBranchPriorityUI}
          onSetOrder={handleSetBranchOrder}
          onRename={handleRenameBranch}
          onRemove={handleRemoveBranch}
          height={panelHeight}
          onHeightChange={setStripHeight}
          renameSignal={renameNonce}
          t={t}
        />
      ) : (
      <TimelineEditor
        players={timelinePlayers}
        selectedItem={selectedItem}
        selectedItemLabel={getItemDisplayName(selectedItem)}
        duration={timelineDuration}
        onDurationChange={handleDurationChange}
        speed={playbackSpeed}
        onSpeedCycle={handleSpeedCycle}
        onTimingMenu={handleTimingMenu}
        onDurationDragEnd={(now, before) => setToast({ message: t('branch.edgeDrag', { now: fmtTime(now), before: fmtTime(before) }), id: Date.now() })}
        stripHeight={stripHeight}
        onStripHeightChange={setStripHeight}
        playheadTime={playheadTime}
        isPlaying={isPlaying && !masterPlayActive}
        onPlayToggle={handlePlayToggle}
        onScrub={handleScrub}
        onTimelineDragStart={() => { setIsPlaying(false); handleDragStart('undoVerb.moveKeyInTime'); }}
        onTimelineDragEnd={handleDragEnd}
        keyframes={keyframes}
        autoKeyOn={autoKeyOn}
        onAutoKeyToggle={() => setAutoKeyOn(v => !v)}
        onPlayerKey={handleKeyPress}
        onActionPrompt={handleActionPrompt}
        onGraphicFlip={handleGraphicFlip}
        onMarkPrompt={(trackId) => handleMarkPrompt(trackId)}
        onSelectItem={setSelectedItem}
        onItemPositionKey={handleItemPositionKey}
        onBallAttachPrompt={handleBallAttachPrompt}
        onKeyframeTimeChange={handleKeyframeTimeChange}
        onKeyframeRippleChange={handleKeyframeRippleDrag}
        onKeyframeDelete={handleKeyframeDelete}
        onKeyTap={handleTimelineKeyTap}
        onKeyframeClick={handleKeyframeClick}
        keyframeEdit={keyframeEdit}
        onSegmentClick={handleSegmentClick}
        segmentEdit={segmentEdit}
        onGraphicSegClick={handleGraphicSegClick}
        graphicEdit={graphicEdit}
        onZoneVisualSegClick={handleZoneVisualSegClick}
        onZoneVisualKey={handleZoneVisualKey}
        zoneVisualEdit={zoneVisualEdit}
        onBranchBack={handleBackToTree}
        branchName={shownBranch?.name || ''}
        branchId={shownBranch?.id || currentBranchId}
        onRenameBranch={handleRenameBranch}
        lockedTime={lockedStartTime(scenarioTree, shownBranch?.id || currentBranchId)}
      />
      )}

      </div>

      {/* ── Reusable central prompt — shown just above the timeline stack ── */}
      <PromptModal
        prompt={promptState}
        bottom={contextBottom}
      />

      {/* ── MARK PLAYER compass: which of the eight spots, how far out ── */}
      {markPicker && (
        <MarkPickerModal
          title={markPicker.group
            ? t('mark.gapAskMany', { n: markPicker.group.length })
            : t('mark.pickTitle', { who: describeAttachRef(markPicker.value) })}
          hint={t('mark.compassHint')}
          dirNames={MARK_DIRS.map(d => t(`mark.dir.${d}`))}
          dir={markPicker.dir}
          dist={markPicker.dist}
          confirmLabel={t('mark.pickConfirm')}
          cancelLabel={t('confirm.cancel')}
          altLabel={refMarkRepick.current ? t('mark.pickOther') : null}
          onAlt={refMarkRepick.current ? () => {
            const again = refMarkRepick.current;
            setMarkPicker(null);
            if (again) again();
          } : null}
          releaseLabel={refMarkRelease.current ? t('mark.pickStop') : null}
          onRelease={refMarkRelease.current ? () => {
            const stop = refMarkRelease.current;
            setMarkPicker(null);
            if (stop) stop();
          } : null}
          // ✕ REMOVE THIS MARK KEY — the exit touch could not reach until now.
          // "Stop marking" writes a release key and keeps the ground the marker
          // gained; that is the right answer mid-drill but not when the key was
          // laid down by mistake. Deleting the key is what the strip's diamond
          // means, and on a phone it used to need a double-click.
          deleteLabel={markPicker.keyHere ? t('mark.pickDelete') : null}
          onDelete={markPicker.keyHere ? () => {
            const { trackId: mt, time: mkTime } = markPicker;
            setMarkPicker(null);
            // handleKeyframeDelete already announces a MOMENT going (position +
            // attach at one instant are one physical event). Only add the mark
            // sentence when it did not, or the second toast erases the first.
            const moment = (keyframes.find(tr => tr && tr.id === mt)?.position || [])
              .some(k => k && k.time === mkTime);
            handleKeyframeDelete(mt, 'attach', mkTime);
            if (!moment) {
              setToast({
                message: t('mark.deleted', {
                  who: describeAttachRef(mt), t: fmtTime(mkTime),
                }) + t('toast.undoSuffix'),
                id: Date.now(),
              });
            }
          } : null}
          onPreview={(dir, dist) => setMarkPicker(p => (p ? { ...p, dir, dist } : p))}
          onCommit={(dir, dist) => {
            setMarkPicker(null);
            if (markPicker.group) commitMarkGroup(markPicker.group, markPicker.time, dir, dist);
            else commitMark(markPicker.trackId, markPicker.value, markPicker.time, dir, dist);
          }}
          onCancel={() => setMarkPicker(null)}
          bottom={contextBottom}
        />
      )}

      {/* ── Segment transition editor (ball/player movement between keys) ── */}
      {segmentEdit && (() => {
        const segTrack = keyframes.find(k => k.id === segmentEdit.trackId);
        const isBallSeg = segmentEdit.trackId.startsWith('ball:');
        const currentSeg = findSegment(segTrack, segmentEdit.from, segmentEdit.to);
        return (
          <SegmentModal
            title={t('edit.transition')}
            initial={{ ...DEFAULT_SEGMENT, ...currentSeg }}
            controls={[
              {
                type: 'choice', key: 'style', label: t('edit.style'),
                options: [
                  { key: 'straight', label: t('edit.straight') },
                  { key: 'curveLeft', label: t('edit.left') },
                  { key: 'curveRight', label: t('edit.right') },
                ],
              },
              { type: 'slider', key: 'curve', label: t('edit.curve'), min: 0, max: 1, step: 0.01,
                hint: t('edit.curveHint') },
              ...(isBallSeg ? [
                { type: 'slider', key: 'arch', label: t('edit.arch'), min: 0, max: 10, step: 0.1,
                  hint: t('edit.archHint') },
                { type: 'slider', key: 'archPeak', label: t('edit.archPeak'), min: 0, max: 1, step: 0.01,
                  hint: t('edit.archPeakHint') },
              ] : []),
            ]}
            onChange={handleSegmentChange}
            onClose={() => setSegmentEdit(null)}
            bottom={contextBottom}
          />
        );
      })()}

      {/* ── Keyframe height editor (clicked position keyframe) ── */}
      {keyframeEdit && (() => {
        const kfTrack = keyframes.find(k => k.id === keyframeEdit.trackId);
        const kfKey = kfTrack?.position?.find(k => k.time === keyframeEdit.time);
        if (!kfKey) return null;
        const height = kfKey.position?.[1] ?? 0;
        // Label the editor with the item name for clarity — in the coach's
        // language. "Home #7" over a Portuguese panel is a stray, and the item
        // words already live in the dictionary for the bench and the menus.
        const labelName = kfTrack.type === 'player'
          ? `${capWord(t(kfTrack.side === 'home' ? 'side.home' : 'side.away'))} #${kfTrack.index + 1}`
          : keyframeEdit.trackId.replace(/^(ball|cone|target|zone):/, (m, p) => `${capWord(t(`itm.${p}`))} `);
        const kfHeightTime = fmtTime(keyframeEdit.time);
        // Ripple mode: "step this moment" vs "step this moment AND everything
        // after it". The second one is the ripple drag (⌥-drag on a mouse, [ /
        // on a keyboard) — until now it had NO touch twin at all, so a phone
        // could re-time one key but never re-time the back half of a branch.
        const kfRipple = !!keyframeEdit.ripple;
        // Time stepper: nudge THIS key in time without touching the strip.
        // Collisions with another key are refused; moving past the end GROWS
        // the branch (exactly what a long pass does), so the stepper never
        // dead-ends into a keyboard chord you cannot press on a phone.
        const nudgeEditTime = (delta) => {
          const next = snapKeyTime(keyframeEdit.time + delta);
          if (next < 0) {
            setToast({ message: t('kf.startZero', { t: fmtTime(keyframeEdit.time) }), id: Date.now() });
            return;
          }
          const lanes = ['position', 'attach', 'graphic', 'visual', 'action'];
          const at = (lane, tt) => (kfTrack?.[lane] || []).some(k => Math.abs(k.time - tt) < 1e-9);
          if (kfRipple) {
            // The cut is planned and refused by the ripple mover itself (it is
            // cross-track on purpose: a pass catch is two keys in two tracks at
            // one instant, and the cut must never split it).
            const maxT = Math.max(0, ...lanes.flatMap(l => (kfTrack?.[l] || []).map(k => k.time)));
            const need = Math.max(next, maxT + (next - keyframeEdit.time));
            const stretched = need > timelineDuration;
            handleDragStart('undoVerb.rippleKeys');
            if (stretched) setTimelineDuration(Math.min(600, need));
            handleKeyframeRippleDrag(keyframeEdit.trackId, 'position', keyframeEdit.time, next);
            handleDragEnd();
            setPlayheadTime(next);
            // The branch got LONGER as well as the keys moving — a second change,
            // invisible unless the bar is in front of you, and the only sentence the
            // coach gets about it. It names the button that takes both back at once.
            if (stretched) setToast({ message: t('kf.rippleStretched', { t: kfHeightTime, end: fmtTime(Math.min(600, need)) })
                + t('toast.undoSuffix'), id: Date.now() });
            return;
          }
          // A MOMENT moves as a unit: a pass catch is a position key AND an
          // attach key at the same instant. Stepping only the position key
          // would split the catch — every lane keyed here travels with it,
          // and a key already sitting in ANY lane at the target is a collision.
          const clash = lanes.some(lane => at(lane, next));
          if (clash) {
            setToast({ message: t('kf.blockedAt', { t: fmtTime(next) }), id: Date.now() });
            return;
          }
          const stretched = next > timelineDuration;
          handleDragStart('undoVerb.moveKeyInTime');
          if (stretched) setTimelineDuration(next);
          for (const lane of lanes) {
            if (at(lane, keyframeEdit.time)) handleKeyframeTimeChange(keyframeEdit.trackId, lane, keyframeEdit.time, next);
          }
          handleDragEnd();
          setKeyframeEdit({ ...keyframeEdit, time: next });
          setPlayheadTime(next);
          // Same as the ripple above: the branch grew, and this is the only notice.
          if (stretched) setToast({ message: t('kf.movedStretched', { t: fmtTime(next) })
            + t('toast.undoSuffix'), id: Date.now() });
        };
        // Pitch stepper: move THIS key's spot, in metres, without dragging.
        // The measured twin of dragging the item — and the same 0.25/0.05
        // fine-coarse idea as a grabbed zone vertex, at item scale.
        const nudgeEditXZ = (dx, dz) => {
          handleKeyframeXZNudge(keyframeEdit.trackId, keyframeEdit.time, dx, dz);
        };
        // LAND ON THE SAME SECOND — the S verb, in the editor. This moment
        // jumps to the nearest instant ANOTHER item already keys, so the two
        // arrive together (the tutorials' timing exercise). Until now that was
        // a keyboard chord and nothing else. The button names the target
        // instant AND whose key it is, so the jump is a decision, not a guess.
        const kfSync = nearestOtherKeyTime(keyframeEdit.trackId, keyframeEdit.time);
        return (
          <SegmentModal
            title={t('edit.key')}
            description={`${labelName} · ${kfHeightTime}`}
            initial={{ height, ripple: kfRipple ? 'after' : 'here' }}
            controls={[
              { type: 'slider', key: 'height', label: t('itm.height'), min: 0, max: 10, step: 0.1 },
              {
                type: 'row',
                items: [
                  { label: '◂ 0.5', hint: t('edit.spotAlongHint'), onClick: (ev) => nudgeEditXZ(-(ev?.shiftKey ? 0.1 : 0.5), 0) },
                  { label: '0.5 ▸', hint: t('edit.spotAlongHint'), onClick: (ev) => nudgeEditXZ(ev?.shiftKey ? 0.1 : 0.5, 0) },
                  { label: '▴ 0.5', hint: t('edit.spotAcrossHint'), onClick: (ev) => nudgeEditXZ(0, -(ev?.shiftKey ? 0.1 : 0.5)) },
                  { label: '0.5 ▾', hint: t('edit.spotAcrossHint'), onClick: (ev) => nudgeEditXZ(0, ev?.shiftKey ? 0.1 : 0.5) },
                ],
              },
              // Time steps as a row of four, not two buttons that need a
              // keyboard modifier for the coarse step: on a phone ⇧ does not
              // exist, and "how long does this moment take" is exactly the
              // thing you want to retune without leaving the pitch.
              {
                type: 'row',
                items: [
                  { label: '◂◂ 1 s', hint: t('edit.timeEarlier1'), onClick: () => nudgeEditTime(-1) },
                  { label: '◂ 0.25', hint: t('edit.timeEarlierQ'), onClick: () => nudgeEditTime(-0.25) },
                  { label: '0.25 ▸', hint: t('edit.timeLaterQ'), onClick: () => nudgeEditTime(0.25) },
                  { label: '1 s ▸▸', hint: t('edit.timeLater1'), onClick: () => nudgeEditTime(1) },
                ],
              },
              ...(kfSync ? [{
                type: 'row',
                items: [{
                  label: t('edit.landOn', { t: fmtTime(kfSync.t), who: trackName(kfSync.trackId) }),
                  hint: t('edit.landOnHint', { who: trackName(kfSync.trackId) }),
                  onClick: () => nudgeEditTime(kfSync.t - keyframeEdit.time),
                }],
              }] : []),
              // What the four time steps above should carry. Two labels that
              // say exactly what happens, so the mode is visible BEFORE the
              // press — and the ripple cut finally has a touch twin.
              {
                type: 'choice', key: 'ripple', label: t('edit.stepsMove'),
                options: [
                  { key: 'here', label: t('edit.stepsHere') },
                  { key: 'after', label: t('edit.stepsAfter') },
                ],
              },
              {
                type: 'action', label: t('edit.deleteKey'), danger: true,
                onClick: () => {
                  handleKeyframeDelete(keyframeEdit.trackId, 'position', keyframeEdit.time);
                  setKeyframeEdit(null);
                },
              },
            ]}
            onChange={(config) => {
              if (config.height != null) handleKeyframeHeightChange(keyframeEdit.trackId, keyframeEdit.time, config.height);
              if (config.ripple !== undefined) setKeyframeEdit({ ...keyframeEdit, ripple: config.ripple === 'after' });
            }}
            onClose={() => setKeyframeEdit(null)}
            bottom={contextBottom}
          />
        );
      })()}

      {/* ── Arrow-colour editor for a graphic window (between ON and OFF keys) ── */}
      {graphicEdit && (() => {
        const gTrack = keyframes.find(k => k.id === graphicEdit.trackId);
        const onKey = (gTrack?.graphic || []).find(k => k.time === graphicEdit.start);
        const curColor = onKey?.color || 'default';
        // Item's default arrow colour (home blue / away red / ball gold / else purple)
        const gTrackId = graphicEdit.trackId || '';
        const defaultHex = gTrackId.startsWith('home:') ? '#3b82f6'
          : gTrackId.startsWith('away:') ? '#ef4444'
          : gTrackId.startsWith('ball:') ? '#f59e0b'
          : '#a78bfa';
        return (
          <SegmentModal
            title={t('edit.arrowColour')}
            initial={{ color: curColor }}
            controls={[{
              type: 'color', key: 'color', label: t('itm.color'),
              defaultHex,
            }]}
            onChange={handleGraphicColorChange}
            onClose={() => setGraphicEdit(null)}
            bottom={contextBottom}
          />
        );
      })()}

      {/* ── Ball properties (colour + visibility) — shown while a ball is selected ── */}
      {ballPanelOpen && selectedItem?.type === 'ball' && (
        <BallPanel
          ball={pitchState?.balls?.find(b => b.id === selectedItem.id)}
          onChange={(patch) => handleBallPropertyChange(selectedItem.id, patch)}
          onClose={() => setSelectedItem(null)}
          t={t}
          bottom={contextBottom}
        />
      )}

      {/* ── Zone-visuals editor for a visual window (radius / shape / vertex attachments) ── */}
      {zoneVisualEdit && (() => {
        const zTrack = keyframes.find(k => k.id === zoneVisualEdit.trackId);
        const vk = (zTrack?.visual || []).slice().sort((a, b) => a.time - b.time);
        const key = vk[zoneVisualEdit.keyIndex];
        const zoneId = zoneVisualEdit.trackId.replace(/^zone:/, '');
        const baseZone = pitchState?.zones?.find(z => z.id === zoneId);
        const zoneMode = baseZone?.mode || 'static';
        const val = key?.value || {};
        const windowStart = key ? key.time : 0;
        const windowEnd = zoneVisualEdit.keyIndex + 1 < vk.length ? vk[zoneVisualEdit.keyIndex + 1].time : timelineDuration;
        const up = (patch) => handleZoneVisualChange(zoneVisualEdit.trackId, zoneVisualEdit.keyIndex, patch);
        const fmtTimeShort = fmtTime; // fixed-width MM:SS.CC everywhere
        const rowLabel = (v) => {
          if (!v || v === 'none') return '—';
          if (v.startsWith('ball:')) return `Ball ${v.slice(5).replace(/^ball-/, '')}`;
          const [s, i] = v.split(':');
          return `${s === 'home' ? 'H' : 'A'}#${parseInt(i, 10) + 1}`;
        };
        const rowBtn = { flex: 1, padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #475569', background: '#1e293b', color: '#e2e8f0', cursor: 'pointer', textAlign: 'left' };
        const lbl = { color: '#94a3b8', fontSize: 11, fontWeight: 700, marginBottom: 4 };
        return (
          <div className="kv-zone-panel" style={{
            ...contextPanelPosition(contextBottom), width: 210,
            maxWidth: 'calc(100vw - 16px)',
            background: '#0f172a', border: '1px solid #334155', borderRadius: 12,
            padding: 0, zIndex: 250, boxShadow: '0 8px 30px rgba(0,0,0,0.5)', color: '#e2e8f0',
          }}>
            <ContextCloseBar label={t('zv.close')} onClick={() => setZoneVisualEdit(null)} />
            <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#c4b5fd' }}>{t('zv.title')}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtTimeShort(windowStart)} → {fmtTimeShort(windowEnd)}</span>
            </div>

            {zoneMode === 'circle' && (
              <div style={{ marginBottom: 10 }}>
                <div style={lbl}>{t('zv.radius', { m: Math.round((val.radius ?? 3) * 10) / 10 })}</div>
                <input type="range" min={0.5} max={12} step={0.1}
                  value={val.radius ?? 3}
                  onChange={(e) => up({ radius: parseFloat(e.target.value) })}
                  style={{ width: '100%', accentColor: '#a78bfa' }} />
              </div>
            )}

            {(zoneMode === 'static' || zoneMode === 'dynamic') && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" style={{ ...rowBtn, textAlign: 'center', background: '#312e81', borderColor: '#6366f1' }}
                    onClick={() => { setZoneEditRequest({ zoneId, ts: Date.now() }); setZoneVisualEdit(null); }}>
                    {t('zv.editShape')}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button type="button" style={{ ...rowBtn, textAlign: 'center', background: val.closed !== false ? '#312e81' : '#1e293b', borderColor: val.closed !== false ? '#6366f1' : '#475569' }}
                    onClick={() => up({ closed: val.closed === false })}>{t('zv.closed')}</button>
                  <button type="button" style={{ ...rowBtn, textAlign: 'center', background: val.closed === false ? '#312e81' : '#1e293b', borderColor: val.closed === false ? '#6366f1' : '#475569' }}
                    onClick={() => up({ closed: val.closed !== false ? false : true })}>{t('zv.open')}</button>
                </div>
              </div>
            )}

            {zoneMode === 'dynamic' && (
              <div style={{ marginBottom: 10 }}>
                {/* The DRAG twin is the short path — saying only "click Link"
                    hides a gesture half the users already know from the ball. */}
                <div style={lbl} title={t('zv.bindHint')}>{t('zv.vertexHint')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(val.points || []).map((p, pi) => {
                    const ref = (val.pointRefs || [])[pi]?.ref || null;
                    return (
                      <div key={pi} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span style={{ width: 52, fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>V{pi + 1}:</span>
                        <span style={{ flex: 1, fontSize: 11, color: ref ? '#a5b4fc' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {ref ? `→ ${rowLabel(ref)}` : t('zv.notAttached')}
                        </span>
                        <button type="button" style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, border: '1px solid #6366f1', background: '#312e81', color: '#e2e8f0', cursor: 'pointer', flexShrink: 0 }}
                          title={t('zv.linkTitle', { n: pi + 1 })}
                          onClick={() => { setZoneLinkRequest({ zoneId, pointIndex: pi, ts: Date.now() }); }}>{t('zv.link')}</button>
                        {ref && (
                          <button type="button" style={{ fontSize: 11, padding: '3px 6px', borderRadius: 5, border: '1px solid #475569', background: '#1e293b', color: '#94a3b8', cursor: 'pointer', flexShrink: 0 }}
                            title={t('zv.free', { n: pi + 1, who: trackName(ref) })}
                            aria-label={t('zv.free', { n: pi + 1, who: trackName(ref) })}
                            onClick={() => handleZonePointRefChange(zoneId, pi, null)}>✕</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 10 }}>
              <div style={lbl}>{t('itm.color')}</div>
              <input type="color" value={val.color || '#ffeb3b'}
                onChange={(e) => up({ color: e.target.value })}
                style={{ width: '100%', height: 26, border: 'none', borderRadius: 6, background: 'transparent', cursor: 'pointer' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={lbl}>{t('zv.opacity', { p: Math.round((val.opacity ?? 0.35) * 100) })}</div>
              {/* 0% is valid and hides the zone for this key's window */}
              <input type="range" min={0} max={0.9} step={0.05}
                value={val.opacity ?? 0.35}
                onChange={(e) => up({ opacity: parseFloat(e.target.value) })}
                style={{ width: '100%', accentColor: '#a78bfa' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>{t('zv.thickness', { n: val.thickness ?? 2 })}</div>
              <input type="range" min={1} max={10} step={1}
                value={val.thickness ?? 2}
                onChange={(e) => up({ thickness: parseInt(e.target.value, 10) })}
                style={{ width: '100%', accentColor: '#a78bfa' }} />
            </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modals (team selection + scenario selection) ── */}
      {teamModalTarget && (
        <TeamSelectionModal
          teams={teams}
          currentTeamId={teamModalTarget === 'home' ? homeTeamId : awayTeamId}
          onSelect={handleTeamSelect}
          onClose={() => setTeamModalTarget(null)}
          title={t(teamModalTarget === 'home' ? 'teams.selectHome' : 'teams.selectAway')}
          // The empty state used to say "create teams from the dashboard" — a
          // sentence pointing at another website. One button, straight here.
          onCreateTeam={() => { setTeamModalTarget(null); setShowTeamEditor(true); }}
        />
      )}
      {nameState && (
        <NameModal
          title={nameState.title}
          label={nameState.label}
          defaultValue={nameState.defaultValue}
          confirmLabel={nameState.confirmLabel}
          onSubmit={nameState.onSubmit}
          onCancel={nameState.onCancel}
        />
      )}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          onConfirm={confirmState.onConfirm}
          onCancel={confirmState.onCancel}
        />
      )}
      {showScenarioModal && (
        <ScenarioSelectionModal
          scenarios={scenarios}
          onSelect={handleScenarioSelect}
          onNew={handleNewScenario}
          onClose={() => setShowScenarioModal(false)}
          onRefresh={refreshScenarios}
          refreshing={scenariosLoading}
          onRename={handleScenarioRename}
          onDelete={handleScenarioDelete}
          onTogglePublic={handleScenarioTogglePublic}
        />
      )}
      {showPublicModal && (
        <PublicScenarioModal
          onSelect={handlePublicScenarioSelect}
          onClose={() => setShowPublicModal(false)}
          openMode={!account}
        />
      )}
      {showAccountModal && account && (
        <AccountModal
          account={account}
          isPremium={isPremium}
          monthlyPrice={pricing?.monthly}
          onClose={() => setShowAccountModal(false)}
          onLogout={() => { setShowAccountModal(false); handleLogout(); }}
          onUpgrade={() => { setShowAccountModal(false); handleUpgrade('monthly'); }}
          onManage={handleManageSubscription}
          onChangeName={handleAccountChangeName}
          onChangePassword={handleAccountChangePassword}
          onRequestDeletion={(pw, conf) => authAPI.requestDeletion(pw, conf)}
        />
      )}
      {showTeamEditor && account && (
        <TeamEditorModal
          teams={teams}
          account={account}
          homeTeamId={homeTeamId}
          awayTeamId={awayTeamId}
          onClose={() => setShowTeamEditor(false)}
          onTeamsChanged={refreshTeams}
          onApply={handleTeamApply}
        />
      )}
      {showHelpModal && <HelpModal onClose={() => setShowHelpModal(false)} />}

      {/* ── Login modal (shown when user clicks Login button) ── */}
      {showLoginModal && (
        <LoginScreen
          onLogin={handleLogin}
          onClose={() => setShowLoginModal(false)}
          // The footer's way in: the guides open HERE, in the app, instead of
          // the old link that navigated the whole page to the backend website.
          onOpenGuides={() => { setShowLoginModal(false); setShowHelpModal(true); }}
        />
      )}

      {/* ── Info modal (premium/upgrade/limitation messages) ── */}
      {infoModal && (
        <InfoModal
          {...infoModal}
          onDismiss={() => {
            const cb = infoModal.onDismiss;
            setInfoModal(null);
            if (cb) cb();
          }}
        />
      )}

      {/* ── Welcome modal (non-premium users on first load) ── */}
      {defaultScenarioNotice && !showWelcomeModal && !infoModal && (
        <InfoModal
          title={t('app.defaultScenarioTitle')}
          message={t(defaultScenarioNotice)}
          type="warning"
          dismissLabel={t('modal.gotIt')}
          onDismiss={() => setDefaultScenarioNotice(null)}
        />
      )}
      {showWelcomeModal && (
        <InfoModal
          title={t('modal.welcome.title')}
          message={t('modal.welcome.msg')}
          type="info"
          dismissLabel={t('modal.gotIt')}
          onDismiss={() => setShowWelcomeModal(false)}
        />
      )}

      {/* Only guidance and failures need a notice. Routine edit confirmations
          with Undo hints are already represented by the editor and toolbar. */}
      {toast && !isPlaying && !toast.message.includes(t('toast.undoSuffix')) && (
        <div className="kv-toast" role="status" aria-live="polite" style={{
          position: 'fixed',
          bottom: panelHeight + (showMaster ? MASTER_H : 0) + 14,
          left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(15, 23, 42, 0.97)', color: '#e2e8f0', border: '1px solid #334155',
          borderRadius: '12px', padding: '14px 16px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', pointerEvents: 'none',
        }}>
          <svg className="kv-toast-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7v1" />
          </svg>
          <div className="kv-toast-content">
            {macChords(toast.message).split(' · ').map((part, i) => (
                <span key={i} className={i === 0 ? 'kv-toast-heading' : 'kv-toast-detail'}>{part}</span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Root export — the whole app is wrapped in the language provider so every
 * component can translate via useI18n().
 */
export default function AppRoot() {
  return (
    <LanguageProvider>
      <App />
    </LanguageProvider>
  );
}
